import { deriveProjectAssets } from "../utils/projectAssets";
import { deliveryTimestampDisplay, formatRunDeliveryPeriod, operationalTimeZone, runDeliveryPeriod } from "../utils/operationalDeliveryDay";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useHistory } from "../context/HistoryContext";
import { DEFAULT_CONFIG } from "../components/config/defaultConfig";
import { getBatteryDetails } from "../services/api";
import { ReferenceArea } from "recharts";
import {
  Paper,
  Typography,
  Box,
  Stack,
  Grid,
  Snackbar,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControlLabel,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  Tabs,
  Tab,
  IconButton,
  SvgIcon,
  Dialog,
  AppBar,
  Toolbar,
  Popover,
  Button,
  Drawer,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MultilineChartRoundedIcon from "@mui/icons-material/MultilineChartRounded";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ElectricMeterOutlinedIcon from "@mui/icons-material/ElectricMeterOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import WindPowerOutlinedIcon from "@mui/icons-material/WindPowerOutlined";
import {
  LineChart,
  Line,
  ComposedChart,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
  Brush,
} from "recharts";
import {
  listRunsByProject,
  getRunSummary,
  fetchMarketMetrics,
  fetchDamPrices,
  DAM_MARKET_FORECAST_PROVIDER,
  fetchIntradayPrices,
  fetchXbidPrices,
  fetchXbidCompare,
  fetchWeatherForecast,
  fetchWindWeather,
  exportBessExcel,
  exportBidScheduleCsv,
  exportEconomicsOverviewExcel,
  getBidSchedule,
  getBatteryStateHistory,
  listBidSchedules,
  getLatestBatteryState,
  getBidScheduleSettings,
  saveBidScheduleSettings,
  updateBidScheduleSettings,
  refreshBidSchedule,
  updateBidScheduleRows,
  submitBidSchedule,
  reopenBidSchedule,
  triggerPvGenerationRun,
  fetchPvGenerationRuns,
  fetchPvGenerationRunSeries,
  fetchPvMeasuredGeneration,
  exportPvGenerationOverview,
  fetchBalancingData,
} from "../services/api";
import { useAuth } from "../context/AuthContext";
import { useDemo } from "../context/DemoContext";
import { useFile } from "../context/FileContext";
import { useEngine } from "../context/EngineContext";
import MaybeTooltip from "../components/MaybeTooltip";
import PvGenerationPanel from "../components/PvGenerationPanel";
import ForecastConvergencePanel from "../components/ForecastConvergencePanel";
import BalancingForecastPanel from "../components/BalancingForecastPanel";
import XbidForecastPanel from "../components/XbidForecastPanel";
import PublicationNote from "../components/PublicationNote";
import MarketInfoDialog from "../components/MarketInfoDialog";
import WindGenerationPanel from "../components/WindGenerationPanel";
import PvGenerationChart from "../components/pvGeneration/PvGenerationChart";
import { computeSeriesEnergyMwh } from "../components/pvGeneration/pvGenerationUtils";
import {
  getSupportedMarketZone,
  getSupportedMarketsLabel,
  isUnsupportedMarketZone,
  getDamForecastMarketsLabel,
  normalizeMarketZone,
  supportsDamForecast,
  SUPPORTED_MARKETS,
} from "../utils/marketSupport";
import { formatApiError } from "../utils/apiErrors";
import {
  getDemoBidWriteDisabledMessage,
  getDemoExportDisabledMessage,
  getDemoWindSystemDisabledMessage,
  getDemoMarketDataDisabledMessage,
} from "../utils/demoAccess";
import { SunCloudIcon, SolarBoltIcon } from "../components/customTabIcons";
import AppDatePicker from "../components/common/AppDatePicker";
import MarketBidBook from "../components/MarketBidBook";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";

function parseLocalDateTime(isoString) {
  if (!isoString || typeof isoString !== "string") return new Date();
  const match =
    isoString.match(
      /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/
    ) || [];
  const [, y, m, d, hh, mm, ss] = match;
  if (!y) return new Date(isoString);
  return new Date(
    Number(y),
    Number(m) - 1,
    Number(d),
    Number(hh),
    Number(mm),
    ss ? Number(ss) : 0
  );
}

function parseWallClockTimestamp(value) {
  if (!value || typeof value !== "string") return new Date(value);
  const match =
    value.match(
      /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/
    ) || [];
  const [, y, m, d, hh, mm, ss] = match;
  if (!y) return new Date(value);
  return new Date(
    Number(y),
    Number(m) - 1,
    Number(d),
    Number(hh),
    Number(mm),
    ss ? Number(ss) : 0
  );
}

function isValidTimeZone(timeZone) {
  if (!timeZone) return false;
  try {
    Intl.DateTimeFormat("en-US", { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}
function formatDateLocal(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function getTimeZoneDateParts(date, timeZone) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    hourCycle: "h23",
  });
  const parts = formatter.formatToParts(date);
  const map = Object.fromEntries(
    parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value])
  );
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour) % 24,
    minute: Number(map.minute),
    second: Number(map.second),
  };
}

function formatDateInTimeZone(date, timeZone) {
  const parts = getTimeZoneDateParts(date, timeZone);
  if (!parts) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
}

function getTimeZoneOffsetMs(date, timeZone) {
  const parts = getTimeZoneDateParts(date, timeZone);
  if (!parts) return 0;
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    date.getMilliseconds()
  );
  return asUtc - date.getTime();
}

function buildDateTimeInTimeZone(dateStr, timeZone, endOfDay = false) {
  const match = String(dateStr || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, year, month, day] = match;
  const targetUtc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0
  );
  let guess = targetUtc;
  for (let i = 0; i < 5; i += 1) {
    const offset = getTimeZoneOffsetMs(new Date(guess), timeZone);
    const nextGuess = targetUtc - offset;
    if (Math.abs(nextGuess - guess) < 1) {
      guess = nextGuess;
      break;
    }
    guess = nextGuess;
  }
  return new Date(guess);
}

function parseDateTimeInTimeZone(value, timeZone) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (!timeZone || !isValidTimeZone(timeZone)) {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (/[zZ]|[+-]\d{2}:\d{2}$/.test(raw)) {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const match =
    raw.match(
      /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d{1,3}))?$/
    ) || [];
  const [, year, month, day, hour, minute, second = "0", millisecond = "0"] = match;
  if (!year) {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const targetUtc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
    Number(millisecond.padEnd(3, "0"))
  );
  let guess = targetUtc;
  for (let i = 0; i < 5; i += 1) {
    const offset = getTimeZoneOffsetMs(new Date(guess), timeZone);
    const nextGuess = targetUtc - offset;
    if (Math.abs(nextGuess - guess) < 1) {
      guess = nextGuess;
      break;
    }
    guess = nextGuess;
  }
  return new Date(guess);
}

function buildCalendarRangeWindow(dateRange, timeZone) {
  const startKey = dateRange?.start || "";
  const endKey = dateRange?.end || "";
  if (!startKey || !endKey) {
    return { startDate: null, endDate: null, requestStart: "", requestEnd: "" };
  }

  const hasTimeZone = isValidTimeZone(timeZone);
  const startDate = hasTimeZone
    ? buildDateTimeInTimeZone(startKey, timeZone, false)
    : parseLocalDateTime(`${startKey}T00:00:00`);
  const endDate = hasTimeZone
    ? buildDateTimeInTimeZone(endKey, timeZone, true)
    : parseLocalDateTime(`${endKey}T23:59:59`);

  return {
    startDate,
    endDate,
    requestStart:
      hasTimeZone && startDate ? formatDateInTimeZone(startDate, "UTC") : startKey,
    requestEnd: hasTimeZone && endDate ? formatDateInTimeZone(endDate, "UTC") : endKey,
  };
}

function formatSimulationBoundaryDate(value, timeZone, treatExclusiveEnd = false) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return typeof value === "string" ? String(value).slice(0, 10) : "";
  }
  if (!timeZone) return formatDateLocal(date);

  if (treatExclusiveEnd) {
    const parts = getTimeZoneDateParts(date, timeZone);
    if (parts && parts.hour === 0 && parts.minute === 0 && parts.second === 0) {
      return formatDateInTimeZone(new Date(date.getTime() - 1), timeZone);
    }
  }

  return formatDateInTimeZone(date, timeZone);
}

function formatDisplayDate(value) {
  if (!value) return "";
  const parts = value.split("-");
  if (parts.length !== 3) return value;
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
}

function addDaysToDateKey(value, days) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  const [, year, month, day] = match;
  const next = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day) + days));
  return formatDateInTimeZone(next, "UTC");
}

function parseDateKeyToLocalDate(value, endOfDay = false) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, year, month, day] = match;
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0
  );
}

function buildTimestampDisplayParts(value) {
  if (typeof value === "string") {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (match) {
      const [, year, month, day, hour, minute] = match;
      return {
        dateKey: `${year}-${month}-${day}`,
        short: `${day}/${month}, ${hour}:${minute}`,
        long: `${day}/${month}/${year}, ${hour}:${minute}`,
      };
    }
  }
  const date = value instanceof Date ? value : value ? new Date(value) : null;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return { dateKey: "", short: "", long: "" };
  }
  const pad = (n) => String(n).padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hour = pad(date.getHours());
  const minute = pad(date.getMinutes());
  return {
    dateKey: `${year}-${month}-${day}`,
    short: `${day}/${month}, ${hour}:${minute}`,
    long: `${day}/${month}/${year}, ${hour}:${minute}`,
  };
}

function defaultDPlus1() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return formatDateLocal(d);
}
function defaultToday() {
  return formatDateLocal(new Date());
}
function buildDefaultRangeDates() {
  const today = defaultToday();
  const tomorrow = defaultDPlus1();
  const todayStart = parseLocalDateTime(`${today}T00:00:00`);
  const todayEnd = parseLocalDateTime(`${today}T23:59:59`);
  const tomorrowStart = parseLocalDateTime(`${tomorrow}T00:00:00`);
  const tomorrowEnd = parseLocalDateTime(`${tomorrow}T23:59:59`);
  return {
    value: {
      mix: [todayStart, todayEnd],
      generation: [todayStart, todayEnd],
      load: [todayStart, todayEnd],
      dam: [tomorrowStart, tomorrowEnd],
      weather: [tomorrowStart, tomorrowEnd],
      simulation: [tomorrowStart, tomorrowEnd],
    },
    month: {
      mix: todayStart,
      generation: todayStart,
      load: todayStart,
      dam: tomorrowStart,
      weather: tomorrowStart,
      simulation: tomorrowStart,
    },
  };
}

function buildRangeDatesFromInterval(startValue, endValue) {
  const fallback = defaultDPlus1();
  const startKey = startValue || endValue || fallback;
  const endKey = endValue || startValue || startKey;
  const startDate = parseLocalDateTime(`${startKey}T00:00:00`);
  const endDate = parseLocalDateTime(`${endKey}T23:59:59`);
  const [normalizedStart, normalizedEnd] =
    startDate <= endDate ? [startKey, endKey] : [endKey, startKey];
  const normalizedStartDate = parseLocalDateTime(`${normalizedStart}T00:00:00`);
  const normalizedEndDate = parseLocalDateTime(`${normalizedEnd}T23:59:59`);
  const makeRange = () => [normalizedStartDate, normalizedEndDate];
  return {
    start: normalizedStart,
    end: normalizedEnd,
    marketFilters: {
      mix: { start: normalizedStart, end: normalizedEnd },
      generation: { start: normalizedStart, end: normalizedEnd },
      load: { start: normalizedStart, end: normalizedEnd },
    },
    value: {
      mix: makeRange(),
      generation: makeRange(),
      load: makeRange(),
      dam: makeRange(),
      weather: makeRange(),
      simulation: makeRange(),
    },
    month: {
      mix: normalizedStartDate,
      generation: normalizedStartDate,
      load: normalizedStartDate,
      dam: normalizedStartDate,
      weather: normalizedStartDate,
      simulation: normalizedStartDate,
    },
  };
}

const WEATHER_MODE_OPTIONS = [
  { key: "forecast", label: "Forecast" },
  { key: "historical", label: "Historical" },
  { key: "both", label: "Both" },
];

const BALANCING_DATASET_OPTIONS = [
  { key: "imbalance_prices", label: "Imbalance Prices", unit: "EUR/MWh" },
  { key: "activated_balancing", label: "Activated Balancing", unit: "EUR/MWh" },
  { key: "reserve_prices", label: "Reserve Prices", unit: "EUR/MW" },
  { key: "reserve_amounts", label: "Reserve Amounts", unit: "MW" },
];
const BALANCING_DATASET_UNITS = Object.fromEntries(
  BALANCING_DATASET_OPTIONS.map((option) => [option.key, option.unit])
);
const BALANCING_SERIES_COLORS = [
  "#1565c0",
  "#00897b",
  "#ef6c00",
  "#8e24aa",
  "#c62828",
  "#2e7d32",
  "#455a64",
  "#f9a825",
];
const BALANCING_NON_NUMERIC_FIELDS = new Set([
  "delivery_time",
  "zone_code",
  "reserve_product",
  "entries",
  "direction",
  "reserve_type",
]);

function toBalancingNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function flattenBalancingActivatedRows(rows) {
  return (rows || []).flatMap((row) => {
    const { entries, ...base } = row || {};
    if (!Array.isArray(entries) || entries.length === 0) return [];
    return entries.map((entry) => ({ ...base, ...(entry || {}) }));
  });
}

function collectBalancingNumericFields(rows, dataset) {
  const excluded = new Set(BALANCING_NON_NUMERIC_FIELDS);
  if (dataset !== "activated_balancing") excluded.add("price");
  const stats = new Map();
  (rows || []).forEach((row) => {
    Object.entries(row || {}).forEach(([key, value]) => {
      if (excluded.has(key)) return;
      if (toBalancingNumber(value) == null) return;
      stats.set(key, (stats.get(key) || 0) + 1);
    });
  });
  return Array.from(stats.entries())
    .sort((left, right) => {
      if (right[1] !== left[1]) return right[1] - left[1];
      return left[0].localeCompare(right[0]);
    })
    .map(([key]) => key);
}

function humanizeBalancingKey(key) {
  return String(key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function getBalancingMetricSelectorLabel(dataset, fields) {
  if (!Array.isArray(fields) || fields.length <= 1) return "Metric";
  if (dataset === "reserve_prices" || dataset === "reserve_amounts") return "Direction";
  return "Metric";
}

function getBalancingGroup(row, dataset, includeZone) {
  if (dataset === "imbalance_prices") {
    const zone = row?.zone_code || "Unknown";
    return { id: zone, label: zone };
  }
  const zoneSuffix = includeZone && row?.zone_code ? ` · ${row.zone_code}` : "";
  if (dataset === "activated_balancing") {
    const type = row?.reserve_type || "Unknown";
    const direction = row?.direction ? ` ${row.direction}` : "";
    return { id: `${type}${direction}${zoneSuffix}`, label: `${type}${direction}${zoneSuffix}` };
  }
  const product = row?.reserve_product || "Unknown";
  return { id: `${product}${zoneSuffix}`, label: `${product}${zoneSuffix}` };
}

function buildBalancingChartData(rows, valueFields, dataset, includeZone = false) {
  const fields = Array.isArray(valueFields)
    ? valueFields.filter(Boolean)
    : valueFields
      ? [valueFields]
      : [];
  if (!fields.length) return { rows: [], series: [] };
  if (dataset === "imbalance_prices") {
    const series = fields
      .filter((field) =>
        (rows || []).some((row) => toBalancingNumber(row?.[field]) != null)
      )
      .sort((left, right) => left.localeCompare(right))
      .map((field, index) => ({
        id: field,
        label: humanizeBalancingKey(field),
        color: BALANCING_SERIES_COLORS[index % BALANCING_SERIES_COLORS.length],
      }));
    const byTime = new Map();
    (rows || []).forEach((row) => {
      const time = row?.delivery_time;
      const timeMs = time ? new Date(time).getTime() : NaN;
      if (!Number.isFinite(timeMs)) return;
      const bucket = byTime.get(timeMs) || { time, aggregations: {} };
      series.forEach((seriesItem) => {
        const value = toBalancingNumber(row?.[seriesItem.id]);
        if (value == null) return;
        const aggregate = bucket.aggregations[seriesItem.id] || { sum: 0, count: 0 };
        aggregate.sum += value;
        aggregate.count += 1;
        bucket.aggregations[seriesItem.id] = aggregate;
      });
      byTime.set(timeMs, bucket);
    });
    const chartRows = Array.from(byTime.entries())
      .sort((left, right) => left[0] - right[0])
      .map(([, bucket]) => {
        const nextRow = { time: bucket.time };
        series.forEach((seriesItem) => {
          const aggregate = bucket.aggregations[seriesItem.id];
          nextRow[seriesItem.id] = aggregate ? aggregate.sum / aggregate.count : null;
        });
        return nextRow;
      });
    return { rows: chartRows, series };
  }
  const [valueField] = fields;
  if (!valueField) return { rows: [], series: [] };
  const groups = new Map();
  const byTime = new Map();
  (rows || []).forEach((row) => {
    const time = row?.delivery_time;
    const timeMs = time ? new Date(time).getTime() : NaN;
    const value = toBalancingNumber(row?.[valueField]);
    if (!Number.isFinite(timeMs) || value == null) return;
    const group = getBalancingGroup(row, dataset, includeZone);
    groups.set(group.id, group);
    const bucket = byTime.get(timeMs) || { time, aggregations: {} };
    const aggregate = bucket.aggregations[group.id] || { sum: 0, count: 0 };
    aggregate.sum += value;
    aggregate.count += 1;
    bucket.aggregations[group.id] = aggregate;
    byTime.set(timeMs, bucket);
  });
  const series = Array.from(groups.values())
    .sort((left, right) => String(left.label).localeCompare(String(right.label)))
    .map((group, index) => ({
      ...group,
      color: BALANCING_SERIES_COLORS[index % BALANCING_SERIES_COLORS.length],
    }));
  const chartRows = Array.from(byTime.entries())
    .sort((left, right) => left[0] - right[0])
    .map(([, bucket]) => {
      const nextRow = { time: bucket.time };
      series.forEach((seriesItem) => {
        const aggregate = bucket.aggregations[seriesItem.id];
        nextRow[seriesItem.id] = aggregate ? aggregate.sum / aggregate.count : null;
      });
      return nextRow;
    });
  return { rows: chartRows, series };
}
function pickField(obj, names) {
  if (!obj) return null;
  for (const name of names) {
    if (obj[name] != null) return obj[name];
  }
  return null;
}

function formatDashboardError(err, fallback = "Failed to complete request.") {
  const raw = String(err?.message ?? err ?? "");
  const message = formatApiError(err, fallback);
  if (/open-meteo/i.test(raw) || /open-meteo/i.test(message)) {
    return "Weather data failed to load. Please try again.";
  }
  return message;
}

function formatPvGenerationError(err) {
  const status = Number(err?.status || 0);
  const raw = String(err?.message ?? "").trim().toLowerCase();
  if (status === 404 || status === 501 || status === 502 || status === 503 || raw === "not found") {
    return "Not Found";
  }
  return formatDashboardError(err, "Failed to load PV generation data.");
}

function formatSummaryMetricNumber(value, decimals = 2) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  return Number(value).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function formatCurrency(value, decimals = 2) {
  if (value == null || !Number.isFinite(Number(value))) return "N/A";
  return `${Number(value).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} EUR`;
}

function formatEnergyMWh(value, decimals = 3) {
  if (value == null || !Number.isFinite(Number(value))) return "N/A";
  return `${Number(value).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} MWh`;
}

function formatEnergyKWh(value, decimals = 2) {
  if (value == null || !Number.isFinite(Number(value))) return "N/A";
  return `${Number(value).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} kWh`;
}

function formatPercentValue(value, decimals = 2) {
  if (value == null || !Number.isFinite(Number(value))) return "N/A";
  return `${Number(value).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}%`;
}

function formatDateTimeValue(value) {
  if (!value) return "N/A";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB");
}

function formatDateValue(value) {
  if (!value) return "N/A";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB");
}

function formatBatteryHealthError(err) {
  const message = formatDashboardError(err, "").trim();
  if (!message) return "Battery health backend is not available right now.";
  return `Battery health backend error: ${message}`;
}

function formatBidDayLabel(value) {
  if (!value) return "N/A";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return String(value);
  return `${match[3]}/${match[2]}/${match[1]}`;
}

function sortSchedulesLatestFirst(a, b) {
  const dayCompare = String(b?.bid_day || "").localeCompare(String(a?.bid_day || ""));
  if (dayCompare !== 0) return dayCompare;
  const submittedTime = new Date(b?.submitted_at || b?.updated_at || 0).getTime() -
    new Date(a?.submitted_at || a?.updated_at || 0).getTime();
  if (submittedTime !== 0) return submittedTime;
  return new Date(b?.updated_at || 0).getTime() - new Date(a?.updated_at || 0).getTime();
}

function getScheduleSourceRunTimestamp(schedule, runs = []) {
  const sourceRunId = schedule?.source_run_id;
  const matchedRun = sourceRunId
    ? runs.find((run) => normalizeRunId(run) === String(sourceRunId))
    : null;

  return (
    matchedRun?.started_at ||
    matchedRun?.created_at ||
    schedule?.source_run_started_at ||
    schedule?.source_run_created_at ||
    schedule?.source_run_at ||
    null
  );
}

function getScheduleFinancialTotals(schedule) {
  const totals = schedule?.totals || {};
  const parseFiniteNumber = (value) =>
    value != null && Number.isFinite(Number(value)) ? Number(value) : null;
  const hasBackendTotals = Object.keys(totals).length > 0;
  const isSubmitted = schedule?.status === "submitted";
  const showActualFinancials = isSubmitted && hasBackendTotals;
  const settledSellRevenueEur =
    showActualFinancials ? parseFiniteNumber(totals?.actual_sell_revenue_eur) : null;
  const settledBuyCostEur =
    showActualFinancials ? parseFiniteNumber(totals?.actual_buy_cost_eur) : null;
  const settledNetRevenueEur =
    showActualFinancials ? parseFiniteNumber(totals?.actual_net_revenue_eur) : null;
  const grossMarketProfitEur = showActualFinancials
    ? parseFiniteNumber(totals?.gross_market_profit_eur)
    : null;
  const degradationMethod = showActualFinancials
    ? normalizeDegradationMethod(
        totals?.degradation_method,
        getDefaultBidStrategySettings().degradationMethod
      )
    : null;
  const degradationBasisEnergyMwh = showActualFinancials
    ? parseFiniteNumber(totals?.degradation_basis_energy_mwh)
    : null;
  const degradationCostEur = showActualFinancials
    ? parseFiniteNumber(totals?.degradation_cost_eur)
    : null;
  const marketFeeEur = showActualFinancials ? parseFiniteNumber(totals?.market_fee_eur) : null;
  const gridImportFeeEur = showActualFinancials
    ? parseFiniteNumber(totals?.grid_import_fee_eur)
    : null;
  const gridExportFeeEur = showActualFinancials
    ? parseFiniteNumber(totals?.grid_export_fee_eur)
    : null;
  const operatingCostsEur = showActualFinancials
    ? parseFiniteNumber(totals?.operating_costs_eur)
    : null;
  const netOperatingProfitEur = showActualFinancials
    ? parseFiniteNumber(totals?.net_operating_profit_eur)
    : null;
  const netEconomicProfitEur = showActualFinancials
    ? parseFiniteNumber(totals?.net_economic_profit_eur)
    : null;
  const acceptedBuyEnergyMwh = showActualFinancials
    ? parseFiniteNumber(totals?.accepted_buy_energy_mwh)
    : null;
  const acceptedSellEnergyMwh = showActualFinancials
    ? parseFiniteNumber(totals?.accepted_sell_energy_mwh)
    : null;
  const acceptedTradedEnergyMwh = showActualFinancials
    ? parseFiniteNumber(totals?.accepted_traded_energy_mwh)
    : null;

  return {
    hasBackendTotals,
    showActualFinancials,
    settledSellRevenueEur,
    settledBuyCostEur,
    settledNetRevenueEur,
    grossMarketProfitEur,
    degradationMethod,
    degradationBasisEnergyMwh,
    degradationCostEur,
    marketFeeEur,
    gridImportFeeEur,
    gridExportFeeEur,
    operatingCostsEur,
    netOperatingProfitEur,
    netEconomicProfitEur,
    acceptedBuyEnergyMwh,
    acceptedSellEnergyMwh,
    acceptedTradedEnergyMwh,
  };
}

function normalizeBatteryHealthState(payload) {
  if (!payload || typeof payload !== "object") return null;
  const parseFiniteNumber = (value) =>
    value != null && Number.isFinite(Number(value)) ? Number(value) : null;
  return {
    project_id: payload.project_id != null ? String(payload.project_id) : "",
    asset_id: payload.asset_id != null ? String(payload.asset_id) : "",
    market: payload.market != null ? String(payload.market) : "",
    state_day: payload.state_day != null ? String(payload.state_day) : "",
    schedule_id: payload.schedule_id != null ? String(payload.schedule_id) : "",
    soh_start_pct: parseFiniteNumber(payload.soh_start_pct),
    soh_end_pct: parseFiniteNumber(payload.soh_end_pct),
    daily_soh_loss_pct: parseFiniteNumber(payload.daily_soh_loss_pct),
    nominal_energy_kwh: parseFiniteNumber(payload.nominal_energy_kwh),
    available_energy_kwh: parseFiniteNumber(payload.available_energy_kwh),
    actual_throughput_kwh: parseFiniteNumber(payload.actual_throughput_kwh),
    ending_actual_soc_pct: parseFiniteNumber(payload.ending_actual_soc_pct),
    created_at: payload.created_at != null ? String(payload.created_at) : "",
    updated_at: payload.updated_at != null ? String(payload.updated_at) : "",
  };
}

const BATTERY_HEALTH_HISTORY_METRICS = [
  {
    key: "soh_end_pct",
    label: "State of Health (%)",
    decimals: [2, 4],
    valueFormatter: (value) => formatPercentValue(value, 4),
    axisFormatter: (value) =>
      Number.isFinite(Number(value))
        ? Number(value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 4 })
        : value,
  },
  {
    key: "available_energy_kwh",
    label: "Available Energy (kWh)",
    decimals: [2, 2],
    valueFormatter: (value) => formatEnergyKWh(value, 2),
    axisFormatter: (value) =>
      Number.isFinite(Number(value))
        ? Number(value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : value,
  },
  {
    key: "daily_soh_loss_pct",
    label: "Daily SOH Loss (%)",
    decimals: [4, 6],
    valueFormatter: (value) => formatPercentValue(value, 6),
    axisFormatter: (value) =>
      Number.isFinite(Number(value))
        ? Number(value).toLocaleString("en-GB", { minimumFractionDigits: 4, maximumFractionDigits: 6 })
        : value,
  },
  {
    key: "actual_throughput_kwh",
    label: "Actual Throughput (kWh)",
    decimals: [2, 2],
    valueFormatter: (value) => formatEnergyKWh(value, 2),
    axisFormatter: (value) =>
      Number.isFinite(Number(value))
        ? Number(value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : value,
  },
];

function enumerateDateKeys(startDateKey, endDateKey, maxDays = 180) {
  const start = parseDateKeyToLocalDate(startDateKey);
  const end = parseDateKeyToLocalDate(endDateKey);
  if (!start || !end) return [];

  const from = start <= end ? start : end;
  const to = start <= end ? end : start;
  const dates = [];
  const cursor = new Date(from);

  while (cursor <= to && dates.length < maxDays) {
    dates.push(formatDateLocal(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
}

function buildChartData(runResult, assetKey, deliveryTimeZone) {
  if (!runResult) {
    return {
      data: [],
      start: null,
      end: null,
      resolution: null,
      available: [],
      assets: [],
      hasExplicitTimeIndex: false,
      startDateKey: "",
      endDateKey: "",
    };
  }
  const base = runResult.result || runResult.data || runResult;
  const startISO =
    base.start ||
    base.time_start ||
    base.meta?.start ||
    base.started_at;
  const resolutionStr =
    base.resolution ||
    base.time_resolution ||
    base.meta?.resolution ||
    "15min";
  // locate energy flows block
  let energyFlows = base.energy_flows || base.energyFlows || null;
  let tsSource =
    base.energy_flows ||
    base.timeseries ||
    base.result?.timeseries ||
    base.data?.timeseries ||
    base.output?.timeseries ||
    base.timeseries_output ||
    base;
  let assetKeys = [];
  if (energyFlows && typeof energyFlows === "object") {
    assetKeys = Object.keys(energyFlows);
    if (assetKeys.length > 0) {
      const chosenKey = assetKey && assetKeys.includes(assetKey) ? assetKey : assetKeys[0];
      tsSource = energyFlows[chosenKey] || tsSource;
      assetKey = chosenKey;
    }
  }
  const seriesBlock = tsSource?.series || tsSource;
  const timeIndex = tsSource?.time_index || tsSource?.timeIndex || [];
  const hasExplicitTimeIndex = Array.isArray(timeIndex) && timeIndex.length > 0;
  const pv = pickField(seriesBlock, ["pv", "PV", "PV_gen", "pv_gen", "pv_generation"]) || [];
  const pcharge =
    pickField(seriesBlock, ["p_charge", "charge", "P_charge"]) || [];
  const pvcharge =
    pickField(seriesBlock, ["pv_charge", "PV_charge"]) || [];
  const gridcharge =
    pickField(seriesBlock, ["grid_charge", "Grid_charge"]) || [];
  const pdischarge =
    pickField(seriesBlock, ["pdischarge", "p_discharge", "discharge", "P_discharge"]) || [];
  const psell =
    pickField(seriesBlock, ["psell", "p_sell", "sell", "export", "P_sell"]) || [];
  const price =
    pickField(seriesBlock, ["price", "prices", "prices_eur_mwh", "LMP", "lmp"]) || [];
  const soc =
    pickField(seriesBlock, ["soc", "SoC", "SOC"]) || [];
  const n = Math.max(
    pv.length,
    pcharge.length,
    pvcharge.length,
    gridcharge.length,
    pdischarge.length,
    psell.length,
    price.length,
    timeIndex.length,
    soc.length,
  );
  const data = [];
  for (let i = 0; i < n; i += 1) {
    const rawTimeValue = timeIndex[i] != null ? timeIndex[i] : null;
    const t =
      rawTimeValue != null
        ? (deliveryTimeZone ? parseDateTimeInTimeZone(rawTimeValue, deliveryTimeZone) : parseWallClockTimestamp(rawTimeValue)) || new Date(NaN)
        : new Date((deliveryTimeZone ? parseDateTimeInTimeZone(startISO, deliveryTimeZone) : parseLocalDateTime(startISO))?.getTime() + i * 15 * 60 * 1000);
    const display = deliveryTimeZone
      ? deliveryTimestampDisplay(t, deliveryTimeZone)
      : buildTimestampDisplayParts(rawTimeValue != null ? rawTimeValue : t);
    data.push({
      time: t,
      timeMs: Number.isFinite(t.getTime()) ? t.getTime() : null,
      timeRaw: rawTimeValue,
      timeDate: display.dateKey,
      timeDisplayShort: display.short,
      timeDisplayLong: display.long,
      pv: pv[i] ?? null,
      pcharge: pcharge[i] ?? null,
      pvcharge: pvcharge[i] ?? null,
      gridcharge: gridcharge[i] ?? null,
      pdischarge: pdischarge[i] ?? null,
      psell: psell[i] ?? null,
      price: price[i] ?? null,
      soc: soc[i] ?? null,
    });
  }
  const available = seriesBlock ? Object.keys(seriesBlock) : [];
  const start = data.length ? data[0].time : null;
  const end = data.length ? data[data.length - 1].time : null;
  const startDateKey = data.length ? data[0].timeDate || "" : "";
  const endDateKey = data.length ? data[data.length - 1].timeDate || "" : "";
  return {
    data,
    start,
    end,
    startDateKey,
    endDateKey,
    resolution: resolutionStr,
    available,
    assets: assetKeys,
    hasExplicitTimeIndex,
  };
}
const toNumberSafe = (val) => {
  if (val === null || val === undefined) return null;
  const cleaned =
    typeof val === "string" ? val.replace(/[^0-9+.\-eE]/g, "") : val;
  const num = typeof cleaned === "string" ? Number.parseFloat(cleaned) : Number(cleaned);
  return Number.isFinite(num) ? num : null;
};
// Keep native timestamps; no bucketing
const hourKey = (value) => {
  const d = value instanceof Date ? value : value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime()) ? d.getTime() : null;
};
const normalizeEntsoeLabel = (label) =>
  typeof label === "string" ? label.split("__")[0].trim() : "";

const MARKET_OPTION_LABELS = {
  GR: "Greece (GR)",
  DE: "Germany (DE)",
  FR: "France (FR)",
  NL: "Netherlands (NL)",
  HU: "Hungary (HU)",
  BG: "Bulgaria (BG)",
};

const isConsumptionField = (label) =>
  typeof label === "string" && label.includes("__Actual Consumption");

function mapGenerationFieldToMetric(field, market) {
  const label = normalizeEntsoeLabel(field);
  if (!label || label === "date" || label === "total_generation") return null;

  switch (label) {
    case "Solar":
      return "fuel_mix_solar";
    case "Wind":
      return "fuel_mix_wind";
    case "Wind Onshore":
      return market === "GR" ? "fuel_mix_wind" : "fuel_mix_wind_onshore";
    case "Wind Offshore":
      return "fuel_mix_wind_offshore";
    case "Fossil Gas":
      return "fuel_mix_fossil_gas";
    case "Fossil Oil":
      return "fuel_mix_fossil_oil";
    case "Fossil Brown coal/Lignite":
      return market === "GR"
        ? "fuel_mix_lignite"
        : "fuel_mix_fossil_brown_coal_or_lignite";
    case "Hydro Pumped Storage":
      return "fuel_mix_hydro_pumped_storage";
    case "Hydro Water Reservoir":
      return "fuel_mix_hydro_water_reservoir";
    case "Biomass":
      return "fuel_mix_biomass";
    case "Hydro Run-of-river and poundage":
      return "fuel_mix_hydro_run_of_river_and_poundage";
    case "Nuclear":
      return "fuel_mix_nuclear";
    case "Waste":
      return "fuel_mix_waste";
    case "Fossil Hard coal":
      return "fuel_mix_fossil_hard_coal";
    case "Energy storage":
      return "fuel_mix_energy_storage";
    case "Fossil Coal-derived gas":
      return "fuel_mix_fossil_coal_derived_gas";
    case "Geothermal":
      return "fuel_mix_geothermal";
    case "Other":
      return "fuel_mix_other";
    case "Other renewable":
      return "fuel_mix_other_renewable";
    default:
      return null;
  }
}

function normalizeEntsoeGenerationSeries(response, market) {
  const rows = Array.isArray(response?.generation) ? response.generation : [];
  return rows
    .map((row) => {
      const time = row?.date ? new Date(row.date) : null;
      if (!time || Number.isNaN(time.getTime())) return null;
      const next = { time };
      Object.entries(row).forEach(([key, value]) => {
        if (key === "date") return;
        if (key === "total_generation") {
          next[RT_SYSTEM_GENERATION_METRIC] = toNumberSafe(value);
          return;
        }
        if (isConsumptionField(key)) return;
        const metric = mapGenerationFieldToMetric(key, market);
        if (!metric) return;
        next[metric] = toNumberSafe(value);
      });
      return next;
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time);
}

function normalizeEntsoeForecastSeries(response, market) {
  const map = new Map();

  const generationRows = Array.isArray(response?.generation_forecast)
    ? response.generation_forecast
    : [];
  generationRows.forEach((row) => {
    const time = row?.date ? new Date(row.date) : null;
    if (!time || Number.isNaN(time.getTime())) return;
    const key = time.getTime();
    const next = map.get(key) || { time };
    next.system_gen_forecast = toNumberSafe(row?.generation_forecast);
    map.set(key, next);
  });

  const windSolarRows = Array.isArray(response?.wind_and_solar_forecast)
    ? response.wind_and_solar_forecast
    : [];
  windSolarRows.forEach((row) => {
    const time = row?.date ? new Date(row.date) : null;
    if (!time || Number.isNaN(time.getTime())) return;
    const key = time.getTime();
    const next = map.get(key) || { time };
    next.solar_forecast = toNumberSafe(row?.Solar);
    if (market === "GR") {
      next.wind_forecast = toNumberSafe(row?.["Wind Onshore"] ?? row?.Wind);
    } else {
      next.wind_onshore_forecast = toNumberSafe(row?.["Wind Onshore"]);
      next.wind_offshore_forecast = toNumberSafe(row?.["Wind Offshore"]);
    }
    map.set(key, next);
  });

  return Array.from(map.values()).sort((a, b) => a.time - b.time);
}

function normalizeEntsoeLoadSeries(response) {
  const rows = Array.isArray(response?.load_and_forecast)
    ? response.load_and_forecast
    : [];
  return rows
    .map((row) => {
      const time = row?.date ? new Date(row.date) : null;
      if (!time || Number.isNaN(time.getTime())) return null;
      return {
        time,
        real_time_system_load: toNumberSafe(row?.["Actual Load"]),
        system_load_forecast: toNumberSafe(row?.["Forecasted Load"]),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time);
}

function normalizeWeatherSeries(response) {
  const rows = response?.weather || response || [];
  return rows
    .map((row) => {
      const tVal = row.time || row.datetime || row.date || row.timestamp;
      const time = tVal ? new Date(tVal) : null;
      if (!time || Number.isNaN(time.getTime())) return null;
      const ghi = toNumberSafe(row.ghi ?? row.GHI ?? row.irr_ghi ?? row.irradiance_ghi);
      const dni = toNumberSafe(row.dni ?? row.DNI ?? row.irr_dni ?? row.irradiance_dni);
      const dhi = toNumberSafe(row.dhi ?? row.DHI ?? row.irr_dhi ?? row.irradiance_dhi);
      const temp = toNumberSafe(row.temp ?? row.temperature);
      const humidity = toNumberSafe(row.humidity ?? row.rh ?? row.relative_humidity);
      const wind = toNumberSafe(row.wind_speed ?? row.wind ?? row.wind_speed_ms);
      return { time, ghi, dni, dhi, temp, humidity, wind_speed: wind };
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time);
}
// Weather inputs consumed by the wind generation model (wind speed at 10 m,
// upper-level wind, 2 m temperature, surface pressure). Pressure is converted
// from Pa to hPa for display.
function normalizeWindWeatherSeries(response) {
  const rows = response?.weather || response || [];
  return rows
    .map((row) => {
      const tVal = row.time || row.datetime || row.date || row.timestamp;
      const time = tVal ? new Date(tVal) : null;
      if (!time || Number.isNaN(time.getTime())) return null;
      const pressurePa = toNumberSafe(row.pressure ?? row.pressure_pa);
      return {
        time,
        wind_speed: toNumberSafe(row.wind_speed ?? row.wind ?? row.wind_speed_ms),
        wind_speed_upper: toNumberSafe(row.wind_speed_upper),
        temp: toNumberSafe(row.temp ?? row.temperature),
        pressure: pressurePa != null ? pressurePa / 100 : null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time);
}
const WIND_WEATHER_KEYS = ["wind_speed", "wind_speed_upper", "temp", "pressure"];
function mergeForecastActualWind(forecastRows, actualRows) {
  const map = new Map();
  const addRows = (rows, suffix) => {
    (rows || []).forEach((row) => {
      const t = row?.time instanceof Date ? row.time.getTime() : new Date(row?.time || "").getTime();
      if (!Number.isFinite(t)) return;
      const entry = map.get(t) || { time: new Date(t) };
      WIND_WEATHER_KEYS.forEach((key) => {
        entry[`${key}_${suffix}`] = row?.[key] ?? null;
      });
      map.set(t, entry);
    });
  };
  addRows(forecastRows, "forecast");
  addRows(actualRows, "actual");
  return Array.from(map.values()).sort((a, b) => a.time - b.time);
}
const FORECAST_FUTURE_LIMIT_DAYS = 11;
const FORECAST_PAST_LIMIT_DAYS = 70;
function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
function buildMonthDays(baseDate) {
  const year = baseDate.getFullYear();
  const month = baseDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const startWeekday = firstDay.getDay(); // 0 = Sunday
  const totalDays = daysInMonth(year, month);
  const days = [];
  for (let i = 0; i < startWeekday; i += 1) {
    days.push(null);
  }
  for (let d = 1; d <= totalDays; d += 1) {
    days.push(new Date(year, month, d));
  }
  return { days, year, month };
}
const RANGE_START = new Date(2024, 0, 1);
const RANGE_END = new Date(2035, 11, 31, 23, 59, 59, 999);
function SimpleRangeCalendar({
  value,
  onChange,
  month,
  onMonthChange,
  highlightColor = "rgba(33, 150, 243, 0.2)",
  edgeColor = "rgba(33, 150, 243, 0.55)",
  onClose,
  minDate: minDateProp = RANGE_START,
  maxDate: maxDateProp = RANGE_END,
}) {
  const [draftRange, setDraftRange] = useState(value || [null, null]);
  const [hoverDate, setHoverDate] = useState(null);
  const [start, end] = draftRange;
  const scrollRef = useRef(null);
  const navScrollRef = useRef(null);
  const monthSectionRefs = useRef([]);
  const scrollFrameRef = useRef(null);
  const activeIndexRef = useRef(0);
  const shouldAutoAlignMonthRef = useRef(false);
  const clampDate = (d) => {
    if (!d) return null;
    const min = minDateProp ? new Date(minDateProp) : new Date(RANGE_START);
    const max = maxDateProp ? new Date(maxDateProp) : new Date(RANGE_END);
    if (d < min) return new Date(min);
    if (d > max) return new Date(max);
    return d;
  };
  const months = useMemo(() => {
    const arr = [];
    const cursor = new Date(RANGE_START.getFullYear(), RANGE_START.getMonth(), 1);
    while (cursor <= RANGE_END) {
      arr.push(new Date(cursor));
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return arr;
  }, []);
  const getMonthIndex = useCallback((date) => {
    if (!date) return 0;
    return months.findIndex(
      (m) => m.getFullYear() === date.getFullYear() && m.getMonth() === date.getMonth()
    );
  }, [months]);
  const monthFormatter = useMemo(
    () => new Intl.DateTimeFormat("en", { month: "long", year: "numeric" }),
    []
  );
  const monthMeta = useMemo(
    () =>
      months.map((monthDate, idx) => {
        const { days, year, month: m } = buildMonthDays(monthDate);
        const label = monthFormatter.format(new Date(year, m, 1));
        return { days, year, m, label, idx };
      }),
    [months, monthFormatter]
  );
  const normalizedStart = clampDate(start);
  const normalizedEnd = clampDate(end);
  const previewEnd =
    normalizedStart &&
    !normalizedEnd &&
    hoverDate &&
    hoverDate >= (minDateProp ? new Date(minDateProp) : RANGE_START) &&
    hoverDate <= (maxDateProp ? new Date(maxDateProp) : RANGE_END)
      ? clampDate(hoverDate)
      : null;
  const activeIndex = useMemo(() => {
    const base = normalizedStart || month || new Date();
    const idx = getMonthIndex(base);
    return idx >= 0 ? idx : 0;
  }, [normalizedStart, month, getMonthIndex]);
  const [currentMonthIndex, setCurrentMonthIndex] = useState(activeIndex);
  const isSameDay = (a, b) =>
    a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const inRange = (day) => {
    if (!normalizedStart || !day) return false;
    const endPoint = normalizedEnd || previewEnd;
    if (!endPoint) return false;
    const [from, to] = normalizedStart <= endPoint
      ? [normalizedStart, endPoint]
      : [endPoint, normalizedStart];
    return day >= from && day <= to;
  };
  const isOutOfRange = (day) => {
    if (!day) return true;
    const min = minDateProp ? new Date(minDateProp) : RANGE_START;
    const max = maxDateProp ? new Date(maxDateProp) : RANGE_END;
    return day < min || day > max;
  };
  const toStartOfDay = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const handleToday = () => {
    const today = toStartOfDay(new Date());
    const clamped = clampDate(today);
    if (!clamped) return;
    const nextRange = [clamped, clamped];
    const monthIndex = getMonthIndex(clamped);
    shouldAutoAlignMonthRef.current = true;
    setCurrentMonthIndex(monthIndex);
    setDraftRange(nextRange);
    onChange(nextRange);
    if (onMonthChange) onMonthChange(new Date(clamped));
  };
  const handleDayClick = (day) => {
    if (!day || isOutOfRange(day)) return;
    const safeDay = clampDate(day);
    const pushChange = (nextRange) => {
      setDraftRange(nextRange);
      onChange(nextRange);
    };
    if (!normalizedStart || (normalizedStart && normalizedEnd)) {
      setHoverDate(null);
      const nextRange = [safeDay, null];
      shouldAutoAlignMonthRef.current = true;
      setCurrentMonthIndex(getMonthIndex(safeDay));
      pushChange(nextRange);
      if (onMonthChange) onMonthChange(new Date(safeDay));
      return;
    }
    const s = normalizedStart;
    const nextRange = safeDay < s ? [safeDay, s] : [s, safeDay];
    setHoverDate(null);
    shouldAutoAlignMonthRef.current = true;
    setCurrentMonthIndex(getMonthIndex(safeDay));
    pushChange(nextRange);
    if (onMonthChange) onMonthChange(new Date(safeDay));
    if (onClose) onClose();
  };
  const goToMonthIndex = useCallback(
    (index, notifyParent = true) => {
      const safeIndex = Math.max(0, Math.min(index, months.length - 1));
      shouldAutoAlignMonthRef.current = true;
      setCurrentMonthIndex(safeIndex);
      if (notifyParent && onMonthChange && months[safeIndex]) {
        onMonthChange(new Date(months[safeIndex]));
      }
    },
    [months, onMonthChange]
  );
  const monthNavItems = useMemo(
    () =>
      months.map((monthDate, idx) => ({
        idx,
        label: new Intl.DateTimeFormat("en", { month: "short" }).format(monthDate),
        year: monthDate.getFullYear(),
        isYearStart: idx === 0 || monthDate.getMonth() === 0,
      })),
    [months]
  );
  const syncMonthIndexFromScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const viewportTop = scrollRef.current.scrollTop;
    const viewportBottom = viewportTop + scrollRef.current.clientHeight;
    const refs = monthSectionRefs.current;
    if (!refs.length) return;
    let nextIndex = activeIndexRef.current;
    let bestVisibleHeight = -1;
    for (let i = 0; i < refs.length; i += 1) {
      const node = refs[i];
      if (!node) continue;
      const top = node.offsetTop;
      const bottom = top + node.offsetHeight;
      const visibleTop = Math.max(top, viewportTop);
      const visibleBottom = Math.min(bottom, viewportBottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      if (visibleHeight > bestVisibleHeight) {
        bestVisibleHeight = visibleHeight;
        nextIndex = i;
      }
    }
    if (nextIndex !== activeIndexRef.current) {
      activeIndexRef.current = nextIndex;
      setCurrentMonthIndex(nextIndex);
    }
  }, []);
  const handleCalendarScroll = useCallback(
    () => {
      if (scrollFrameRef.current != null) return;
      scrollFrameRef.current = requestAnimationFrame(() => {
        scrollFrameRef.current = null;
        syncMonthIndexFromScroll();
      });
    },
    [syncMonthIndexFromScroll]
  );
  useEffect(() => {
    activeIndexRef.current = currentMonthIndex;
  }, [currentMonthIndex]);
  useEffect(() => {
    shouldAutoAlignMonthRef.current = true;
    setCurrentMonthIndex(activeIndex);
  }, [activeIndex]);
  useEffect(() => {
    if (!navScrollRef.current) return;
    const target = navScrollRef.current.querySelector(
      `[data-nav-month-index="${currentMonthIndex}"]`
    );
    if (target) {
      target.scrollIntoView({ block: "center", behavior: "auto" });
    }
  }, [currentMonthIndex]);
  useEffect(() => {
    if (!shouldAutoAlignMonthRef.current) return;
    if (!scrollRef.current) return;
    const target = scrollRef.current.querySelector(
      `[data-month-index="${currentMonthIndex}"]`
    );
    if (target) {
      requestAnimationFrame(() => {
        target.scrollIntoView({ block: "start", behavior: "auto" });
      });
    }
    shouldAutoAlignMonthRef.current = false;
  }, [currentMonthIndex]);
  useEffect(() => {
    return () => {
      if (scrollFrameRef.current != null) {
        cancelAnimationFrame(scrollFrameRef.current);
      }
    };
  }, []);
  useEffect(() => {
    setDraftRange(value || [null, null]);
  }, [value]);
  useEffect(() => {
    if (normalizedEnd) {
      setHoverDate(null);
    }
  }, [normalizedEnd]);
  return (
    <Box sx={{ minWidth: 340, maxHeight: 480, position: "relative", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1, pt: 0.5, pb: 1 }}>
        <Button size="small" variant="outlined" onClick={handleToday}>
          Today
        </Button>
        <IconButton size="small" onClick={onClose} aria-label="Close calendar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Box
          ref={scrollRef}
          onScroll={handleCalendarScroll}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            pr: 1.25,
            pl: 1,
            pb: 1,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {monthMeta.map(({ days, year, m, label, idx }) => (
            <Box
              key={`${year}-${m}-${idx}`}
              sx={{ mb: 2 }}
              data-month-index={idx}
              ref={(node) => {
                monthSectionRefs.current[idx] = node;
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, textAlign: "center" }}>
                {label}
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5, fontSize: 12 }}>
                {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                  <Typography key={`${d}-${i}`} align="center" sx={{ fontWeight: 700, color: "text.secondary" }}>
                    {d}
                  </Typography>
                ))}
                {days.map((day, i) => {
                  const selectedStart = isSameDay(day, normalizedStart);
                  const selectedEnd = isSameDay(day, normalizedEnd);
                  const range = inRange(day);
                  const disabled = isOutOfRange(day);
                  return (
                    <Box
                      key={i}
                      onClick={() => handleDayClick(day)}
                      onMouseEnter={() => {
                        if (day && !disabled && normalizedStart && !normalizedEnd) {
                          setHoverDate(day);
                        }
                      }}
                      onMouseLeave={() => {
                        if (normalizedStart && !normalizedEnd) {
                          setHoverDate(null);
                        }
                      }}
                      sx={{
                        height: 34,
                        borderRadius: 1,
                        bgcolor: selectedStart || selectedEnd ? edgeColor : range ? highlightColor : "transparent",
                        color: selectedStart || selectedEnd ? "primary.contrastText" : "text.primary",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: day && !disabled ? "pointer" : "default",
                        opacity: day ? (disabled ? 0.35 : 1) : 0.25,
                        "&:hover": day && !disabled ? { bgcolor: range ? "primary.light" : "action.hover" } : {},
                      }}
                      data-date={day ? day.getTime() : undefined}
                    >
                      {day ? day.getDate() : ""}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
        <Box
          ref={navScrollRef}
          sx={{
            width: 64,
            overflowY: "auto",
            minHeight: 0,
            pr: 0.5,
            pl: 0.25,
            pb: 1,
            borderLeft: "1px solid",
            borderColor: "divider",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {monthNavItems.map((item) => (
            <Box key={`nav-${item.idx}`} sx={{ mb: 0.4 }}>
              {item.isYearStart ? (
                <Typography
                  variant="caption"
                  sx={{ display: "block", px: 1, pb: 0.35, pt: item.idx === 0 ? 0 : 0.75, fontWeight: 700, color: "text.secondary" }}
                >
                  {item.year}
                </Typography>
              ) : null}
              <Box
                data-nav-month-index={item.idx}
                onClick={() => {
                  goToMonthIndex(item.idx, true);
                }}
                sx={{
                  mx: 0.5,
                  px: 1,
                  py: 0.6,
                  borderRadius: 1.2,
                  textAlign: "center",
                  fontSize: 13,
                  fontWeight: currentMonthIndex === item.idx ? 700 : 500,
                  color: currentMonthIndex === item.idx ? "primary.main" : "text.secondary",
                  backgroundColor:
                    currentMonthIndex === item.idx ? "rgba(33, 150, 243, 0.14)" : "transparent",
                  cursor: "pointer",
                  transition: "background-color 120ms ease, color 120ms ease",
                  "&:hover": {
                    backgroundColor:
                      currentMonthIndex === item.idx
                        ? "rgba(33, 150, 243, 0.18)"
                        : "action.hover",
                    color: "text.primary",
                  },
                }}
              >
                {item.label}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
const AVAILABLE_MARKETS = SUPPORTED_MARKETS;
const INTRADAY_AUCTION_PRODUCTS = ["IDA1", "IDA2", "IDA3"];
const INTRADAY_CONTINUOUS_PRODUCTS = ["XBID"];
const INTRADAY_PRODUCTS = [...INTRADAY_AUCTION_PRODUCTS, ...INTRADAY_CONTINUOUS_PRODUCTS];
const INTRADAY_PRODUCT_LABELS = {
  IDA1: "SIDC IDA1",
  IDA2: "SIDC IDA2",
  IDA3: "SIDC IDA3",
  XBID: "XBID",
};
const ACTUAL_PRICE_COMPARISON_OPTIONS = [
  { key: "dam", label: "DAM Actual", color: "#c62828" },
  { key: "ida1", label: "IDA1 Actual", color: "#6a1b9a" },
  { key: "ida2", label: "IDA2 Actual", color: "#00838f" },
  { key: "ida3", label: "IDA3 Actual", color: "#ad1457" },
];
const MARKET_MIX_METRICS = {
  // HU: οι 14 τύποι παραγωγής που επιστρέφει όντως η ENTSO-E για τη ζώνη HU
  // (επαληθευμένο με scripts/probe_entsoe_fundamentals.py — 15 στήλες, από τις
  // οποίες η "Fossil Oil__Actual Consumption" φιλτράρεται ως consumption).
  // Ο mapGenerationFieldToMetric τις καλύπτει όλες χωρίς αλλαγή. Προσοχή: για
  // κάθε αγορά πλην GR, το "Wind Onshore" γίνεται fuel_mix_wind_onshore και το
  // λιγνίτης γίνεται fuel_mix_fossil_brown_coal_or_lignite.
  HU: [
    "fuel_mix_solar",
    "fuel_mix_wind_onshore",
    "fuel_mix_nuclear",
    "fuel_mix_fossil_gas",
    "fuel_mix_fossil_hard_coal",
    "fuel_mix_fossil_brown_coal_or_lignite",
    "fuel_mix_fossil_oil",
    "fuel_mix_biomass",
    "fuel_mix_waste",
    "fuel_mix_geothermal",
    "fuel_mix_hydro_run_of_river_and_poundage",
    "fuel_mix_hydro_water_reservoir",
    "fuel_mix_other_renewable",
    "fuel_mix_other",
    "rt_system_generation",
  ],
  GR: [
    "fuel_mix_solar",
    "fuel_mix_wind",
    "fuel_mix_fossil_gas",
    "fuel_mix_fossil_oil",
    "fuel_mix_lignite",
    "fuel_mix_hydro_pumped_storage",
    "fuel_mix_hydro_water_reservoir",
    "rt_system_generation",
  ],
  FR: [
    "fuel_mix_solar",
    "fuel_mix_wind_onshore",
    "fuel_mix_wind_offshore",
    "fuel_mix_fossil_gas",
    "fuel_mix_fossil_oil",
    "fuel_mix_hydro_pumped_storage",
    "fuel_mix_hydro_water_reservoir",
    "fuel_mix_biomass",
    "fuel_mix_hydro_run_of_river_and_poundage",
    "fuel_mix_nuclear",
    "fuel_mix_waste",
    "fuel_mix_fossil_hard_coal",
    "fuel_mix_energy_storage",
    "rt_system_generation",
  ],
  DE: [
    "fuel_mix_solar",
    "fuel_mix_wind_onshore",
    "fuel_mix_wind_offshore",
    "fuel_mix_fossil_gas",
    "fuel_mix_fossil_oil",
    "fuel_mix_hydro_pumped_storage",
    "fuel_mix_hydro_water_reservoir",
    "fuel_mix_biomass",
    "fuel_mix_hydro_run_of_river_and_poundage",
    "fuel_mix_fossil_coal_derived_gas",
    "fuel_mix_waste",
    "fuel_mix_fossil_hard_coal",
    "fuel_mix_geothermal",
    "fuel_mix_other",
    "fuel_mix_fossil_brown_coal_or_lignite",
    "fuel_mix_other_renewable",
    "rt_system_generation",
  ],
  NL: [
    "fuel_mix_solar",
    "fuel_mix_wind_onshore",
    "fuel_mix_wind_offshore",
    "fuel_mix_fossil_gas",
    "fuel_mix_biomass",
    "fuel_mix_hydro_run_of_river_and_poundage",
    "fuel_mix_waste",
    "fuel_mix_fossil_hard_coal",
    "fuel_mix_other",
    "fuel_mix_nuclear",
    "rt_system_generation",
  ],
  // BG: οι 12 τύποι παραγωγής που επιστρέφει όντως η ENTSO-E για τη ζώνη BG
  // (επαληθευμένο 17/9/2026 με scripts/probe_entsoe_fundamentals.py --markets BG:
  // 13 στήλες, από τις οποίες η "Energy storage__Actual Consumption"
  // φιλτράρεται ως consumption). Ο mapGenerationFieldToMetric τις καλύπτει
  // όλες χωρίς αλλαγή. Δεν υπάρχουν υπεράκτια αιολικά ούτε πετρέλαιο.
  BG: [
    "fuel_mix_solar",
    "fuel_mix_wind_onshore",
    "fuel_mix_nuclear",
    "fuel_mix_fossil_gas",
    "fuel_mix_fossil_hard_coal",
    "fuel_mix_fossil_brown_coal_or_lignite",
    "fuel_mix_biomass",
    "fuel_mix_waste",
    "fuel_mix_energy_storage",
    "fuel_mix_hydro_pumped_storage",
    "fuel_mix_hydro_run_of_river_and_poundage",
    "fuel_mix_hydro_water_reservoir",
    "rt_system_generation",
  ],
};
const BASE_MIX_METRICS = MARKET_MIX_METRICS[AVAILABLE_MARKETS[0]] || [];
const MARKET_GENERATION_METRICS = {
  // HU: το wind_and_solar_forecast της ENTSO-E επιστρέφει 2 στήλες (Solar, Wind
  // Onshore) όπως στην Ελλάδα — καμία υπεράκτια αιολική. Το κλειδί όμως είναι
  // wind_onshore_forecast και ΟΧΙ wind_forecast: το normalizeEntsoeForecastSeries
  // δίνει το wind_forecast μόνο για market === "GR".
  HU: ["system_gen_forecast", "wind_onshore_forecast", "solar_forecast"],
  GR: ["system_gen_forecast", "wind_forecast", "solar_forecast"],
  FR: [
    "system_gen_forecast",
    "wind_onshore_forecast",
    "wind_offshore_forecast",
    "solar_forecast",
  ],
  DE: [
    "system_gen_forecast",
    "wind_onshore_forecast",
    "wind_offshore_forecast",
    "solar_forecast",
  ],
  NL: [
    "system_gen_forecast",
    "wind_onshore_forecast",
    "wind_offshore_forecast",
    "solar_forecast",
  ],
  // BG: το wind_and_solar_forecast επιστρέφει 2 στήλες (Solar, Wind Onshore),
  // όπως και η HU - καμία υπεράκτια αιολική. Το κλειδί είναι
  // wind_onshore_forecast και ΟΧΙ wind_forecast: το
  // normalizeEntsoeForecastSeries δίνει το wind_forecast μόνο για market === "GR".
  BG: ["system_gen_forecast", "wind_onshore_forecast", "solar_forecast"],
};
const ALL_MIX_METRICS = Array.from(
  new Set(Object.values(MARKET_MIX_METRICS).flat())
);
const BASE_GENERATION_METRICS = MARKET_GENERATION_METRICS[AVAILABLE_MARKETS[0]] || [];
const ALL_GENERATION_METRICS = Array.from(
  new Set(Object.values(MARKET_GENERATION_METRICS).flat())
);
const RT_SYSTEM_GENERATION_METRIC = "rt_system_generation";
const LOAD_METRICS = ["real_time_system_load", "system_load_forecast"];
const QUARTER_HOUR_STEP_MS = 15 * 60 * 1000;
const HOURLY_STEP_MS = 60 * 60 * 1000;
const QUARTER_MINUTES = new Set([15, 30, 45]);
const inferSeriesStepMs = (series) => {
  if (!Array.isArray(series) || series.length < 2) return null;
  const timestamps = series
    .map((row) => {
      const time = row?.time;
      return time instanceof Date && !Number.isNaN(time.getTime()) ? time.getTime() : null;
    })
    .filter((value) => value != null)
    .sort((a, b) => a - b);
  if (timestamps.length < 2) return null;
  for (let i = 1; i < timestamps.length; i += 1) {
    const diff = timestamps[i] - timestamps[i - 1];
    if (diff > 0) return diff;
  }
  return null;
};
const detectEnergyMixStep = (series) => {
  if (!series || !series.length) return QUARTER_HOUR_STEP_MS;
  const hasQuarterMinute = series.some((row) => {
    const time = row?.time;
    if (!(time instanceof Date)) return false;
    return QUARTER_MINUTES.has(time.getMinutes());
  });
  return hasQuarterMinute ? QUARTER_HOUR_STEP_MS : HOURLY_STEP_MS;
};

const DAM_METRICS_META = {
  count: {
    label: "Timestamp Count",
    help: "Number of intervals used in the calculation (P50 vs Actual).",
    kind: "info",
  },
  mae: {
    label: "Average Error (MAE)",
    help: "Average absolute difference between the P50 forecast and the actual price. Lower is better.",
    kind: "lower",
    bands: { good: 18, ok: 35 },
  },
  rmse: {
    label: "Spike Penalty (RMSE)",
    help: "Error metric that penalizes large misses more strongly than MAE. Lower is better.",
    kind: "lower",
    bands: { good: 25, ok: 45 },
  },
  smape: {
    label: "sMAPE (%)",
    help: "Symmetric % error. Can inflate when prices are near 0 or negative. Lower is better.",
    kind: "lower",
    bands: { good: 25, ok: 50 },
  },
  // directional_accuracy: {
  //   label: "Directional Accuracy (%)",
  //   help: "How often the forecast got the direction (up/down vs previous interval) correct. Higher is better. 50% is random.",
  //   kind: "higher",
  //   bands: { good: 60, ok: 52 }, // >=60 good, >=52 ok, <52 poor
  // },
  correlation: {
    label: "Trend Match (Pearson)",
    help: "How closely the forecast follows the overall ups and downs of the actual price. Higher is better.",
    kind: "higher",
    bands: { good: 0.8, ok: 0.55 },
  },
  bias: {
    label: "Systematic Offset (Bias)",
    help: "Average signed error (Forecast - Actual). Close to 0 is best. Positive means over-forecasting.",
    kind: "abs_lower",
    bands: { good: 5, ok: 12 },
  },
  nrmse_pct: {
    label: "nRMSE (%)",
    help: "RMSE normalized by typical price level. Useful across markets. Lower is better.",
    kind: "lower",
    bands: { good: 35, ok: 55 },
  },
  spearman: {
    label: "Ordering Match (Spearman)",
    help: "How well the forecast preserves the ordering of cheaper and more expensive intervals. Higher is better.",
    kind: "higher",
    bands: { good: 0.8, ok: 0.55 },
  },
  top10_recall_pct: {
    label: "Peak Capture (%)",
    help: "Share of the highest 10% actual price intervals that were correctly flagged by the forecast. Higher is better.",
    kind: "higher",
    bands: { good: 60, ok: 35 },
  },
};

const DAM_VISIBLE_METRIC_KEYS = ["count", "mae", "rmse", "correlation", "bias", "spearman", "top10_recall_pct"];

// A score needs enough scored intervals to mean anything. This used to be 2,
// which on 2026-08-27 produced "Overall Score: 100/100 (Good)" for IDA2 and
// IDA3 off FOUR intervals: at n=4 Pearson and Spearman are ~1.00 almost by
// construction, and "peak capture" looks for the top 10% of 4 intervals - a
// single interval, which is trivially captured, so 100%. Three of the five
// score components saturate without having measured anything.
//
// Both conditions must hold: an absolute floor AND a share of the intervals
// that actually carry a forecast (see `expected` in computeDamForecastMetrics -
// 96 for IDA1/IDA2, 48 for IDA3). Below either, the panel says how many
// actuals have landed instead of showing a score.
const INTRADAY_FORECAST_METRICS_MIN_COUNT = 24;
const INTRADAY_FORECAST_METRICS_MIN_COVERAGE = 0.5;
const DAM_OVERALL_SCORE_COMPONENTS = [
  { key: "mae", weight: 0.4 },
  { key: "bias", weight: 0.15 },
  { key: "spearman", weight: 0.2 },
  { key: "top10_recall_pct", weight: 0.15 },
  { key: "correlation", weight: 0.1 },
];

// THE 0-100 COMPOSITE SCORE IS OFF BY DEFAULT, and not as a matter of taste.
// Two properties disqualify it from sitting beside a price forecast someone is
// about to bid on:
//
//   1. NO RESOLUTION WHERE THE FORECASTS ACTUALLY LIVE. Each component is
//      scored 100 / 60 / 20 by band, and the MAE band's "good" cutoff is 18
//      EUR/MWh. GR intraday runs at MAE 3-12, so 3.37 and 17.9 BOTH score 100
//      on the component carrying 40% of the weight. A fivefold difference in
//      error is invisible.
//   2. IT IS DISCONTINUOUS. MAE 18.1 instead of 17.9 drops that component from
//      100 to 60 - sixteen points off the headline for 0.2 EUR/MWh. A number
//      that jumps at an arbitrary threshold is not a score.
//
// Why it matters commercially rather than cosmetically: "100/100 (Good)"
// printed beside a forecast reads as a WARRANTY. Shown to a lender or an
// offtaker it is a quality claim the platform cannot support. Measured
// 2026-08-27: IDA2 and IDA3 both displayed 100/100 off four intervals.
//
// What takes its place is the honest headline below - the primary metric and
// the sample it was computed on, in the same space. The individual metric
// tiles are UNCHANGED: each shows a real measured value beside its own label,
// which is fine. Spearman, Pearson and peak capture stay visible but are no
// longer combined into anything - measured on GR they sit near 0.95 and
// saturate, so they carry little information and must not drive a headline.
//
// Flip to true only to inspect the old behaviour; do not ship it on.
const SHOW_COMPOSITE_FORECAST_SCORE = false;

// The replacement headline: the metric that actually decides the acceptance
// rule (MAE, in EUR/MWh, directly interpretable) plus the sample behind it.
// Returns null when there is nothing honest to say.
const forecastHeadlineLabel = (metrics, sampleLabel = "") => {
  if (!metrics) return null;
  const mae = metrics.mae;
  if (mae == null || Number.isNaN(Number(mae))) return null;
  return `MAE ${Number(mae).toFixed(2)} EUR/MWh${sampleLabel}`;
};

const DAM_METRIC_DECIMALS = {
  count: 0,
  mae: 2,
  rmse: 2,
  smape: 2,
  // directional_accuracy: 2,
  correlation: 2,

  bias: 2,
  nrmse_pct: 2,
  p10_p90_coverage_pct: 2,
  p10_p90_avg_width: 2,
  pinball_avg: 3,
  spearman: 2,
  top10_recall_pct: 2,
};
const DAM_ACTUAL_PRICE_FIELDS = [
  "actual",
  "actual_price",
  "actualPrice",
  "price_actual",
  "dam_actual",
  "settled_price",
];
const DAM_ACTUAL_AVAILABILITY_FIELDS = [
  "actual_available",
  "actualAvailable",
  "has_actual",
  "hasActual",
  "has_actual_price",
  "hasActualPrice",
  "is_actual_available",
  "isActualAvailable",
];

const normalizeAvailabilityFlag = (value) => {
  if (value == null) return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return Number.isFinite(value) ? value !== 0 : null;
  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return null;
  if (["true", "1", "yes", "y", "available", "valid"].includes(normalized)) {
    return true;
  }
  if (
    [
      "false",
      "0",
      "no",
      "n",
      "unavailable",
      "missing",
      "null",
      "none",
      "nan",
      "n/a",
      "na",
    ].includes(normalized)
  ) {
    return false;
  }
  return null;
};

const getDamDeliveryTime = (row, timeZone) => {
  const value =
    row?.delivery_start ??
    row?.delivery_time ??
    row?.deliveryTime ??
    row?.timestamp ??
    row?.time ??
    row?.date;
  return parseDateTimeInTimeZone(value, timeZone);
};

const getPublishedAtTime = (row, timeZone) => {
  const value =
    row?.published_at ??
    row?.publishedAt ??
    row?.publication_time ??
    row?.publicationTime;
  return parseDateTimeInTimeZone(value, timeZone);
};

const pickLatestPublishedRow = (currentRow, nextRow) => {
  if (!currentRow) return nextRow;
  if (!nextRow) return currentRow;
  const currentVersion = toNumberSafe(
    currentRow?.publicationVersion ?? currentRow?.publication_version
  );
  const nextVersion = toNumberSafe(
    nextRow?.publicationVersion ?? nextRow?.publication_version
  );
  if (nextVersion != null && currentVersion != null && nextVersion !== currentVersion) {
    return nextVersion > currentVersion ? nextRow : currentRow;
  }
  const currentPublishedAt = getPublishedAtTime(currentRow)?.getTime() ?? -Infinity;
  const nextPublishedAt = getPublishedAtTime(nextRow)?.getTime() ?? -Infinity;
  return nextPublishedAt >= currentPublishedAt ? nextRow : currentRow;
};

const normalizeSparsePriceSeries = (rows) => {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const time = row?.time instanceof Date ? row.time : getDamDeliveryTime(row);
    const key = time && !Number.isNaN(time.getTime()) ? time.getTime() : null;
    if (key == null) return;
    const current = map.get(key);
    map.set(
      key,
      pickLatestPublishedRow(current, {
        ...(current || {}),
        ...row,
        time,
      })
    );
  });
  return Array.from(map.values()).sort((a, b) => a.time - b.time);
};

const filterSeriesByTimeWindow = (rows, startDate, endDate) => {
  const startMs = startDate instanceof Date ? startDate.getTime() : NaN;
  const endMs = endDate instanceof Date ? endDate.getTime() : NaN;
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return Array.isArray(rows) ? rows : [];
  }
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const time = row?.time instanceof Date ? row.time : getDamDeliveryTime(row);
    const timeMs = time instanceof Date ? time.getTime() : NaN;
    return Number.isFinite(timeMs) && timeMs >= startMs && timeMs <= endMs;
  });
};
const buildActualPriceComparisonSeries = (seriesMap) => {
  const timeline = new Map();
  Object.entries(seriesMap || {}).forEach(([seriesKey, rows]) => {
    (Array.isArray(rows) ? rows : []).forEach((row) => {
      const time = row?.time instanceof Date ? row.time : getDamDeliveryTime(row);
      const actual = toNumberSafe(row?.actual);
      const key = time && !Number.isNaN(time.getTime()) ? time.getTime() : null;
      if (key == null || actual == null) return;
      const current = timeline.get(key) || { time };
      current[seriesKey] = actual;
      timeline.set(key, current);
    });
  });
  return Array.from(timeline.values()).sort((a, b) => a.time - b.time);
};

const MARKET_PRICE_COLORS = {
  actual: "#c62828",
  p10: "#2e7d32",
  p50: "#1565c0",
  p90: "#ef6c00",
  vwap: "#1565c0",
  min: "#2e7d32",
  max: "#ef6c00",
};
// XBID (continuous) chart config. With no filter selected the chart shows the
// market Total (VWAP/Min/Max); each selected side / asset / technology value is
// drawn as its own comparison line.
const XBID_SERIES_COLORS = { vwap: "#1565c0", min: "#2e7d32", max: "#ef6c00", volume: "#6a1b9a" };
const XBID_GROUP_PALETTE = ["#1565c0", "#ef6c00", "#2e7d32", "#6a1b9a", "#c62828", "#00838f", "#ad1457", "#5d4037"];
const XBID_DURATIONS = [15, 30, 60];
const XBID_SIDES = ["Buy", "Sell"];
const XBID_ASSETS = ["LOAD", "SUPPLY", "STORAGE"];
const XBID_TECHNOLOGIES = ["RES", "Natural Gas", "Big Hydro", "LV", "MV", "PUMP", "Storage"];
// Technology (CLASSIFICATION) is a subgroup of ASSET_DESCR — cascades the
// Technology filter options from the selected asset(s).
const XBID_ASSET_TECH = {
  LOAD: ["LV", "MV", "PUMP"],
  SUPPLY: ["Big Hydro", "Natural Gas", "RES"],
  STORAGE: ["Storage"],
};
// Multi-select helpers mirroring the balancing-market filter behaviour.
const normalizeMultiSelectValue = (value) =>
  Array.isArray(value) ? value : typeof value === "string" ? value.split(",").filter(Boolean) : [];
const renderXbidFilterValue = (selected) => {
  if (!selected || !selected.length) return ""; // empty until the user selects something
  return selected.join(", "); // full list; field truncates with an ellipsis when it overflows
};
const MARKET_FORECAST_DASH = "6 4";

const normalizeDamActualPrice = (row) => {
  const actual = toNumberSafe(pickField(row, DAM_ACTUAL_PRICE_FIELDS));
  if (actual == null) return null;

  const availability = normalizeAvailabilityFlag(
    pickField(row, DAM_ACTUAL_AVAILABILITY_FIELDS)
  );
  if (availability === false) return null;
  if (availability === true) return actual;

  return actual;
};

const percentileSorted = (sortedValues, percentile) => {
  if (!sortedValues.length) return null;
  const index = (sortedValues.length - 1) * percentile;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sortedValues[lower];
  return (
    sortedValues[lower] +
    (sortedValues[upper] - sortedValues[lower]) * (index - lower)
  );
};

const computeDamActualStats = (series) => {
  const samples = (Array.isArray(series) ? series : [])
    .map((row) => ({
      value: toNumberSafe(row?.actual),
      time: getDamDeliveryTime(row),
    }))
    .filter((sample) => sample.value != null);

  if (!samples.length) return null;

  const values = samples.map((sample) => sample.value);
  const sortedValues = [...values].sort((a, b) => a - b);
  const sum = values.reduce((acc, value) => acc + value, 0);
  const mean = sum / values.length;
  const variance =
    values.reduce((acc, value) => acc + (value - mean) ** 2, 0) / values.length;
  const minSample = samples.reduce(
    (best, sample) => (sample.value < best.value ? sample : best),
    samples[0]
  );
  const peakSample = samples.reduce(
    (best, sample) => (sample.value > best.value ? sample : best),
    samples[0]
  );
  const q1 = percentileSorted(sortedValues, 0.25);
  const q3 = percentileSorted(sortedValues, 0.75);

  return {
    count: samples.length,
    min: minSample.value,
    minTime: minSample.time,
    peak: peakSample.value,
    peakTime: peakSample.time,
    mean,
    stdDev: Math.sqrt(variance),
    median: percentileSorted(sortedValues, 0.5),
    p10: percentileSorted(sortedValues, 0.1),
    p90: percentileSorted(sortedValues, 0.9),
    range: peakSample.value - minSample.value,
    iqr: q3 != null && q1 != null ? q3 - q1 : null,
  };
};

const computePearsonCorrelation = (xValues, yValues) => {
  if (xValues.length < 2 || yValues.length !== xValues.length) return null;
  const meanX = xValues.reduce((sum, value) => sum + value, 0) / xValues.length;
  const meanY = yValues.reduce((sum, value) => sum + value, 0) / yValues.length;
  let numerator = 0;
  let xVariance = 0;
  let yVariance = 0;
  xValues.forEach((x, index) => {
    const dx = x - meanX;
    const dy = yValues[index] - meanY;
    numerator += dx * dy;
    xVariance += dx * dx;
    yVariance += dy * dy;
  });
  const denominator = Math.sqrt(xVariance * yVariance);
  return denominator ? numerator / denominator : null;
};

const rankValues = (values) => {
  const sorted = values
    .map((value, index) => ({ value, index }))
    .sort((a, b) => a.value - b.value);
  const ranks = Array(values.length);
  let cursor = 0;
  while (cursor < sorted.length) {
    let end = cursor;
    while (end + 1 < sorted.length && sorted[end + 1].value === sorted[cursor].value) {
      end += 1;
    }
    const averageRank = (cursor + end + 2) / 2;
    for (let i = cursor; i <= end; i += 1) {
      ranks[sorted[i].index] = averageRank;
    }
    cursor = end + 1;
  }
  return ranks;
};

function computeDamForecastMetrics(series) {
  const pairs = (Array.isArray(series) ? series : [])
    .map((row) => ({
      actual: toNumberSafe(row?.actual),
      forecast: toNumberSafe(row?.p50),
    }))
    .filter((pair) => pair.actual != null && pair.forecast != null);

  if (!pairs.length) return null;

  const actuals = pairs.map((pair) => pair.actual);
  const forecasts = pairs.map((pair) => pair.forecast);
  const errors = pairs.map((pair) => pair.forecast - pair.actual);
  const absErrors = errors.map((error) => Math.abs(error));
  const squaredErrors = errors.map((error) => error ** 2);
  const count = pairs.length;
  const mae = absErrors.reduce((sum, value) => sum + value, 0) / count;
  const rmse = Math.sqrt(squaredErrors.reduce((sum, value) => sum + value, 0) / count);
  const bias = errors.reduce((sum, value) => sum + value, 0) / count;
  const meanAbsActual =
    actuals.reduce((sum, value) => sum + Math.abs(value), 0) / count;
  const smapeTerms = pairs
    .map((pair, index) => {
      const denominator = (Math.abs(pair.actual) + Math.abs(pair.forecast)) / 2;
      return denominator ? absErrors[index] / denominator : null;
    })
    .filter((value) => value != null);
  const topCount = Math.max(1, Math.ceil(count * 0.1));
  const topActual = new Set(
    pairs
      .map((pair, index) => ({ index, value: pair.actual }))
      .sort((a, b) => b.value - a.value)
      .slice(0, topCount)
      .map((item) => item.index)
  );
  const topForecast = new Set(
    pairs
      .map((pair, index) => ({ index, value: pair.forecast }))
      .sort((a, b) => b.value - a.value)
      .slice(0, topCount)
      .map((item) => item.index)
  );
  const capturedPeakCount = [...topActual].filter((index) => topForecast.has(index)).length;

  // How many intervals COULD have been scored, i.e. how many carry a forecast.
  // Auction-agnostic on purpose: a full IDA1/IDA2 day is 96 intervals but a full
  // IDA3 day is 48 (it clears at 10:00 CET on the delivery day and only covers
  // the rest of it), so a hardcoded 96 would mislabel every IDA3 day as half
  // missing. `count / expected` is the honest coverage of the metrics below.
  const expected = (Array.isArray(series) ? series : []).filter(
    (row) => toNumberSafe(row?.p50) != null
  ).length;

  return {
    count,
    expected,
    coverage_pct: expected ? (count / expected) * 100 : null,
    mae,
    rmse,
    smape:
      smapeTerms.length > 0
        ? (smapeTerms.reduce((sum, value) => sum + value, 0) / smapeTerms.length) * 100
        : null,
    correlation: computePearsonCorrelation(forecasts, actuals),
    bias,
    nrmse_pct: meanAbsActual ? (rmse / meanAbsActual) * 100 : null,
    spearman:
      count > 1
        ? computePearsonCorrelation(rankValues(forecasts), rankValues(actuals))
        : null,
    top10_recall_pct: (capturedPeakCount / topCount) * 100,
  };
}

const classifyMetric = (key, value) => {
  const meta = DAM_METRICS_META[key];
  if (!meta || value == null || Number.isNaN(Number(value)) || !meta.bands) return null;

  const v = Number(value);

  const { good, ok } = meta.bands;

  if (meta.kind === "lower") {
    if (v <= good) return "good";
    if (v <= ok) return "ok";
    return "poor";
  }

  if (meta.kind === "higher") {
    if (v >= good) return "good";
    if (v >= ok) return "ok";
    return "poor";
  }

  if (meta.kind === "abs_lower") {
    const av = Math.abs(v);
    if (av <= good) return "good";
    if (av <= ok) return "ok";
    return "poor";
  }

  return null;
};
const metricChipLabel = (cls) => {
  if (cls === "good") return "Good";
  if (cls === "ok") return "OK";
  if (cls === "poor") return "Poor";
  return "";
};
const computeOverallForecastScore = (metrics) => {
  if (!metrics) return null;

  const parts = DAM_OVERALL_SCORE_COMPONENTS
    .map((component) => {
      const status = classifyMetric(component.key, metrics[component.key]);
      if (!status) return null;

      const score = status === "good" ? 100 : status === "ok" ? 60 : 20;

      return { ...component, score, status };
    })
    .filter(Boolean);

  if (!parts.length) return null;

  const totalWeight = parts.reduce((sum, part) => sum + part.weight, 0);
  if (!totalWeight) return null;

  const weightedScore =
    parts.reduce((sum, part) => sum + part.score * part.weight, 0) / totalWeight;

  const allPoor = parts.every((part) => part.status === "poor");
  const status = allPoor
    ? "poor"
    : weightedScore >= 75
    ? "good"
    : weightedScore >= 45
    ? "ok"
    : "poor";

  return {
    score: weightedScore,
    status,
    parts,
  };
};

const SERIES_COLORS = {
  fuel_mix_solar: "#ffca28",
  fuel_mix_wind: "#0d47a1",
  fuel_mix_wind_onshore: "#1e88e5",
  fuel_mix_wind_offshore: "#1565c0",
  fuel_mix_fossil_gas: "#6d4c41",
  fuel_mix_fossil_oil: "#8d6e63",
  fuel_mix_lignite: "#5d4037",
  fuel_mix_hydro_pumped_storage: "#00796b",
  fuel_mix_hydro_water_reservoir: "#0288d1",
  fuel_mix_biomass: "#7e5100",
  fuel_mix_hydro_run_of_river_and_poundage: "#00897b",
  fuel_mix_nuclear: "#fdd835",
  fuel_mix_waste: "#4e342e",
  fuel_mix_fossil_hard_coal: "#3e2723",
  fuel_mix_energy_storage: "#00acc1",
  fuel_mix_fossil_coal_derived_gas: "#004d40",
  fuel_mix_geothermal: "#6a1b9a",
  fuel_mix_other: "#455a64",
  fuel_mix_fossil_brown_coal_or_lignite: "#5d4037",
  fuel_mix_other_renewable: "#2e7d32",
  [RT_SYSTEM_GENERATION_METRIC]: "#1b5e20",
  system_gen_forecast: "#8bc34a",
  wind_onshore_forecast: "#64b5f6",
  wind_offshore_forecast: "#1976d2",
  wind_forecast: "#64b5f6",
  solar_forecast: "#ffca28",
  real_time_system_load: "#c62828",
  system_load_forecast: "#283593",
};
const SERIES_LABELS = {
  fuel_mix_solar: "Solar",
  fuel_mix_wind: "Wind",
  fuel_mix_wind_onshore: "Wind (Onshore)",
  fuel_mix_wind_offshore: "Wind (Offshore)",
  fuel_mix_fossil_gas: "Gas",
  fuel_mix_fossil_oil: "Oil",
  fuel_mix_lignite: "Lignite",
  fuel_mix_hydro_pumped_storage: "Hydro (Pumped Storage)",
  fuel_mix_hydro_water_reservoir: "Hydro (Reservoir)",
  fuel_mix_biomass: "Biomass",
  fuel_mix_hydro_run_of_river_and_poundage: "Hydro (Run-of-River)",
  fuel_mix_nuclear: "Nuclear",
  fuel_mix_waste: "Waste",
  fuel_mix_fossil_hard_coal: "Hard Coal",
  fuel_mix_energy_storage: "Energy Storage",
  fuel_mix_fossil_coal_derived_gas: "Coal-derived Gas",
  fuel_mix_geothermal: "Geothermal",
  fuel_mix_other: "Other",
  fuel_mix_fossil_brown_coal_or_lignite: "Brown Coal",
  fuel_mix_other_renewable: "Other Renewable",
  [RT_SYSTEM_GENERATION_METRIC]: "System Generation",
  system_gen_forecast: "System Generation Forecast",
  wind_onshore_forecast: "Wind Forecast (Onshore)",
  wind_offshore_forecast: "Wind Forecast (Offshore)",
  wind_forecast: "Wind Forecast",
  solar_forecast: "Solar Forecast",
  real_time_system_load: "Real-time Load",
  system_load_forecast: "Load Forecast",
};
const DEFAULT_VISIBILITY = {
  mix: Object.fromEntries(
    ALL_MIX_METRICS.map((m) => [m, true])
  ),
  generation: Object.fromEntries(ALL_GENERATION_METRICS.map((m) => [m, true])),
  load: Object.fromEntries(LOAD_METRICS.map((m) => [m, true])),
};
const IRRADIANCE_KEYS = ["ghi", "dni", "dhi"];
const WEATHER_DEFAULT_VISIBILITY = {
  irradiance: Object.fromEntries(IRRADIANCE_KEYS.map((k) => [k, true])),
  temperature: { temp: true },
  humidity: { humidity: true },
  wind: { wind_speed: true },
};
const BIDDING_ZONE_LABELS = {
  GR: "Greece (GR)",
  FR: "France (FR)",
  DE: "Germany (DE)",
  NL: "Netherlands (NL)",
  HU: "Hungary (HU)",
  BG: "Bulgaria (BG)",
};
const BIDDING_ZONE_TIME_ZONES = {
  GR: "Europe/Athens",
  FR: "Europe/Paris",
  DE: "Europe/Berlin",
  NL: "Europe/Amsterdam",
  HU: "Europe/Budapest",
  // Χωρίς αυτό η BG πέφτει σε "UTC" και το κόψιμο της τοπικής ημέρας
  // παράδοσης μετατοπίζεται κατά 2-3 ώρες, σιωπηλά.
  BG: "Europe/Sofia",
};

function getShortTimeZoneLabel(timeZone, date = new Date()) {
  if (!timeZone || !isValidTimeZone(timeZone)) return "UTC";
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      timeZoneName: "short",
    }).formatToParts(date);
    return parts.find((part) => part.type === "timeZoneName")?.value || timeZone;
  } catch {
    return timeZone;
  }
}
const FULLSCREEN_BTN_SX = {
  position: "absolute",
  right: 10,
  bottom: 10,
  width: 32,
  height: 32,
  borderRadius: 1,
  backgroundColor: "background.paper",
  boxShadow: 1,
  zIndex: 2,
};
const MARKET_VIEW_TOGGLE_SX = {
  border: "1px solid",
  borderColor: "primary.main",
  borderRadius: "999px",
  p: "2px",
  gap: "2px",
  bgcolor: (theme) =>
    theme.palette.mode === "dark" ? "rgba(25, 118, 210, 0.18)" : "rgba(25, 118, 210, 0.08)",
  "& .MuiToggleButtonGroup-grouped": {
    border: 0,
    borderRadius: "999px !important",
    width: 32,
    height: 32,
    color: "primary.main",
    "& .MuiSvgIcon-root": {
      fontSize: 17,
    },
    "&.Mui-selected": {
      bgcolor: "primary.main",
      color: "#fff",
      "&:hover": {
        bgcolor: "primary.dark",
      },
    },
  },
};
const MARKET_SELECTOR_TOGGLE_SX = {
  alignSelf: "flex-start",
  width: "fit-content",
  display: "inline-flex",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "999px",
  p: "3px",
  gap: "4px",
  flexWrap: "wrap",
  maxWidth: "100%",
  bgcolor: (theme) =>
    theme.palette.mode === "dark" ? "rgba(148, 163, 184, 0.12)" : "rgba(148, 163, 184, 0.08)",
  "& .MuiToggleButtonGroup-grouped": {
    border: 0,
    borderRadius: "999px !important",
    minHeight: 34,
    px: 1.5,
    whiteSpace: "nowrap",
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.8125rem",
    lineHeight: 1.2,
    color: "text.secondary",
    "&.Mui-selected": {
      bgcolor: "primary.main",
      color: "#fff",
      "&:hover": {
        bgcolor: "primary.dark",
      },
    },
  },
};
const MARKET_TABLE_CONTAINER_SX = {
  height: "100%",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  "& .MuiTableCell-head": {
    bgcolor: "background.paper",
    fontWeight: 700,
  },
  "& .MuiTableRow-root:nth-of-type(even)": {
    bgcolor: "rgba(15, 23, 42, 0.03)",
  },
};
const FullscreenGlyph = (props) => (
  <SvgIcon
    {...props}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 9.5V5h4.5" />
    <path d="M20 9.5V5h-4.5" />
    <path d="M4 14.5V19h4.5" />
    <path d="M20 14.5V19h-4.5" />
  </SvgIcon>
);
const CHART_MARGIN = { left: 24, right: 40, bottom: 48, top: 28 };
const RUN_ID_CANDIDATES = ["id", "run_id", "runId", "uid", "_id"];

function normalizeRunId(run) {
  if (!run) return "";
  for (const field of RUN_ID_CANDIDATES) {
    const value = run[field];
    if (value !== undefined && value !== null && value !== "") {
      return String(value);
    }
  }
  return "";
}

function matchesRunId(run, desired) {
  if (!desired) return false;
  const target = String(desired);
  return RUN_ID_CANDIDATES.some((field) => {
    const value = run?.[field];
    return value !== undefined && value !== null && value !== "" && String(value) === target;
  });
}
const withAlpha = (color, alpha = "44") =>
  typeof color === "string" && /^#[0-9A-Fa-f]{6}$/.test(color) ? `${color}${alpha}` : color;
const BAR_CHART_PROPS = {
  barCategoryGap: "0%",
  barGap: "-90%",
};
const AXIS_TICK_SX = { fontSize: 12, fontWeight: 700, fill: "currentColor" };
const MARKET_AXIS_LINE_PROPS = { stroke: "currentColor", strokeWidth: 1.5, opacity: 0.72 };
const MARKET_TICK_LINE_PROPS = { stroke: "currentColor", strokeWidth: 1, opacity: 0.72 };
const AXIS_TITLE_STYLE = {
  fill: "currentColor",
  fontWeight: 650,
  fontSize: 15,
  textAnchor: "middle",
  dominantBaseline: "middle",
};
const OPTIMIZATION_LEFT_AXIS_PROPS = {
  tick: AXIS_TICK_SX,
  width: 104,
  tickMargin: 8,
};
const OPTIMIZATION_LEFT_AXIS_LABEL_OFFSET = 20;

const ENERGY_SERIES_COLOR_MAP = {
  pv: "#fb9315",
  pcharge: "#04b30c",
  pvcharge: "#bd9e04",
  gridcharge: "#7297a7",
  pdischarge: "#d32f2f",
  psell: "#1976d2",
  deg_efc: "#8e24aa",
};
const POWER_KEYS_TO_MW = ["pv", "pcharge", "pvcharge", "gridcharge", "pdischarge", "psell"];
const ENERGY_SERIES = [
  { key: "pv", filterLabel: "PV Generation", chartLabel: "PV Generation [kW]" },
  { key: "pcharge", filterLabel: "Total Charge", chartLabel: "Total Charge [kW]" },
  { key: "pvcharge", filterLabel: "PV Charge", chartLabel: "PV Charge [kW]" },
  { key: "gridcharge", filterLabel: "Grid Charge", chartLabel: "Grid Charge [kW]" },
  { key: "pdischarge", filterLabel: "Discharge", chartLabel: "Discharge [kW]" },
  { key: "psell", filterLabel: "Sell to grid", chartLabel: "Sell to grid [kW]" },
  {
    key: "deg_efc",
    dataKey: "deg_efc",
    filterLabel: "Cumulative Cycles (EFC)",
    chartLabel: "Cumulative Cycles (EFC)",
    color: "#8e24aa",
    axis: "right", // <--- NEW: indicates it should use right axis
    preferred: "line", // keep it line even when chart mode is bar/area
  },
].map((series) => ({
  ...series,
  color: ENERGY_SERIES_COLOR_MAP[series.key] || "#90a4ae",
}));
const STANDALONE_BESS_HIDDEN_ENERGY_SERIES = new Set(["pv", "pvcharge", "gridcharge", "pdischarge"]);
const ENERGY_LINE_STROKE_WIDTH = 3;
export default function Dashboard() {
  const history = useHistory();
  const cfg = useMemo(() => {
    return history.present ?? DEFAULT_CONFIG;
  }, [history.present]);
  const selectedBatteries = useMemo(() => cfg?.bess?.selected ?? [], [cfg?.bess?.selected]);
  const [detailsCache, setDetailsCache] = useState({});
  useEffect(() => {
    let cancelled = false;

    async function loadAll() {
      const names = (selectedBatteries || []).map((b) => b?.name).filter(Boolean);
      if (!names.length) return;

      // fetch only those missing
      const missing = names.filter((n) => !detailsCache[n]);
      if (!missing.length) return;

      try {
        const fetched = await Promise.all(
          missing.map(async (name) => {
            const data = await getBatteryDetails(name);
            return [name, data];
          })
        );

        if (!cancelled) {
          setDetailsCache((prev) => {
            const next = { ...prev };
            for (const [name, data] of fetched) next[name] = data;
            return next;
          });
        }
      } catch (e) {
        // optionally show notice; don't crash charts
        console.warn("Failed to load battery details:", e);
      }
    }

    loadAll();
    return () => { cancelled = true; };
    // NOTE: intentionally NOT depending on detailsCache to avoid loops
  }, [detailsCache, selectedBatteries]);
  const assetsMeta = useMemo(() => {
    const out = {};
    for (const b of selectedBatteries || []) {
      const name = b?.name;
      if (!name) continue;

      const details = detailsCache[name];
      const chars = details?.characteristics || {};

      const socMin = chars.soc_min; // already %
      const socMax = chars.soc_max; // already %

      out[name] = {
        socMin: Number.isFinite(Number(socMin)) ? Number(socMin) : null,
        socMax: Number.isFinite(Number(socMax)) ? Number(socMax) : null,
        count: Number.isFinite(Number(b?.count)) ? Number(b.count) : 1,
      };
    }
    return out;
  }, [selectedBatteries, detailsCache]);

  const { accessToken } = useAuth();
  const { demoProfile } = useDemo();
  const { currentProject } = useFile();
  const { config } = useEngine();
  const [searchParams] = useSearchParams();
  const initialRunIdRef = useRef(searchParams.get("runId") || "");
  const hasRunResultsContext = Boolean(searchParams.get("runId"));
  const initialViewParam = searchParams.get("view");
  const initialMarketTabParam = searchParams.get("marketTab");
  const initialViewState =
    initialViewParam === "optimization" ||
    initialViewParam === "market" ||
    initialViewParam === "bid_schedule" ||
    initialViewParam === "economics"
      ? initialViewParam
      : "weather";
  const lastRunParamRef = useRef(initialRunIdRef.current || "");
  const [runs, setRuns] = useState([]);
  const [pendingRunId, setPendingRunId] = useState(initialRunIdRef.current || "");
  const [selectedRunId, setSelectedRunId] = useState("");
  const [runDeliveryInfo, setRunDeliveryInfo] = useState(null);
  const [runResult, setRunResult] = useState(null);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [loadingResult, setLoadingResult] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportError, setExportError] = useState("");
  const [error, setError] = useState("");
  const [seriesVisible, setSeriesVisible] = useState({
    pv: true,
    pcharge: true,
    pvcharge: true,
    gridcharge: true,
    pdischarge: true,
    psell: true,
    deg_efc: false,
  });
  const showEfcAxis = Boolean(seriesVisible?.deg_efc);
  const [chartDisplayMode, setChartDisplayMode] = useState("area");
  const [energyDisplayMode, setEnergyDisplayMode] = useState("chart");
  const [priceDisplayMode, setPriceDisplayMode] = useState("line");
  const [dateFilter, setDateFilter] = useState({ start: "", end: "" });
  const [assetKey, setAssetKey] = useState("");
  const [kpis, setKpis] = useState({});
  const [view, setView] = useState(initialViewState); // weather | market | optimization | bid_schedule | economics
  const [marketFilters, setMarketFilters] = useState(() => {
    const d1 = defaultDPlus1();
    const today = defaultToday();
    return {
      mix: { start: today, end: today },
      generation: { start: d1, end: d1 },
      load: { start: d1, end: d1 },
      pvGenTrigger: { start: d1, end: d1 },
      windGenTrigger: { start: d1, end: d1 },
    };
  });
  const [fullscreen, setFullscreen] = useState(null); // "energy" | "price" | "dam" | "mix" | "generation" | "load" | null
  const [selectedMarket, setSelectedMarket] = useState(AVAILABLE_MARKETS[0]);
  const marketDefaultKeyRef = useRef("");
  const [bidDay, setBidDay] = useState(defaultDPlus1());
  const [bidSchedule, setBidSchedule] = useState(null);
  const [bidScheduleLoading, setBidScheduleLoading] = useState(false);
  const [bidScheduleError, setBidScheduleError] = useState("");
  const [bidScheduleHistory, setBidScheduleHistory] = useState([]);
  const [bidScheduleHistoryLoading, setBidScheduleHistoryLoading] = useState(false);
  const [bidScheduleHistoryError, setBidScheduleHistoryError] = useState("");
  const [batteryHealthState, setBatteryHealthState] = useState(null);
  const [batteryHealthLoading, setBatteryHealthLoading] = useState(false);
  const [batteryHealthError, setBatteryHealthError] = useState("");
  const [batteryHealthHistory, setBatteryHealthHistory] = useState([]);
  const [batteryHealthHistoryLoading, setBatteryHealthHistoryLoading] = useState(false);
  const [batteryHealthHistoryError, setBatteryHealthHistoryError] = useState("");
  const [batteryHealthHistoryMetric, setBatteryHealthHistoryMetric] = useState("soh_end_pct");
  const [batteryHealthHistoryDialogOpen, setBatteryHealthHistoryDialogOpen] = useState(false);
  const [economicsRefreshTick, setEconomicsRefreshTick] = useState(0);
  const [economicsRange, setEconomicsRange] = useState({ start: "", end: "" });
  const [economicsTableRange, setEconomicsTableRange] = useState({ start: "", end: "" });
  const [selectedEconomicsProcess, setSelectedEconomicsProcess] = useState(null);
  const [economicsDetailsProcess, setEconomicsDetailsProcess] = useState(null);
  const [bidScheduleNotice, setBidScheduleNotice] = useState("");
  const [bidScheduleNoticeSeverity, setBidScheduleNoticeSeverity] = useState("info");
  const [bidScheduleNoticeOpen, setBidScheduleNoticeOpen] = useState(false);
  const [bidRefreshing, setBidRefreshing] = useState(false);
  const [bidExporting, setBidExporting] = useState(false);
  const [bidExportError, setBidExportError] = useState("");
  const [economicsExporting, setEconomicsExporting] = useState(false);
  const [economicsExportError, setEconomicsExportError] = useState("");
  const [bidEditMode, setBidEditMode] = useState(false);
  const [bidDraftRows, setBidDraftRows] = useState([]);
  const [bidStrategySettings, setBidStrategySettings] = useState(getDefaultBidStrategySettings());
  const [bidSavedStrategySettings, setBidSavedStrategySettings] = useState(getDefaultBidStrategySettings());
  const [bidStrategyEditMode, setBidStrategyEditMode] = useState(false);
  const [bidStrategySaving, setBidStrategySaving] = useState(false);
  const [bidStrategyExpanded, setBidStrategyExpanded] = useState(false);
  const [marketEconomicsAdvancedExpanded, setMarketEconomicsAdvancedExpanded] = useState(false);
  const [marketSeries, setMarketSeries] = useState({ mix: [], generation: [], load: [] });
  const [marketLoading, setMarketLoading] = useState({ mix: false, generation: false, load: false });
  const [marketError, setMarketError] = useState({ mix: "", generation: "", load: "" });
  const [marketNotices, setMarketNotices] = useState({
    mix: [],
    generation: [],
    load: [],
    dam: [],
    intraday: [],
  });
  const pushMarketNotice = useCallback((section, message, severity = "info") => {
    if (!message) return;
    setMarketNotices((prev) => ({
      ...prev,
      [section]: [...(prev[section] || []), { message, severity }],
    }));
  }, []);
  const clearMarketNotice = useCallback((section) => {
    setMarketNotices((prev) => ({
      ...prev,
      [section]: [],
    }));
  }, []);
  const showBidScheduleNotice = useCallback((message, severity = "info") => {
    setBidScheduleNotice(message || "");
    setBidScheduleNoticeSeverity(severity);
    setBidScheduleNoticeOpen(Boolean(message));
  }, []);
  const handleBidScheduleNoticeClose = useCallback((event, reason) => {
    if (reason === "clickaway") return;
    setBidScheduleNoticeOpen(false);
  }, []);
  const refreshEconomicsTab = useCallback(() => {
    setEconomicsRefreshTick((value) => value + 1);
  }, []);
  const [marketVisible, setMarketVisible] = useState(DEFAULT_VISIBILITY);
  const seededRunIntervalRef = useRef({ runId: "", start: "", end: "" });
  const [marketDisplayMode, setMarketDisplayMode] = useState({
    dam: "chart",
    intraday: "chart",
    compare: "chart",
    balancing: "chart",
    mix: "chart",
    generation: "chart",
    load: "chart",
  });
  const [marketWorkspaceTab, setMarketWorkspaceTab] = useState(
    initialMarketTabParam === "fundamentals" ? "fundamentals" : "prices"
  );
  const [marketInsightsSubtab, setMarketInsightsSubtab] = useState("generation");
  const currentDashboardPageTitle = useMemo(() => {
    if (view === "optimization") return "Historic Runs";
    if (view === "bid_schedule") return "Bid Schedules";
    if (view === "economics") return "Economics";
    if (view === "market") return "Market Data";
    if (view === "weather") return "Weather & Generation";
    return "Dashboard";
  }, [view]);
  const currentDashboardPageSubtitle = useMemo(() => {
    if (view === "optimization") {
      return "Review historical optimization runs, inspect the selected run, and export its results.";
    }
    if (view === "bid_schedule") {
      return "Prepare, inspect, and manage bid schedules for the selected project and asset.";
    }
    if (view === "economics") {
      return "Track settled schedule economics, operating margins, and degradation-adjusted performance.";
    }
    if (view === "market") {
      return "Assess market pricing and published system fundamentals for the selected bidding zone across the chosen analysis interval.";
    }
    if (view === "weather") {
      return "Inspect weather inputs and PV / wind generation series used by the project.";
    }
    return "";
  }, [view]);
  const isStandaloneBessProject = cfg?.systemType === "Standalone BESS";
  const visibleEnergySeriesConfig = useMemo(
    () =>
      isStandaloneBessProject
        ? ENERGY_SERIES.filter((series) => !STANDALONE_BESS_HIDDEN_ENERGY_SERIES.has(series.key))
        : ENERGY_SERIES,
    [isStandaloneBessProject]
  );
  const getMixMetricsForMarket = (market) =>
    MARKET_MIX_METRICS[market] || BASE_MIX_METRICS;
  const projectBiddingZone = useMemo(
    () =>
      normalizeMarketZone(
        config?.biddingZone ||
          config?.market ||
          config?.bidding_zone ||
          currentProject?.config?.biddingZone ||
          currentProject?.config?.market ||
          currentProject?.config?.bidding_zone
      ),
    [
      config?.biddingZone,
      config?.market,
      config?.bidding_zone,
      currentProject?.config?.biddingZone,
      currentProject?.config?.market,
      currentProject?.config?.bidding_zone,
    ]
  );
  const supportedProjectMarket = useMemo(
    () => getSupportedMarketZone(projectBiddingZone),
    [projectBiddingZone]
  );
  const biddingZoneLabel = useMemo(
    () => BIDDING_ZONE_LABELS[selectedMarket] || selectedMarket || "N/A",
    [selectedMarket]
  );
  const selectedMarketTimeZone = useMemo(
    () => BIDDING_ZONE_TIME_ZONES[selectedMarket] || "UTC",
    [selectedMarket]
  );
  const selectedMarketTimeZoneLabel = useMemo(
    () => getShortTimeZoneLabel(selectedMarketTimeZone),
    [selectedMarketTimeZone]
  );
  const marketStrategyDisabled = useMemo(
    () => isUnsupportedMarketZone(projectBiddingZone),
    [projectBiddingZone]
  );
  const supportedMarketsLabel = useMemo(() => getSupportedMarketsLabel(), []);
  const demoExportDisabledMessage = useMemo(
    () => getDemoExportDisabledMessage(demoProfile),
    [demoProfile]
  );
  const bidWriteDisabledMessage = useMemo(
    () => getDemoBidWriteDisabledMessage(demoProfile),
    [demoProfile]
  );
  const bidWriteDisabled = Boolean(bidWriteDisabledMessage);
  const windGenerationDisabledMessage = useMemo(
    () => getDemoWindSystemDisabledMessage(demoProfile),
    [demoProfile]
  );
  const windGenerationDisabled = Boolean(windGenerationDisabledMessage);
  const marketDataDisabledMessage = useMemo(
    () => getDemoMarketDataDisabledMessage(demoProfile),
    [demoProfile]
  );
  const marketDataDisabled = Boolean(marketDataDisabledMessage);
  const currentMixMetrics = useMemo(
    () => getMixMetricsForMarket(selectedMarket),
    [selectedMarket]
  );
  const currentGenerationMetrics = useMemo(
    () => MARKET_GENERATION_METRICS[selectedMarket] || BASE_GENERATION_METRICS,
    [selectedMarket]
  );

  const handleExportExcel = async () => {
    setExportError("");
    if (demoExportDisabledMessage) {
      setExportError(demoExportDisabledMessage);
      return;
    }
    if (!runSuccess) {
      setExportError("Run is not successful - no Excel available.");
      return;
    }

    try {
      setExportingExcel(true);
      if (!currentProject?.id) {
        throw new Error("Cannot export: project_id is missing.");
      }
      if (!selectedRunId) {
        throw new Error("Cannot export: run_id is missing.");
      }
      const batteryName =
        assetKey || config?.bess?.activeName || selectedBatteries?.[0]?.name || "";
      if (!batteryName) {
        throw new Error("Cannot export: battery_name is missing.");
      }

      const payload = {
        project_id: String(currentProject.id),
        run_id: String(selectedRunId),
        battery_name: batteryName,
      };

      const { blob, filename } = await exportBessExcel(payload, accessToken);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "opt_results.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(formatDashboardError(err, "Failed to export Excel."));
    } finally {
      setExportingExcel(false);
    }
  };

  const handleMarketChange = useCallback((value) => {
    setSelectedMarket(value);
    setMarketSeries({ mix: [], generation: [], load: [] });
    setMarketError({ mix: "", generation: "", load: "" });
    setMarketLoading({ mix: false, generation: false, load: false });
    clearMarketNotice("mix");
    clearMarketNotice("generation");
    clearMarketNotice("load");
    clearMarketNotice("dam");
    clearMarketNotice("intraday");
    const seeded = seededRunIntervalRef.current;
    const runScopedPreset =
      hasRunResultsContext && seeded.start
        ? buildRangeDatesFromInterval(seeded.start, seeded.end)
        : null;
    const defaultRanges = runScopedPreset || buildDefaultRangeDates();
    const formatRange = (range) => ({
      start: formatDateLocal(range[0]),
      end: formatDateLocal(range[1]),
    });
    setMarketFilters({
      mix: formatRange(defaultRanges.value.mix),
      generation: formatRange(defaultRanges.value.generation),
      load: formatRange(defaultRanges.value.load),
    });
    setRangeValue(defaultRanges.value);
    setRangeMonth(defaultRanges.month);
    const damDefault = runScopedPreset
      ? { start: runScopedPreset.start, end: runScopedPreset.end }
      : { start: defaultDPlus1(), end: defaultDPlus1() };
    setDamRange(damDefault);
    setDamSeries([]);
    setDamError("");
    setDamMetrics(null);
    setDamLoading(false);
    setIntradayRange(
      runScopedPreset
        ? { start: runScopedPreset.start, end: runScopedPreset.end }
        : { start: defaultToday(), end: defaultToday() }
    );
    setIntradaySeries([]);
    setIntradayError("");
    setIntradayLoading(false);
    setIntradayProduct(INTRADAY_PRODUCTS[0]);
    setIntradaySeriesMode("both");
    setCompareRange({ start: defaultToday(), end: defaultToday() });
    setActualComparisonSeries([]);
    setActualComparisonError("");
    setActualComparisonLoading(false);
    setWeatherRange(
      runScopedPreset
        ? { start: runScopedPreset.start, end: runScopedPreset.end }
        : { start: defaultDPlus1(), end: defaultDPlus1() }
    );
    if (runScopedPreset) {
      setBidDay(runScopedPreset.start);
    }
  }, [clearMarketNotice, hasRunResultsContext]);
  useEffect(() => {
    const nextDefaultMarket = supportedProjectMarket || AVAILABLE_MARKETS[0];
    const projectKey = `${currentProject?.id || "none"}:${nextDefaultMarket}`;
    if (marketDefaultKeyRef.current === projectKey) return;
    marketDefaultKeyRef.current = projectKey;
    handleMarketChange(nextDefaultMarket);
  }, [currentProject?.id, supportedProjectMarket, handleMarketChange]);
  const [rangeAnchor, setRangeAnchor] = useState(null);
  const [rangeSection, setRangeSection] = useState(null);
  const defaultRangeDates = useMemo(() => {
    const today = defaultToday();
    const tomorrow = defaultDPlus1();
    const todayStart = parseLocalDateTime(`${today}T00:00:00`);
    const todayEnd = parseLocalDateTime(`${today}T23:59:59`);
    const tomorrowStart = parseLocalDateTime(`${tomorrow}T00:00:00`);
    const tomorrowEnd = parseLocalDateTime(`${tomorrow}T23:59:59`);
    return {
      value: {
        mix: [todayStart, todayEnd],
        generation: [todayStart, todayEnd],
        load: [todayStart, todayEnd],
        dam: [tomorrowStart, tomorrowEnd],
        intraday: [todayStart, todayEnd],
        compare: [todayStart, todayEnd],
        balancing: [todayStart, todayEnd],
        weather: [tomorrowStart, tomorrowEnd],
        simulation: [tomorrowStart, tomorrowEnd],
      },
      month: {
        mix: todayStart,
        generation: todayStart,
        load: todayStart,
        dam: tomorrowStart,
        intraday: todayStart,
        compare: todayStart,
        balancing: todayStart,
        weather: tomorrowStart,
        simulation: tomorrowStart,
      },
    };
  }, []);
  const [rangeValue, setRangeValue] = useState(defaultRangeDates.value);
  const [rangeMonth, setRangeMonth] = useState(defaultRangeDates.month);
  const [damRange, setDamRange] = useState(() => {
    const d1 = defaultDPlus1();
    return { start: d1, end: d1 };
  });
  const [damSeries, setDamSeries] = useState([]);
  const [damLoading, setDamLoading] = useState(false);
  const [damError, setDamError] = useState("");
  const [damMetrics, setDamMetrics] = useState(null);
  const [showDamForecastMetrics, setShowDamForecastMetrics] = useState(true);
  const [showDamActualStats, setShowDamActualStats] = useState(false);
  const [damVisible, setDamVisible] = useState({
    actual: true,
    p10: true,
    p50: true,
    p90: true,
  });
  const damActualStats = useMemo(() => computeDamActualStats(damSeries), [damSeries]);
  const hasDamActualPriceData = Boolean(damActualStats?.count);
  const showDamActualSeries = hasDamActualPriceData && damVisible.actual;
  // Αγορά μόνο πραγματικών τιμών: η πλατφόρμα δεν δημοσιεύει πρόβλεψη
  // DAM για κάθε αγορά του SUPPORTED_MARKETS (βλ. DAM_FORECAST_MARKETS στο
  // utils/marketSupport.js). Τα p10/p50/p90 έρχονται null για τέτοια αγορά,
  // οπότε χωρίς αυτόν τον έλεγχο ο χρήστης βλέπει τρία checkbox πρόβλεψης
  // που δεν εναλλάσσουν τίποτα και ένα "Forecast Metrics" που δεν δείχνει
  // ποτέ μετρικές - δηλαδή υπόσχεση δεδομένων που δεν υπάρχουν.
  const marketPublishesDamForecast = supportsDamForecast(selectedMarket);
  const [intradayRange, setIntradayRange] = useState(() => {
    const today = defaultToday();
    return { start: today, end: today };
  });
  const [intradaySeries, setIntradaySeries] = useState([]);
  const [intradayLoading, setIntradayLoading] = useState(false);
  const [intradayError, setIntradayError] = useState("");
  const [intradayProduct, setIntradayProduct] = useState(INTRADAY_PRODUCTS[0]);
  const [intradaySeriesMode, setIntradaySeriesMode] = useState("both");
  const [intradayVisible, setIntradayVisible] = useState({
    actual: true,
    p10: true,
    p50: true,
    p90: true,
    vwap: true,
    min: true,
    max: true,
  });
  const [showIntradayForecastMetrics, setShowIntradayForecastMetrics] = useState(true);
  const [showIntradayActualStats, setShowIntradayActualStats] = useState(false);
  const intradayActualStats = useMemo(() => computeDamActualStats(intradaySeries), [intradaySeries]);
  const hasIntradayActualPriceData = Boolean(intradayActualStats?.count);
  const showIntradayActualSeries = hasIntradayActualPriceData && intradayVisible.actual;
  const isXbidProduct = intradayProduct === "XBID";
  const [xbidDuration, setXbidDuration] = useState(15);
  const [xbidShowVolume, setXbidShowVolume] = useState(false);
  const [xbidSides, setXbidSides] = useState(["Sell"]);
  const [xbidAssets, setXbidAssets] = useState(["SUPPLY"]);
  const [xbidTechnologies, setXbidTechnologies] = useState(() =>
    XBID_TECHNOLOGIES.filter((t) => XBID_ASSET_TECH.SUPPLY.includes(t))
  );
  const [xbidVisible, setXbidVisible] = useState({});
  const [xbidGroups, setXbidGroups] = useState([]);
  const xbidHasSelection =
    xbidSides.length > 0 || xbidAssets.length > 0 || xbidTechnologies.length > 0;
  // Technology (CLASSIFICATION) options cascade from the selected asset(s).
  const xbidAvailableTechnologies = useMemo(() => {
    if (!xbidAssets.length) return XBID_TECHNOLOGIES;
    const allowed = new Set();
    xbidAssets.forEach((a) => (XBID_ASSET_TECH[a] || []).forEach((t) => allowed.add(t)));
    return XBID_TECHNOLOGIES.filter((t) => allowed.has(t));
  }, [xbidAssets]);
  // Default to all technologies of the chosen asset selected.
  useEffect(() => {
    setXbidTechnologies(xbidAvailableTechnologies);
  }, [xbidAvailableTechnologies]);
  // No filter -> market Total (VWAP/Min/Max). With filters -> each selected group
  // gets its own VWAP (solid) + Min + Max (dashed), one colour per group.
  const xbidChartSeries = useMemo(() => {
    if (!xbidHasSelection) {
      return [
        { key: "vwap", label: "VWAP", color: XBID_SERIES_COLORS.vwap, dashed: false },
        { key: "min", label: "Min Price", color: XBID_SERIES_COLORS.min, dashed: true },
        { key: "max", label: "Max Price", color: XBID_SERIES_COLORS.max, dashed: true },
      ];
    }
    return xbidGroups.flatMap((label, i) => {
      const color = XBID_GROUP_PALETTE[i % XBID_GROUP_PALETTE.length];
      return [
        { key: `${label}::vwap`, label: `${label} VWAP`, color, dashed: false },
        { key: `${label}::min`, label: `${label} Min`, color, dashed: true },
        { key: `${label}::max`, label: `${label} Max`, color, dashed: true },
      ];
    });
  }, [xbidHasSelection, xbidGroups]);
  const xbidVolumeSeries = useMemo(() => {
    if (!xbidHasSelection) {
      return [{ key: "volume", label: "Volume", color: XBID_SERIES_COLORS.volume }];
    }
    return xbidGroups.map((label, i) => ({
      key: `vol::${label}`,
      label: `${label} vol`,
      color: XBID_GROUP_PALETTE[i % XBID_GROUP_PALETTE.length],
    }));
  }, [xbidHasSelection, xbidGroups]);
  const intradayMetrics = useMemo(() => computeDamForecastMetrics(intradaySeries), [intradaySeries]);
  const [compareRange, setCompareRange] = useState(() => {
    const today = defaultToday();
    return { start: today, end: today };
  });
  const [actualComparisonSeries, setActualComparisonSeries] = useState([]);
  const [actualComparisonLoading, setActualComparisonLoading] = useState(false);
  const [actualComparisonError, setActualComparisonError] = useState("");
  const [actualComparisonVisible, setActualComparisonVisible] = useState({
    dam: true,
    ida1: true,
    ida2: true,
    ida3: true,
  });
  const [balancingRange, setBalancingRange] = useState(() => {
    const today = defaultToday();
    return { start: today, end: today };
  });
  const [balancingDataset, setBalancingDataset] = useState("imbalance_prices");
  const [balancingRows, setBalancingRows] = useState([]);
  const [balancingLoading, setBalancingLoading] = useState(false);
  const [balancingFetchError, setBalancingFetchError] = useState("");
  const [balancingMetric, setBalancingMetric] = useState("");
  const [balancingZone, setBalancingZone] = useState("all");
  const [hiddenBalancingSeries, setHiddenBalancingSeries] = useState([]);
  const [weatherRange, setWeatherRange] = useState(() => {
    const d1 = defaultDPlus1();
    return { start: d1, end: d1 };
  });
  const [weatherMode, setWeatherMode] = useState("forecast"); // forecast | historical
  const [weatherSeries, setWeatherSeries] = useState({ irradiance: [], temperature: [], humidity: [], wind: [] });
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState("");
  const [weatherNotices, setWeatherNotices] = useState([]);
  const [weatherVisible, setWeatherVisible] = useState(WEATHER_DEFAULT_VISIBILITY);
  const [weatherWorkspaceTab, setWeatherWorkspaceTab] = useState("weather"); // weather | generation
  const [windWeatherSeries, setWindWeatherSeries] = useState([]);
  const [windWeatherLoading, setWindWeatherLoading] = useState(false);
  const [windWeatherError, setWindWeatherError] = useState("");
  const [windWeatherNotices, setWindWeatherNotices] = useState([]);
  const [windWeatherUpperLabel, setWindWeatherUpperLabel] = useState("");
  const [windWeatherVisible, setWindWeatherVisible] = useState({ ten: true, upper: true });
  const [windGenRunType, setWindGenRunType] = useState("forecast"); // forecast | actual_weather
  const [pvGenRunType, setPvGenRunType] = useState("forecast"); // forecast | actual_weather
  const [pvGenTriggering, setPvGenTriggering] = useState(false);
  const [pvGenTriggerError, setPvGenTriggerError] = useState("");
  const [pvGenRuns, setPvGenRuns] = useState([]);
  const [pvGenRunsLoading, setPvGenRunsLoading] = useState(false);
  const [pvGenRunsError, setPvGenRunsError] = useState("");
  const [pvGenSelectedRunIds, setPvGenSelectedRunIds] = useState([]);
  const [pvGenSeriesByRun, setPvGenSeriesByRun] = useState({});
  const [pvGenSeriesLoadingIds, setPvGenSeriesLoadingIds] = useState([]);
  // Always attempt to include measured SCADA data when available; there is no
  // user-facing toggle since projects without a SCADA connection have nothing to toggle.
  const [pvGenIncludeMeasured] = useState(true);
  const [pvGenMeasuredSeries, setPvGenMeasuredSeries] = useState([]);
  const [pvGenMeasuredAvailable, setPvGenMeasuredAvailable] = useState(false);
  const [pvGenMeasuredLoading, setPvGenMeasuredLoading] = useState(false);
  const [pvGenMeasuredError, setPvGenMeasuredError] = useState("");
  const [pvGenExporting, setPvGenExporting] = useState(false);
  const [pvGenExportError, setPvGenExportError] = useState("");
  const [pvGenConvergenceDate, setPvGenConvergenceDate] = useState(() => defaultDPlus1());
  const [pvGenConvergenceMeasured, setPvGenConvergenceMeasured] = useState(null);
  const [pvGenConvergenceMeasuredLoading, setPvGenConvergenceMeasuredLoading] = useState(false);
  const [pvGenConvergenceMeasuredError, setPvGenConvergenceMeasuredError] = useState("");
  const mixChartData = useMemo(() => {
    return (marketSeries.mix || []).map((row) => {
      const tKey = hourKey(row?.time);
      const time = tKey ? new Date(tKey) : row?.time || null;
      return { ...row, time };
    });
  }, [marketSeries.mix]);
  const generationChartData = useMemo(() => {
    return (marketSeries.generation || []).map((row) => {
      const tKey = hourKey(row?.time);
      const time = tKey ? new Date(tKey) : row?.time || null;
      return { ...row, time };
    });
  }, [marketSeries.generation]);
  // Ensure total is always present in the data fed to charts
  const { hasPv: projectHasPv, hasWind: windConfigEnabled } = deriveProjectAssets(config);
  const weatherAsset = windConfigEnabled ? "wind" : "pv";
  const windLocationActive = Boolean(
    windConfigEnabled &&
      config?.wind?.useProjectLocation === false &&
      config?.wind?.loc?.lat !== "" &&
      config?.wind?.loc?.lat != null &&
      config?.wind?.loc?.lon !== "" &&
      config?.wind?.loc?.lon != null
  );
  // Weather location follows the selected asset: PV uses the project location,
  // Wind uses the wind park location when one is configured.
  const weatherLat =
    weatherAsset === "wind" && windLocationActive
      ? config.wind.loc.lat
      : config?.loc_info?.lat ?? null;
  const weatherLon =
    weatherAsset === "wind" && windLocationActive
      ? config.wind.loc.lon
      : config?.loc_info?.lon ?? null;
  const weatherTz = config?.timeZone ?? null;
  const optimizationChartTimeZone = useMemo(() => {
    // A run keeps its original clock even after the project settings change.
    const candidate = runDeliveryInfo?.timezone || runDeliveryInfo?.tz ||
      config?.timeZone ||
      config?.timezone ||
      currentProject?.timeZone ||
      currentProject?.timezone ||
      "";
    if (isValidTimeZone(candidate)) return candidate;
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  }, [runDeliveryInfo, config?.timeZone, config?.timezone, currentProject?.timeZone, currentProject?.timezone]);
  const applyRunIntervalPresets = useCallback((startValue, endValue, runId = "") => {
    const preset = buildRangeDatesFromInterval(startValue, endValue);
    setDateFilter({ start: preset.start, end: preset.end });
    setMarketFilters(preset.marketFilters);
    setRangeValue((prev) => ({
      ...prev,
      dam: preset.value.dam,
      intraday: preset.value.mix,
      compare: [parseLocalDateTime(`${defaultToday()}T00:00:00`), parseLocalDateTime(`${defaultToday()}T23:59:59`)],
      weather: preset.value.weather,
      simulation: preset.value.simulation,
      mix: preset.value.mix,
      generation: preset.value.generation,
      load: preset.value.load,
    }));
    setRangeMonth((prev) => ({
      ...prev,
      dam: preset.month.dam,
      intraday: preset.month.mix,
      compare: parseLocalDateTime(`${defaultToday()}T00:00:00`),
      weather: preset.month.weather,
      simulation: preset.month.simulation,
      mix: preset.month.mix,
      generation: preset.month.generation,
      load: preset.month.load,
    }));
    setDamRange({ start: preset.start, end: preset.end });
    setIntradayRange({ start: preset.start, end: preset.end });
    setCompareRange({ start: defaultToday(), end: defaultToday() });
    setWeatherRange({ start: preset.start, end: preset.end });
    setBidDay(preset.start);
    setEconomicsRange({ start: preset.start, end: preset.end });
    setEconomicsTableRange({ start: preset.start, end: preset.end });
    seededRunIntervalRef.current = {
      runId: String(runId || ""),
      start: preset.start,
      end: preset.end,
    };
  }, []);
  // Reset optimization state when project changes to avoid showing stale results.
  useEffect(() => {
    setRuns([]);
    setPendingRunId("");
    setSelectedRunId("");
    setRunResult(null);
    setKpis({});
    setAssetKey("");
    setDateFilter({ start: "", end: "" });
    setError("");
    setBidSchedule(null);
    setBidScheduleError("");
    setBatteryHealthState(null);
    setBatteryHealthError("");
    showBidScheduleNotice("");
    setBidExportError("");
    setBidEditMode(false);
    setBidDraftRows([]);
    setBidStrategySettings(getDefaultBidStrategySettings());
    setBidSavedStrategySettings(getDefaultBidStrategySettings());
    setBidStrategyEditMode(false);
    setBidDay(defaultDPlus1());
    seededRunIntervalRef.current = { runId: "", start: "", end: "" };
  }, [currentProject?.id, showBidScheduleNotice]);
  // Fetch runs when project changes
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const fetchRuns = async () => {
      if (!currentProject?.id) return;
      setLoadingRuns(true);
      setError("");
      try {
        const list = await listRunsByProject(currentProject.id, accessToken, {
          signal: controller.signal,
        });
        if (!active) return;
        const sorted = Array.isArray(list)
          ? [...list].sort(
              (a, b) =>
                new Date(b.started_at || b.created_at || 0) -
                new Date(a.started_at || a.created_at || 0)
            )
          : [];
        setRuns(sorted);
        if (sorted.length > 0) {
          const desired = pendingRunId || initialRunIdRef.current;
          const found = desired ? sorted.find((r) => matchesRunId(r, desired)) : null;
          if (found) {
            setSelectedRunId(normalizeRunId(found));
          } else if (desired) {
            setSelectedRunId(String(desired));
          } else {
            setSelectedRunId(normalizeRunId(sorted[0]));
          }
          initialRunIdRef.current = "";
        } else {
          const desired = pendingRunId || initialRunIdRef.current;
          setSelectedRunId(desired ? String(desired) : "");
        }
      } catch (err) {
        if (err?.name === "AbortError") return;
        if (!active) return;
        setError(formatDashboardError(err, "Failed to load runs."));
        setRuns([]);
        setSelectedRunId("");
      } finally {
        if (active) setLoadingRuns(false);
      }
    };
    fetchRuns();
    return () => {
      active = false;
      controller.abort();
    };
  }, [currentProject?.id, accessToken, pendingRunId]);
  // Fetch summary/result for selected run
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const fetchSummary = async () => {
      if (!selectedRunId) {
        setRunResult(null);
        return;
      }
      setLoadingResult(true);
      setError("");
      try {
        const summary = await getRunSummary(selectedRunId, accessToken, {
          signal: controller.signal,
        });
        if (!active) return;
        const normalizedResult = summary?.result || summary?.data || summary || null;
        const summaryChartData = buildChartData(normalizedResult, "", summary?.timezone || optimizationChartTimeZone);
        setRunResult(normalizedResult);
        setRunDeliveryInfo((previous) => {
          const next = {
            run_id: String(selectedRunId),
            delivery_day: summary?.delivery_day,
            timezone: summary?.timezone,
            input_payload: summary?.input_payload,
            simStart: summary?.start || summary?.time_start || summary?.meta?.start || summary?.simulation_start,
            simEnd: summary?.end || summary?.time_end || summary?.meta?.end || summary?.simulation_end,
          };
          return JSON.stringify(previous) === JSON.stringify(next) ? previous : next;
        });
        setKpis(summary?.kpis || {});
        // if summary contains start/end of simulation, seed filter
        const simStart =
          summary?.start ||
          summary?.time_start ||
          summary?.meta?.start ||
          summary?.simulation_start ||
          null;
        const simEnd =
          summary?.end ||
          summary?.time_end ||
          summary?.meta?.end ||
          summary?.simulation_end ||
          null;
        const deliveryPeriod = runDeliveryPeriod({
          ...summary, simStart, simEnd,
        }, optimizationChartTimeZone);
        const simulationStartDate = deliveryPeriod.start;
        const simulationEndDate = deliveryPeriod.end;
        if (hasRunResultsContext && simulationStartDate) {
          const seeded = seededRunIntervalRef.current;
          if (
            seeded.runId !== String(selectedRunId) ||
            seeded.start !== simulationStartDate ||
            seeded.end !== simulationEndDate
          ) {
            applyRunIntervalPresets(simulationStartDate, simulationEndDate, selectedRunId);
          }
        } else if (simStart && simEnd && !summaryChartData.hasExplicitTimeIndex) {
          setDateFilter({
            start: simulationStartDate,
            end: simulationEndDate,
          });
        }
        // seed asset if available
        const assets =
          (summary?.result || summary?.data || summary)?.energy_flows
            ? Object.keys((summary?.result || summary?.data || summary).energy_flows)
            : [];
        if (assets.length > 0) setAssetKey((prev) => (prev && assets.includes(prev) ? prev : assets[0]));
      } catch (err) {
        if (err?.name === "AbortError") return;
        if (!active) return;
        setError(formatDashboardError(err, "Failed to load run summary."));
        setRunResult(null);
      } finally {
        if (active) setLoadingResult(false);
      }
    };
    fetchSummary();
    return () => {
      active = false;
      controller.abort();
    };
  }, [
    accessToken,
    applyRunIntervalPresets,
    hasRunResultsContext,
    optimizationChartTimeZone,
    selectedRunId,
  ]);
  useEffect(() => {
    let active = true;
    const loadBidSettings = async () => {
      if (!currentProject?.id || !assetKey) {
        if (active) setBidStrategySettings(getDefaultBidStrategySettings());
        return;
      }
      try {
        const payload = await getBidScheduleSettings(
          {
            project_id: currentProject.id,
            asset_id: assetKey,
          },
          accessToken
        );
        if (!active) return;
        const normalized = normalizeBidStrategySettings(payload?.settings || {});
        setBidSavedStrategySettings(normalized);
        setBidStrategySettings(normalized);
        setBidStrategyEditMode(false);
      } catch (err) {
        if (!active) return;
        const defaults = getDefaultBidStrategySettings();
        setBidSavedStrategySettings(defaults);
        setBidStrategySettings(defaults);
        setBidStrategyEditMode(false);
        showBidScheduleNotice(
          formatDashboardError(err, "Falling back to default bid settings."),
          "warning"
        );
      }
    };
    loadBidSettings();
    return () => {
      active = false;
    };
  }, [accessToken, assetKey, currentProject?.id, showBidScheduleNotice]);
  useEffect(() => {
    let active = true;
    const loadBidSchedule = async () => {
      if (!currentProject?.id || !assetKey || !bidDay) {
        if (active) {
          setBidSchedule(null);
          setBidScheduleLoading(false);
        }
        return;
      }
      setBidScheduleLoading(true);
      setBidScheduleError("");
      showBidScheduleNotice("");
      try {
        const existing = await getBidSchedule(
          {
            project_id: currentProject.id,
            asset_id: assetKey,
            bid_day: bidDay,
            market: selectedMarket,
          },
          accessToken
        );
        if (!active) return;
        setBidSchedule(existing);
      } catch (err) {
        if (!active) return;
        setBidSchedule(null);
        setBidScheduleError(formatDashboardError(err, "Failed to load the bid schedule."));
      } finally {
        if (active) setBidScheduleLoading(false);
      }
    };
    loadBidSchedule();
    return () => {
      active = false;
    };
  }, [accessToken, assetKey, bidDay, currentProject?.id, economicsRefreshTick, selectedMarket, showBidScheduleNotice]);
  useEffect(() => {
    let active = true;
    const loadBatteryHealthState = async () => {
      if (!currentProject?.id || !assetKey) {
        if (active) {
          setBatteryHealthState(null);
          setBatteryHealthLoading(false);
          setBatteryHealthError("");
        }
        return;
      }
      setBatteryHealthLoading(true);
      setBatteryHealthError("");
      try {
        const payload = await getLatestBatteryState(
          {
            project_id: currentProject.id,
            asset_id: assetKey,
            market: selectedMarket,
          },
          accessToken
        );
        if (!active) return;
        setBatteryHealthState(normalizeBatteryHealthState(payload));
      } catch (err) {
        if (!active) return;
        setBatteryHealthState(null);
        setBatteryHealthError(formatBatteryHealthError(err));
      } finally {
        if (active) setBatteryHealthLoading(false);
      }
    };
    loadBatteryHealthState();
    return () => {
      active = false;
    };
  }, [accessToken, assetKey, currentProject?.id, economicsRefreshTick, selectedMarket]);
  useEffect(() => {
    let active = true;
    const loadBatteryHealthHistory = async () => {
      if (!currentProject?.id || !assetKey) {
        if (active) {
          setBatteryHealthHistory([]);
          setBatteryHealthHistoryLoading(false);
          setBatteryHealthHistoryError("");
        }
        return;
      }
      setBatteryHealthHistoryLoading(true);
      setBatteryHealthHistoryError("");
      try {
        const payload = await getBatteryStateHistory(
          {
            project_id: currentProject.id,
            asset_id: assetKey,
            market: selectedMarket,
          },
          accessToken
        );
        if (!active) return;
        const items = Array.isArray(payload?.items)
          ? payload.items
          : Array.isArray(payload)
            ? payload
            : [];
        const normalized = items
          .map((item) => normalizeBatteryHealthState(item))
          .filter(Boolean)
          .sort((a, b) => String(a.state_day || "").localeCompare(String(b.state_day || "")));
        setBatteryHealthHistory(normalized);
      } catch (err) {
        if (!active) return;
        setBatteryHealthHistory([]);
        setBatteryHealthHistoryError(formatBatteryHealthError(err));
      } finally {
        if (active) setBatteryHealthHistoryLoading(false);
      }
    };
    loadBatteryHealthHistory();
    return () => {
      active = false;
    };
  }, [
    accessToken,
    assetKey,
    currentProject?.id,
    economicsRefreshTick,
    selectedMarket,
  ]);
  useEffect(() => {
    let active = true;
    const loadBidScheduleHistory = async () => {
      if (!currentProject?.id || !assetKey) {
        if (active) {
          setBidScheduleHistory([]);
          setBidScheduleHistoryLoading(false);
          setBidScheduleHistoryError("");
        }
        return;
      }

      setBidScheduleHistoryLoading(true);
      setBidScheduleHistoryError("");
      try {
        const items = await listBidSchedules(
          {
            project_id: currentProject.id,
            asset_id: assetKey,
            market: selectedMarket,
          },
          accessToken
        );
        if (!active) return;
        if (Array.isArray(items) && items.length > 0) {
          setBidScheduleHistory(items);
          return;
        }

        const runDates = runs
          .map((run) => {
            const value = run?.started_at || run?.created_at || "";
            const dateKey = String(value).slice(0, 10);
            return /^\d{4}-\d{2}-\d{2}$/.test(dateKey) ? dateKey : "";
          })
          .filter(Boolean)
          .sort();
        const fallbackStart = runDates[0] || bidSchedule?.bid_day || bidDay;
        const fallbackEnd = [bidSchedule?.bid_day, bidDay, defaultDPlus1()]
          .filter(Boolean)
          .sort()
          .slice(-1)[0] || bidDay;
        const dateKeys = enumerateDateKeys(fallbackStart, fallbackEnd, 180);

        if (dateKeys.length === 0) {
          setBidScheduleHistory([]);
          return;
        }

        const scanned = await Promise.all(
          dateKeys.map((dateKey) =>
            getBidSchedule(
              {
                project_id: currentProject.id,
                asset_id: assetKey,
                bid_day: dateKey,
                market: selectedMarket,
              },
              accessToken
            ).catch(() => null)
          )
        );
        if (!active) return;
        setBidScheduleHistory(scanned.filter(Boolean));
      } catch (err) {
        if (!active) return;
        setBidScheduleHistory([]);
        setBidScheduleHistoryError("");
      } finally {
        if (active) setBidScheduleHistoryLoading(false);
      }
    };
    loadBidScheduleHistory();
    return () => {
      active = false;
    };
  }, [accessToken, assetKey, bidDay, bidSchedule?.bid_day, currentProject?.id, economicsRefreshTick, runs, selectedMarket]);
  const chartData = useMemo(() => buildChartData(runResult, assetKey, optimizationChartTimeZone), [runResult, assetKey, optimizationChartTimeZone]);
  const bidAssetOptions = useMemo(() => {
    const configured = (selectedBatteries || []).map((battery) => battery?.name).filter(Boolean);
    const merged = [...configured, ...(chartData.assets || [])];
    return Array.from(new Set(merged));
  }, [chartData.assets, selectedBatteries]);
  const runSuccess = useMemo(() => {
    if (!runResult) return false;

    // common shapes you may have
    const successFlag =
      runResult.success ??
      runResult?.result?.success ??
      runResult?.data?.success ??
      null;

    if (typeof successFlag === "boolean") return successFlag;

    // fallback: if we have series data, treat as success
    return Array.isArray(chartData?.data) && chartData.data.length > 0;
  }, [runResult, chartData.data]);
  const simulationMinDate =
    chartData.data.length > 0
      ? chartData.hasExplicitTimeIndex
        ? parseDateKeyToLocalDate(chartData.startDateKey, false)
        : chartData.start || chartData.data[0]?.time
      : null;
  const simulationMaxDate =
    chartData.data.length > 0
      ? chartData.hasExplicitTimeIndex
        ? parseDateKeyToLocalDate(chartData.endDateKey, true)
        : chartData.end || chartData.data[chartData.data.length - 1]?.time
      : null;

  // Sync view/run selection from URL params
  useEffect(() => {
    const qpView = searchParams.get("view");
    if (qpView && ["weather", "market", "optimization", "bid_schedule", "economics"].includes(qpView)) {
      setView((current) => (current === qpView ? current : qpView));
    }
    const qpMarketTab = searchParams.get("marketTab");
    if (qpMarketTab === "fundamentals" || qpMarketTab === "prices") {
      setMarketWorkspaceTab((current) => (current === qpMarketTab ? current : qpMarketTab));
    }

    const qpRun = searchParams.get("runId") || "";
    if (!qpRun) {
      lastRunParamRef.current = "";
    } else if (qpRun !== lastRunParamRef.current) {
      lastRunParamRef.current = qpRun;
      setPendingRunId(qpRun);
    }
  }, [searchParams]);
  // Seed date filter from data range
  useEffect(() => {
    if (chartData.data.length === 0) {
      if (
        hasRunResultsContext &&
        seededRunIntervalRef.current.runId === String(selectedRunId || "") &&
        seededRunIntervalRef.current.start
      ) {
        setDateFilter({
          start: seededRunIntervalRef.current.start,
          end: seededRunIntervalRef.current.end,
        });
        return;
      }
      setDateFilter({ start: "", end: "" });
      return;
    }

    const minTime = chartData.start;
    const maxTime = chartData.end;
    const nextStart = chartData.hasExplicitTimeIndex
      ? chartData.startDateKey || ""
      : minTime
        ? formatSimulationBoundaryDate(minTime, optimizationChartTimeZone, false)
        : "";
    const nextEnd = chartData.hasExplicitTimeIndex
      ? chartData.endDateKey || ""
      : maxTime
        ? formatSimulationBoundaryDate(maxTime, optimizationChartTimeZone, true)
        : "";

    if (hasRunResultsContext && nextStart && nextEnd) {
      const seeded = seededRunIntervalRef.current;
      if (
        seeded.runId !== String(selectedRunId || "") ||
        seeded.start !== nextStart ||
        seeded.end !== nextEnd
      ) {
        applyRunIntervalPresets(nextStart, nextEnd, selectedRunId);
      } else {
        setDateFilter({
          start: seeded.start,
          end: seeded.end,
        });
      }
      return;
    }

    setDateFilter({ start: nextStart, end: nextEnd });
  }, [
    applyRunIntervalPresets,
    chartData.start,
    chartData.end,
    chartData.startDateKey,
    chartData.endDateKey,
    chartData.hasExplicitTimeIndex,
    chartData.data.length,
    hasRunResultsContext,
    optimizationChartTimeZone,
    selectedRunId,
  ]);
  const filteredChartData = useMemo(() => {
    if (!chartData.data.length) return chartData;
    if (chartData.hasExplicitTimeIndex) {
      const filtered = chartData.data.filter(
        (d) =>
          (!dateFilter.start || d.timeDate >= dateFilter.start) &&
          (!dateFilter.end || d.timeDate <= dateFilter.end)
      );
      return { ...chartData, data: filtered };
    }
    const startDate = dateFilter.start
      ? buildDateTimeInTimeZone(dateFilter.start, optimizationChartTimeZone, false)
      : chartData.data[0].time;
    const endDate = dateFilter.end
      ? buildDateTimeInTimeZone(dateFilter.end, optimizationChartTimeZone, true)
      : chartData.data[chartData.data.length - 1].time;
    const filtered = chartData.data.filter(
      (d) => d.time >= startDate && d.time <= endDate
    );
    return { ...chartData, data: filtered };
  }, [chartData, dateFilter, optimizationChartTimeZone]);
  const bidScheduleRows = useMemo(
    () => (Array.isArray(bidSchedule?.rows) ? bidSchedule.rows : []),
    [bidSchedule?.rows]
  );
  const hasBidScheduleRows = bidScheduleRows.length > 0;
  const bidScheduleStatusLabel = !hasBidScheduleRows
    ? "-"
    : bidSchedule?.status === "submitted"
      ? "Finalised in Pythia"
      : "Draft";
  const canRefreshDraftFromLatestRun =
    bidSchedule?.status === "draft" &&
    !bidScheduleLoading &&
    !bidRefreshing &&
    Boolean(assetKey) &&
    Boolean(bidDay) &&
    !bidEditMode;
  useEffect(() => {
    if (!bidEditMode) {
      setBidDraftRows(bidScheduleRows);
    }
  }, [bidEditMode, bidScheduleRows]);
  const normalizedSavedBidStrategySettings = useMemo(
    () => normalizeBidStrategySettings(bidSavedStrategySettings || {}),
    [bidSavedStrategySettings]
  );
  const bidStrategyValidation = useMemo(() => {
    const settings = normalizeBidStrategySettings(bidStrategySettings);
    const marginLimit = settings.marginType === "percent" ? 100 : Number.POSITIVE_INFINITY;
    const positiveOrNull = (value) => toNullableNumber(value);
    const sellMargin = positiveOrNull(settings.sellMargin);
    const buyMargin = positiveOrNull(settings.buyMargin);
    const minSellPriceEurPerMwh = toNullableNumber(settings.minSellPriceEurPerMwh);
    const maxSellPriceEurPerMwh = toNullableNumber(settings.maxSellPriceEurPerMwh);
    const minBuyPriceEurPerMwh = toNullableNumber(settings.minBuyPriceEurPerMwh);
    const maxBuyPriceEurPerMwh = toNullableNumber(settings.maxBuyPriceEurPerMwh);
    const minBidQuantityMw = positiveOrNull(settings.minBidQuantityMw);
    const quantityStepMw = positiveOrNull(settings.quantityStepMw);
    const priceTickEurPerMwh = positiveOrNull(settings.priceTickEurPerMwh);
    const damTradingFeeEurPerMWh = positiveOrNull(
      settings.marketEconomics?.damFees?.tradingFeeEurPerMWh
    );
    const damClearingFeeEurPerMWh = positiveOrNull(
      settings.marketEconomics?.damFees?.clearingFeeEurPerMWh
    );
    const brpFeeValue = positiveOrNull(settings.marketEconomics?.brpAggregator?.value);
    const annualMembershipCostEur = positiveOrNull(
      settings.marketEconomics?.fixedMarketCosts?.annualMembershipCostEur
    );
    const clearingAccountMonthlyCostEur = positiveOrNull(
      settings.marketEconomics?.fixedMarketCosts?.clearingAccountMonthlyCostEur
    );
    const marketDataMonthlyCostEur = positiveOrNull(
      settings.marketEconomics?.fixedMarketCosts?.marketDataMonthlyCostEur
    );
    const collateralAmountEur = positiveOrNull(
      settings.marketEconomics?.collateralCost?.collateralAmountEur
    );
    const annualCostOfCapitalPct = positiveOrNull(
      settings.marketEconomics?.collateralCost?.annualCostOfCapitalPct
    );
    const errors = {
      sellMargin:
        sellMargin == null || Number.isNaN(sellMargin) || sellMargin < 0 || sellMargin > marginLimit
          ? `Enter a valid sell margin${settings.marginType === "percent" ? " between 0 and 100" : ""}.`
          : "",
      buyMargin:
        buyMargin == null || Number.isNaN(buyMargin) || buyMargin < 0 || buyMargin > marginLimit
          ? `Enter a valid buy margin${settings.marginType === "percent" ? " between 0 and 100" : ""}.`
          : "",
      minSellPriceEurPerMwh:
        Number.isNaN(minSellPriceEurPerMwh) ? "Enter a valid minimum sell price." : "",
      maxSellPriceEurPerMwh:
        Number.isNaN(maxSellPriceEurPerMwh)
          ? "Enter a valid maximum sell price."
          : minSellPriceEurPerMwh != null &&
              maxSellPriceEurPerMwh != null &&
              minSellPriceEurPerMwh > maxSellPriceEurPerMwh
            ? "Max sell price must be at least min sell price."
            : "",
      minBuyPriceEurPerMwh:
        Number.isNaN(minBuyPriceEurPerMwh) ? "Enter a valid minimum buy price." : "",
      maxBuyPriceEurPerMwh:
        Number.isNaN(maxBuyPriceEurPerMwh)
          ? "Enter a valid maximum buy price."
          : minBuyPriceEurPerMwh != null &&
              maxBuyPriceEurPerMwh != null &&
              minBuyPriceEurPerMwh > maxBuyPriceEurPerMwh
            ? "Max buy price must be at least min buy price."
            : "",
      minBidQuantityMw:
        minBidQuantityMw == null || Number.isNaN(minBidQuantityMw) || minBidQuantityMw < 0
          ? "Enter a valid minimum bid quantity."
          : "",
      quantityStepMw:
        quantityStepMw == null || Number.isNaN(quantityStepMw) || quantityStepMw <= 0
          ? "Enter a valid quantity step greater than 0."
          : "",
      priceTickEurPerMwh:
        priceTickEurPerMwh == null || Number.isNaN(priceTickEurPerMwh) || priceTickEurPerMwh <= 0
          ? "Enter a valid price tick greater than 0."
          : "",
      damTradingFeeEurPerMWh:
        damTradingFeeEurPerMWh == null ||
        Number.isNaN(damTradingFeeEurPerMWh) ||
        damTradingFeeEurPerMWh < 0
          ? "Enter a valid DAM trading fee."
          : "",
      damClearingFeeEurPerMWh:
        damClearingFeeEurPerMWh == null ||
        Number.isNaN(damClearingFeeEurPerMWh) ||
        damClearingFeeEurPerMWh < 0
          ? "Enter a valid DAM clearing fee."
          : "",
      brpAggregatorValue:
        settings.marketEconomics?.brpAggregator?.enabled &&
        (brpFeeValue == null || Number.isNaN(brpFeeValue) || brpFeeValue < 0)
          ? "Enter a valid BRP / Aggregator fee value."
          : "",
      annualMembershipCostEur:
        settings.marketEconomics?.fixedMarketCosts?.enabled &&
        (annualMembershipCostEur == null ||
          Number.isNaN(annualMembershipCostEur) ||
          annualMembershipCostEur < 0)
          ? "Enter a valid annual market membership cost."
          : "",
      clearingAccountMonthlyCostEur:
        settings.marketEconomics?.fixedMarketCosts?.enabled &&
        (clearingAccountMonthlyCostEur == null ||
          Number.isNaN(clearingAccountMonthlyCostEur) ||
          clearingAccountMonthlyCostEur < 0)
          ? "Enter a valid clearing account cost."
          : "",
      marketDataMonthlyCostEur:
        settings.marketEconomics?.fixedMarketCosts?.enabled &&
        (marketDataMonthlyCostEur == null ||
          Number.isNaN(marketDataMonthlyCostEur) ||
          marketDataMonthlyCostEur < 0)
          ? "Enter a valid market data / platform access cost."
          : "",
      collateralAmountEur:
        settings.marketEconomics?.collateralCost?.enabled &&
        (collateralAmountEur == null || Number.isNaN(collateralAmountEur) || collateralAmountEur < 0)
          ? "Enter a valid collateral amount."
          : "",
      annualCostOfCapitalPct:
        settings.marketEconomics?.collateralCost?.enabled &&
        (annualCostOfCapitalPct == null ||
          Number.isNaN(annualCostOfCapitalPct) ||
          annualCostOfCapitalPct < 0)
          ? "Enter a valid annual cost of capital."
          : "",
    };
    return {
      settings,
      errors,
      isValid: Object.values(errors).every((value) => !value),
    };
  }, [bidStrategySettings]);
  const bidStrategyDirty = useMemo(
    () =>
      JSON.stringify(bidStrategyValidation.settings) !== JSON.stringify(normalizedSavedBidStrategySettings),
    [bidStrategyValidation.settings, normalizedSavedBidStrategySettings]
  );
  const totalDamTransactionFee = useMemo(() => {
    const tradingFee = toNullableNumber(
      bidStrategySettings?.marketEconomics?.damFees?.tradingFeeEurPerMWh
    );
    const clearingFee = toNullableNumber(
      bidStrategySettings?.marketEconomics?.damFees?.clearingFeeEurPerMWh
    );
    return (Number.isFinite(tradingFee) ? tradingFee : 0) + (Number.isFinite(clearingFee) ? clearingFee : 0);
  }, [bidStrategySettings]);
  const brpAggregatorFeeUnitLabel = useMemo(() => {
    const feeType = bidStrategySettings?.marketEconomics?.brpAggregator?.feeType;
    if (feeType === "percent_of_gross_margin") return "% of gross margin";
    if (feeType === "fixed_monthly") return "EUR/month";
    return "EUR/MWh traded";
  }, [bidStrategySettings]);
  const marketEconomicsDailyEquivalents = useMemo(() => {
    const fixedMarketCosts = bidStrategySettings?.marketEconomics?.fixedMarketCosts || {};
    const collateralCost = bidStrategySettings?.marketEconomics?.collateralCost || {};
    const brpAggregator = bidStrategySettings?.marketEconomics?.brpAggregator || {};
    const annualMembershipCostEur =
      Number(toNullableNumber(fixedMarketCosts.annualMembershipCostEur)) || 0;
    const clearingAccountMonthlyCostEur =
      Number(toNullableNumber(fixedMarketCosts.clearingAccountMonthlyCostEur)) || 0;
    const marketDataMonthlyCostEur =
      Number(toNullableNumber(fixedMarketCosts.marketDataMonthlyCostEur)) || 0;
    const collateralAmountEur = Number(toNullableNumber(collateralCost.collateralAmountEur)) || 0;
    const annualCostOfCapitalPct =
      Number(toNullableNumber(collateralCost.annualCostOfCapitalPct)) || 0;
    const brpValue = Number(toNullableNumber(brpAggregator.value)) || 0;
    const annualMembershipDailyEur = annualMembershipCostEur / 365;
    const clearingAccountDailyEur = clearingAccountMonthlyCostEur / 30;
    const marketDataDailyEur = marketDataMonthlyCostEur / 30;
    const fixedMarketCostsDailyEur =
      annualMembershipDailyEur + clearingAccountDailyEur + marketDataDailyEur;
    const collateralDailyEur =
      ((collateralAmountEur * annualCostOfCapitalPct) / 100) / 365;
    const brpFixedDailyEur =
      brpAggregator.enabled && brpAggregator.feeType === "fixed_monthly" ? brpValue / 30 : 0;
    return {
      annualMembershipDailyEur,
      clearingAccountDailyEur,
      marketDataDailyEur,
      fixedMarketCostsDailyEur,
      collateralDailyEur,
      brpFixedDailyEur,
      totalAllocatedDailyEur:
        fixedMarketCostsDailyEur + collateralDailyEur + brpFixedDailyEur,
    };
  }, [bidStrategySettings]);
  const handleBidStrategyEditStart = useCallback(() => {
    if (bidWriteDisabledMessage) {
      showBidScheduleNotice(bidWriteDisabledMessage, "warning");
      return;
    }
    setBidStrategySettings(normalizedSavedBidStrategySettings);
    setBidStrategyEditMode(true);
  }, [bidWriteDisabledMessage, normalizedSavedBidStrategySettings, showBidScheduleNotice]);
  const handleBidStrategyEditCancel = useCallback(() => {
    setBidStrategySettings(normalizedSavedBidStrategySettings);
    setBidStrategyEditMode(false);
    showBidScheduleNotice("Unsaved bidding strategy changes were discarded.");
  }, [normalizedSavedBidStrategySettings, showBidScheduleNotice]);
  const refreshDraftFromLatestRun = useCallback(
    async ({ silent = false, runId = null } = {}) => {
      if (!bidSchedule?.id) return null;
      if (bidRefreshing) return null;
      if (bidWriteDisabledMessage) {
        if (!silent) {
          showBidScheduleNotice(bidWriteDisabledMessage, "warning");
        }
        return null;
      }
      if (bidStrategyDirty) {
        showBidScheduleNotice(
          "You have unsaved bidding strategy settings. Save them before refreshing from the latest run.",
          "warning"
        );
        return null;
      }
      try {
        setBidRefreshing(true);
        const saved = await refreshBidSchedule(bidSchedule.id, accessToken, runId);
        setBidSchedule(saved);
        setBidDraftRows(Array.isArray(saved?.rows) ? saved.rows : []);
        setBidScheduleError("");
        showBidScheduleNotice(
          saved?.source_run_id
            ? `Draft refreshed from run ${saved.source_run_id}.`
            : "Draft refreshed from the latest eligible optimization run.",
          "success"
        );
        setBidEditMode(false);
        return saved;
      } catch (err) {
        if (!silent) {
          setBidScheduleError(formatDashboardError(err, "Failed to refresh the bid schedule."));
        }
        return null;
      } finally {
        setBidRefreshing(false);
      }
    },
    [accessToken, bidRefreshing, bidSchedule?.id, bidStrategyDirty, bidWriteDisabledMessage, showBidScheduleNotice]
  );
  const bidScheduleMetrics = useMemo(() => {
    const rows = bidEditMode ? bidDraftRows : bidScheduleRows;
    if (!rows.length) {
      return {
        sellEnergyMWh: 0,
        buyEnergyMWh: 0,
        avgSellPrice: null,
        avgBuyPrice: null,
      };
    }
    let sellEnergyMWh = 0;
    let buyEnergyMWh = 0;
    let sellWeightedPrice = 0;
    let buyWeightedPrice = 0;

    rows.forEach((row) => {
      const energy = Number(row?.energy_mwh || 0);
      const price = Number(row?.bid_price || 0);
      if (row?.side === "sell") {
        sellEnergyMWh += energy;
        sellWeightedPrice += energy * price;
      } else if (row?.side === "buy") {
        buyEnergyMWh += energy;
        buyWeightedPrice += energy * price;
      }
    });

    return {
      sellEnergyMWh,
      buyEnergyMWh,
      avgSellPrice: sellEnergyMWh > 0 ? sellWeightedPrice / sellEnergyMWh : null,
      avgBuyPrice: buyEnergyMWh > 0 ? buyWeightedPrice / buyEnergyMWh : null,
    };
  }, [bidDraftRows, bidEditMode, bidScheduleRows]);
  const bidScheduleRevenueTotals = useMemo(() => {
    const {
      hasBackendTotals,
      showActualFinancials,
      settledSellRevenueEur,
      settledBuyCostEur,
      settledNetRevenueEur,
      grossMarketProfitEur,
      degradationMethod,
      degradationBasisEnergyMwh,
      degradationCostEur,
      marketFeeEur,
      gridImportFeeEur,
      gridExportFeeEur,
      operatingCostsEur,
      netOperatingProfitEur,
      netEconomicProfitEur,
      acceptedBuyEnergyMwh,
      acceptedSellEnergyMwh,
      acceptedTradedEnergyMwh,
    } = getScheduleFinancialTotals(bidSchedule);
    return {
      hasBackendTotals,
      showActualFinancials,
      actualSellRevenueEur: settledSellRevenueEur,
      actualBuyCostEur: settledBuyCostEur,
      actualNetRevenueEur: settledNetRevenueEur,
      grossMarketProfitEur,
      degradationMethod,
      degradationBasisEnergyMwh,
      degradationCostEur,
      marketFeeEur,
      gridImportFeeEur,
      gridExportFeeEur,
      operatingCostsEur,
      netOperatingProfitEur,
      netEconomicProfitEur,
      acceptedBuyEnergyMwh,
      acceptedSellEnergyMwh,
      acceptedTradedEnergyMwh,
    };
  }, [bidSchedule]);
  const bidSummaryDashboard = useMemo(() => {
    const netEnergyMWh = bidScheduleMetrics.sellEnergyMWh - bidScheduleMetrics.buyEnergyMWh;
    const spreadEurPerMwh =
      bidScheduleMetrics.avgSellPrice != null && bidScheduleMetrics.avgBuyPrice != null
        ? bidScheduleMetrics.avgSellPrice - bidScheduleMetrics.avgBuyPrice
        : null;
    const netEconomicProfit = bidScheduleRevenueTotals.netEconomicProfitEur;
    const netOperatingProfit = bidScheduleRevenueTotals.netOperatingProfitEur;
    const getProfitTone = (value) =>
      value == null ? "neutral" : value > 0 ? "positive" : value < 0 ? "negative" : "neutral";
    const economicProfitTone =
      netEconomicProfit != null ? getProfitTone(netEconomicProfit) : getProfitTone(netOperatingProfit);
    const operatingProfitTone = getProfitTone(netOperatingProfit);
    const actualFinancialsState = bidSchedule?.status === "draft"
      ? "pending"
      : bidScheduleRevenueTotals.showActualFinancials
        ? "available"
        : "missing";

    return {
      netEconomicProfit,
      netOperatingProfit,
      economicProfitTone,
      operatingProfitTone,
      actualFinancialsState,
      sections: [
        {
          key: "energy",
          title: "Energy",
          metrics: [
            { label: "Sold Energy", value: bidScheduleMetrics.sellEnergyMWh, unit: "MWh", decimals: 3 },
            { label: "Bought Energy", value: bidScheduleMetrics.buyEnergyMWh, unit: "MWh", decimals: 3 },
            { label: "Net Energy", value: netEnergyMWh, unit: "MWh", decimals: 3 },
          ],
        },
        {
          key: "prices",
          title: "Prices",
          metrics: [
            { label: "Avg Bid Sell Price", value: bidScheduleMetrics.avgSellPrice, unit: "EUR/MWh", decimals: 2 },
            { label: "Avg Bid Buy Price", value: bidScheduleMetrics.avgBuyPrice, unit: "EUR/MWh", decimals: 2 },
            { label: "Bid Spread", value: spreadEurPerMwh, unit: "EUR/MWh", decimals: 2 },
          ],
        },
        {
          key: "settlement",
          title: "Accepted Energy",
          metrics: [
            { label: "Accepted Sell Energy", value: bidScheduleRevenueTotals.acceptedSellEnergyMwh, unit: "MWh", decimals: 3 },
            { label: "Accepted Buy Energy", value: bidScheduleRevenueTotals.acceptedBuyEnergyMwh, unit: "MWh", decimals: 3 },
            { label: "Accepted Traded Energy", value: bidScheduleRevenueTotals.acceptedTradedEnergyMwh, unit: "MWh", decimals: 3 },
          ],
        },
        {
          key: "financials",
          title: "Financials",
          metrics: [
            { label: "Gross Market Profit", value: bidScheduleRevenueTotals.grossMarketProfitEur, unit: "EUR", decimals: 2 },
            { label: "Operating Costs", value: bidScheduleRevenueTotals.operatingCostsEur, unit: "EUR", decimals: 2 },
            { label: "Degradation Cost", value: bidScheduleRevenueTotals.degradationCostEur, unit: "EUR", decimals: 2 },
          ],
        },
      ],
    };
  }, [bidSchedule?.status, bidScheduleMetrics, bidScheduleRevenueTotals]);
  const batteryHealthDashboard = useMemo(() => {
    const state = batteryHealthState;
    const healthTone =
      state?.soh_end_pct == null
        ? "neutral"
        : state.soh_end_pct >= 90
          ? "positive"
          : state.soh_end_pct >= 75
            ? "warning"
            : "negative";
    return {
      healthTone,
      hasData: Boolean(state),
      primaryValue: formatPercentValue(state?.soh_end_pct, 2),
      metrics: [
        { label: "Available Energy", value: formatEnergyKWh(state?.available_energy_kwh, 2) },
        { label: "Nominal Energy", value: formatEnergyKWh(state?.nominal_energy_kwh, 2) },
        { label: "Daily SOH Loss", value: formatPercentValue(state?.daily_soh_loss_pct, 6) },
        { label: "Actual Throughput", value: formatEnergyKWh(state?.actual_throughput_kwh, 2) },
        { label: "Ending Actual SoC", value: formatPercentValue(state?.ending_actual_soc_pct, 2) },
        { label: "Last Updated", value: formatDateValue(state?.state_day) },
      ],
    };
  }, [batteryHealthState]);
  const selectedBatteryHealthHistoryMetric = useMemo(
    () =>
      BATTERY_HEALTH_HISTORY_METRICS.find(
        (metric) => metric.key === batteryHealthHistoryMetric
      ) || BATTERY_HEALTH_HISTORY_METRICS[0],
    [batteryHealthHistoryMetric]
  );
  const batteryHealthHistoryChartData = useMemo(
    () =>
      (Array.isArray(batteryHealthHistory) ? batteryHealthHistory : []).map((item) => ({
        ...item,
        time: parseDateKeyToLocalDate(item?.state_day, false),
        metricValue:
          item?.[selectedBatteryHealthHistoryMetric.key] != null
            ? Number(item[selectedBatteryHealthHistoryMetric.key])
            : null,
      })),
    [batteryHealthHistory, selectedBatteryHealthHistoryMetric]
  );
  const formatBatteryHealthHistoryDay = useCallback((value) => {
    const date = value instanceof Date ? value : value ? new Date(value) : null;
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "-";
    return formatDisplayDate(formatDateLocal(date));
  }, []);
  const formatBatteryHealthHistoryValue = useCallback((metricKey, value) => {
    const numericValue = value != null && Number.isFinite(Number(value)) ? Number(value) : null;
    if (numericValue == null) return "-";
    switch (metricKey) {
      case "soh_end_pct":
        return formatPercentValue(numericValue, 4);
      case "available_energy_kwh":
        return formatEnergyKWh(numericValue, 2);
      case "daily_soh_loss_pct":
        return formatPercentValue(numericValue, 6);
      case "actual_throughput_kwh":
        return formatEnergyKWh(numericValue, 2);
      default:
        return numericValue.toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 4,
        });
    }
  }, []);
  const renderBatteryHealthHistoryTooltip = useCallback(
    ({ active, payload }) => {
      if (!active || !Array.isArray(payload) || payload.length === 0) return null;
      const row = payload[0]?.payload;
      if (!row) return null;
      return (
        <Box
          sx={{
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            boxShadow: 3,
            px: 1.25,
            py: 1,
            minWidth: 220,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {row.state_day ? formatDisplayDate(row.state_day) : "-"}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#1565c0" }}>
            {selectedBatteryHealthHistoryMetric.label}:{" "}
            {formatBatteryHealthHistoryValue(
              selectedBatteryHealthHistoryMetric.key,
              row.metricValue
            )}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: 0.35 }}>
            Daily SOH Loss:{" "}
            {formatBatteryHealthHistoryValue("daily_soh_loss_pct", row.daily_soh_loss_pct)}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
            Actual Throughput:{" "}
            {formatBatteryHealthHistoryValue("actual_throughput_kwh", row.actual_throughput_kwh)}
          </Typography>
        </Box>
      );
    },
    [
      formatBatteryHealthHistoryValue,
      selectedBatteryHealthHistoryMetric,
    ]
  );
  const economicsOverviewChartColors = useMemo(
    () => ({
      grid: "rgba(148,163,184,0.16)",
      axis: "rgba(148,163,184,0.28)",
      tick: "#cbd5e1",
    }),
    []
  );
  const bidEconomicsSummary = useMemo(
    () => summarizeBidScheduleEconomics(bidScheduleRows, bidDay),
    [bidDay, bidScheduleRows]
  );
  const economicsHistorySchedules = useMemo(() => {
    const rawItems = Array.isArray(bidScheduleHistory) ? bidScheduleHistory : [];
    const normalized = rawItems
      .filter((item) => item && item.status === "submitted")
      .map((schedule) => {
        const summary = summarizeBidScheduleEconomics(schedule?.rows || [], schedule?.bid_day || "");
        const financials = getScheduleFinancialTotals(schedule);
        return {
          ...schedule,
          summary,
          financials,
        };
      })
      .filter((item) => item?.bid_day)
      .sort(sortSchedulesLatestFirst);

    const hasCurrentSubmittedSchedule =
      bidSchedule?.status === "submitted" &&
      normalized.some((item) => String(item?.id || "") === String(bidSchedule?.id || ""));

    if (!hasCurrentSubmittedSchedule && bidSchedule?.status === "submitted" && bidSchedule?.bid_day) {
      normalized.unshift({
        ...bidSchedule,
        summary: summarizeBidScheduleEconomics(bidSchedule?.rows || [], bidSchedule?.bid_day || bidDay),
        financials: getScheduleFinancialTotals(bidSchedule),
      });
    }

    const deduped = [];
    const seen = new Set();
    normalized.forEach((item) => {
      const key = String(item?.id || `${item?.asset_id || ""}:${item?.bid_day || ""}:${item?.submitted_at || item?.updated_at || ""}`);
      if (seen.has(key)) return;
      seen.add(key);
      deduped.push(item);
    });
    return deduped.sort(sortSchedulesLatestFirst);
  }, [bidDay, bidSchedule, bidScheduleHistory]);
  const economicsAvailableBounds = useMemo(() => {
    const dates = economicsHistorySchedules.map((item) => String(item?.bid_day || "")).filter(Boolean).sort();
    return {
      start: dates[0] || "",
      end: dates[dates.length - 1] || "",
    };
  }, [economicsHistorySchedules]);
  useEffect(() => {
    if (!economicsAvailableBounds.start || !economicsAvailableBounds.end) {
      setEconomicsRange({ start: "", end: "" });
      setEconomicsTableRange({ start: "", end: "" });
      return;
    }
    if (
      hasRunResultsContext &&
      seededRunIntervalRef.current.runId === String(selectedRunId || "") &&
      seededRunIntervalRef.current.start
    ) {
      setEconomicsRange({
        start: seededRunIntervalRef.current.start,
        end: seededRunIntervalRef.current.end,
      });
      setEconomicsTableRange({
        start: seededRunIntervalRef.current.start,
        end: seededRunIntervalRef.current.end,
      });
      return;
    }
    setEconomicsRange(economicsAvailableBounds);
    setEconomicsTableRange(economicsAvailableBounds);
  }, [economicsAvailableBounds, hasRunResultsContext, selectedRunId]);
  const economicsAllChartData = useMemo(() => {
    let cumulative = 0;
    return economicsHistorySchedules
      .slice()
      .sort((a, b) => String(a?.bid_day || "").localeCompare(String(b?.bid_day || "")))
      .map((item, index) => {
        const dailyNet =
          item?.financials?.netOperatingProfitEur != null
            ? Number(item.financials.netOperatingProfitEur)
            : null;
        const grossMarketProfit =
          item?.financials?.grossMarketProfitEur != null
            ? Number(item.financials.grossMarketProfitEur)
            : null;
        const operatingCosts =
          item?.financials?.operatingCostsEur != null
            ? Number(item.financials.operatingCostsEur)
            : null;
        const sellRevenue =
          item?.financials?.settledSellRevenueEur != null
            ? Number(item.financials.settledSellRevenueEur)
            : null;
        const buyCost =
          item?.financials?.settledBuyCostEur != null
            ? Number(item.financials.settledBuyCostEur)
            : null;
        if (dailyNet != null) cumulative += dailyNet;
        return {
          id: item?.id || `${item?.bid_day || "row"}-${index}`,
          bidDay: item?.bid_day || "",
          bidDayLabel: formatBidDayLabel(item?.bid_day || ""),
          dailyNetOperatingProfit: dailyNet,
          cumulativeNetOperatingProfit: cumulative,
          grossMarketProfit,
          operatingCosts,
          settledSellRevenue: sellRevenue,
          settledBuyCost: buyCost,
        };
      });
  }, [economicsHistorySchedules]);
  const economicsChartData = useMemo(() => {
    const start = economicsRange.start || economicsAvailableBounds.start;
    const end = economicsRange.end || economicsAvailableBounds.end;
    return economicsAllChartData.filter((item) => {
      const day = String(item?.bidDay || "");
      if (!day) return false;
      if (start && day < start) return false;
      if (end && day > end) return false;
      return true;
    });
  }, [
    economicsAllChartData,
    economicsAvailableBounds.end,
    economicsAvailableBounds.start,
    economicsRange.end,
    economicsRange.start,
  ]);
  const economicsChartSchedules = useMemo(() => {
    const start = economicsRange.start || economicsAvailableBounds.start;
    const end = economicsRange.end || economicsAvailableBounds.end;
    return economicsHistorySchedules
      .filter((item) => {
        const day = String(item?.bid_day || "");
        if (!day) return false;
        if (start && day < start) return false;
        if (end && day > end) return false;
        return true;
      })
      .slice()
      .sort((a, b) => String(a?.bid_day || "").localeCompare(String(b?.bid_day || "")));
  }, [
    economicsAvailableBounds.end,
    economicsAvailableBounds.start,
    economicsHistorySchedules,
    economicsRange.end,
    economicsRange.start,
  ]);
  const economicsChartKpis = useMemo(() => {
    const last = economicsChartData[economicsChartData.length - 1] || null;
    const sumFinite = (values) => {
      const finiteValues = values.filter((value) => value != null && Number.isFinite(Number(value)));
      if (!finiteValues.length) return null;
      return finiteValues.reduce((sum, value) => sum + Number(value), 0);
    };
    return {
      currentCumulativeNetOperatingProfit: last?.cumulativeNetOperatingProfit ?? null,
      netOperatingProfitOverPeriod: sumFinite(
        economicsChartData.map((item) => item?.dailyNetOperatingProfit ?? null)
      ),
      grossMarketProfit: sumFinite(
        economicsChartSchedules.map((item) => item?.financials?.grossMarketProfitEur ?? null)
      ),
      operatingCosts: sumFinite(
        economicsChartSchedules.map((item) => item?.financials?.operatingCostsEur ?? null)
      ),
      settledSellRevenue: sumFinite(
        economicsChartSchedules.map((item) => item?.financials?.settledSellRevenueEur ?? null)
      ),
      bidDays: economicsChartSchedules.length,
    };
  }, [economicsChartData, economicsChartSchedules]);
  const economicsTableSchedules = useMemo(() => {
    const start = economicsTableRange.start || economicsAvailableBounds.start;
    const end = economicsTableRange.end || economicsAvailableBounds.end;
    return economicsHistorySchedules.filter((item) => {
      const day = String(item?.bid_day || "");
      if (!day) return false;
      if (start && day < start) return false;
      if (end && day > end) return false;
      return true;
    });
  }, [economicsAvailableBounds.end, economicsAvailableBounds.start, economicsHistorySchedules, economicsTableRange.end, economicsTableRange.start]);
  const economicsExportStartDate = economicsRange.start || economicsAvailableBounds.start || "";
  const economicsExportEndDate = economicsRange.end || economicsAvailableBounds.end || "";
  const economicsExportMissingFilters = useMemo(
    () =>
      !currentProject?.id ||
      !assetKey ||
      !selectedMarket ||
      !economicsExportStartDate ||
      !economicsExportEndDate,
    [assetKey, currentProject?.id, economicsExportEndDate, economicsExportStartDate, selectedMarket]
  );
  const economicsExportValidationError = useMemo(() => {
    if (!economicsExportStartDate || !economicsExportEndDate) return "";
    if (economicsExportStartDate > economicsExportEndDate) {
      return "Start date must be on or before end date.";
    }
    return "";
  }, [economicsExportEndDate, economicsExportStartDate]);
  const activeEconomicsSchedule = useMemo(() => {
    if (selectedEconomicsProcess) return selectedEconomicsProcess;
    const latestScheduleWithSettledResults =
      economicsHistorySchedules.find((item) => {
        const financials = item?.financials || {};
        const summary = item?.summary || {};
        return (
          Boolean(financials?.showActualFinancials) ||
          Number(summary?.acceptedCount || 0) > 0
        );
      }) || null;
    if (bidSchedule?.status === "submitted" && bidSchedule?.bid_day) {
      const currentSubmittedSchedule = {
        ...bidSchedule,
        summary: summarizeBidScheduleEconomics(bidSchedule?.rows || [], bidSchedule?.bid_day || bidDay),
        financials: getScheduleFinancialTotals(bidSchedule),
      };
      const currentHasSettledResults =
        Boolean(currentSubmittedSchedule?.financials?.showActualFinancials) ||
        Number(currentSubmittedSchedule?.summary?.acceptedCount || 0) > 0;
      if (currentHasSettledResults) return currentSubmittedSchedule;
    }
    return latestScheduleWithSettledResults || economicsHistorySchedules[0] || null;
  }, [bidDay, bidSchedule, economicsHistorySchedules, selectedEconomicsProcess]);
  const activeEconomicsSummary = useMemo(() => {
    if (activeEconomicsSchedule?.summary) return activeEconomicsSchedule.summary;
    return summarizeBidScheduleEconomics(
      activeEconomicsSchedule?.rows || [],
      activeEconomicsSchedule?.bid_day || bidDay
    );
  }, [activeEconomicsSchedule, bidDay]);
  const activeEconomicsFinancials = useMemo(() => {
    if (activeEconomicsSchedule?.financials) return activeEconomicsSchedule.financials;
    return getScheduleFinancialTotals(activeEconomicsSchedule);
  }, [activeEconomicsSchedule]);
  const activeEconomicsSubtitle = useMemo(() => {
    if (selectedEconomicsProcess?.bid_day) {
      return `Selected schedule ${formatBidDayLabel(selectedEconomicsProcess.bid_day)}`;
    }
    const activeHasSettledResults =
      Boolean(activeEconomicsFinancials?.showActualFinancials) ||
      Number(activeEconomicsSummary?.acceptedCount || 0) > 0;
    if (activeHasSettledResults && activeEconomicsSchedule?.bid_day) {
      return `Latest settled schedule ${formatBidDayLabel(activeEconomicsSchedule.bid_day)}`;
    }
    if (activeEconomicsSchedule?.bid_day) {
      return `Latest submitted schedule ${formatBidDayLabel(activeEconomicsSchedule.bid_day)}`;
    }
    return "No submitted schedule selected";
  }, [activeEconomicsFinancials, activeEconomicsSchedule?.bid_day, activeEconomicsSummary, selectedEconomicsProcess]);
  const sourceRunLabel = useMemo(() => {
    const sourceRunId = bidSchedule?.source_run_id;
    if (!sourceRunId) return "Not linked yet";
    const match = runs.find((run) => normalizeRunId(run) === sourceRunId);
    const started =
      match?.started_at ||
      match?.created_at ||
      bidSchedule?.source_run_started_at ||
      bidSchedule?.source_run_created_at ||
      bidSchedule?.source_run_at ||
      "";
    if (!started) return `Run ${String(sourceRunId).slice(0, 8)}`;
    return new Date(started).toLocaleString("en-GB");
  }, [
    bidSchedule?.source_run_id,
    bidSchedule?.source_run_started_at,
    bidSchedule?.source_run_created_at,
    bidSchedule?.source_run_at,
    runs,
  ]);
  const handleBidRowChange = useCallback(
    (rowId, field, rawValue) => {
      if (!bidSchedule || bidSchedule.status !== "draft") return;
      const parsedValue =
        field === "side"
          ? rawValue
          : rawValue === ""
            ? ""
            : Number(rawValue);
      const nextRows = (bidEditMode ? bidDraftRows : bidScheduleRows).map((row) =>
        row.id === rowId ? updateBidRowValue(row, field, parsedValue) : row
      );
      setBidDraftRows(nextRows);
    },
    [bidDraftRows, bidEditMode, bidSchedule, bidScheduleRows]
  );
  const handleBidEditStart = useCallback(() => {
    setBidDraftRows(bidScheduleRows);
    setBidEditMode(true);
  }, [bidScheduleRows]);
  const handleBidEditCancel = useCallback(() => {
    setBidDraftRows(bidScheduleRows);
    setBidEditMode(false);
    showBidScheduleNotice("Unsaved bid edits were discarded.");
  }, [bidScheduleRows, showBidScheduleNotice]);
  const handleBidEditSave = useCallback(async () => {
    if (!bidSchedule) return;
    if (bidWriteDisabledMessage) {
      showBidScheduleNotice(bidWriteDisabledMessage, "warning");
      return;
    }
    const saved = await updateBidScheduleRows(bidSchedule.id, bidDraftRows, accessToken);
    setBidSchedule(saved);
    showBidScheduleNotice("Bid edits saved.", "success");
    setBidEditMode(false);
  }, [accessToken, bidDraftRows, bidSchedule, bidWriteDisabledMessage, showBidScheduleNotice]);
  const handleBidSubmit = useCallback(async () => {
    if (!bidSchedule) return;
    if (bidWriteDisabledMessage) {
      showBidScheduleNotice(bidWriteDisabledMessage, "warning");
      return;
    }
    const scheduleId = bidSchedule.id;
    if (!scheduleId) throw new Error("Cannot submit bid schedule: missing schedule id.");
    const saved = await submitBidSchedule(scheduleId, accessToken);
    setBidSchedule(saved);
    showBidScheduleNotice("Schedule finalised in Pythia. No exchange orders were sent.", "success");
    setBidEditMode(false);
  }, [accessToken, bidSchedule, bidWriteDisabledMessage, showBidScheduleNotice]);
  const handleBidReopen = useCallback(async () => {
    if (!bidSchedule) return;
    if (bidWriteDisabledMessage) {
      showBidScheduleNotice(bidWriteDisabledMessage, "warning");
      return;
    }
    const scheduleId = bidSchedule.id;
    if (!scheduleId) throw new Error("Cannot re-open bid schedule: missing schedule id.");
    const saved = await reopenBidSchedule(scheduleId, accessToken);
    setBidSchedule(saved);
    setBidDraftRows(Array.isArray(saved?.rows) ? saved.rows : []);
    showBidScheduleNotice("Bid schedule re-opened as draft.", "success");
  }, [accessToken, bidSchedule, bidWriteDisabledMessage, showBidScheduleNotice]);
  const setNestedBidStrategyValue = useCallback((source, path, nextValue) => {
    const segments = Array.isArray(path) ? path : String(path).split(".");
    const result = { ...source };
    let cursor = result;
    for (let index = 0; index < segments.length - 1; index += 1) {
      const segment = segments[index];
      cursor[segment] = cursor[segment] && typeof cursor[segment] === "object" ? { ...cursor[segment] } : {};
      cursor = cursor[segment];
    }
    cursor[segments[segments.length - 1]] = nextValue;
    return result;
  }, []);
  const handleBidStrategySettingChange = useCallback((field, rawValue) => {
    setBidStrategySettings((prev) => ({
      ...prev,
      [field]:
        field === "sellForecastBasis" ||
        field === "buyForecastBasis" ||
        field === "marginType" ||
        field === "degradationMethod"
          ? rawValue
          : isNullableBidPriceBoundField(field)
            ? toNullableNumber(rawValue)
            : rawValue === ""
              ? ""
              : Number.isFinite(Number(rawValue))
                ? Math.abs(Number(rawValue))
                : "",
    }));
  }, []);
  const handleBidStrategyNestedSettingChange = useCallback(
    (path, rawValue, options = {}) => {
      setBidStrategySettings((prev) => {
        const { kind = "number", absolute = true, emptyValue = "" } = options;
        let nextValue = rawValue;
        if (kind === "boolean") {
          nextValue = Boolean(rawValue);
        } else if (kind === "string") {
          nextValue = rawValue;
        } else {
          nextValue =
            rawValue === ""
              ? emptyValue
              : Number.isFinite(Number(rawValue))
                ? absolute
                  ? Math.abs(Number(rawValue))
                  : Number(rawValue)
                : emptyValue;
        }
        return setNestedBidStrategyValue(prev, path, nextValue);
      });
    },
    [setNestedBidStrategyValue]
  );
  const handleBidStrategySave = useCallback(async () => {
    if (!currentProject?.id || !assetKey) return;
    if (bidWriteDisabledMessage) {
      showBidScheduleNotice(bidWriteDisabledMessage, "warning");
      return;
    }
    if (!bidStrategyValidation.isValid) return;
    setBidStrategySaving(true);
    try {
      const savedSettingsPayload = await saveBidScheduleSettings(
        currentProject.id,
        assetKey,
        bidStrategyValidation.settings,
        accessToken
      );
      const refreshedSchedule = bidSchedule?.id
        ? await updateBidScheduleSettings(
            bidSchedule.id,
            bidStrategyValidation.settings,
            accessToken
          )
        : null;
      const normalized = normalizeBidStrategySettings(
        savedSettingsPayload?.settings || bidStrategyValidation.settings
      );
      setBidSavedStrategySettings(normalized);
      setBidStrategySettings(normalized);
      setBidStrategyEditMode(false);
      if (refreshedSchedule) {
        setBidSchedule(refreshedSchedule);
        setBidDraftRows(Array.isArray(refreshedSchedule?.rows) ? refreshedSchedule.rows : []);
      }
      showBidScheduleNotice("Bidding strategy settings saved.", "success");
      setBidScheduleError("");
    } catch (err) {
      setBidScheduleError(formatDashboardError(err, "Failed to save bidding strategy settings."));
    } finally {
      setBidStrategySaving(false);
    }
  }, [accessToken, assetKey, bidSchedule?.id, bidStrategyValidation, bidWriteDisabledMessage, currentProject?.id, showBidScheduleNotice]);
  const handleBidExport = useCallback(async () => {
    setBidExportError("");
    if (demoExportDisabledMessage) {
      setBidExportError(demoExportDisabledMessage);
      return;
    }
    try {
      if (!bidSchedule?.id) {
        throw new Error("Cannot export bids: bid schedule has not been saved yet.");
      }

      setBidExporting(true);
      const { blob, filename } = await exportBidScheduleCsv(bidSchedule.id, accessToken);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `bid_schedule_${bidDay}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setBidExportError(formatDashboardError(err, "Failed to export bids."));
    } finally {
      setBidExporting(false);
    }
  }, [accessToken, bidDay, bidSchedule, demoExportDisabledMessage]);
  const handleEconomicsExport = useCallback(async () => {
    setEconomicsExportError("");

    if (demoExportDisabledMessage) {
      setEconomicsExportError(demoExportDisabledMessage);
      return;
    }

    if (economicsExportMissingFilters) {
      setEconomicsExportError(
        "Select a project, BESS asset, market, and valid date range before exporting the economics report."
      );
      return;
    }
    if (economicsExportValidationError) {
      setEconomicsExportError(economicsExportValidationError);
      return;
    }

    try {
      setEconomicsExporting(true);
      const { blob, filename } = await exportEconomicsOverviewExcel(
        {
          projectId: currentProject?.id,
          assetId: assetKey,
          market: selectedMarket,
          startDate: economicsExportStartDate,
          endDate: economicsExportEndDate,
        },
        accessToken
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "economics_overview.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setEconomicsExportError(
        formatDashboardError(err, "Failed to export economics report.")
      );
    } finally {
      setEconomicsExporting(false);
    }
  }, [
    accessToken,
    assetKey,
    currentProject?.id,
    economicsExportEndDate,
    economicsExportMissingFilters,
    economicsExportStartDate,
    economicsExportValidationError,
    demoExportDisabledMessage,
    selectedMarket,
  ]);

  // const energySeriesWithDegradation = useMemo(() => {
  //   const rows = filteredChartData?.data ?? [];
  //   if (!rows.length) return rows;

  //   const chosen = assetKey || chartData.assets?.[0];

  //   // Capacity in kWh (make sure KPI really is kWh)
  //   const capRaw = chosen ? (kpis?.[chosen]?.Capacity ?? kpis?.[chosen]?.capacity) : null;
  //   const capacityKWh = capRaw != null ? Number(capRaw) : null;

  //   // OPTIONAL: if you have these in KPIs/config, use them
  //   const socMin = chosen ? Number(kpis?.[chosen]?.minSoc ?? kpis?.[chosen]?.min_soc) : null;
  //   const socMax = chosen ? Number(kpis?.[chosen]?.maxSoc ?? kpis?.[chosen]?.max_soc) : null;

  //   if (!capacityKWh || !Number.isFinite(capacityKWh) || capacityKWh <= 0) {
  //     return rows.map((r) => ({ ...r, deg_efc: 0 }));
  //   }

  //   // Use usable window if available, else fallback to full capacity
  //   const usableKWh =
  //     Number.isFinite(socMin) && Number.isFinite(socMax) && socMax > socMin
  //       ? (socMax - socMin) * capacityKWh
  //       : capacityKWh;

  //   let cumEfc = 0;

  //   return rows.map((r, i) => {
  //     const t0 = new Date(r.time).getTime();
  //     const tPrev = i > 0 ? new Date(rows[i - 1].time).getTime() : null;
  //     const dtHours =
  //       i > 0 && Number.isFinite(t0) && Number.isFinite(tPrev) && t0 > tPrev
  //         ? (t0 - tPrev) / 3600000
  //         : 0.25; // fallback

  //     const chargeKW = Math.max(0, Number(r.pcharge ?? 0));
  //     const dischargeKW = Math.max(0, Number(r.pdischarge ?? 0));

  //     const eCh = chargeKW * dtHours;       // kWh
  //     const eDis = dischargeKW * dtHours;   // kWh

  //     // EFC increment = (charge + discharge) / (2 * usable)
  //     cumEfc += (eCh + eDis) / (2 * usableKWh);

  //     return { ...r, deg_efc: cumEfc };
  //   });
  // }, [filteredChartData?.data, assetKey, chartData.assets, kpis]);

  const energySeriesWithDegradation = useMemo(() => {
    const rows = filteredChartData?.data ?? [];
    if (!rows.length) return rows;

    const chosen = assetKey || chartData.assets?.[0];

    const toNumber = (value) => {
      if (value === null || value === undefined || value === "") return null;

      const n = Number(String(value).replace("%", "").trim());
      return Number.isFinite(n) ? n : null;
    };

    const toSocFraction = (value, fallback) => {
      const n = toNumber(value);

      if (n === null) return fallback;

      // 95 means 95%
      if (n > 1) return n / 100;

      // 0.95 means already fraction
      return n;
    };

    const capacityRaw = chosen
      ? toNumber(
          kpis?.[chosen]?.Capacity,
          kpis?.[chosen]?.capacity,
          kpis?.[chosen]?.Energy,
          kpis?.[chosen]?.energy
        )
      : null;

    const capacityMWh = capacityRaw;

    if (!capacityMWh || capacityMWh <= 0) {
      return rows.map((r) => ({ ...r, deg_efc: 0 }));
    }

    const socMin = chosen
      ? toSocFraction(
          kpis?.[chosen]?.minSoc ??
            kpis?.[chosen]?.min_soc ??
            kpis?.[chosen]?.socMin ??
            kpis?.[chosen]?.soc_min ??
            5,
          0.05
        )
      : 0.05;

    const socMax = chosen
      ? toSocFraction(
          kpis?.[chosen]?.maxSoc ??
            kpis?.[chosen]?.max_soc ??
            kpis?.[chosen]?.socMax ??
            kpis?.[chosen]?.soc_max ??
            95,
          0.95
        )
      : 0.95;

    const usableCapacityMWh =
      Number.isFinite(socMin) &&
      Number.isFinite(socMax) &&
      socMax > socMin
        ? (socMax - socMin) * capacityMWh
        : capacityMWh;

    let cumulativeEfc = 0;

    return rows.map((r, i) => {
      const t0 = new Date(r.time).getTime();
      const tPrev = i > 0 ? new Date(rows[i - 1].time).getTime() : null;

      const dtHours =
        i > 0 &&
        Number.isFinite(t0) &&
        Number.isFinite(tPrev) &&
        t0 > tPrev
          ? (t0 - tPrev) / 3600000
          : 0.25;

      // These are MW, based on your chart/cards.
      const chargeMW = Math.max(0, Number(r.pcharge ?? 0));
      const dischargeMW = Math.max(0, Number(r.pdischarge ?? 0));

      const chargedMWh = chargeMW * dtHours;
      const dischargedMWh = dischargeMW * dtHours;

      cumulativeEfc +=
        (chargedMWh + dischargedMWh) / (2 * usableCapacityMWh);

      return {
        ...r,
        deg_efc: cumulativeEfc,
      };
    });
  }, [filteredChartData?.data, assetKey, chartData.assets, kpis]);


  const socSeries = useMemo(() => {
    const rows = filteredChartData?.data ?? [];
    if (!rows.length) return [];

    const chosen = assetKey || chartData.assets?.[0];
    const meta = chosen ? assetsMeta?.[chosen] : null;

    const socMin = meta?.socMin ?? null; // %
    const socMax = meta?.socMax ?? null; // %

    return rows.map((r) => ({
      time: r.time,
      timeMs: r.timeMs,
      timeRaw: r.timeRaw,
      timeDate: r.timeDate,
      timeDisplayShort: r.timeDisplayShort,
      timeDisplayLong: r.timeDisplayLong,
      soc: r.soc != null && Number.isFinite(Number(r.soc)) ? Number(r.soc) : null, // backend %
      socMin,
      socMax,
    }));
  }, [filteredChartData?.data, assetKey, chartData.assets, assetsMeta]);

  const profitSeries = useMemo(() => {
    const rows = filteredChartData?.data ?? [];
    if (!rows.length) return [];

    let cum = 0;

    return rows.map((r, i) => {
      const t0 = new Date(r.time).getTime();
      const tPrev = i > 0 ? new Date(rows[i - 1].time).getTime() : null;
      const dtHours =
        i > 0 && Number.isFinite(t0) && Number.isFinite(tPrev) && t0 > tPrev
          ? (t0 - tPrev) / 3600000
          : 0.25;

      const price = Number(r.price ?? 0); // your chart uses €/kWh
      const sellKW = Math.max(0, Number(r.psell ?? r.sell_to_grid ?? 0));
      const buyKW = Math.max(0, Number(r.pbuy ?? 0)); // if you have it

      const eSell = sellKW * dtHours; // kWh
      const eBuy = buyKW * dtHours;   // kWh

      const profitStep = eSell * price - eBuy * price; // € (if buy uses same price; adjust if needed)
      cum += profitStep;

      return {
        time: r.time,
        timeMs: r.timeMs,
        timeRaw: r.timeRaw,
        timeDate: r.timeDate,
        timeDisplayShort: r.timeDisplayShort,
        timeDisplayLong: r.timeDisplayLong,
        profit_step: profitStep,
        profit_cum: cum,
      };
    });
  }, [filteredChartData?.data]);
  void profitSeries;

  const derivedSeries = useMemo(() => {
    const rows = energySeriesWithDegradation ?? [];
    if (!rows.length) return { rows, daily: [] };

    // dtHours from timestamps (fallback 0.25)
    const dtHoursAt = (i) => {
      if (i === 0) return 0.25;
      const t0 = new Date(rows[i].time).getTime();
      const t1 = new Date(rows[i - 1].time).getTime();
      return Number.isFinite(t0) && Number.isFinite(t1) && t0 > t1 ? (t0 - t1) / 3600000 : 0.25;
    };

    const out = rows.map((r, i) => {
      const dt = dtHoursAt(i);

      const pCharge = Number(r.pcharge ?? 0);       // kW (>=0)
      const pGridCh = Number(r.gridcharge ?? 0);    // kW (>=0)
      const pSell   = Number(r.psell ?? 0);         // kW (>=0)
      const pDis    = Number(r.pdischarge ?? 0);    // kW (>=0)
      const price   = Number(r.price ?? 0);         // €/kWh or €/MWh depending on your axis

      // Net POI power convention:
      // export positive, import negative
      // If psell already represents POI export, and gridcharge represents import for charging:
      const pPoi = (pSell ?? 0) - (pGridCh ?? 0);
      void price;

      // energy and throughput
      const eCh  = Math.max(0, pCharge) * dt; // kWh
      const eDis = Math.max(0, pDis) * dt;    // kWh
      const throughputKWh = eCh + eDis;

      return { ...r, p_poi: pPoi, throughput_kwh: throughputKWh, _dtHours: dt };
    });

    // Daily aggregation (local date)
    const byDay = new Map();
    for (const r of out) {
      const d = new Date(r.time);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const cur = byDay.get(key) ?? { day: key, throughput_kwh: 0, efc_delta: 0, profit_eur: 0 };
      cur.throughput_kwh += Number(r.throughput_kwh ?? 0);

      // efc delta = last - first (we'll compute later; store last seen)
      cur._lastEfc = Number(r.deg_efc ?? 0);
      cur._firstEfc = cur._firstEfc ?? Number(r.deg_efc ?? 0);

      // If you have per-step profit/cost, add here. If not, skip for now.
      // cur.profit_eur += Number(r.step_profit ?? 0);

      byDay.set(key, cur);
    }

    const daily = Array.from(byDay.values()).map((x) => ({
      day: x.day,
      throughput_mwh: x.throughput_kwh / 1000,
      efc_delta: (x._lastEfc ?? 0) - (x._firstEfc ?? 0),
      // profit_eur: x.profit_eur,
    }));

    return { rows: out, daily };
  }, [energySeriesWithDegradation]);
  
    const toMw = (value) => {
      const n = Number(value);
      return Number.isFinite(n) ? n / 1000 : null;
    };
  
    const toEurPerMwh = (value) => {
      const n = Number(value);
      return Number.isFinite(n) ? n * 1000 : null;
    };
  
    const optimizationDisplayRows = useMemo(() => {
      return (energySeriesWithDegradation || []).map((row) => {
        const next = {
          ...row,
          price_eur_mwh: toEurPerMwh(row.price),
        };
  
        POWER_KEYS_TO_MW.forEach((key) => {
          next[`${key}_mw`] = toMw(row[key]);
        });
  
        return next;
      });
    }, [energySeriesWithDegradation]);
  
    const priceDispatchRows = useMemo(() => {
      return (derivedSeries.rows || []).map((row) => ({
        ...row,
        price_eur_mwh: toEurPerMwh(row.price),
        p_poi_mw: toMw(row.p_poi),
      }));
    }, [derivedSeries.rows]);
  
    const formatMwAxisValue = (value) => {
      const n = Number(value);
      return Number.isFinite(n)
        ? n.toLocaleString("en-GB", { maximumFractionDigits: 2 })
        : value;
    };
  
    const formatEurMwhAxisValue = (value) => {
      const n = Number(value);
      return Number.isFinite(n)
        ? n.toLocaleString("en-GB", { maximumFractionDigits: 0 })
        : value;
    };
  
    const formatPriceEurMwhTooltipValue = (value) => {
      const n = Number(value);
      return Number.isFinite(n) ? `${n.toFixed(2)} €/MWh` : "N/A";
    };

  const kpiSummary = useMemo(() => {
    const rows = derivedSeries.rows ?? [];
    if (!rows.length) return null;

    const throughputMWh = rows.reduce((a, r) => a + (Number(r.throughput_kwh ?? 0) / 1000), 0);
    const efcStart = Number(rows[0].deg_efc ?? 0);
    const efcEnd = Number(rows[rows.length - 1].deg_efc ?? 0);
    const efcDelta = efcEnd - efcStart;

    return { throughputMWh, efcDelta };
  }, [derivedSeries.rows]);
  void kpiSummary;

  const energySeriesStats = useMemo(() => {
    const stats = {};
    energySeriesWithDegradation.forEach((row) => {
      visibleEnergySeriesConfig.forEach((series) => {
        const value = toNumberSafe(row[series.key]);
        if (value != null) {
          const existing = stats[series.key] || { sum: 0, max: 0 };
          stats[series.key] = {
            sum: existing.sum + Math.abs(value),
            max: Math.max(existing.max, Math.abs(value)),
          };
        }
      });
    });
    return stats;
  }, [energySeriesWithDegradation, visibleEnergySeriesConfig]);

  const energySeriesRanking = useMemo(() => {
    return visibleEnergySeriesConfig.map((series, index) => {
      const entry = energySeriesStats[series.key] || { sum: 0, max: 0 };
      return { series, sum: entry.sum, max: entry.max, index };
    }).sort((a, b) => {
      if (a.max !== b.max) return b.max - a.max;
      if (a.sum !== b.sum) return b.sum - a.sum;
      return a.index - b.index;
    });
  }, [energySeriesStats, visibleEnergySeriesConfig]);

  const energySeriesBarOrder = useMemo(() => energySeriesRanking, [energySeriesRanking]);

  const renderEnergySeriesElements = (mode = chartDisplayMode) => {
    const getEnergyDisplayKey = (series) =>
      series.key === "deg_efc" ? "deg_efc" : `${series.key}_mw`;

    // BAR MODE: keep your ordering, but special-case deg_efc
    if (mode === "bar") {
      const bars = energySeriesBarOrder.map(({ series }) => {
        // never render deg_efc as a bar
        if (series.key === "deg_efc") return null;

        return (
          <Bar
            key={`bar-${series.key}`}
            dataKey={getEnergyDisplayKey(series)}
            name={series.chartLabel}
            fill={series.color}
            hide={!seriesVisible[series.key]}
            yAxisId="left"
          />
        );
      });

      // if deg_efc selected, overlay it as a line on right axis
      if (seriesVisible.deg_efc) {
        bars.push(
          <Line
            key="line-deg_efc"
            dataKey="deg_efc"
            name="Cumulative Cycles (EFC)"
            stroke="#8e24aa"
            dot={false}
            strokeWidth={ENERGY_LINE_STROKE_WIDTH}
            type="monotone"
            yAxisId="right"
          />
        );
      }

      return bars;
    }

    // LINE / AREA MODE
    return visibleEnergySeriesConfig.map((series) => {
      if (!seriesVisible[series.key]) return null;

      // Special-case degradation: always a LINE on right axis
      if (series.key === "deg_efc") {
        return (
          <Line
            key={`line-${series.key}`}
            dataKey="deg_efc"
            name={series.chartLabel}
            stroke={series.color}
            dot={false}
            strokeWidth={ENERGY_LINE_STROKE_WIDTH}
            type="monotone"
            yAxisId="right"
          />
        );
      }

      // Normal series on left axis
      if (mode === "area") {
        return (
          <Area
            key={`area-${series.key}`}
            dataKey={getEnergyDisplayKey(series)}
            name={series.chartLabel}
            stroke={series.color}
            fill={withAlpha(series.color || "#000")}
            strokeWidth={ENERGY_LINE_STROKE_WIDTH}
            type="monotone"
            dot={false}
            yAxisId="left"
          />
        );
      }

      return (
        <Line
          key={`line-${series.key}`}
          dataKey={getEnergyDisplayKey(series)}
          name={series.chartLabel}
          stroke={series.color}
          dot={false}
          strokeWidth={ENERGY_LINE_STROKE_WIDTH}
          yAxisId="left"
        />
      );
    });
  };
  const handleMarketFetch = useCallback(
    async (section) => {
      const metrics =
        section === "mix"
          ? currentMixMetrics
          : section === "generation"
          ? currentGenerationMetrics
          : LOAD_METRICS;
      const filter = marketFilters[section];
      if (!metrics || !filter?.start || !filter?.end) {
        setMarketError((prev) => ({ ...prev, [section]: "Please select a time interval." }));
        return;
      }
      let startDate = filter.start;
      let endDate = filter.end;
      let startObj = parseLocalDateTime(`${startDate}T00:00:00`);
      let endObj = parseLocalDateTime(`${endDate}T23:59:59`);
      if (startObj > endObj) {
        [startDate, endDate] = [endDate, startDate];
        [startObj, endObj] = [endObj, startObj];
      }
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const limitDaysMap = {
        mix: 0,
        generation: 1,
        load: 1,
        dam: 1,
      };
      const limitDays = limitDaysMap[section] ?? 1;
      const limitEnd = new Date(todayStart.getTime());
      limitEnd.setDate(limitEnd.getDate() + limitDays);
      limitEnd.setHours(23, 59, 59, 999);
      const limitLabel = formatDisplayDate(formatDateLocal(limitEnd));
      const rangeBeyondLimit = endObj > limitEnd;
      const rangeStartAfterLimit = startObj > limitEnd;
      clearMarketNotice(section);
      if (rangeStartAfterLimit) {
        pushMarketNotice(
          section,
          `Data is available up to ${limitLabel}; the selected window lies entirely beyond that date.`,
          "warning"
        );
      } else if (rangeBeyondLimit) {
        pushMarketNotice(
          section,
          `Data is available up to ${limitLabel}; showing available data within that window.`,
          "info"
        );
      }
      setMarketLoading((prev) => ({ ...prev, [section]: true }));
      setMarketError((prev) => ({ ...prev, [section]: "" }));
      try {
        const requestMetrics =
          section === "mix"
            ? "generation"
            : section === "generation"
            ? ["generation_forecast", "wind_and_solar_forecast"]
            : "load_and_forecast";
        const res = await fetchMarketMetrics(
          {
            metrics: requestMetrics,
            startDate,
            endDate,
            market: selectedMarket,
          },
          accessToken
        );
        const series =
          section === "mix"
            ? normalizeEntsoeGenerationSeries(res, selectedMarket)
            : section === "generation"
            ? normalizeEntsoeForecastSeries(res, selectedMarket)
            : normalizeEntsoeLoadSeries(res);
        const bounded = series
          .filter((row) => row?.time && row.time >= startObj && row.time <= endObj)
          .reduce((acc, row) => {
            acc.push(row);
            return acc;
          }, []);
        const step = section === "mix" ? detectEnergyMixStep(bounded) : undefined;
        const finalSeries = buildSeriesTimeline(bounded, startObj, endObj, step);
        setMarketSeries((prev) => ({ ...prev, [section]: finalSeries }));
        if (!bounded.length && !rangeBeyondLimit && !rangeStartAfterLimit) {
          setMarketError((prev) => ({
            ...prev,
            [section]: "No avilable data for the selected window and market.",
          }));
        }
      } catch (err) {
        setMarketError((prev) => ({
          ...prev,
          [section]: formatDashboardError(err, "Failed to fetch market data."),
        }));
      } finally {
        setMarketLoading((prev) => ({ ...prev, [section]: false }));
      }
    },
    [
      accessToken,
      currentGenerationMetrics,
      currentMixMetrics,
      marketFilters,
      selectedMarket,
      pushMarketNotice,
      clearMarketNotice,
    ]
  );
  const handleDamFetch = useCallback(async () => {
    if (!damRange.start || !damRange.end) {
      setDamError("Please select a time interval.");
      return;
    }
    const marketTimeZone = BIDDING_ZONE_TIME_ZONES[selectedMarket] || "UTC";
    const { startDate, endDate } = buildCalendarRangeWindow(
      { start: damRange.start, end: damRange.end },
      marketTimeZone
    );
    if (!startDate || !endDate) {
      setDamError("Please select a valid time interval.");
      return;
    }
    const marketTodayKey = formatDateInTimeZone(new Date(), marketTimeZone);
    const limitDateKey = addDaysToDateKey(marketTodayKey, 1);
    const limitLabel = formatDisplayDate(limitDateKey);
    const rangeBeyondLimit = damRange.end > limitDateKey;
    const rangeStartAfterLimit = damRange.start > limitDateKey;
    clearMarketNotice("dam");
    if (rangeStartAfterLimit) {
      pushMarketNotice(
        "dam",
        `DAM data is available up to ${limitLabel}; the selected window lies entirely beyond that date.`,
        "warning"
      );
    } else if (rangeBeyondLimit) {
      pushMarketNotice(
        "dam",
        `DAM data is available up to ${limitLabel}; showing available data within that window.`,
        "info"
      );
    }
    setDamLoading(true);
    setDamError("");
    setDamMetrics(null);
    try {
      const res = await fetchDamPrices(
        {
          market: selectedMarket,
          start: damRange.start,
          end: damRange.end,
          series: "both",
          // Without this the panel reads dam_market_prices unfiltered - the
          // legacy Forecast Valley column - so the DAM chart would go blank the
          // day that feed stops, while a complete in-house forecast sat in
          // dam_price_forecasts.
          provider: DAM_MARKET_FORECAST_PROVIDER,
        },
        accessToken
      );
      const rawRows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      const roundPrice = (value) => (value == null ? value : Math.round(value * 100) / 100);
      const data = rawRows.map((row) => ({
        time: getDamDeliveryTime(row, marketTimeZone),
        p10: roundPrice(toNumberSafe(row.p10)),
        p50: roundPrice(toNumberSafe(row.p50)),
        p90: roundPrice(toNumberSafe(row.p90)),
        actual: roundPrice(normalizeDamActualPrice(row)),
      }));
      const finalSeries = normalizeSparsePriceSeries(data);
      setDamSeries(finalSeries);
      setDamMetrics(computeDamForecastMetrics(finalSeries));
    } catch (err) {
      setDamError(formatDashboardError(err, "Failed to fetch DAM prices."));
      setDamSeries([]);
      setDamMetrics(null);
    } finally {
      setDamLoading(false);
    }
  }, [accessToken, clearMarketNotice, damRange.end, damRange.start, pushMarketNotice, selectedMarket]);
  const handleIntradayFetch = useCallback(async () => {
    if (!intradayRange.start || !intradayRange.end) {
      setIntradayError("Please select a time interval.");
      return;
    }
    const marketTimeZone = BIDDING_ZONE_TIME_ZONES[selectedMarket] || "UTC";
    const { startDate, endDate, requestStart, requestEnd } = buildCalendarRangeWindow(
      { start: intradayRange.start, end: intradayRange.end },
      marketTimeZone
    );
    if (!startDate || !endDate) {
      setIntradayError("Please select a valid time interval.");
      return;
    }
    clearMarketNotice("intraday");
    setIntradayLoading(true);
    setIntradayError("");
    try {
      let mappedRows;
      if (intradayProduct === "XBID" && xbidHasSelection) {
        const res = await fetchXbidCompare(
          {
            market: selectedMarket,
            start: requestStart,
            end: requestEnd,
            duration: xbidDuration,
            sides: xbidSides,
            assets: xbidAssets,
            technologies: xbidTechnologies,
          },
          accessToken
        );
        setXbidGroups(res.labels || []);
        mappedRows = (res.series || []).map((row) => {
          const mapped = { time: getDamDeliveryTime(row, marketTimeZone) };
          (res.labels || []).forEach((lbl) => {
            mapped[`${lbl}::vwap`] = toNumberSafe(row?.vwap?.[lbl]);
            mapped[`${lbl}::min`] = toNumberSafe(row?.min?.[lbl]);
            mapped[`${lbl}::max`] = toNumberSafe(row?.max?.[lbl]);
            mapped[`vol::${lbl}`] = toNumberSafe(row?.volume?.[lbl]);
          });
          return mapped;
        });
      } else if (intradayProduct === "XBID") {
        const res = await fetchXbidPrices(
          {
            market: selectedMarket,
            start: requestStart,
            end: requestEnd,
            duration: xbidDuration,
          },
          accessToken
        );
        setXbidGroups([]);
        mappedRows = (Array.isArray(res) ? res : []).map((row) => ({
          time: getDamDeliveryTime(row, marketTimeZone),
          vwap: toNumberSafe(row?.vwap),
          min: toNumberSafe(row?.min),
          max: toNumberSafe(row?.max),
          volume: toNumberSafe(row?.volume),
        }));
      } else {
        const res = await fetchIntradayPrices(
          {
            product: intradayProduct,
            market: selectedMarket,
            start: requestStart,
            end: requestEnd,
            series: intradaySeriesMode,
          },
          accessToken
        );
        // Forecasts arrive with full float precision; round to 2 decimals at
        // ingestion so tooltip, table and metrics all agree (same as DAM).
        const roundPrice = (value) => (value == null ? value : Math.round(value * 100) / 100);
        mappedRows = (Array.isArray(res) ? res : []).map((row) => ({
          time: getDamDeliveryTime(row, marketTimeZone),
          publishedAt: getPublishedAtTime(row, marketTimeZone),
          publicationVersion: toNumberSafe(row?.publication_version ?? row?.publicationVersion),
          p10: roundPrice(toNumberSafe(row?.p10)),
          p50: roundPrice(toNumberSafe(row?.p50)),
          p90: roundPrice(toNumberSafe(row?.p90)),
          actual: roundPrice(toNumberSafe(row?.actual)),
        }));
      }
      const normalizedSeries = filterSeriesByTimeWindow(
        normalizeSparsePriceSeries(mappedRows),
        startDate,
        endDate
      );
      const finalSeries = buildSeriesTimeline(normalizedSeries, startDate, endDate);
      setIntradaySeries(finalSeries);
      if (!finalSeries.length) {
        pushMarketNotice(
          "intraday",
          `No ${intradayProduct} price rows were returned for the selected window.`,
          "info"
        );
      }
    } catch (err) {
      setIntradayError(formatDashboardError(err, "Failed to fetch intraday prices."));
      setIntradaySeries([]);
    } finally {
      setIntradayLoading(false);
    }
  }, [
    accessToken,
    clearMarketNotice,
    intradayProduct,
    intradayRange.end,
    intradayRange.start,
    intradaySeriesMode,
    pushMarketNotice,
    selectedMarket,
    xbidHasSelection,
    xbidSides,
    xbidAssets,
    xbidTechnologies,
    xbidDuration,
  ]);
  const handleActualComparisonFetch = useCallback(async () => {
    if (!compareRange.start || !compareRange.end) {
      setActualComparisonError("Please select a time interval.");
      return;
    }
    const marketTimeZone = BIDDING_ZONE_TIME_ZONES[selectedMarket] || "UTC";
    const { startDate, endDate, requestStart, requestEnd } = buildCalendarRangeWindow(
      { start: compareRange.start, end: compareRange.end },
      marketTimeZone
    );
    setActualComparisonLoading(true);
    setActualComparisonError("");
    try {
      const [damRes, ida1Res, ida2Res, ida3Res] = await Promise.all([
        fetchDamPrices(
          {
            market: selectedMarket,
            start: compareRange.start,
            end: compareRange.end,
            series: "both",
            provider: DAM_MARKET_FORECAST_PROVIDER,
          },
          accessToken
        ),
        fetchIntradayPrices(
          {
            product: "IDA1",
            market: selectedMarket,
            start: requestStart,
            end: requestEnd,
            series: "actual",
          },
          accessToken
        ),
        fetchIntradayPrices(
          {
            product: "IDA2",
            market: selectedMarket,
            start: requestStart,
            end: requestEnd,
            series: "actual",
          },
          accessToken
        ),
        fetchIntradayPrices(
          {
            product: "IDA3",
            market: selectedMarket,
            start: requestStart,
            end: requestEnd,
            series: "actual",
          },
          accessToken
        ),
      ]);
      const normalizeActualRows = (rows) =>
        normalizeSparsePriceSeries(
          (Array.isArray(rows) ? rows : []).map((row) => ({
            time: getDamDeliveryTime(row, marketTimeZone),
            actual: toNumberSafe(row?.actual),
          }))
        );
      const comparison = buildActualPriceComparisonSeries({
        dam: filterSeriesByTimeWindow(
          normalizeActualRows(
            (Array.isArray(damRes?.data) ? damRes.data : []).map((row) => ({
              time: getDamDeliveryTime(row, marketTimeZone),
              actual: normalizeDamActualPrice(row),
            }))
          ),
          startDate,
          endDate
        ),
        ida1: filterSeriesByTimeWindow(normalizeActualRows(ida1Res), startDate, endDate),
        ida2: filterSeriesByTimeWindow(normalizeActualRows(ida2Res), startDate, endDate),
        ida3: filterSeriesByTimeWindow(normalizeActualRows(ida3Res), startDate, endDate),
      });
      setActualComparisonSeries(comparison);
    } catch (err) {
      setActualComparisonError(
        formatDashboardError(err, "Failed to fetch actual price comparison data.")
      );
      setActualComparisonSeries([]);
    } finally {
      setActualComparisonLoading(false);
    }
  }, [accessToken, compareRange.end, compareRange.start, selectedMarket]);
  const handleBalancingFetch = useCallback(async () => {
    if (!balancingRange.start || !balancingRange.end) {
      setBalancingFetchError("Please select a time interval.");
      return;
    }
    setBalancingLoading(true);
    setBalancingFetchError("");
    try {
      const res = await fetchBalancingData(
        {
          market: selectedMarket,
          start: balancingRange.start,
          end: balancingRange.end,
          datasets: [balancingDataset],
        },
        accessToken
      );
      setBalancingRows(res?.data?.[balancingDataset] || []);
    } catch (err) {
      const fallbackMessage =
        Number(err?.status) === 404
          ? "Balancing endpoint is not available on the configured backend."
          : "Failed to fetch balancing market data.";
      setBalancingFetchError(formatApiError(err, fallbackMessage));
      setBalancingRows([]);
    } finally {
      setBalancingLoading(false);
    }
  }, [accessToken, balancingDataset, balancingRange.end, balancingRange.start, selectedMarket]);
  const balancingActiveRows = useMemo(
    () =>
      balancingDataset === "activated_balancing"
        ? flattenBalancingActivatedRows(balancingRows)
        : balancingRows,
    [balancingDataset, balancingRows]
  );
  const balancingZones = useMemo(
    () =>
      Array.from(
        new Set((balancingActiveRows || []).map((row) => row?.zone_code).filter(Boolean))
      ).sort(),
    [balancingActiveRows]
  );
  useEffect(() => {
    setBalancingZone((current) =>
      current === "all" || balancingZones.includes(current) ? current : "all"
    );
  }, [balancingZones]);
  const balancingFilteredRows = useMemo(
    () =>
      balancingZone === "all"
        ? balancingActiveRows
        : (balancingActiveRows || []).filter((row) => row?.zone_code === balancingZone),
    [balancingActiveRows, balancingZone]
  );
  const balancingNumericFields = useMemo(
    () => collectBalancingNumericFields(balancingFilteredRows, balancingDataset),
    [balancingFilteredRows, balancingDataset]
  );
  useEffect(() => {
    if (!balancingNumericFields.length) {
      setBalancingMetric("");
      return;
    }
    setBalancingMetric((current) =>
      current && balancingNumericFields.includes(current) ? current : balancingNumericFields[0]
    );
  }, [balancingNumericFields]);
  const balancingValueFields = useMemo(
    () =>
      balancingDataset === "imbalance_prices"
        ? balancingNumericFields
        : balancingMetric
          ? [balancingMetric]
          : [],
    [balancingDataset, balancingMetric, balancingNumericFields]
  );
  const balancingChart = useMemo(
    () =>
      buildBalancingChartData(
        balancingFilteredRows,
        balancingValueFields,
        balancingDataset,
        balancingZone === "all" && balancingZones.length > 1
      ),
    [balancingFilteredRows, balancingValueFields, balancingDataset, balancingZone, balancingZones]
  );
  useEffect(() => {
    const availableSeriesIds = new Set(balancingChart.series.map((seriesItem) => seriesItem.id));
    setHiddenBalancingSeries((current) =>
      current.filter((seriesId) => availableSeriesIds.has(seriesId))
    );
  }, [balancingChart.series]);
  const toggleBalancingSeriesVisibility = useCallback((seriesId) => {
    if (!seriesId) return;
    setHiddenBalancingSeries((current) =>
      current.includes(seriesId)
        ? current.filter((item) => item !== seriesId)
        : [...current, seriesId]
    );
  }, []);
  const buildSeriesTimeline = (series, startDate, endDate, stepMs) => {
    if (!startDate || !endDate) return series;
    const startMs = startDate.getTime();
    const endMs = endDate.getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || startMs > endMs) {
      return [];
    }
    const timeMap = new Map();
    series.forEach((row) => {
      const key = hourKey(row?.time);
      if (key != null) timeMap.set(key, row);
    });
    const result = [];
    const inferredStep = inferSeriesStepMs(series);
    const step =
      Number.isFinite(stepMs) && stepMs > 0
        ? stepMs
        : inferredStep || QUARTER_HOUR_STEP_MS;
    let cursor = new Date(startMs);
    cursor.setSeconds(0, 0);
    while (cursor.getTime() <= endMs) {
      const key = cursor.getTime();
      if (timeMap.has(key)) {
        result.push(timeMap.get(key));
      } else {
        result.push({ time: new Date(cursor) });
      }
      cursor = new Date(cursor.getTime() + step);
    }
    return result;
  };

  const handleWeatherFetch = useCallback(async () => {
    const { start, end } = weatherRange || {};
    const lat = weatherLat;
    const lon = weatherLon;
    const tz = weatherTz;
    if (lat == null || lon == null || tz == null) {
      setWeatherError("Project configuration is missing coordinates or timezone.");
      return;
    }
    if (!start || !end) {
      setWeatherError("Please select a time interval.");
      return;
    }
    setWeatherLoading(true);
    setWeatherError("");
    setWeatherNotices([]);
    const startDate = parseLocalDateTime(`${start}T00:00:00`);
    const requestedEndDate = parseLocalDateTime(`${end}T23:59:59`);
    const todayStart = parseLocalDateTime(`${formatDateLocal(new Date())}T00:00:00`);
    const isHistoricalMode = weatherMode === "historical";
    let truncatedEndDate = requestedEndDate;
    const notices = [];
    const addNotice = (message, severity = "info") => {
      if (!message) return;
      notices.push({ message, severity });
    };
    const flushNotices = () => {
      setWeatherNotices([...notices]);
    };

    const needsHistoricalTruncate =
      (isHistoricalMode || weatherMode === "both") &&
      requestedEndDate &&
      todayStart &&
      requestedEndDate.getTime() >= todayStart.getTime();
    if (needsHistoricalTruncate) {
      truncatedEndDate = new Date(todayStart.getTime() - 1);
      if (startDate && truncatedEndDate.getTime() >= startDate.getTime()) {
        addNotice(
          "Historical data is available through yesterday; showing data up to D-1.",
          "info"
        );
      }
    }
    let historicalRequestAvailable = true;
    const requestedRangeInFuture =
      truncatedEndDate && startDate && truncatedEndDate < startDate;
    if (requestedRangeInFuture) {
      addNotice(
        "Historical data is available through yesterday; the requested interval lies entirely after D-1.",
        "warning"
      );
      if (weatherMode !== "both") {
        const placeholder = buildSeriesTimeline([], startDate, requestedEndDate);
        setWeatherSeries({
          irradiance: placeholder,
          temperature: placeholder,
          humidity: placeholder,
          wind: placeholder,
        });
        setWeatherLoading(false);
        flushNotices();
        return;
      }
      historicalRequestAvailable = false;
    }

    const isForecastMode = weatherMode === "forecast" || weatherMode === "both";
    let forecastAdjustedStart = startDate ? new Date(startDate.getTime()) : null;
    let forecastAdjustedEnd = requestedEndDate
      ? new Date(requestedEndDate.getTime())
      : null;
    let forecastFetchEnabled = isForecastMode;

    if (isForecastMode && todayStart) {
      const forecastHorizonEnd = new Date(todayStart.getTime());
      forecastHorizonEnd.setDate(
        forecastHorizonEnd.getDate() + FORECAST_FUTURE_LIMIT_DAYS
      );
      const forecastPastLimit = new Date(todayStart.getTime());
      forecastPastLimit.setDate(
        forecastPastLimit.getDate() - FORECAST_PAST_LIMIT_DAYS
      );
      const horizonDateLabel = formatDisplayDate(formatDateLocal(forecastHorizonEnd));
      const requestedEndMs = requestedEndDate?.getTime();
      const entireWindowBeforePastLimit =
        requestedEndMs != null &&
        requestedEndMs < forecastPastLimit.getTime();

      if (entireWindowBeforePastLimit) {
        forecastAdjustedStart = new Date(forecastPastLimit);
        forecastAdjustedEnd = new Date(forecastPastLimit);
        const earliestDate = formatDisplayDate(
          formatDateLocal(forecastPastLimit)
        );
        addNotice(
          `Forecast data is only available for the last 70 days; available data from ${earliestDate}.`,
          "warning"
        );
      }

      if (
        forecastAdjustedStart &&
        forecastAdjustedStart.getTime() > forecastHorizonEnd.getTime()
      ) {
        forecastFetchEnabled = false;
        const placeholder = buildSeriesTimeline([], startDate, requestedEndDate);
        setWeatherSeries({
          irradiance: placeholder,
          temperature: placeholder,
          humidity: placeholder,
          wind: placeholder,
        });
        setWeatherLoading(false);
        addNotice(
          `Forecast is available up to +11 days ahead (until ${horizonDateLabel}); the requested interval lies entirely after D+11.`,
          "warning"
        );
        flushNotices();
        return;
      }

      if (
        forecastAdjustedEnd &&
        forecastAdjustedEnd.getTime() > forecastHorizonEnd.getTime()
      ) {
        forecastAdjustedEnd = forecastHorizonEnd;
        addNotice(
          `Forecast is available up to +11 days ahead (until ${horizonDateLabel}); showing available data within that window.`,
          "info"
        );
      }
      if (
        forecastAdjustedStart &&
        forecastAdjustedStart.getTime() < forecastPastLimit.getTime()
      ) {
        const earliestDate = formatDisplayDate(
          formatDateLocal(forecastPastLimit)
        );
        if (
          !(
            forecastAdjustedEnd &&
            forecastAdjustedEnd.getTime() < forecastPastLimit.getTime()
          )
        ) {
          addNotice(
            `Forecast data is only available for the last 70 days; showing data from ${earliestDate}.`,
            "info"
          );
        }
        forecastAdjustedStart = forecastPastLimit;
      }
      if (
        forecastAdjustedStart &&
        forecastAdjustedEnd &&
        forecastAdjustedStart.getTime() > forecastAdjustedEnd.getTime()
      ) {
        const placeholder = buildSeriesTimeline([], startDate, requestedEndDate);
        setWeatherSeries({
          irradiance: placeholder,
          temperature: placeholder,
          humidity: placeholder,
          wind: placeholder,
        });
        setWeatherLoading(false);
        flushNotices();
        return;
      }
    }

    const filterForecastRows = (rows) => {
      if (!isForecastMode) return rows;
      const lower = forecastAdjustedStart?.getTime() ?? null;
      const upper = forecastAdjustedEnd?.getTime() ?? null;
      return rows.filter((row) => {
        const time = row?.time instanceof Date
          ? row.time.getTime()
          : row?.time
            ? new Date(row.time).getTime()
            : null;
        if (!Number.isFinite(time)) return false;
        if (lower !== null && time < lower) return false;
        if (upper !== null && time > upper) return false;
        return true;
      });
    };

    const forecastFetchStart =
      forecastFetchEnabled && forecastAdjustedStart
        ? formatDateLocal(forecastAdjustedStart)
        : start;
    const forecastFetchEnd =
      forecastFetchEnabled && forecastAdjustedEnd
        ? formatDateLocal(forecastAdjustedEnd)
        : end;

    try {
      if (weatherMode === "both") {
        const forecastPromise = forecastFetchEnabled
          ? fetchWeatherForecast(
              {
                lat,
                lon,
                tz,
                start_date: forecastFetchStart,
                end_date: forecastFetchEnd,
                type: "forecast",
              },
              accessToken
            )
          : Promise.resolve([]);
        const actualFetchPromise = historicalRequestAvailable
          ? fetchWeatherForecast(
              {
                lat,
                lon,
                tz,
                start_date: start,
                end_date: truncatedEndDate
                  ? formatDateLocal(truncatedEndDate)
                  : end,
                type: "historical",
              },
              accessToken
            )
          : Promise.resolve([]);
        const [forecastRes, actualRes] = await Promise.all([
          forecastPromise,
          actualFetchPromise,
        ]);
        const merged = mergeForecastActual(
          filterForecastRows(normalizeWeatherSeries(forecastRes)),
          normalizeWeatherSeries(actualRes)
        );
        const keepHistoricalOnly = weatherMode === "historical";
        const filteredSeries =
          keepHistoricalOnly && truncatedEndDate
            ? merged.filter((row) => row?.time && row.time.getTime() <= truncatedEndDate.getTime())
            : merged;
        const finalSeries = buildSeriesTimeline(filteredSeries, startDate, requestedEndDate);
        setWeatherSeries({
          irradiance: finalSeries,
          temperature: finalSeries,
          humidity: finalSeries,
          wind: finalSeries,
        });
        return;
      }
      const fetchEnd =
        isHistoricalMode && truncatedEndDate
          ? formatDateLocal(truncatedEndDate)
          : forecastFetchEnd;
      const shouldFetch = isHistoricalMode ? historicalRequestAvailable : forecastFetchEnabled;
      const fetchStart = isHistoricalMode ? start : forecastFetchStart;
      const res = shouldFetch
        ? await fetchWeatherForecast(
            {
              lat,
              lon,
              tz,
              start_date: fetchStart,
              end_date: fetchEnd,
              type: weatherMode,
            },
            accessToken
          )
        : [];
      const series = filterForecastRows(normalizeWeatherSeries(res));
      const limitedSeries = truncatedEndDate
        ? series.filter((row) => row?.time && row.time.getTime() <= truncatedEndDate.getTime())
        : series;
      const finalSeries = buildSeriesTimeline(limitedSeries, startDate, requestedEndDate);
      setWeatherSeries({
        irradiance: finalSeries,
        temperature: finalSeries,
        humidity: finalSeries,
        wind: finalSeries,
      });

    } catch (err) {
      setWeatherError(formatDashboardError(err, "Failed to fetch weather data."));
      const placeholder = buildSeriesTimeline([], startDate, requestedEndDate);
      setWeatherSeries({
        irradiance: placeholder,
        temperature: placeholder,
        humidity: placeholder,
        wind: placeholder,
      });
    } finally {
      setWeatherLoading(false);
      flushNotices();
    }
  // currentProject?.id dropped: this callback never reads it, so listing it only
  // forced a rebuild on every project switch.
  }, [accessToken, weatherMode, weatherRange, weatherLat, weatherLon, weatherTz]);

  // Weather inputs for the wind generation model (wind speed @10m + upper
  // level, 2m temperature, surface pressure). Mirrors handleWeatherFetch's
  // mode handling (forecast / historical / both) in a compact form.
  const handleWindWeatherFetch = useCallback(async () => {
    const { start, end } = weatherRange || {};
    const lat = weatherLat;
    const lon = weatherLon;
    const tz = weatherTz;
    if (lat == null || lon == null || tz == null) {
      setWindWeatherError("Project configuration is missing coordinates or timezone.");
      return;
    }
    if (!start || !end) {
      setWindWeatherError("Please select a time interval.");
      return;
    }
    setWindWeatherLoading(true);
    setWindWeatherError("");
    setWindWeatherNotices([]);
    const notices = [];
    const addNotice = (message, severity = "info") => {
      if (message) notices.push({ message, severity });
    };
    const startDate = parseLocalDateTime(`${start}T00:00:00`);
    const requestedEndDate = parseLocalDateTime(`${end}T23:59:59`);
    const todayStart = parseLocalDateTime(`${formatDateLocal(new Date())}T00:00:00`);
    const wantsForecast = weatherMode === "forecast" || weatherMode === "both";
    const wantsHistorical = weatherMode === "historical" || weatherMode === "both";

    // Historical data is available through yesterday.
    let historicalAvailable = wantsHistorical;
    let truncatedEndDate = requestedEndDate;
    if (wantsHistorical && requestedEndDate && todayStart && requestedEndDate >= todayStart) {
      truncatedEndDate = new Date(todayStart.getTime() - 1);
      if (startDate && truncatedEndDate >= startDate) {
        addNotice("Historical data is available through yesterday; showing data up to D-1.");
      } else {
        historicalAvailable = false;
        addNotice(
          "Historical data is available through yesterday; the requested interval lies entirely after D-1.",
          "warning"
        );
      }
    }

    // Forecast data covers -70 days to +11 days around today.
    let forecastAvailable = wantsForecast;
    let forecastStart = startDate ? new Date(startDate.getTime()) : null;
    let forecastEnd = requestedEndDate ? new Date(requestedEndDate.getTime()) : null;
    if (wantsForecast && todayStart) {
      const horizonEnd = new Date(todayStart.getTime());
      horizonEnd.setDate(horizonEnd.getDate() + FORECAST_FUTURE_LIMIT_DAYS);
      const pastLimit = new Date(todayStart.getTime());
      pastLimit.setDate(pastLimit.getDate() - FORECAST_PAST_LIMIT_DAYS);
      if (forecastStart && forecastStart > horizonEnd) {
        forecastAvailable = false;
        addNotice(
          `Forecast is available up to +${FORECAST_FUTURE_LIMIT_DAYS} days ahead; the requested interval lies entirely after that horizon.`,
          "warning"
        );
      }
      if (forecastEnd && forecastEnd > horizonEnd) {
        forecastEnd = horizonEnd;
        addNotice(
          `Forecast is available up to +${FORECAST_FUTURE_LIMIT_DAYS} days ahead; showing available data within that window.`
        );
      }
      if (forecastEnd && forecastEnd < pastLimit) {
        forecastAvailable = false;
        addNotice(
          `Forecast data is only available for the last ${FORECAST_PAST_LIMIT_DAYS} days.`,
          "warning"
        );
      } else if (forecastStart && forecastStart < pastLimit) {
        forecastStart = pastLimit;
        addNotice(
          `Forecast data is only available for the last ${FORECAST_PAST_LIMIT_DAYS} days; showing data from ${formatDisplayDate(formatDateLocal(pastLimit))}.`
        );
      }
      if (forecastStart && forecastEnd && forecastStart > forecastEnd) forecastAvailable = false;
    }

    try {
      const upperHeights = [];
      const doFetch = async (type, fetchStart, fetchEnd) => {
        const payload = await fetchWindWeather(
          { lat, lon, tz, start_date: fetchStart, end_date: fetchEnd, type },
          accessToken
        );
        if (payload?.upper_wind_height_m) upperHeights.push(payload.upper_wind_height_m);
        return normalizeWindWeatherSeries(payload);
      };
      const [forecastRows, actualRows] = await Promise.all([
        forecastAvailable
          ? doFetch("forecast", formatDateLocal(forecastStart), formatDateLocal(forecastEnd))
          : Promise.resolve([]),
        historicalAvailable
          ? doFetch("historical", start, formatDateLocal(truncatedEndDate))
          : Promise.resolve([]),
      ]);
      let rows;
      if (weatherMode === "both") {
        rows = mergeForecastActualWind(forecastRows, actualRows);
      } else {
        rows = weatherMode === "forecast" ? forecastRows : actualRows;
      }
      setWindWeatherUpperLabel(
        upperHeights.length ? `${[...new Set(upperHeights)].sort((a, b) => a - b).join(" / ")} m` : ""
      );
      setWindWeatherSeries(buildSeriesTimeline(rows, startDate, requestedEndDate));
    } catch (err) {
      setWindWeatherError(formatDashboardError(err, "Failed to fetch wind weather data."));
      setWindWeatherSeries(buildSeriesTimeline([], startDate, requestedEndDate));
    } finally {
      setWindWeatherLoading(false);
      setWindWeatherNotices(notices);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // currentProject?.id dropped: this callback never reads it, so listing it only
  // forced a rebuild on every project switch.
  }, [accessToken, weatherMode, weatherRange, weatherLat, weatherLon, weatherTz]);

  useEffect(() => {
    if (view !== "market" || marketWorkspaceTab !== "prices" || !selectedMarket) return;
    if (damRange.start && damRange.end) {
      handleDamFetch();
    }
  }, [
    damRange.end,
    damRange.start,
    handleDamFetch,
    marketWorkspaceTab,
    selectedMarket,
    view,
  ]);
  useEffect(() => {
    if (view !== "market" || marketWorkspaceTab !== "prices" || !selectedMarket) return;
    if (intradayRange.start && intradayRange.end && intradayProduct) {
      handleIntradayFetch();
    }
  }, [
    handleIntradayFetch,
    intradayProduct,
    intradayRange.end,
    intradayRange.start,
    marketWorkspaceTab,
    selectedMarket,
    view,
  ]);
  useEffect(() => {
    if (view !== "market" || marketWorkspaceTab !== "prices" || !selectedMarket) return;
    if (compareRange.start && compareRange.end) {
      handleActualComparisonFetch();
    }
  }, [
    compareRange.end,
    compareRange.start,
    handleActualComparisonFetch,
    marketWorkspaceTab,
    selectedMarket,
    view,
  ]);
  useEffect(() => {
    if (view !== "market" || marketWorkspaceTab !== "prices") return;
    if (balancingRange.start && balancingRange.end) {
      handleBalancingFetch();
    }
  }, [
    balancingRange.end,
    balancingRange.start,
    handleBalancingFetch,
    marketWorkspaceTab,
    selectedMarket,
    view,
  ]);
  useEffect(() => {
    if (view !== "market" || marketWorkspaceTab !== "fundamentals" || !selectedMarket) return;
    if (marketFilters.mix?.start && marketFilters.mix?.end) {
      handleMarketFetch("mix");
    }
    if (marketFilters.generation?.start && marketFilters.generation?.end) {
      handleMarketFetch("generation");
    }
    if (marketFilters.load?.start && marketFilters.load?.end) {
      handleMarketFetch("load");
    }
  }, [
    handleMarketFetch,
    marketFilters.generation?.end,
    marketFilters.generation?.start,
    marketFilters.load?.end,
    marketFilters.load?.start,
    marketFilters.mix?.end,
    marketFilters.mix?.start,
    marketWorkspaceTab,
    selectedMarket,
    view,
  ]);
  useEffect(() => {
    if (view !== "weather" || weatherAsset !== "pv") return;
    if (weatherRange.start && weatherRange.end && weatherLat != null && weatherLon != null && weatherTz) {
      handleWeatherFetch();
    }
  }, [
    handleWeatherFetch,
    view,
    weatherAsset,
    weatherLat,
    weatherLon,
    weatherRange.end,
    weatherRange.start,
    weatherTz,
  ]);
  useEffect(() => {
    if (view !== "weather" || weatherAsset !== "wind") return;
    if (weatherRange.start && weatherRange.end && weatherLat != null && weatherLon != null && weatherTz) {
      handleWindWeatherFetch();
    }
  }, [
    handleWindWeatherFetch,
    view,
    weatherAsset,
    weatherLat,
    weatherLon,
    weatherRange.end,
    weatherRange.start,
    weatherTz,
  ]);
  const pvGenProjectId = currentProject?.id;
  const pvGenTriggerRange = useMemo(
    () => marketFilters.pvGenTrigger || { start: "", end: "" },
    [marketFilters.pvGenTrigger]
  );
  const handlePvGenRunsFetch = useCallback(async () => {
    if (!pvGenProjectId) return;
    setPvGenRunsLoading(true);
    setPvGenRunsError("");
    try {
      const runs = await fetchPvGenerationRuns({ projectId: pvGenProjectId }, accessToken);
      setPvGenRuns(Array.isArray(runs) ? runs : []);
    } catch (err) {
      setPvGenRunsError(formatPvGenerationError(err));
    } finally {
      setPvGenRunsLoading(false);
    }
  }, [accessToken, pvGenProjectId]);
  useEffect(() => {
    if (view !== "weather" || weatherWorkspaceTab !== "generation" || weatherAsset !== "pv" || isStandaloneBessProject) return;
    handlePvGenRunsFetch();
  }, [view, weatherWorkspaceTab, weatherAsset, isStandaloneBessProject, handlePvGenRunsFetch]);
  const pvGenPendingRunIds = useMemo(
    () =>
      pvGenRuns
        .filter((r) => r?.status === "queued" || r?.status === "running")
        .map((r) => r.run_id),
    [pvGenRuns]
  );
  useEffect(() => {
    if (view !== "weather" || weatherWorkspaceTab !== "generation" || weatherAsset !== "pv" || isStandaloneBessProject) return;
    if (pvGenPendingRunIds.length === 0) return;
    const interval = setInterval(() => {
      handlePvGenRunsFetch();
    }, 5000);
    return () => clearInterval(interval);
  }, [view, weatherWorkspaceTab, weatherAsset, isStandaloneBessProject, pvGenPendingRunIds.length, handlePvGenRunsFetch]);
  const pvGenAutoTrackRef = useRef(true);
  useEffect(() => {
    if (!pvGenAutoTrackRef.current) return;
    const latest = pvGenRuns
      .filter((r) => r?.status === "completed")
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0];
    if (latest) setPvGenSelectedRunIds([latest.run_id]);
  }, [pvGenRuns]);
  const pvGenHistoricalRun = useMemo(
    () => pvGenRuns.find((r) => r.run_type === "actual_weather" && pvGenSelectedRunIds.includes(r.run_id)) || null,
    [pvGenRuns, pvGenSelectedRunIds]
  );
  const pvGenForecastRuns = useMemo(
    () => pvGenRuns.filter((r) => r.run_type === "forecast" && pvGenSelectedRunIds.includes(r.run_id)),
    [pvGenRuns, pvGenSelectedRunIds]
  );
  const handlePvGenTrigger = useCallback(async () => {
    if (!pvGenProjectId) return;
    if (!pvGenTriggerRange.start || !pvGenTriggerRange.end) {
      setPvGenTriggerError("Select a date range before running the estimation.");
      return;
    }
    setPvGenTriggering(true);
    setPvGenTriggerError("");
    try {
      await triggerPvGenerationRun(
        {
          projectId: pvGenProjectId,
          start_date: pvGenTriggerRange.start,
          end_date: pvGenTriggerRange.end,
          tz: weatherTz,
          runType: pvGenRunType,
        },
        accessToken
      );
      await handlePvGenRunsFetch();
    } catch (err) {
      setPvGenTriggerError(formatPvGenerationError(err));
    } finally {
      setPvGenTriggering(false);
    }
  }, [
    accessToken,
    pvGenProjectId,
    pvGenTriggerRange,
    pvGenRunType,
    weatherTz,
    handlePvGenRunsFetch,
  ]);
  const handlePvGenRunToggle = useCallback(
    (runId) => {
      const type = pvGenRuns.find((r) => r.run_id === runId)?.run_type;
      pvGenAutoTrackRef.current = false;
      setPvGenSelectedRunIds((prev) => {
        if (prev.includes(runId)) return prev.filter((id) => id !== runId);
        // Historical-weather stays a single baseline run; forecast runs can pile up
        // so several forecasts for the same period can be plotted together.
        if (type === "actual_weather") {
          const kept = prev.filter((id) => pvGenRuns.find((r) => r.run_id === id)?.run_type !== type);
          return [...kept, runId];
        }
        return [...prev, runId];
      });
    },
    [pvGenRuns]
  );
  const pvGenConvergenceRuns = useMemo(() => {
    if (!pvGenConvergenceDate) return [];
    return pvGenRuns
      .filter(
        (r) =>
          r?.run_type === "forecast" &&
          r?.status === "completed" &&
          r?.start_date &&
          r?.end_date &&
          r.start_date <= pvGenConvergenceDate &&
          r.end_date >= pvGenConvergenceDate
      )
      .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
  }, [pvGenRuns, pvGenConvergenceDate]);
  const pvGenConvergenceActualRun = useMemo(() => {
    if (!pvGenConvergenceDate) return null;
    return (
      pvGenRuns
        .filter(
          (r) =>
            r?.run_type === "actual_weather" &&
            r?.status === "completed" &&
            r?.start_date &&
            r?.end_date &&
            r.start_date <= pvGenConvergenceDate &&
            r.end_date >= pvGenConvergenceDate
        )
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0] || null
    );
  }, [pvGenRuns, pvGenConvergenceDate]);
  const pvGenNeededRunIds = useMemo(() => {
    const ids = new Set(pvGenSelectedRunIds);
    pvGenConvergenceRuns.forEach((r) => ids.add(r.run_id));
    if (pvGenConvergenceActualRun) ids.add(pvGenConvergenceActualRun.run_id);
    return Array.from(ids);
  }, [pvGenSelectedRunIds, pvGenConvergenceRuns, pvGenConvergenceActualRun]);
  useEffect(() => {
    if (!pvGenProjectId) return;
    const idsToFetch = pvGenNeededRunIds.filter(
      (id) => !pvGenSeriesByRun[id] && !pvGenSeriesLoadingIds.includes(id)
    );
    if (idsToFetch.length === 0) return;
    setPvGenSeriesLoadingIds((prev) => [...prev, ...idsToFetch]);
    idsToFetch.forEach(async (runId) => {
      try {
        const data = await fetchPvGenerationRunSeries(
          { projectId: pvGenProjectId, runId },
          accessToken
        );
        setPvGenSeriesByRun((prev) => ({ ...prev, [runId]: data?.series || [] }));
      } catch (err) {
        setPvGenRunsError(formatPvGenerationError(err));
      } finally {
        setPvGenSeriesLoadingIds((prev) => prev.filter((id) => id !== runId));
      }
    });
  }, [pvGenNeededRunIds, pvGenProjectId, accessToken, pvGenSeriesByRun, pvGenSeriesLoadingIds]);
  useEffect(() => {
    if (view !== "weather" || weatherWorkspaceTab !== "generation" || weatherAsset !== "pv" || isStandaloneBessProject) return;
    if (!pvGenConvergenceDate || !pvGenProjectId) return;
    let cancelled = false;
    (async () => {
      setPvGenConvergenceMeasuredLoading(true);
      setPvGenConvergenceMeasuredError("");
      try {
        const res = await fetchPvMeasuredGeneration(
          {
            projectId: pvGenProjectId,
            start_date: pvGenConvergenceDate,
            end_date: pvGenConvergenceDate,
          },
          accessToken
        );
        if (cancelled) return;
        setPvGenConvergenceMeasured(res || { available: false });
      } catch (err) {
        if (cancelled) return;
        setPvGenConvergenceMeasuredError(formatPvGenerationError(err));
      } finally {
        if (!cancelled) setPvGenConvergenceMeasuredLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [view, weatherWorkspaceTab, weatherAsset, isStandaloneBessProject, pvGenConvergenceDate, pvGenProjectId, accessToken]);
  useEffect(() => {
    if (view !== "weather" || weatherWorkspaceTab !== "generation" || weatherAsset !== "pv" || isStandaloneBessProject) return;
    if (!pvGenIncludeMeasured || !pvGenProjectId) return;
    if (!pvGenTriggerRange.start || !pvGenTriggerRange.end) return;
    let cancelled = false;
    (async () => {
      setPvGenMeasuredLoading(true);
      setPvGenMeasuredError("");
      try {
        const res = await fetchPvMeasuredGeneration(
          {
            projectId: pvGenProjectId,
            start_date: pvGenTriggerRange.start,
            end_date: pvGenTriggerRange.end,
          },
          accessToken
        );
        if (cancelled) return;
        setPvGenMeasuredAvailable(!!res?.available);
        setPvGenMeasuredSeries(res?.available ? res.series || [] : []);
      } catch (err) {
        if (cancelled) return;
        setPvGenMeasuredError(formatPvGenerationError(err));
      } finally {
        if (!cancelled) setPvGenMeasuredLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    view,
    weatherWorkspaceTab,
    weatherAsset,
    isStandaloneBessProject,
    pvGenIncludeMeasured,
    pvGenProjectId,
    pvGenTriggerRange.start,
    pvGenTriggerRange.end,
    accessToken,
  ]);
  const handlePvGenExport = useCallback(async () => {
    if (!pvGenProjectId) return;
    if (pvGenSelectedRunIds.length === 0 && !pvGenIncludeMeasured) {
      setPvGenExportError("Select at least one run or include measured data before exporting.");
      return;
    }
    setPvGenExporting(true);
    setPvGenExportError("");
    try {
      const { blob, filename } = await exportPvGenerationOverview(
        {
          projectId: pvGenProjectId,
          runIds: pvGenSelectedRunIds,
          includeMeasured: pvGenIncludeMeasured,
          start_date: pvGenTriggerRange.start,
          end_date: pvGenTriggerRange.end,
        },
        accessToken
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "pv_generation_overview.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setPvGenExportError(formatPvGenerationError(err));
    } finally {
      setPvGenExporting(false);
    }
  }, [accessToken, pvGenProjectId, pvGenSelectedRunIds, pvGenIncludeMeasured, pvGenTriggerRange]);
  const handlePvGenExportRun = useCallback(
    async (run) => {
      if (!pvGenProjectId || !run) return;
      const { blob, filename } = await exportPvGenerationOverview(
        {
          projectId: pvGenProjectId,
          runIds: [run.run_id],
          includeMeasured: false,
          start_date: run.start_date,
          end_date: run.end_date,
        },
        accessToken
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `pv_generation_${run.run_id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
    [accessToken, pvGenProjectId]
  );
  const handlePvGenRerun = useCallback(
    async (run) => {
      if (!pvGenProjectId || !run) return;
      await triggerPvGenerationRun(
        {
          projectId: pvGenProjectId,
          start_date: run.start_date,
          end_date: run.end_date,
          tz: weatherTz,
          runType: run.run_type,
        },
        accessToken
      );
      await handlePvGenRunsFetch();
    },
    [accessToken, pvGenProjectId, weatherTz, handlePvGenRunsFetch]
  );
  const marketDisplayTimeZone = selectedMarketTimeZone;
  const formatTimeLabel = (value) =>
    new Date(value).toLocaleString("en-GB", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  const formatTimeLabelShort = (value) =>
    new Date(value).toLocaleString("en-GB", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  const formatTimeLabelLong = useCallback(
    (value) =>
      new Date(value).toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }),
    []
  );
  const formatMarketTimeLabel = useCallback(
    (value) =>
      new Date(value).toLocaleString("en-GB", {
        timeZone: marketDisplayTimeZone,
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }),
    [marketDisplayTimeZone]
  );
  const formatMarketTimeLabelShort = useCallback(
    (value) =>
      new Date(value).toLocaleString("en-GB", {
        timeZone: marketDisplayTimeZone,
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }),
    [marketDisplayTimeZone]
  );
  const formatMarketTimeLabelLong = useCallback(
    (value) =>
      new Date(value).toLocaleString("en-GB", {
        timeZone: marketDisplayTimeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }),
    [marketDisplayTimeZone]
  );
  const getDashboardTimeXAxisProps = useCallback(
    (fontWeight = 700) => ({
      dataKey: "time",
      tick: { fontSize: 12, fontWeight },
      tickFormatter: formatTimeLabel,
      minTickGap: 28,
    }),
    []
  );
  const formatOptimizationLabelFromPayload = formatTimeLabelLong;
  const formatOptimizationTickFromRows = useCallback(() => formatTimeLabelShort, []);
  const getOptimizationXAxisProps = useCallback(
    (_rows, fontWeight = 700) => getDashboardTimeXAxisProps(fontWeight),
    [getDashboardTimeXAxisProps]
  );
  const formatTooltipValue = (value) => {
    if (value == null || value === "") return "N/A";
    return value;
  };
  const formatTwoDecimalTooltipValue = (value) => {
    if (value == null || value === "") return "N/A";
    if (typeof value === "number" && Number.isFinite(value)) return value.toFixed(2);
    return value;
  };
  const formatNumberWithCommas = (value, { decimals = 0 } = {}) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "N/A";
    return n.toLocaleString("en-GB", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };
  const formatTooltipSmart = (value, _name, entry) => {
    const key = entry?.dataKey; // e.g. "price", "p_poi", etc.

    // PRICE
    if (key === "price_eur_mwh") return formatPriceEurMwhTooltipValue(value);

    // POWER / POI
    if (key === "p_poi_mw") return `${formatNumberWithCommas(value, { decimals: 3 })} MW`;

    if (typeof key === "string" && key.endsWith("_mw")) {
      return `${formatNumberWithCommas(value, { decimals: 3 })} MW`;
    }

    // EFC axis (if you want)
    if (key === "efc" || key === "cycles" || key === "EFC") {
      return formatNumberWithCommas(value, { decimals: 2 });
    }

    // Default for everything else: commas + 2 decimals (adjust if you want)
    return formatNumberWithCommas(value, { decimals: 2 });
  };
  const formatOptimizationEnergyValue = useCallback((value, unit = "MW") => {
    if (value == null || value === "") return "N/A";
    const numericValue = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numericValue)) return String(value);
    const formatted = numericValue.toLocaleString("en-GB", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });
    return unit ? `${formatted} ${unit}` : formatted;
  }, []);
  const formatOptimizationEnergyAxisValue = useCallback((value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue)
      ? numericValue.toLocaleString("en-GB", { maximumFractionDigits: 4 })
      : value;
  }, []);
  const formatMarketTableValue = useCallback((value, decimals = 2) => {
    if (value == null || value === "") return "-";
    const numericValue = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numericValue)) return String(value);
    return numericValue.toLocaleString("en-GB", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }, []);
  const getMarketTableEndTime = useCallback((rows, index) => {
    const current = rows[index]?.time ? new Date(rows[index].time) : null;
    if (!current || Number.isNaN(current.getTime())) return null;
    const next = rows[index + 1]?.time ? new Date(rows[index + 1].time) : null;
    if (next && !Number.isNaN(next.getTime())) return next;
    const previous = rows[index - 1]?.time ? new Date(rows[index - 1].time) : null;
    if (previous && !Number.isNaN(previous.getTime())) {
      return new Date(current.getTime() + (current.getTime() - previous.getTime()));
    }
    return new Date(current.getTime() + 60 * 60 * 1000);
  }, []);
  const formatMarketTableTimeRange = useCallback((rows, index) => {
    const start = rows[index]?.time ? new Date(rows[index].time) : null;
    if (!start || Number.isNaN(start.getTime())) return "-";
    const end = getMarketTableEndTime(rows, index);
    if (!end || Number.isNaN(end.getTime())) return formatMarketTimeLabelLong(start);
    return `${formatMarketTimeLabelLong(start)} - ${formatMarketTimeLabelLong(end)}`;
  }, [formatMarketTimeLabelLong, getMarketTableEndTime]);
  const renderMarketDisplayToggle = useCallback(
    (section) => (
      <ToggleButtonGroup
        size="small"
        exclusive
        value={marketDisplayMode[section]}
        onChange={(_event, value) => {
          if (value) {
            setMarketDisplayMode((prev) => ({ ...prev, [section]: value }));
          }
        }}
        sx={MARKET_VIEW_TOGGLE_SX}
      >
        <Tooltip title="Graph view" arrow>
          <ToggleButton value="chart" aria-label="Graph view">
            <MultilineChartRoundedIcon fontSize="inherit" />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Table view" arrow>
          <ToggleButton value="table" aria-label="Table view">
            <TableChartOutlinedIcon fontSize="inherit" />
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>
    ),
    [marketDisplayMode]
  );
  const renderMarketDataTable = useCallback(
    ({ rows, columns, valueFormatter, timeHeader = "MTU" }) => {
      if (!columns.length) {
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No datasets selected. Use the toggles above to show columns.
            </Typography>
          </Box>
        );
      }
      return (
        <TableContainer sx={MARKET_TABLE_CONTAINER_SX}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ minWidth: 280 }}>
                  {timeHeader}
                </TableCell>
                {columns.map((column) => (
                  <TableCell key={column.key} align="center" sx={{ minWidth: 170 }}>
                    <Stack spacing={0.25} alignItems="center">
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {column.label}
                      </Typography>
                      {column.unit ? (
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                          ({column.unit})
                        </Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={`${row?.time ?? "row"}-${index}`} hover>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    {formatMarketTableTimeRange(rows, index)}
                  </TableCell>
                  {columns.map((column) => (
                    <TableCell key={column.key} align="center">
                      {valueFormatter(row?.[column.key], column, row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    },
    [formatMarketTableTimeRange]
  );
  const renderOptimizationEnergyTable = useCallback(() => {
    const columns = visibleEnergySeriesConfig
      .filter((series) => seriesVisible[series.key])
      .map((series) => ({
        key: series.key === "deg_efc" ? "deg_efc" : `${series.key}_mw`,
        label: series.filterLabel,
        unit: series.key === "deg_efc" ? "EFC" : "MW",
      }));
    return renderMarketDataTable({
      rows: optimizationDisplayRows,
      columns,
      timeHeader: "MTU",
      valueFormatter: (value, column) =>
        column.key === "deg_efc"
          ? formatOptimizationEnergyValue(value, "EFC")
          : formatOptimizationEnergyValue(value, "MW"),
    });
  }, [
    formatOptimizationEnergyValue,
    optimizationDisplayRows,
    renderMarketDataTable,
    seriesVisible,
    visibleEnergySeriesConfig,
  ]);
  const formatWeatherTooltipValue = (value) => {
    if (value == null || value === "") return "N/A";
    if (typeof value === "number" && Number.isFinite(value)) return value.toFixed(2);
    return value;
  };
  const deriveTooltipValue = (entry) => {
    if (!entry || !entry.payload) return null;
    let targetKey = entry.payloadKey ?? entry.dataKey;
    if (Array.isArray(targetKey)) {
      targetKey = targetKey[0];
    }
    if (typeof targetKey === "string") {
      if (targetKey.endsWith("Start") || targetKey.endsWith("End")) {
        targetKey = targetKey.replace(/Start$|End$/, "");
      }
      if (entry.payload && entry.payload[targetKey] != null) {
        return entry.payload[targetKey];
      }
    }
    return entry.value;
  };

  const DashboardTooltip = ({
    active,
    payload,
    label,
    labelFormatter = formatTimeLabel,
    valueFormatter = formatTooltipValue,
    note,
  }) => {
    if (!active || !payload?.length) return null;
    return (
      <Box
        sx={{
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          boxShadow: 2,
          p: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
          {labelFormatter(label, payload)}
        </Typography>
        {payload.map((entry, idx) => (
          <Box
            key={`${entry?.dataKey || entry?.name || "value"}-${idx}`}
            sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
          >
            <Typography variant="body2" sx={{ color: entry.color, fontWeight: 600 }}>
              {entry.name || entry.dataKey}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {valueFormatter(deriveTooltipValue(entry), entry.name || entry.dataKey, entry)}
            </Typography>
          </Box>
        ))}
        {note && (
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, display: "block", fontWeight: 600 }}>
            {note}
          </Typography>
        )}
      </Box>
    );
  };
  const weatherTooltipNote = weatherMode === "both" ? "Dashed lines = forecast." : null;
  const mergeForecastActual = (forecastRows, actualRows) => {
    const map = new Map();
    const addRows = (rows, suffix) => {
      rows.forEach((row) => {
        const t = row?.time instanceof Date ? row.time.getTime() : new Date(row?.time || "").getTime();
        if (!Number.isFinite(t)) return;
        const entry = map.get(t) || { time: new Date(t) };
        entry[`ghi_${suffix}`] = row?.ghi ?? null;
        entry[`dni_${suffix}`] = row?.dni ?? null;
        entry[`dhi_${suffix}`] = row?.dhi ?? null;
        entry[`temp_${suffix}`] = row?.temp ?? null;
        entry[`humidity_${suffix}`] = row?.humidity ?? null;
        entry[`wind_speed_${suffix}`] = row?.wind_speed ?? null;
        map.set(t, entry);
      });
    };
    addRows(forecastRows || [], "forecast");
    addRows(actualRows || [], "actual");
    return Array.from(map.values()).sort((a, b) => a.time - b.time);
  };
  const colorForIrradiance = (k) => (k === "ghi" ? "#1565c0" : k === "dni" ? "#f57f17" : "#2e7d32");
  const renderIrradianceLines = () => {
    const lines = [];
    IRRADIANCE_KEYS.forEach((k) => {
      if (!weatherVisible.irradiance[k]) return;
      const stroke = colorForIrradiance(k);
      if (weatherMode === "both") {
        lines.push(
          <Line
            key={`${k}-forecast`}
            dataKey={`${k}_forecast`}
            name={`${k.toUpperCase()} (forecast)`}
            stroke={stroke}
            strokeDasharray="6 4"
            dot={false}
            strokeWidth={2}
            connectNulls={false}
          />,
          <Line
            key={`${k}-actual`}
            dataKey={`${k}_actual`}
            name={`${k.toUpperCase()} (actual)`}
            stroke={stroke}
            dot={false}
            strokeWidth={2.6}
            connectNulls={false}
          />
        );
      } else {
        lines.push(
          <Line
            key={k}
            dataKey={k}
            name={k.toUpperCase()}
            stroke={stroke}
            dot={false}
            strokeWidth={2.2}
            connectNulls={false}
          />
        );
      }
    });
    return lines;
  };
  const renderTemperatureLines = () => {
    if (!weatherVisible.temperature.temp) return null;
    const stroke = "#ef6c00";
    if (weatherMode === "both") {
      return [
        <Line
          key="temp-forecast"
          dataKey="temp_forecast"
          name="Temp (forecast)"
          stroke={stroke}
          strokeDasharray="6 4"
          dot={false}
          strokeWidth={2}
          connectNulls={false}
        />,
        <Line
          key="temp-actual"
          dataKey="temp_actual"
          name="Temp (actual)"
          stroke={stroke}
          dot={false}
          strokeWidth={2.4}
          connectNulls={false}
        />,
      ];
    }
    return (
      <Line dataKey="temp" name="Temp" stroke={stroke} dot={false} strokeWidth={2.2} connectNulls={false} />
    );
  };
  const renderHumidityLines = () => {
    if (!weatherVisible.humidity.humidity) return null;
    const stroke = "#1565c0";
    if (weatherMode === "both") {
      return [
        <Line
          key="humidity-forecast"
          dataKey="humidity_forecast"
          name="Humidity (forecast)"
          stroke={stroke}
          strokeDasharray="6 4"
          dot={false}
          strokeWidth={2}
          connectNulls={false}
        />,
        <Line
          key="humidity-actual"
          dataKey="humidity_actual"
          name="Humidity (actual)"
          stroke={stroke}
          dot={false}
          strokeWidth={2.4}
          connectNulls={false}
        />,
      ];
    }
    return (
      <Line dataKey="humidity" name="Humidity" stroke={stroke} dot={false} strokeWidth={2.2} connectNulls={false} />
    );
  };
  const renderWindLines = () => {
    if (!weatherVisible.wind.wind_speed) return null;
    const stroke = "#2e7d32";
    if (weatherMode === "both") {
      return [
        <Line
          key="wind-forecast"
          dataKey="wind_speed_forecast"
          name="Wind speed (forecast)"
          stroke={stroke}
          strokeDasharray="6 4"
          dot={false}
          strokeWidth={2}
          connectNulls={false}
        />,
        <Line
          key="wind-actual"
          dataKey="wind_speed_actual"
          name="Wind speed (actual)"
          stroke={stroke}
          dot={false}
          strokeWidth={2.4}
          connectNulls={false}
        />,
      ];
    }
    return (
      <Line dataKey="wind_speed" name="Wind speed" stroke={stroke} dot={false} strokeWidth={2.2} connectNulls={false} />
    );
  };
  // Wind-asset weather charts (inputs of the wind generation model).
  const renderWindAssetSpeedLines = () => {
    const upperName = `Wind ${windWeatherUpperLabel || "upper level"}`;
    const lines = [];
    const pushPair = (key, name, stroke, visible) => {
      if (!visible) return;
      if (weatherMode === "both") {
        lines.push(
          <Line
            key={`${key}-forecast`}
            dataKey={`${key}_forecast`}
            name={`${name} (forecast)`}
            stroke={stroke}
            strokeDasharray="6 4"
            dot={false}
            strokeWidth={2}
            connectNulls={false}
          />,
          <Line
            key={`${key}-actual`}
            dataKey={`${key}_actual`}
            name={`${name} (actual)`}
            stroke={stroke}
            dot={false}
            strokeWidth={2.4}
            connectNulls={false}
          />
        );
      } else {
        lines.push(
          <Line key={key} dataKey={key} name={name} stroke={stroke} dot={false} strokeWidth={2.2} connectNulls={false} />
        );
      }
    };
    pushPair("wind_speed", "Wind 10 m", "#2e7d32", windWeatherVisible.ten);
    pushPair("wind_speed_upper", upperName, "#1565c0", windWeatherVisible.upper);
    return lines;
  };
  const renderWindAssetTempLines = () => {
    const stroke = "#ef6c00";
    if (weatherMode === "both") {
      return [
        <Line
          key="temp-forecast"
          dataKey="temp_forecast"
          name="Temp 2 m (forecast)"
          stroke={stroke}
          strokeDasharray="6 4"
          dot={false}
          strokeWidth={2}
          connectNulls={false}
        />,
        <Line
          key="temp-actual"
          dataKey="temp_actual"
          name="Temp 2 m (actual)"
          stroke={stroke}
          dot={false}
          strokeWidth={2.4}
          connectNulls={false}
        />,
      ];
    }
    return (
      <Line dataKey="temp" name="Temp 2 m" stroke={stroke} dot={false} strokeWidth={2.2} connectNulls={false} />
    );
  };
  const renderWindAssetPressureLines = () => {
    const stroke = "#5e35b1";
    if (weatherMode === "both") {
      return [
        <Line
          key="pressure-forecast"
          dataKey="pressure_forecast"
          name="Surface pressure (forecast)"
          stroke={stroke}
          strokeDasharray="6 4"
          dot={false}
          strokeWidth={2}
          connectNulls={false}
        />,
        <Line
          key="pressure-actual"
          dataKey="pressure_actual"
          name="Surface pressure (actual)"
          stroke={stroke}
          dot={false}
          strokeWidth={2.4}
          connectNulls={false}
        />,
      ];
    }
    return (
      <Line
        dataKey="pressure"
        name="Surface pressure"
        stroke={stroke}
        dot={false}
        strokeWidth={2.2}
        connectNulls={false}
      />
    );
  };
  const formatDamMetricValue = (key, value) => {
  if (value == null || Number.isNaN(Number(value))) return "--";
    const decimals = DAM_METRIC_DECIMALS[key] ?? 2;
    return Number(value).toLocaleString("en-GB", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };
  const formatDamActualPriceNumber = (value, decimals = 2) => {
    if (value == null || Number.isNaN(Number(value))) return "--";
    return Number(value).toLocaleString("en-GB", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };
  const formatDamActualPriceValue = (value, decimals = 2) => {
    const formatted = formatDamActualPriceNumber(value, decimals);
    return formatted === "--" ? formatted : `${formatted} EUR/MWh`;
  };
  const formatDamActualBandValue = (low, high) => {
    if (low == null || high == null) return "--";
    return `${formatDamActualPriceNumber(low)} - ${formatDamActualPriceNumber(high)}`;
  };
  const formatDamActualStatsTime = (value) =>
    value ? `at ${formatMarketTimeLabelLong(value)}` : "";
  const renderDamActualStats = () => {
    if (!damActualStats) return null;

    const cards = [
      {
        key: "count",
        label: "Actual Points",
        value: formatDamMetricValue("count", damActualStats.count),
        detail: "priced intervals",
        help: "Intervals with a non-empty actual price.",
      },
      {
        key: "min",
        label: "Minimum",
        value: formatDamActualPriceValue(damActualStats.min),
        detail: formatDamActualStatsTime(damActualStats.minTime),
        help: "Lowest actual price in the selected window.",
      },
      {
        key: "peak",
        label: "Peak",
        value: formatDamActualPriceValue(damActualStats.peak),
        detail: formatDamActualStatsTime(damActualStats.peakTime),
        help: "Highest actual price in the selected window.",
      },
      {
        key: "mean",
        label: "Mean",
        value: formatDamActualPriceValue(damActualStats.mean),
        detail: "average actual price",
        help: "Arithmetic mean of available actual prices.",
      },
      {
        key: "stdDev",
        label: "Std Dev",
        value: formatDamActualPriceValue(damActualStats.stdDev),
        detail: "population standard deviation",
        help: "Dispersion of actual prices around the mean.",
      },
      {
        key: "median",
        label: "Median",
        value: formatDamActualPriceValue(damActualStats.median),
        detail: "50th percentile",
        help: "Middle actual price after sorting the selected window.",
      },
      {
        key: "p10p90",
        label: "P10-P90",
        value: formatDamActualBandValue(damActualStats.p10, damActualStats.p90),
        detail: "EUR/MWh, middle 80% band",
        help: "Tenth and ninetieth percentiles of actual prices.",
      },
      {
        key: "range",
        label: "Range",
        value: formatDamActualPriceValue(damActualStats.range),
        detail: `IQR ${formatDamActualPriceValue(damActualStats.iqr)}`,
        help: "Peak minus minimum; IQR is the 75th minus 25th percentile.",
      },
    ];

    return (
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Actual price stats
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
              Computed from available actual price intervals only.
            </Typography>
          </Box>
        </Stack>

        <Grid container spacing={1.25}>
          {cards.map((card) => (
            <Grid item xs={12} sm={6} md={3} lg={2} key={card.key}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  height: "100%",
                  minHeight: 88,
                  borderColor: "divider",
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={card.label}
                  >
                    {card.label}
                  </Typography>
                  <Tooltip title={card.help} placement="top" arrow>
                    <IconButton size="small" sx={{ p: 0.25 }}>
                      <InfoOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.25,
                    mt: 0.25,
                    overflowWrap: "anywhere",
                    fontSize: card.key === "p10p90" ? { xs: "0.95rem", md: "0.9rem" } : undefined,
                  }}
                >
                  {card.value}
                </Typography>
                {card.detail && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "text.secondary",
                      fontWeight: 600,
                      lineHeight: 1.25,
                      mt: 0.35,
                    }}
                  >
                    {card.detail}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };
  const renderDamMetrics = () => {
    if (!hasDamActualPriceData || !damMetrics) return null;

    // COVERAGE GUARD, kept from the Forecast-vs-Actual view when the two were
    // merged. Without it a score computed from a handful of early-published
    // actuals - 4 of 96 at breakfast - renders as a confident final grade for
    // the day, and there is nothing on the panel to say it is not one. A
    // partial day gets the count instead of a number.
    const forecastRowCount = damSeries.filter((row) => row.p50 != null).length;
    const actualCoverage = damSeries.filter((row) => row.actual != null).length;
    if (forecastRowCount > 0 && actualCoverage < forecastRowCount) {
      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Forecast metrics
          </Typography>
          <Alert severity="info" variant="outlined" sx={{ borderRadius: 2, mt: 0.75 }}>
            {`Partial actuals available: ${actualCoverage} / ${forecastRowCount}. Final metrics pending.`}
          </Alert>
        </Box>
      );
    }

    const keys = DAM_VISIBLE_METRIC_KEYS.filter((k) => damMetrics[k] != null);
    if (!keys.length) return null;

    const overall = computeOverallForecastScore(damMetrics);

    return (
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Forecast metrics
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
              Measured against the P50 forecast for this delivery day only - a single day, not a
              track record.
            </Typography>
          </Box>

          {SHOW_COMPOSITE_FORECAST_SCORE && overall && (
            <Chip
              size="small"
              label={`Overall Score: ${Math.round(overall.score)}/100 (${metricChipLabel(overall.status)})`}
              variant="outlined"
              sx={{
                fontWeight: 700,
                borderRadius: 2,
              }}
              color={overall.status === "good" ? "success" : overall.status === "ok" ? "warning" : "error"}
            />
          )}
          {!SHOW_COMPOSITE_FORECAST_SCORE && forecastHeadlineLabel(damMetrics) && (
            <Chip
              size="small"
              label={forecastHeadlineLabel(damMetrics)}
              variant="outlined"
              sx={{ fontWeight: 700, borderRadius: 2 }}
            />
          )}
        </Stack>

        <Grid container spacing={1.25}>
          {keys.map((key) => {
            const meta = DAM_METRICS_META[key];
            const cls = classifyMetric(key, damMetrics[key]);

            return (
              <Grid item xs={12} sm={6} md={3} lg={2} key={key}>
                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, height: "100%" }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        title={meta.label}
                      >
                        {meta.label}
                      </Typography>

                      <Tooltip title={meta.help} placement="top" arrow>
                        <IconButton size="small" sx={{ p: 0.25 }}>
                          <InfoOutlinedIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    {cls && (
                      <Chip
                        size="small"
                        label={metricChipLabel(cls)}
                        variant="outlined"
                        color={cls === "good" ? "success" : cls === "ok" ? "warning" : "error"}
                        sx={{ height: 20, fontWeight: 700 }}
                      />
                    )}
                  </Stack>

                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, mt: 0.25 }}>
                    {formatDamMetricValue(key, damMetrics[key])}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };
  const renderIntradayActualStats = () => {
    if (!intradayActualStats) return null;

    const cards = [
      {
        key: "count",
        label: "Actual Points",
        value: formatDamMetricValue("count", intradayActualStats.count),
        detail: "priced intervals",
        help: "Intervals with a non-empty actual price.",
      },
      {
        key: "min",
        label: "Minimum",
        value: formatDamActualPriceValue(intradayActualStats.min),
        detail: formatDamActualStatsTime(intradayActualStats.minTime),
        help: "Lowest actual price in the selected window.",
      },
      {
        key: "peak",
        label: "Peak",
        value: formatDamActualPriceValue(intradayActualStats.peak),
        detail: formatDamActualStatsTime(intradayActualStats.peakTime),
        help: "Highest actual price in the selected window.",
      },
      {
        key: "mean",
        label: "Mean",
        value: formatDamActualPriceValue(intradayActualStats.mean),
        detail: "average actual price",
        help: "Arithmetic mean of available actual prices.",
      },
      {
        key: "stdDev",
        label: "Std Dev",
        value: formatDamActualPriceValue(intradayActualStats.stdDev),
        detail: "population standard deviation",
        help: "Dispersion of actual prices around the mean.",
      },
      {
        key: "median",
        label: "Median",
        value: formatDamActualPriceValue(intradayActualStats.median),
        detail: "50th percentile",
        help: "Middle actual price after sorting the selected window.",
      },
      {
        key: "p10p90",
        label: "P10-P90",
        value: formatDamActualBandValue(intradayActualStats.p10, intradayActualStats.p90),
        detail: "EUR/MWh, middle 80% band",
        help: "Tenth and ninetieth percentiles of actual prices.",
      },
      {
        key: "range",
        label: "Range",
        value: formatDamActualPriceValue(intradayActualStats.range),
        detail: `IQR ${formatDamActualPriceValue(intradayActualStats.iqr)}`,
        help: "Peak minus minimum; IQR is the 75th minus 25th percentile.",
      },
    ];

    return (
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Actual price stats
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
              Computed from available actual price intervals only.
            </Typography>
          </Box>
        </Stack>

        <Grid container spacing={1.25}>
          {cards.map((card) => (
            <Grid item xs={12} sm={6} md={3} lg={2} key={card.key}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  height: "100%",
                  minHeight: 88,
                  borderColor: "divider",
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={card.label}
                  >
                    {card.label}
                  </Typography>
                  <Tooltip title={card.help} placement="top" arrow>
                    <IconButton size="small" sx={{ p: 0.25 }}>
                      <InfoOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.25,
                    mt: 0.25,
                    overflowWrap: "anywhere",
                    fontSize: card.key === "p10p90" ? { xs: "0.95rem", md: "0.9rem" } : undefined,
                  }}
                >
                  {card.value}
                </Typography>
                {card.detail && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "text.secondary",
                      fontWeight: 600,
                      lineHeight: 1.25,
                      mt: 0.35,
                    }}
                  >
                    {card.detail}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };
  const renderIntradayMetrics = () => {
    if (!intradaySeries.length) return null;

    const scoredCount = intradayMetrics?.count ?? 0;
    const scorableCount = intradayMetrics?.expected ?? 0;
    const enoughIntervals =
      scoredCount >= INTRADAY_FORECAST_METRICS_MIN_COUNT &&
      (!scorableCount || scoredCount >= scorableCount * INTRADAY_FORECAST_METRICS_MIN_COVERAGE);

    if (!intradayMetrics || !enoughIntervals) {
      return (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
            {scoredCount > 0 && scorableCount > 0
              ? `Partial actuals available: ${scoredCount} / ${scorableCount} intervals. ` +
                "No score is shown yet — on this few intervals the ranking metrics saturate " +
                "and the number would read better than it is."
              : "Forecast metrics unavailable — waiting for both actual and forecast values."}
          </Alert>
        </Box>
      );
    }

    const keys = DAM_VISIBLE_METRIC_KEYS.filter((k) => intradayMetrics[k] != null);
    if (!keys.length) return null;

    const overall = computeOverallForecastScore(intradayMetrics);
    // The score is only as trustworthy as its sample: state the sample next to it.
    // Say what the two numbers ARE, because for IDA3 they are permanently
    // unequal and the bare "48/96" reads as a shortfall every single day.
    //
    // MEASURED 2026-08-31 on the live IDA3 panel: expected=96 (the forecast
    // produces a P50 for all 96 MTUs) while count=48 (IDA3 only CLEARS 48 -
    // it covers 13:00-01:00 EET per HEnEx Technical Decision 10). So an IDA3
    // day sits at exactly 48/96 = 50%, which is precisely the coverage
    // threshold below - one missing actual and the whole metrics block
    // disappears behind "Partial actuals available". That knife edge is the
    // real defect and needs the product's true expected count, which lives in
    // the backend (henex_intraday_service._expected_mtu_count) and is NOT
    // re-derived here on purpose. Until it is served, the wording at least
    // stops implying that half the day is missing.
    const coverageLabel = scorableCount
      ? ` · scored on ${scoredCount} of ${scorableCount} forecast intervals`
      : "";

    return (
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Forecast metrics
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
              Measured against the P50 forecast for this delivery day only - a single day, not a
              track record.
            </Typography>
          </Box>

          {SHOW_COMPOSITE_FORECAST_SCORE && overall && (
            <Chip
              size="small"
              label={`Overall Score: ${Math.round(overall.score)}/100 (${metricChipLabel(overall.status)})${coverageLabel}`}
              variant="outlined"
              sx={{
                fontWeight: 700,
                borderRadius: 2,
              }}
              color={overall.status === "good" ? "success" : overall.status === "ok" ? "warning" : "error"}
            />
          )}
          {!SHOW_COMPOSITE_FORECAST_SCORE && forecastHeadlineLabel(intradayMetrics, coverageLabel) && (
            <Chip
              size="small"
              label={forecastHeadlineLabel(intradayMetrics, coverageLabel)}
              variant="outlined"
              sx={{ fontWeight: 700, borderRadius: 2 }}
            />
          )}
        </Stack>

        <Grid container spacing={1.25}>
          {keys.map((key) => {
            const meta = DAM_METRICS_META[key];
            const cls = classifyMetric(key, intradayMetrics[key]);

            return (
              <Grid item xs={12} sm={6} md={3} lg={2} key={key}>
                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, height: "100%" }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        title={meta.label}
                      >
                        {meta.label}
                      </Typography>

                      <Tooltip title={meta.help} placement="top" arrow>
                        <IconButton size="small" sx={{ p: 0.25 }}>
                          <InfoOutlinedIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    {cls && (
                      <Chip
                        size="small"
                        label={metricChipLabel(cls)}
                        variant="outlined"
                        color={cls === "good" ? "success" : cls === "ok" ? "warning" : "error"}
                        sx={{ height: 20, fontWeight: 700 }}
                      />
                    )}
                  </Stack>

                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, mt: 0.25 }}>
                    {formatDamMetricValue(key, intradayMetrics[key])}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };
  const handleWeatherModeChange = (mode) => {
    setWeatherMode(mode);
    setWeatherNotices([]);
    setWeatherError("");
    setWeatherSeries({
      irradiance: [],
      temperature: [],
      humidity: [],
      wind: [],
    });
  };

  const openRangePicker = (section, event) => {
    setRangeSection(section);
    setRangeAnchor(event.currentTarget);
    const fallback = defaultDPlus1();
    const filter =
      section === "dam"
        ? damRange
        : section === "intraday"
        ? intradayRange
        : section === "compare"
        ? compareRange
        : section === "balancing"
        ? balancingRange
        : section === "weather"
        ? weatherRange
        : section === "simulation"
        ? dateFilter
        : marketFilters[section] || { start: "", end: "" };
    const startStr = filter.start || fallback;
    const endStr = filter.end || fallback;
    const startDate = parseLocalDateTime(`${startStr}T00:00:00`);
    const endDate = parseLocalDateTime(`${endStr}T23:59:59`);
    setRangeMonth((prev) => ({
      ...prev,
      [section]: startDate || new Date(),
    }));
    setRangeValue((prev) => ({
      ...prev,
      [section]: [
        startDate,
        endDate,
      ],
    }));
  };
  const closeRangePicker = () => {
    setRangeAnchor(null);
    setRangeSection(null);
  };
  const rangeLabel = (section) => {
    const filter =
      section === "dam"
        ? damRange
        : section === "intraday"
        ? intradayRange
        : section === "compare"
        ? compareRange
        : section === "balancing"
        ? balancingRange
        : section === "weather"
        ? weatherRange
        : section === "simulation"
        ? dateFilter
        : marketFilters[section];
    if (!filter?.start || !filter?.end) return "Select range";
    return (
      <>
        <Box component="span">{formatDisplayDate(filter.start)}</Box>
        <Box component="span" sx={{ mx: 0.5, display: "inline-flex", alignItems: "center" }}>
          <ArrowRightAltIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box component="span">{formatDisplayDate(filter.end)}</Box>
      </>
    );
  };

  const handleRangeChange = (section, newValue) => {
    setRangeValue((prev) => ({ ...prev, [section]: newValue }));
    const [start, end] = newValue;
    if (start && end) {
      const startStr = formatDateLocal(start);
      const endStr = formatDateLocal(end);
      const s = parseLocalDateTime(`${startStr}T00:00:00`);
      const e = parseLocalDateTime(`${endStr}T23:59:59`);
      const [normStart, normEnd] = s > e ? [endStr, startStr] : [startStr, endStr];
      if (section === "dam") {
        setDamRange({ start: normStart, end: normEnd });
      } else if (section === "intraday") {
        setIntradayRange({ start: normStart, end: normEnd });
      } else if (section === "compare") {
        setCompareRange({ start: normStart, end: normEnd });
      } else if (section === "balancing") {
        setBalancingRange({ start: normStart, end: normEnd });
      } else if (section === "weather") {
        setWeatherRange({ start: normStart, end: normEnd });
      } else if (section === "simulation") {
        setDateFilter({ start: normStart, end: normEnd });
      } else {
        setMarketFilters((prev) => ({
          ...prev,
          [section]: { start: normStart, end: normEnd },
        }));
      }
    }
  };
  const balancingUnitLabel = BALANCING_DATASET_UNITS[balancingDataset] || "";
  const balancingMetricSelectorLabel = getBalancingMetricSelectorLabel(
    balancingDataset,
    balancingNumericFields
  );
  const balancingAxisLabel =
    balancingDataset === "imbalance_prices"
      ? balancingUnitLabel
        ? `Imbalance Price [${balancingUnitLabel}]`
        : "Imbalance Price"
      : balancingUnitLabel
        ? `${humanizeBalancingKey(balancingMetric)} [${balancingUnitLabel}]`
        : humanizeBalancingKey(balancingMetric);
  const renderBalancingChart = () => (
    <Stack sx={{ height: "100%" }}>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={balancingChart.rows} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="time"
              tick={{ fontSize: 12, fontWeight: 700 }}
              tickFormatter={formatMarketTimeLabel}
              axisLine={MARKET_AXIS_LINE_PROPS}
              tickLine={MARKET_TICK_LINE_PROPS}
            />
            <YAxis
              width={90}
              tick={{ fontSize: 12, fontWeight: 700 }}
              axisLine={MARKET_AXIS_LINE_PROPS}
              tickLine={MARKET_TICK_LINE_PROPS}
              label={{
                value: balancingAxisLabel,
                angle: -90,
                position: "insideLeft",
                offset: 24,
                style: AXIS_TITLE_STYLE,
              }}
            />
            <RechartsTooltip content={<DashboardTooltip labelFormatter={formatMarketTimeLabel} />} />
            {balancingChart.series.map((seriesItem) => {
              const isHidden = hiddenBalancingSeries.includes(seriesItem.id);
              return (
                <Line
                  key={seriesItem.id}
                  dataKey={seriesItem.id}
                  name={seriesItem.label}
                  stroke={seriesItem.color}
                  dot={false}
                  strokeWidth={2.2}
                  connectNulls={false}
                  hide={isHidden}
                />
              );
            })}
            <Brush
              dataKey="time"
              height={24}
              travellerWidth={10}
              tickFormatter={formatMarketTimeLabelShort}
              stroke="#90a4ae"
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
      <Stack
        direction="row"
        spacing={1.75}
        justifyContent="center"
        alignItems="center"
        sx={{ flexWrap: "wrap", pt: 1.25 }}
      >
        {balancingChart.series.map((seriesItem) => {
          const isHidden = hiddenBalancingSeries.includes(seriesItem.id);
          return (
            <Box
              key={seriesItem.id}
              component="button"
              type="button"
              onClick={() => toggleBalancingSeriesVisibility(seriesItem.id)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                border: 0,
                bgcolor: "transparent",
                p: "4px 8px",
                m: 0,
                cursor: "pointer",
                borderRadius: 999,
                transition: "background-color 120ms ease, transform 120ms ease",
                "&:hover": {
                  bgcolor: "rgba(25, 118, 210, 0.08)",
                },
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 2,
                },
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  border: `2px solid ${seriesItem.color}`,
                  bgcolor: isHidden ? "#fff" : seriesItem.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: isHidden ? "text.secondary" : seriesItem.color,
                  opacity: isHidden ? 0.65 : 1,
                }}
              >
                {seriesItem.label}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
  const renderBalancingContent = () => {
    if (balancingLoading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={20} /> Loading balancing market data...
        </Box>
      );
    }
    if (!balancingChart.rows.length || !balancingChart.series.length) {
      return (
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          No balancing market data loaded yet for the selected dataset and interval.
        </Typography>
      );
    }
    if (marketDisplayMode.balancing === "table") {
      return renderMarketDataTable({
        rows: balancingChart.rows,
        columns: balancingChart.series.map((seriesItem) => ({
          key: seriesItem.id,
          label: seriesItem.label,
          unit: balancingUnitLabel,
        })),
        valueFormatter: (value) => formatMarketTableValue(value, 2),
        timeHeader: "Delivery Interval",
      });
    }
    return renderBalancingChart();
  };
  const fullscreenTitleMap = {
    energy: "Energy Series",
    price: "Used DAM Price (Forecast)",
    dam: "DAM Price",
    intraday: "Intraday Price",
    compare: "Auctions Actual Price Comparison",
    balancing: "Balancing Market",
    mix: "Energy Mix",
    generation: "System Generation Forecast",
    load: "System Load",
    weather: "Weather Data",
    soc: "State of Charge",
    profit: "Profit",
    "weather-irradiance": "Irradiance Components",
    "weather-temperature": "Temperature",
    "weather-humidity": "Humidity",
    "weather-wind": "Wind Speed",
    "pv-generation": "PV Generation Overview",
  };
  const renderFullscreenChart = () => {
    switch (fullscreen) {
      case "energy":
        if (!energySeriesWithDegradation.length) {
          return (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No data available for the selected run.
            </Typography>
          );
        }
        return (
          <ResponsiveContainer width="100%" height="90%">
            {chartDisplayMode === "line" ? (
              <LineChart data={energySeriesWithDegradation} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis {...getOptimizationXAxisProps(energySeriesWithDegradation, 600)} />
                <YAxis
                  yAxisId="left"
                  tick={AXIS_TICK_SX}
                  width={104}
                  tickMargin={8}
                  tickFormatter={formatOptimizationEnergyAxisValue}
                  label={{
                    value: "Power [kW]",
                    angle: -90,
                    position: "insideLeft",
                    offset: 20,
                  }}
                  axisLine={{ strokeWidth: 1.5, stroke: "#90a4ae" }}
                  tickLine={{ stroke: "#90a4ae" }}
                />
                {showEfcAxis && (
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    width={90}
                    tick={{ fontSize: 12, fontWeight: 700 }}
                    label={{
                      value: "Cycles [EFC]",
                      angle: 90,
                      position: "insideRight",
                      offset: 20,
                    }}
                    axisLine={{ strokeWidth: 1.5, stroke: "#90a4ae" }}
                    tickLine={{ stroke: "#90a4ae" }}
                  />
                )}
                <RechartsTooltip
                  content={
                    <DashboardTooltip
                      labelFormatter={(label, payload) => formatOptimizationLabelFromPayload(label, payload, true)}
                      valueFormatter={(value, name, entry) => {
                        const key = entry?.dataKey;
                        if (key === "deg_efc") return formatOptimizationEnergyValue(value, "EFC");
                        if (typeof key === "string") return formatOptimizationEnergyValue(value, "kW");
                        return formatTwoDecimalTooltipValue(value, name, entry);
                      }}
                    />
                  }
                />
                  {renderEnergySeriesElements("line")}
                  <Brush
                    dataKey="time"
                    height={24}
                    travellerWidth={10}
                    tickFormatter={formatOptimizationTickFromRows(energySeriesWithDegradation)}
                    stroke="#90a4ae"
                  />
                </LineChart>
            ) : chartDisplayMode === "bar" ? (
              <BarChart data={energySeriesWithDegradation} margin={CHART_MARGIN} {...BAR_CHART_PROPS}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis {...getOptimizationXAxisProps(energySeriesWithDegradation, 600)} />
                <YAxis
                  yAxisId="left"
                  tick={AXIS_TICK_SX}
                  width={104}
                  tickMargin={8}
                  tickFormatter={formatOptimizationEnergyAxisValue}
                  label={{
                    value: "Power [kW]",
                    angle: -90,
                    position: "insideLeft",
                    offset: 20,
                  }}
                  axisLine={{ strokeWidth: 1.5, stroke: "#90a4ae" }}
                  tickLine={{ stroke: "#90a4ae" }}
                />
                {showEfcAxis && (
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    width={90}
                    tick={{ fontSize: 12, fontWeight: 700 }}
                    label={{
                      value: "Cycles [EFC]",
                      angle: 90,
                      position: "insideRight",
                      offset: 20,
                    }}
                    axisLine={{ strokeWidth: 1.5, stroke: "#90a4ae" }}
                    tickLine={{ stroke: "#90a4ae" }}
                  />
                )}
                <RechartsTooltip
                  content={
                    <DashboardTooltip
                      labelFormatter={(label, payload) => formatOptimizationLabelFromPayload(label, payload, true)}
                      valueFormatter={(value, name, entry) => {
                        const key = entry?.dataKey;
                        if (key === "deg_efc") return formatOptimizationEnergyValue(value, "EFC");
                        if (typeof key === "string") return formatOptimizationEnergyValue(value, "kW");
                        return formatTwoDecimalTooltipValue(value, name, entry);
                      }}
                    />
                  }
                />
                {renderEnergySeriesElements("bar")}
                <Brush
                  dataKey="time"
                  height={24}
                  travellerWidth={10}
                  tickFormatter={formatOptimizationTickFromRows(energySeriesWithDegradation)}
                  stroke="#90a4ae"
                />
              </BarChart>
            ) : (
              <AreaChart data={energySeriesWithDegradation} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis {...getOptimizationXAxisProps(energySeriesWithDegradation, 600)} />
                <YAxis
                  yAxisId="left"
                  tick={AXIS_TICK_SX}
                  width={104}
                  tickMargin={8}
                  tickFormatter={formatOptimizationEnergyAxisValue}
                  label={{
                    value: "Power [kW]",
                    angle: -90,
                    position: "insideLeft",
                    offset: 20,
                  }}
                  axisLine={{ strokeWidth: 1.5, stroke: "#90a4ae" }}
                  tickLine={{ stroke: "#90a4ae" }}
                />
                {showEfcAxis && (
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    width={90}
                    tick={{ fontSize: 12, fontWeight: 700 }}
                    label={{
                      value: "Cycles [EFC]",
                      angle: 90,
                      position: "insideRight",
                      offset: 20,
                    }}
                    axisLine={{ strokeWidth: 1.5, stroke: "#90a4ae" }}
                    tickLine={{ stroke: "#90a4ae" }}
                  />
                )}
                <RechartsTooltip
                  content={
                    <DashboardTooltip
                      labelFormatter={(label, payload) => formatOptimizationLabelFromPayload(label, payload, true)}
                      valueFormatter={(value, name, entry) => {
                        const key = entry?.dataKey;
                        if (key === "deg_efc") return formatOptimizationEnergyValue(value, "EFC");
                        if (typeof key === "string") return formatOptimizationEnergyValue(value, "kW");
                        return formatTwoDecimalTooltipValue(value, name, entry);
                      }}
                    />
                  }
                />
                {renderEnergySeriesElements("area")}
                <Brush
                  dataKey="time"
                  height={24}
                  travellerWidth={10}
                  tickFormatter={formatOptimizationTickFromRows(energySeriesWithDegradation)}
                  stroke="#90a4ae"
                />
              </AreaChart>
            )}
          </ResponsiveContainer>
        );
      case "price":
        if (!energySeriesWithDegradation.length) {
          return (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No data available for the selected run.
            </Typography>
          );
        }
        return (
          <ResponsiveContainer width="100%" height="90%">
            {priceDisplayMode === "line" ? (
              <LineChart data={energySeriesWithDegradation} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis {...getOptimizationXAxisProps(energySeriesWithDegradation, 600)} />
                <YAxis
                  tick={AXIS_TICK_SX}
                  width={104}
                  tickMargin={8}
                  label={{
                    value: "Price [€/kWh]",
                    angle: -90,
                    position: "insideLeft",
                    offset: 28,
                    dy: 32,
                  }}
                  axisLine={{ strokeWidth: 1.5, stroke: "#90a4ae" }}
                  tickLine={{ stroke: "#90a4ae" }}
                />
                <RechartsTooltip
                  content={
                    <DashboardTooltip
                      labelFormatter={(label, payload) => formatOptimizationLabelFromPayload(label, payload, true)}
                      valueFormatter={formatTwoDecimalTooltipValue}
                    />
                  }
                />
                <Line dataKey="price" name="Price" stroke="#6a1b9a" dot={false} strokeWidth={2} />
                <Brush
                  dataKey="time"
                  height={24}
                  travellerWidth={10}
                  tickFormatter={formatOptimizationTickFromRows(energySeriesWithDegradation)}
                  stroke="#90a4ae"
                />
              </LineChart>
            ) : (
                <BarChart
                  data={energySeriesWithDegradation}
                  margin={CHART_MARGIN}
                barCategoryGap="0%"
                barGap="-100%"
                >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis {...getOptimizationXAxisProps(energySeriesWithDegradation, 600)} />
                <YAxis
                  tick={AXIS_TICK_SX}
                  width={104}
                  tickMargin={8}
                  label={{
                    value: "Price [€/kWh]",
                    angle: -90,
                    position: "insideLeft",
                    offset: 28,
                    dy: 32,
                  }}
                  axisLine={{ strokeWidth: 1.5, stroke: "#90a4ae" }}
                  tickLine={{ stroke: "#90a4ae" }}
                />
                <RechartsTooltip
                  content={
                    <DashboardTooltip
                      labelFormatter={(label, payload) => formatOptimizationLabelFromPayload(label, payload, true)}
                      valueFormatter={formatTwoDecimalTooltipValue}
                    />
                  }
                />
                <Bar dataKey="price" name="Price" fill="#6a1b9a" barSize={14} />
                <Brush
                  dataKey="time"
                  height={24}
                  travellerWidth={10}
                  tickFormatter={formatOptimizationTickFromRows(energySeriesWithDegradation)}
                  stroke="#90a4ae"
                />
              </BarChart>
            )}
          </ResponsiveContainer>
        );
      case "dam":
        if (!damSeries.length) {
          return (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No data loaded yet. Select a date interval to load DAM prices.
            </Typography>
          );
        }
        return (
          <Stack spacing={2} sx={{ height: "90%" }}>
            {showDamActualStats && renderDamActualStats()}
            {showDamForecastMetrics && renderDamMetrics()}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={damSeries} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 12, fontWeight: 700 }}
                    tickFormatter={formatMarketTimeLabel}
                    axisLine={MARKET_AXIS_LINE_PROPS}
                    tickLine={MARKET_TICK_LINE_PROPS}
                  />
                  <YAxis
                    tick={AXIS_TICK_SX}
                    width={104}
                    tickMargin={8}
                    axisLine={MARKET_AXIS_LINE_PROPS}
                    tickLine={MARKET_TICK_LINE_PROPS}
                    label={{
                      value: "Price [€/MWh]",
                      angle: -90,
                      position: "insideLeft",
                      offset: 24,
                      style: AXIS_TITLE_STYLE,
                    }}
                  />
                  <RechartsTooltip content={<DashboardTooltip labelFormatter={formatMarketTimeLabel} />} />
                  {showDamActualSeries && (
                    <Line dataKey="actual" name="Actual" stroke={MARKET_PRICE_COLORS.actual} dot={false} strokeWidth={2.4} />
                  )}
                  {damVisible.p10 && (
                    <Line
                      dataKey="p10"
                      name="Forecast P10"
                      stroke={MARKET_PRICE_COLORS.p10}
                      dot={false}
                      strokeWidth={2}
                      strokeDasharray={MARKET_FORECAST_DASH}
                      strokeOpacity={0.85}
                      connectNulls={false}
                    />
                  )}
                  {damVisible.p50 && (
                    <Line
                      dataKey="p50"
                      name="Forecast P50"
                      stroke={MARKET_PRICE_COLORS.p50}
                      dot={false}
                      strokeWidth={2.2}
                      strokeDasharray={MARKET_FORECAST_DASH}
                      strokeOpacity={0.95}
                      connectNulls={false}
                    />
                  )}
                  {damVisible.p90 && (
                    <Line
                      dataKey="p90"
                      name="Forecast P90"
                      stroke={MARKET_PRICE_COLORS.p90}
                      dot={false}
                      strokeWidth={2}
                      strokeDasharray={MARKET_FORECAST_DASH}
                      strokeOpacity={0.85}
                      connectNulls={false}
                    />
                  )}
                <Brush
                  dataKey="time"
                  height={24}
                  travellerWidth={10}
                  tickFormatter={formatMarketTimeLabelShort}
                  stroke="#90a4ae"
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Stack>
      );
      case "intraday":
        if (!intradaySeries.length) {
          return (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No intraday data loaded yet. Select a product or date interval to load prices.
            </Typography>
          );
        }
        return (
          <Stack spacing={2} sx={{ height: "90%" }}>
            {!isXbidProduct && showIntradayActualStats && renderIntradayActualStats()}
            {!isXbidProduct && showIntradayForecastMetrics && renderIntradayMetrics()}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={intradaySeries} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 12, fontWeight: 700 }}
                    tickFormatter={formatMarketTimeLabel}
                    axisLine={MARKET_AXIS_LINE_PROPS}
                    tickLine={MARKET_TICK_LINE_PROPS}
                  />
                  <YAxis
                    tick={AXIS_TICK_SX}
                    width={104}
                    tickMargin={8}
                    axisLine={MARKET_AXIS_LINE_PROPS}
                    tickLine={MARKET_TICK_LINE_PROPS}
                    label={{
                      value: "Price [€/MWh]",
                      angle: -90,
                      position: "insideLeft",
                      offset: 24,
                      style: AXIS_TITLE_STYLE,
                    }}
                  />
                  <RechartsTooltip content={<DashboardTooltip labelFormatter={formatMarketTimeLabel} />} />
                  {isXbidProduct && xbidShowVolume && (
                    <YAxis
                      yAxisId="vol"
                      orientation="right"
                      width={72}
                      tick={AXIS_TICK_SX}
                      tickMargin={8}
                      axisLine={MARKET_AXIS_LINE_PROPS}
                      tickLine={MARKET_TICK_LINE_PROPS}
                      label={{
                        value: "Volume [MW]",
                        angle: 90,
                        position: "insideRight",
                        offset: 16,
                        style: AXIS_TITLE_STYLE,
                      }}
                    />
                  )}
                  {isXbidProduct &&
                    xbidShowVolume &&
                    xbidVolumeSeries.map((s) => (
                      <Bar
                        key={s.key}
                        yAxisId="vol"
                        dataKey={s.key}
                        name={`${s.label} (MW)`}
                        fill={s.color}
                        fillOpacity={0.28}
                        stackId={undefined}
                        isAnimationActive={false}
                      />
                    ))}
                  {isXbidProduct &&
                    xbidChartSeries.map((s) =>
                      xbidVisible[s.key] !== false ? (
                        <Line
                          key={s.key}
                          dataKey={s.key}
                          name={s.label}
                          stroke={s.color}
                          dot={false}
                          strokeWidth={2.2}
                          strokeDasharray={s.dashed ? MARKET_FORECAST_DASH : undefined}
                          strokeOpacity={s.dashed ? 0.55 : 1}
                          connectNulls={false}
                        />
                      ) : null
                    )}
                  {!isXbidProduct && showIntradayActualSeries && (
                    <Line dataKey="actual" name="Actual" stroke={MARKET_PRICE_COLORS.actual} dot={false} strokeWidth={2.4} />
                  )}
                  {!isXbidProduct && intradaySeriesMode === "both" && intradayVisible.p10 && (
                    <Line dataKey="p10" name="Forecast P10" stroke={MARKET_PRICE_COLORS.p10} dot={false} strokeWidth={2} strokeDasharray={MARKET_FORECAST_DASH} strokeOpacity={0.85} connectNulls={false} />
                  )}
                  {!isXbidProduct && intradaySeriesMode === "both" && intradayVisible.p50 && (
                    <Line dataKey="p50" name="Forecast P50" stroke={MARKET_PRICE_COLORS.p50} dot={false} strokeWidth={2.2} strokeDasharray={MARKET_FORECAST_DASH} strokeOpacity={0.95} connectNulls={false} />
                  )}
                  {!isXbidProduct && intradaySeriesMode === "both" && intradayVisible.p90 && (
                    <Line dataKey="p90" name="Forecast P90" stroke={MARKET_PRICE_COLORS.p90} dot={false} strokeWidth={2} strokeDasharray={MARKET_FORECAST_DASH} strokeOpacity={0.85} connectNulls={false} />
                  )}
                  <Brush
                    dataKey="time"
                    height={24}
                    travellerWidth={10}
                    tickFormatter={formatMarketTimeLabelShort}
                    stroke="#90a4ae"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Stack>
        );
      case "compare": {
        const visibleComparisonOptions = ACTUAL_PRICE_COMPARISON_OPTIONS.filter(
          (seriesItem) => actualComparisonVisible[seriesItem.key]
        );
        if (!actualComparisonSeries.length || !visibleComparisonOptions.length) {
          return (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No actual price comparison data to display for the selected interval.
            </Typography>
          );
        }
        return (
          <ResponsiveContainer width="100%" height="90%">
            <LineChart data={actualComparisonSeries} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 12, fontWeight: 700 }}
                tickFormatter={formatMarketTimeLabel}
                axisLine={MARKET_AXIS_LINE_PROPS}
                tickLine={MARKET_TICK_LINE_PROPS}
              />
              <YAxis
                width={90}
                tick={{ fontSize: 12, fontWeight: 700 }}
                axisLine={MARKET_AXIS_LINE_PROPS}
                tickLine={MARKET_TICK_LINE_PROPS}
                label={{
                  value: "Price [€/MWh]",
                  angle: -90,
                  position: "insideLeft",
                  offset: 24,
                  style: AXIS_TITLE_STYLE,
                }}
              />
              <RechartsTooltip content={<DashboardTooltip labelFormatter={formatMarketTimeLabel} />} />
              {visibleComparisonOptions.map((seriesItem) => (
                <Line
                  key={seriesItem.key}
                  dataKey={seriesItem.key}
                  name={seriesItem.label}
                  stroke={seriesItem.color}
                  dot={false}
                  strokeWidth={2.3}
                />
              ))}
              <Brush
                dataKey="time"
                height={24}
                travellerWidth={10}
                tickFormatter={formatMarketTimeLabelShort}
                stroke="#90a4ae"
              />
            </LineChart>
          </ResponsiveContainer>
        );
      }
      case "balancing":
        return <Box sx={{ height: "90%" }}>{renderBalancingContent()}</Box>;
      case "mix":
        if (!marketSeries.mix.length) {
          return (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No data loaded yet. Select a date interval to load energy mix data.
            </Typography>
          );
        }
        return (
          <ResponsiveContainer width="100%" height="90%">
                  <LineChart data={mixChartData} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 12, fontWeight: 700 }}
                tickFormatter={formatMarketTimeLabel}
                axisLine={MARKET_AXIS_LINE_PROPS}
                tickLine={MARKET_TICK_LINE_PROPS}
              />
              <YAxis
                tick={{ fontSize: 12, fontWeight: 700 }}
                width={90}
                axisLine={MARKET_AXIS_LINE_PROPS}
                tickLine={MARKET_TICK_LINE_PROPS}
                label={{
                  value: "MW",
                  angle: -90,
                  position: "insideLeft",
                  offset: 14,
                }}
              />
              <RechartsTooltip content={<DashboardTooltip labelFormatter={formatMarketTimeLabel} />} />
              {currentMixMetrics
                .filter((m) => marketVisible.mix[m])
                .map((m) => (
                  <Line
                    key={m}
                    dataKey={m}
                    name={m === RT_SYSTEM_GENERATION_METRIC ? "Total Generation" : SERIES_LABELS[m] || m}
                    stroke={SERIES_COLORS[m] || "#1976d2"}
                    dot={false}
                    strokeWidth={2}
                  />
                ))}
              <Brush
                dataKey="time"
                height={24}
                travellerWidth={10}
                tickFormatter={formatMarketTimeLabelShort}
                stroke="#90a4ae"
              />
            </LineChart>
          </ResponsiveContainer>
        );
      case "generation":
        if (!marketSeries.generation.length) {
          return (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No data loaded yet. Select a date interval to load system generation data.
            </Typography>
          );
        }
        return (
          <ResponsiveContainer width="100%" height="90%">
                  <LineChart data={generationChartData} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 12, fontWeight: 700 }}
                tickFormatter={formatMarketTimeLabel}
                axisLine={MARKET_AXIS_LINE_PROPS}
                tickLine={MARKET_TICK_LINE_PROPS}
              />
              <YAxis
                tick={{ fontSize: 12, fontWeight: 700 }}
                width={90}
                axisLine={MARKET_AXIS_LINE_PROPS}
                tickLine={MARKET_TICK_LINE_PROPS}
                label={{
                  value: "MW",
                  angle: -90,
                  position: "insideLeft",
                  offset: 14,
                }}
              />
              <RechartsTooltip content={<DashboardTooltip labelFormatter={formatMarketTimeLabel} />} />
                    {currentGenerationMetrics
                      .filter((m) => marketVisible.generation[m])
                      .map((m) => (
                        <Line
                          key={m}
                          dataKey={m}
                          name={SERIES_LABELS[m] || m}
                          stroke={SERIES_COLORS[m] || "#1976d2"}
                          dot={false}
                          strokeWidth={m.includes("forecast") ? 2 : 2.6}
                          strokeOpacity={m.includes("forecast") ? 0.85 : 1}
                          connectNulls={false}
                        />
                      ))}
              <Brush
                dataKey="time"
                height={24}
                travellerWidth={10}
                tickFormatter={formatMarketTimeLabelShort}
                stroke="#90a4ae"
              />
            </LineChart>
          </ResponsiveContainer>
        );
      case "load":
        if (!marketSeries.load.length) {
          return (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No data loaded yet. Select a date interval to load system load data.
            </Typography>
          );
        }
        return (
          <ResponsiveContainer width="100%" height="90%">
                  <LineChart data={marketSeries.load} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 12, fontWeight: 700 }}
                tickFormatter={formatMarketTimeLabel}
                axisLine={MARKET_AXIS_LINE_PROPS}
                tickLine={MARKET_TICK_LINE_PROPS}
              />
              <YAxis
                tick={{ fontSize: 12, fontWeight: 700 }}
                width={90}
                axisLine={MARKET_AXIS_LINE_PROPS}
                tickLine={MARKET_TICK_LINE_PROPS}
                label={{
                  value: "MW",
                  angle: -90,
                  position: "insideLeft",
                  offset: 14,
                }}
              />
              <RechartsTooltip content={<DashboardTooltip labelFormatter={formatMarketTimeLabel} />} />
              {LOAD_METRICS.filter((m) => marketVisible.load[m]).map((m) => (
                <Line
                  key={m}
                  dataKey={m}
                  name={SERIES_LABELS[m] || m}
                  stroke={SERIES_COLORS[m] || "#1976d2"}
                  dot={false}
                  strokeWidth={m.includes("forecast") ? 2 : 2.6}
                  strokeOpacity={m.includes("forecast") ? 0.85 : 1}
                  connectNulls={false}
                />
              ))}
              <Brush
                dataKey="time"
                height={24}
                travellerWidth={10}
                tickFormatter={formatMarketTimeLabelShort}
                stroke="#90a4ae"
              />
            </LineChart>
          </ResponsiveContainer>
        );
      case "weather":
        if (!weatherSeries.irradiance.length) {
          return (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No data loaded yet. Select a date interval to load weather data.
            </Typography>
          );
        }
        return (
          <Stack spacing={3} sx={{ height: "90%", overflow: "auto" }}>
            <Box sx={{ height: 220 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Irradiance Components
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weatherSeries.irradiance} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tick={{ fontSize: 12, fontWeight: 700 }} tickFormatter={formatTimeLabel} />
                  <YAxis
                    tick={AXIS_TICK_SX}
                    width={104}
                    tickMargin={8}
                    label={{
                      value: "W/mÂ˛",
                      angle: -90,
                      position: "insideLeft",
                      offset: 14,
                    }}
                  />
                  <RechartsTooltip content={<DashboardTooltip labelFormatter={formatTimeLabel} note={weatherTooltipNote} valueFormatter={formatWeatherTooltipValue} />} />
                  {renderIrradianceLines()}
                </LineChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ height: 180 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Temperature
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weatherSeries.temperature} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tick={{ fontSize: 12, fontWeight: 700 }} tickFormatter={formatTimeLabel} />
                  <YAxis
                    tick={{ fontSize: 12, fontWeight: 700 }}
                    width={70}
                    label={{
                      value: "Â°C",
                      angle: -90,
                      position: "insideLeft",
                      offset: 12,
                    }}
                  />
                  <RechartsTooltip content={<DashboardTooltip labelFormatter={formatTimeLabel} note={weatherTooltipNote} valueFormatter={formatWeatherTooltipValue} />} />
                  {renderTemperatureLines()}
                </LineChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ height: 180 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Humidity
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weatherSeries.humidity} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tick={{ fontSize: 12, fontWeight: 700 }} tickFormatter={formatTimeLabel} />
                  <YAxis
                    tick={{ fontSize: 12, fontWeight: 700 }}
                    width={70}
                    label={{
                      value: "%",
                      angle: -90,
                      position: "insideLeft",
                      offset: 12,
                    }}
                  />
                  <RechartsTooltip content={<DashboardTooltip labelFormatter={formatTimeLabel} note={weatherTooltipNote} valueFormatter={formatWeatherTooltipValue} />} />
                  {renderHumidityLines()}
                </LineChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ height: 180 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Wind speed
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weatherSeries.wind} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tick={{ fontSize: 12, fontWeight: 700 }} tickFormatter={formatTimeLabel} />
                  <YAxis
                    tick={{ fontSize: 12, fontWeight: 700 }}
                    width={80}
                    label={{
                      value: "m/s",
                      angle: -90,
                      position: "insideLeft",
                      offset: 14,
                    }}
                  />
                  <RechartsTooltip content={<DashboardTooltip labelFormatter={formatTimeLabel} note={weatherTooltipNote} valueFormatter={formatWeatherTooltipValue} />} />
                  {renderWindLines()}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Stack>
        );
      case "weather-irradiance":
        return (
          <ResponsiveContainer width="100%" height="90%">
            <LineChart data={weatherSeries.irradiance} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" tick={{ fontSize: 12, fontWeight: 700 }} tickFormatter={formatTimeLabel} />
                  <YAxis
                    {...OPTIMIZATION_LEFT_AXIS_PROPS}
                    label={{
                      value: "W/mÂ˛",
                      angle: -90,
                      position: "insideLeft",
                      offset: 14,
                    }}
              />
              <RechartsTooltip content={<DashboardTooltip labelFormatter={formatTimeLabel} note={weatherTooltipNote} valueFormatter={formatWeatherTooltipValue} />} />
              {renderIrradianceLines()}
              <Brush
                dataKey="time"
                height={24}
                travellerWidth={10}
                tickFormatter={formatTimeLabelShort}
                stroke="#90a4ae"
              />
            </LineChart>
          </ResponsiveContainer>
        );
      case "weather-temperature":
        return (
          <ResponsiveContainer width="100%" height="90%">
            <LineChart data={weatherSeries.temperature} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" tick={{ fontSize: 12, fontWeight: 700 }} tickFormatter={formatTimeLabel} />
                  <YAxis
                    tick={{ fontSize: 12, fontWeight: 700 }}
                    width={70}
                    label={{
                      value: "Â°C",
                      angle: -90,
                      position: "insideLeft",
                      offset: 12,
                    }}
              />
              <RechartsTooltip content={<DashboardTooltip labelFormatter={formatTimeLabel} note={weatherTooltipNote} valueFormatter={formatWeatherTooltipValue} />} />
              {renderTemperatureLines()}
              <Brush
                dataKey="time"
                height={24}
                travellerWidth={10}
                tickFormatter={formatTimeLabelShort}
                stroke="#90a4ae"
              />
            </LineChart>
          </ResponsiveContainer>
        );
      case "weather-humidity":
        return (
          <ResponsiveContainer width="100%" height="90%">
            <LineChart data={weatherSeries.humidity} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" tick={{ fontSize: 12, fontWeight: 700 }} tickFormatter={formatTimeLabel} />
              <YAxis
                tick={{ fontSize: 12, fontWeight: 700 }}
                width={70}
                label={{
                  value: "%",
                  angle: -90,
                  position: "insideLeft",
                  offset: 12,
                }}
              />
              <RechartsTooltip content={<DashboardTooltip labelFormatter={formatTimeLabel} note={weatherTooltipNote} valueFormatter={formatWeatherTooltipValue} />} />
              {renderHumidityLines()}
              <Brush
                dataKey="time"
                height={24}
                travellerWidth={10}
                tickFormatter={formatTimeLabelShort}
                stroke="#90a4ae"
              />
            </LineChart>
          </ResponsiveContainer>
        );
      case "weather-wind":
        return (
          <ResponsiveContainer width="100%" height="90%">
            <LineChart data={weatherSeries.wind} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" tick={{ fontSize: 12, fontWeight: 700 }} tickFormatter={formatTimeLabel} />
              <YAxis
                tick={{ fontSize: 12, fontWeight: 700 }}
                width={80}
              label={{
                  value: "m/s",
                  angle: -90,
                  position: "insideLeft",
                  offset: 14,
                }}
              />
              <RechartsTooltip content={<DashboardTooltip labelFormatter={formatTimeLabel} note={weatherTooltipNote} valueFormatter={formatWeatherTooltipValue} />} />
              {renderWindLines()}
              <Brush
                dataKey="time"
                height={24}
                travellerWidth={10}
                tickFormatter={formatTimeLabelShort}
                stroke="#90a4ae"
              />
            </LineChart>
          </ResponsiveContainer>
        );
      case "pv-generation": {
        const pvGenHistoricalSeries = pvGenHistoricalRun ? pvGenSeriesByRun[pvGenHistoricalRun.run_id] : null;
        const pvGenForecastEntries = pvGenForecastRuns.map((run) => ({
          run,
          series: pvGenSeriesByRun[run.run_id] || null,
        }));
        const pvGenHistoricalMwh = pvGenHistoricalSeries ? computeSeriesEnergyMwh(pvGenHistoricalSeries) : null;
        const pvGenForecastRows = pvGenForecastEntries.map(({ run, series }) => {
          const forecastMwh = series ? computeSeriesEnergyMwh(series) : null;
          const differenceMwh =
            pvGenHistoricalMwh != null && forecastMwh != null ? forecastMwh - pvGenHistoricalMwh : null;
          const errorPct =
            differenceMwh != null && pvGenHistoricalMwh ? (differenceMwh / pvGenHistoricalMwh) * 100 : null;
          return { run, forecastMwh, differenceMwh, errorPct, peakDifferenceKw: null, accuracy: null };
        });
        return (
          <Box sx={{ height: "100%" }}>
            <PvGenerationChart
              variant="fullscreen"
              historicalRun={pvGenHistoricalRun}
              forecastEntries={pvGenForecastEntries}
              historicalSeries={pvGenHistoricalSeries}
              measuredSeries={pvGenMeasuredSeries}
              measuredAvailable={pvGenIncludeMeasured && pvGenMeasuredAvailable}
              historicalMwh={pvGenHistoricalMwh}
              forecastRows={pvGenForecastRows}
              onExportComparisonExcel={handlePvGenExport}
              onExportRun={handlePvGenExportRun}
              exporting={pvGenExporting}
              exportError={pvGenExportError}
            />
          </Box>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        "--dashboard-chart-axis-color": (theme) =>
          theme.palette.mode === "dark" ? "rgba(174, 182, 192, 0.88)" : "#0f172a",
        "--dashboard-chart-axis-line-color": (theme) =>
          theme.palette.mode === "dark" ? "rgba(148, 163, 184, 0.62)" : "rgba(15, 23, 42, 0.72)",
        "& .recharts-wrapper, & .recharts-surface, & .recharts-cartesian-axis": {
          color: "var(--dashboard-chart-axis-color)",
        },
        "& .recharts-cartesian-axis-tick-value, & .recharts-label": {
          fill: "var(--dashboard-chart-axis-color)",
        },
        "& .recharts-cartesian-axis-line, & .recharts-cartesian-axis-tick-line": {
          stroke: "var(--dashboard-chart-axis-line-color)",
        },
      }}
    >
      <Box
        sx={
          marketStrategyDisabled
            ? {
                filter: "blur(6px)",
                pointerEvents: "none",
                userSelect: "none",
              }
            : undefined
        }
      >
        <Stack spacing={3}>
          <Paper elevation={4} sx={{ px: 3, pt: 3, pb: 1.5, borderRadius: 3 }}>
            <Stack spacing={0.75}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {currentDashboardPageTitle}
              </Typography>
              {currentDashboardPageSubtitle ? (
                <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 920 }}>
                  {currentDashboardPageSubtitle}
                </Typography>
              ) : null}
            </Stack>
            {view === "optimization" && loadingRuns && (
              <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} /> <span>Loading runs...</span>
              </Box>
            )}
            {view === "optimization" && error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Paper>
          {view === "bid_schedule" && (
        <Stack spacing={3}>
          <MarketBidBook key={currentProject?.id || "unsaved"} deliveryDay={bidDay} accessToken={accessToken}
            writeDisabled={bidWriteDisabled} exportDisabled={Boolean(demoExportDisabledMessage)} />
          <Accordion disableGutters sx={{ borderRadius: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box><Typography fontWeight={700}>DAM schedule from optimisation</Typography>
                <Typography variant="body2" color="text.secondary">Inspect the existing day-ahead dispatch schedule and its results.</Typography></Box>
            </AccordionSummary>
            <AccordionDetails><Stack spacing={3}>
              <Alert severity="info">This schedule is saved in Pythia. Finalising it does not send orders to an exchange. Acceptance and financial figures here are price-based estimates, not exchange execution confirmations.</Alert>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", lg: "center" }}
              justifyContent="space-between"
            >
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} flexWrap="wrap" sx={{ rowGap: 1.5 }}>
                <AppDatePicker
                  label="Delivery day"
                  value={bidDay}
                  onChange={setBidDay}
                  sx={{ width: 220, minWidth: 220, flexShrink: 0 }}
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>BESS Asset</InputLabel>
                  <Select
                    label="BESS Asset"
                    value={assetKey}
                    onChange={(e) => setAssetKey(e.target.value)}
                    disabled={bidAssetOptions.length === 0}
                  >
                    {bidAssetOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip
                    variant="outlined"
                    label="Market: Day-Ahead Market (DAM)"
                    sx={{
                      "& .MuiChip-label": { fontWeight: 700 },
                      borderColor: "rgba(37,99,235,0.35)",
                      color: "#1d4ed8",
                      backgroundColor: "rgba(219,234,254,0.55)",
                    }}
                  />
                  <Chip
                    variant="outlined"
                    label={`Bidding Zone: ${biddingZoneLabel}`}
                    sx={{
                      "& .MuiChip-label": { fontWeight: 700 },
                      borderColor: "rgba(14,116,144,0.28)",
                      color: "#0f766e",
                      backgroundColor: "rgba(204,251,241,0.55)",
                    }}
                  />
                  <Chip
                    color={!hasBidScheduleRows ? "default" : bidSchedule?.status === "submitted" ? "success" : "warning"}
                    label={`Status: ${bidScheduleStatusLabel}`}
                    sx={
                      !hasBidScheduleRows
                        ? {
                            backgroundColor: "rgba(226,232,240,0.95)",
                            color: "rgba(71,85,105,0.95)",
                            "& .MuiChip-label": { fontWeight: 700 },
                          }
                        : undefined
                    }
                  />
                  {bidSchedule?.newer_run_available && (
                    <Chip color="info" variant="outlined" label="Newer run available" />
                  )}
                </Stack>
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ xs: "stretch", md: "center" }}>
                <Button
                  variant="outlined"
                  onClick={handleBidExport}
                  disabled={
                    !bidScheduleRows.length ||
                    bidEditMode ||
                    bidExporting ||
                    bidSchedule?.status !== "submitted" ||
                    Boolean(demoExportDisabledMessage)
                  }
                  sx={{ textTransform: "none" }}
                >
                  {bidExporting ? "Exporting..." : "Export Bids"}
                </Button>
                {bidSchedule?.status === "draft" ? (
                  <>
                    <Button
                      variant="outlined"
                      onClick={() => refreshDraftFromLatestRun()}
                      disabled={!canRefreshDraftFromLatestRun || bidWriteDisabled}
                      startIcon={bidRefreshing ? <CircularProgress size={16} thickness={5} /> : null}
                      sx={{ textTransform: "none" }}
                    >
                      {bidRefreshing ? "Refreshing..." : "Refresh from Latest Run"}
                    </Button>
                    {!bidEditMode ? (
                      <Button
                        variant="outlined"
                        onClick={handleBidEditStart}
                        disabled={!bidScheduleRows.length || bidWriteDisabled}
                        sx={{ textTransform: "none" }}
                      >
                        Edit Bids
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          color="inherit"
                          onClick={handleBidEditCancel}
                          sx={{ textTransform: "none" }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleBidEditSave}
                          disabled={bidWriteDisabled}
                          sx={{ textTransform: "none" }}
                        >
                          Save
                        </Button>
                      </>
                    )}
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleBidSubmit}
                      disabled={!bidScheduleRows.length || bidEditMode || bidWriteDisabled}
                      sx={{ textTransform: "none" }}
                    >
                      Finalise schedule
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleBidReopen}
                    disabled={!bidSchedule || bidWriteDisabled}
                    sx={{ textTransform: "none" }}
                  >
                    Re-Open
                  </Button>
                )}
              </Stack>
            </Stack>
            {bidWriteDisabledMessage && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {bidWriteDisabledMessage}
              </Alert>
            )}
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mt: 2 }} flexWrap="wrap">
              <Typography variant="body2"><strong>Source Run:</strong> {sourceRunLabel}</Typography>
              <Typography variant="body2"><strong>Last Updated:</strong> {bidSchedule?.updated_at ? new Date(bidSchedule.updated_at).toLocaleString("en-GB") : "N/A"}</Typography>
              <Typography variant="body2"><strong>Finalised At:</strong> {bidSchedule?.submitted_at ? new Date(bidSchedule.submitted_at).toLocaleString("en-GB") : "N/A"}</Typography>
            </Stack>
            {bidScheduleError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {bidScheduleError}
              </Alert>
            )}
            {bidExportError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {bidExportError}
              </Alert>
            )}
          </Paper>
          <Paper elevation={3} sx={{ p: { xs: 2, md: 2.25 }, borderRadius: 3 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.25}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              sx={{ mb: bidStrategyExpanded ? 1.25 : 0 }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Bidding Strategy Settings
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  Project-level settings used to convert optimization results into tradable bid prices and quantities for all bid days.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                {bidStrategyExpanded && (
                  bidStrategyEditMode ? (
                    <>
                      <Button
                        variant="outlined"
                        color="inherit"
                        onClick={handleBidStrategyEditCancel}
                        sx={{ textTransform: "none" }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleBidStrategySave}
                        disabled={
                          !currentProject?.id ||
                          !assetKey ||
                          bidStrategySaving ||
                          !bidStrategyDirty ||
                          !bidStrategyValidation.isValid ||
                          bidWriteDisabled
                        }
                        sx={{ textTransform: "none" }}
                      >
                        {bidStrategySaving ? "Saving..." : "Save Settings"}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={handleBidStrategyEditStart}
                      disabled={!currentProject?.id || !assetKey || bidWriteDisabled}
                      sx={{ textTransform: "none" }}
                    >
                      Edit Settings
                    </Button>
                  )
                )}
                <IconButton
                  size="small"
                  onClick={() => setBidStrategyExpanded((prev) => !prev)}
                  title={bidStrategyExpanded ? "Collapse settings" : "Expand settings"}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <ExpandMoreIcon
                    sx={{
                      transform: bidStrategyExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 160ms ease",
                    }}
                  />
                </IconButton>
              </Stack>
            </Stack>
            <Collapse in={bidStrategyExpanded} timeout="auto" unmountOnExit>
            <Grid container spacing={1.25}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={STRATEGY_PANEL_SX}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                    Forecast Basis
                  </Typography>
                  <Grid container spacing={1.1}>
                    <Grid item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          size="small"
                          disabled={!bidStrategyEditMode}
                          sx={{ minWidth: 160, ...(!bidStrategyEditMode ? STRATEGY_READONLY_FIELD_SX : {}) }}
                        >
                        <InputLabel>Sell Forecast Basis</InputLabel>
                        <Select
                          label="Sell Forecast Basis"
                          value={bidStrategySettings.sellForecastBasis}
                          onChange={(e) => handleBidStrategySettingChange("sellForecastBasis", e.target.value)}
                        >
                          <MenuItem value="P10">P10</MenuItem>
                          <MenuItem value="P50">P50</MenuItem>
                          <MenuItem value="P90">P90</MenuItem>
                        </Select>
                      </FormControl>
                      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.15 }}>
                        Percentile used for sell bid pricing.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          size="small"
                          disabled={!bidStrategyEditMode}
                          sx={{ minWidth: 160, ...(!bidStrategyEditMode ? STRATEGY_READONLY_FIELD_SX : {}) }}
                        >
                        <InputLabel>Buy Forecast Basis</InputLabel>
                        <Select
                          label="Buy Forecast Basis"
                          value={bidStrategySettings.buyForecastBasis}
                          onChange={(e) => handleBidStrategySettingChange("buyForecastBasis", e.target.value)}
                        >
                          <MenuItem value="P10">P10</MenuItem>
                          <MenuItem value="P50">P50</MenuItem>
                          <MenuItem value="P90">P90</MenuItem>
                        </Select>
                      </FormControl>
                      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.15 }}>
                        Percentile used for buy bid pricing.
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={STRATEGY_PANEL_SX}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                    Safety Margin Settings
                  </Typography>
                  <Grid container spacing={1.1}>
                    <Grid item xs={12} md={4}>
                        <FormControl
                          fullWidth
                          size="small"
                          disabled={!bidStrategyEditMode}
                          sx={{ minWidth: 210, ...(!bidStrategyEditMode ? STRATEGY_READONLY_FIELD_SX : {}) }}
                        >
                        <InputLabel>Margin Type</InputLabel>
                        <Select
                          label="Margin Type"
                          value={bidStrategySettings.marginType}
                          onChange={(e) => handleBidStrategySettingChange("marginType", e.target.value)}
                        >
                          <MenuItem value="absolute">Absolute (EUR/MWh)</MenuItem>
                          <MenuItem value="percent">Percentage (%)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={
                          bidStrategySettings.marginType === "percent"
                            ? "Sell Margin (%)"
                            : "Sell Margin (EUR/MWh)"
                        }
                        value={bidStrategySettings.sellMargin}
                        onChange={(e) => handleBidStrategySettingChange("sellMargin", e.target.value)}
                        disabled={!bidStrategyEditMode}
                        error={bidStrategyValidation.errors.sellMargin}
                        inputProps={{
                          min: 0,
                          max: bidStrategySettings.marginType === "percent" ? 100 : undefined,
                        }}
                        sx={{ minWidth: 220, ...(!bidStrategyEditMode ? STRATEGY_READONLY_FIELD_SX : {}) }}
                      />
                      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.15 }}>
                        Sell margin is subtracted from the selected sell forecast.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={
                          bidStrategySettings.marginType === "percent"
                            ? "Buy Margin (%)"
                            : "Buy Margin (EUR/MWh)"
                        }
                        value={bidStrategySettings.buyMargin}
                        onChange={(e) => handleBidStrategySettingChange("buyMargin", e.target.value)}
                        disabled={!bidStrategyEditMode}
                        error={bidStrategyValidation.errors.buyMargin}
                        inputProps={{
                          min: 0,
                          max: bidStrategySettings.marginType === "percent" ? 100 : undefined,
                        }}
                        sx={{ minWidth: 220, ...(!bidStrategyEditMode ? STRATEGY_READONLY_FIELD_SX : {}) }}
                      />
                      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.15 }}>
                        Buy margin is added to the selected buy forecast.
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={STRATEGY_PANEL_SX}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                    Price Bounds
                  </Typography>
                  <Grid container spacing={1.1}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Min Sell Price (EUR/MWh)"
                        value={bidStrategySettings.minSellPriceEurPerMwh ?? ""}
                        onChange={(e) =>
                          handleBidStrategySettingChange("minSellPriceEurPerMwh", e.target.value)
                        }
                        disabled={!bidStrategyEditMode}
                        error={Boolean(bidStrategyValidation.errors.minSellPriceEurPerMwh)}
                        helperText={
                          bidStrategyValidation.errors.minSellPriceEurPerMwh ||
                          "Lower floor for generated sell bids."
                        }
                        sx={!bidStrategyEditMode ? STRATEGY_READONLY_FIELD_SX : undefined}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Max Sell Price (EUR/MWh)"
                        value={bidStrategySettings.maxSellPriceEurPerMwh ?? ""}
                        onChange={(e) =>
                          handleBidStrategySettingChange("maxSellPriceEurPerMwh", e.target.value)
                        }
                        disabled={!bidStrategyEditMode}
                        error={Boolean(bidStrategyValidation.errors.maxSellPriceEurPerMwh)}
                        helperText={
                          bidStrategyValidation.errors.maxSellPriceEurPerMwh ||
                          "Upper cap for generated sell bids."
                        }
                        sx={!bidStrategyEditMode ? STRATEGY_READONLY_FIELD_SX : undefined}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Min Buy Price (EUR/MWh)"
                        value={bidStrategySettings.minBuyPriceEurPerMwh ?? ""}
                        onChange={(e) =>
                          handleBidStrategySettingChange("minBuyPriceEurPerMwh", e.target.value)
                        }
                        disabled={!bidStrategyEditMode}
                        error={Boolean(bidStrategyValidation.errors.minBuyPriceEurPerMwh)}
                        helperText={
                          bidStrategyValidation.errors.minBuyPriceEurPerMwh ||
                          "Lower floor for generated buy bids."
                        }
                        sx={!bidStrategyEditMode ? STRATEGY_READONLY_FIELD_SX : undefined}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Max Buy Price (EUR/MWh)"
                        value={bidStrategySettings.maxBuyPriceEurPerMwh ?? ""}
                        onChange={(e) =>
                          handleBidStrategySettingChange("maxBuyPriceEurPerMwh", e.target.value)
                        }
                        disabled={!bidStrategyEditMode}
                        error={Boolean(bidStrategyValidation.errors.maxBuyPriceEurPerMwh)}
                        helperText={
                          bidStrategyValidation.errors.maxBuyPriceEurPerMwh ||
                          "Upper cap for generated buy bids."
                        }
                        sx={!bidStrategyEditMode ? STRATEGY_READONLY_FIELD_SX : undefined}
                      />
                    </Grid>
                  </Grid>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={0.6}
                    sx={{ mt: 0.75, flexWrap: "wrap" }}
                  >
                    <Chip
                      variant="outlined"
                      label={`Sell price bounds: ${formatPriceBoundSummary(
                        bidStrategySettings.minSellPriceEurPerMwh,
                        bidStrategySettings.maxSellPriceEurPerMwh
                      )}`}
                      sx={STRATEGY_BOUNDS_CHIP_SX}
                    />
                    <Chip
                      variant="outlined"
                      label={`Buy price bounds: ${formatPriceBoundSummary(
                        bidStrategySettings.minBuyPriceEurPerMwh,
                        bidStrategySettings.maxBuyPriceEurPerMwh
                      )}`}
                      sx={STRATEGY_BOUNDS_CHIP_SX}
                    />
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={STRATEGY_PANEL_SX}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                    Practical Bid Controls
                  </Typography>
                  <Grid container spacing={1.1}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Minimum Bid Quantity (MW)"
                        value={bidStrategySettings.minBidQuantityMw}
                        onChange={(e) => handleBidStrategySettingChange("minBidQuantityMw", e.target.value)}
                        disabled={!bidStrategyEditMode}
                        error={bidStrategyValidation.errors.minBidQuantityMw}
                        inputProps={{ min: 0, step: "0.01" }}
                        sx={!bidStrategyEditMode ? STRATEGY_READONLY_FIELD_SX : undefined}
                      />
                      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.15 }}>
                        Minimum tradable quantity; bids below this threshold are suppressed and treated as idle.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Quantity Step (MW)"
                        value={bidStrategySettings.quantityStepMw}
                        onChange={(e) => handleBidStrategySettingChange("quantityStepMw", e.target.value)}
                        disabled={!bidStrategyEditMode}
                        error={bidStrategyValidation.errors.quantityStepMw}
                        inputProps={{ min: 0.001, step: "0.01" }}
                        sx={!bidStrategyEditMode ? STRATEGY_READONLY_FIELD_SX : undefined}
                      />
                      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.15 }}>
                        Rounding step for bid quantity.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Price Tick (EUR/MWh)"
                        value={bidStrategySettings.priceTickEurPerMwh}
                        onChange={(e) => handleBidStrategySettingChange("priceTickEurPerMwh", e.target.value)}
                        disabled={!bidStrategyEditMode}
                        error={bidStrategyValidation.errors.priceTickEurPerMwh}
                        inputProps={{ min: 0.001, step: "0.1" }}
                        sx={!bidStrategyEditMode ? STRATEGY_READONLY_FIELD_SX : undefined}
                      />
                      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.15 }}>
                        Rounding step for bid price.
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={STRATEGY_PANEL_SX}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                    Market Operation Economics — DAM Only
                  </Typography>
                  <Grid container spacing={1.1}>
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block", mb: 0.2 }}
                      >
                        DAM Transaction Fees
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="DAM Trading Fee (EUR/MWh executed)"
                        value={bidStrategySettings.marketEconomics.damFees.tradingFeeEurPerMWh}
                        onChange={(e) =>
                          handleBidStrategyNestedSettingChange(
                            "marketEconomics.damFees.tradingFeeEurPerMWh",
                            e.target.value
                          )
                        }
                        disabled={!bidStrategyEditMode}
                        error={Boolean(bidStrategyValidation.errors.damTradingFeeEurPerMWh)}
                        helperText={
                          bidStrategyValidation.errors.damTradingFeeEurPerMWh ||
                          "Applied to executed DAM volume: bought MWh + sold MWh."
                        }
                        inputProps={{ min: 0, step: "0.001" }}
                        sx={!bidStrategyEditMode ? STRATEGY_READONLY_FIELD_SX : undefined}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="DAM Clearing Fee (EUR/MWh executed)"
                        value={bidStrategySettings.marketEconomics.damFees.clearingFeeEurPerMWh}
                        onChange={(e) =>
                          handleBidStrategyNestedSettingChange(
                            "marketEconomics.damFees.clearingFeeEurPerMWh",
                            e.target.value
                          )
                        }
                        disabled={!bidStrategyEditMode}
                        error={Boolean(bidStrategyValidation.errors.damClearingFeeEurPerMWh)}
                        helperText={
                          bidStrategyValidation.errors.damClearingFeeEurPerMWh ||
                          "Applied to executed DAM volume: bought MWh + sold MWh."
                        }
                        inputProps={{ min: 0, step: "0.001" }}
                        sx={!bidStrategyEditMode ? STRATEGY_READONLY_FIELD_SX : undefined}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Total DAM Transaction Fee (EUR/MWh executed)"
                        value={totalDamTransactionFee.toFixed(3)}
                        disabled
                        error={false}
                        helperText={
                          "Readonly total transaction cost applied to bought + sold DAM volume."
                        }
                        inputProps={{ readOnly: true }}
                        sx={STRATEGY_READONLY_FIELD_SX}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <Box
                          sx={{
                            mt: 0.2,
                            px: 1,
                            py: 0.95,
                            borderRadius: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(15,23,42,0.42)"
                                : "rgba(248,250,252,0.9)",
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.45 }}>
                            BRP / Aggregator Service Fee
                          </Typography>
                          <Grid container spacing={1.1}>
                            <Grid item xs={12} md={3}>
                              <FormControlLabel
                                sx={{ ml: 0.1, mt: 0.15 }}
                                control={
                                  <Switch
                                    size="small"
                                    checked={Boolean(
                                      bidStrategySettings.marketEconomics.brpAggregator.enabled
                                    )}
                                    onChange={(e) =>
                                      handleBidStrategyNestedSettingChange(
                                        "marketEconomics.brpAggregator.enabled",
                                        e.target.checked,
                                        { kind: "boolean" }
                                      )
                                    }
                                    disabled={!bidStrategyEditMode}
                                  />
                                }
                                label="Enable BRP / Aggregator Service Fee"
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <FormControl
                                fullWidth
                                size="small"
                                disabled={
                                  !bidStrategyEditMode ||
                                  !bidStrategySettings.marketEconomics.brpAggregator.enabled
                                }
                                sx={{
                                  minWidth: 210,
                                  ...((!bidStrategyEditMode ||
                                    !bidStrategySettings.marketEconomics.brpAggregator.enabled) &&
                                  STRATEGY_READONLY_FIELD_SX),
                                }}
                              >
                                <InputLabel>Fee Structure</InputLabel>
                                <Select
                                  label="Fee Structure"
                                  value={bidStrategySettings.marketEconomics.brpAggregator.feeType}
                                  onChange={(e) =>
                                    handleBidStrategyNestedSettingChange(
                                      "marketEconomics.brpAggregator.feeType",
                                      e.target.value,
                                      { kind: "string" }
                                    )
                                  }
                                >
                                  <MenuItem value="none">No fee</MenuItem>
                                  <MenuItem value="eur_per_mwh_traded">Per traded MWh</MenuItem>
                                  <MenuItem value="percent_of_gross_margin">
                                    Share of gross margin
                                  </MenuItem>
                                  <MenuItem value="fixed_monthly">Fixed monthly fee</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={`Fee Value (${brpAggregatorFeeUnitLabel})`}
                                value={bidStrategySettings.marketEconomics.brpAggregator.value}
                                onChange={(e) =>
                                  handleBidStrategyNestedSettingChange(
                                    "marketEconomics.brpAggregator.value",
                                    e.target.value
                                  )
                                }
                                disabled={
                                  !bidStrategyEditMode ||
                                  !bidStrategySettings.marketEconomics.brpAggregator.enabled
                                }
                                error={Boolean(bidStrategyValidation.errors.brpAggregatorValue)}
                                helperText={
                                  bidStrategyValidation.errors.brpAggregatorValue ||
                                  "Optional fee paid to a market representative or trading intermediary."
                                }
                                inputProps={{ min: 0, step: "0.01" }}
                                sx={
                                  !bidStrategyEditMode ||
                                  !bidStrategySettings.marketEconomics.brpAggregator.enabled
                                    ? STRATEGY_READONLY_FIELD_SX
                                    : undefined
                                }
                              />
                            </Grid>
                            {bidStrategySettings.marketEconomics.brpAggregator.enabled &&
                            bidStrategySettings.marketEconomics.brpAggregator.feeType === "fixed_monthly" ? (
                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="BRP Fixed Daily Equivalent (EUR/day)"
                                  value={marketEconomicsDailyEquivalents.brpFixedDailyEur.toFixed(2)}
                                  disabled
                                  helperText="Readonly: fixed monthly BRP fee / 30."
                                  inputProps={{ readOnly: true }}
                                  sx={STRATEGY_READONLY_FIELD_SX}
                                />
                              </Grid>
                            ) : null}
                          </Grid>
                        </Box>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.95,
                            borderRadius: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                            overflow: "hidden",
                            backgroundColor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(15,23,42,0.42)"
                                : "rgba(248,250,252,0.9)",
                          }}
                        >
                          <Button
                            fullWidth
                            variant="text"
                            onClick={() =>
                              setMarketEconomicsAdvancedExpanded((prev) => !prev)
                            }
                            sx={{
                              px: 0,
                              py: 0.1,
                              color: "text.primary",
                              justifyContent: "space-between",
                              textTransform: "none",
                              fontWeight: 700,
                              borderRadius: 0,
                            }}
                          >
                            <span>Advanced Market Assumptions</span>
                            <ExpandMoreIcon
                              sx={{
                                transform: marketEconomicsAdvancedExpanded
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                                transition: "transform 160ms ease",
                              }}
                            />
                          </Button>
                          <Collapse in={marketEconomicsAdvancedExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ pt: 0.9, pb: 0.1 }}>
                              <Stack spacing={1}>
                                <Box sx={{ width: { xs: "100%", md: 360 } }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Total Allocated Fixed Cost (EUR/day)"
                                    value={marketEconomicsDailyEquivalents.totalAllocatedDailyEur.toFixed(2)}
                                    disabled
                                    helperText="Readonly total of fixed daily allocations from market overhead assumptions."
                                    inputProps={{ readOnly: true }}
                                    sx={STRATEGY_READONLY_FIELD_SX}
                                  />
                                </Box>
                              <Box
                                sx={{
                                  px: 0.9,
                                  py: 0.9,
                                  borderRadius: 1.4,
                                  border: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <FormControlLabel
                                  sx={{ ml: 0.1, mb: 0.85 }}
                                  control={
                                    <Switch
                                      size="small"
                                      checked={Boolean(
                                        bidStrategySettings.marketEconomics.fixedMarketCosts.enabled
                                      )}
                                      onChange={(e) =>
                                        handleBidStrategyNestedSettingChange(
                                          "marketEconomics.fixedMarketCosts.enabled",
                                          e.target.checked,
                                          { kind: "boolean" }
                                        )
                                      }
                                      disabled={!bidStrategyEditMode}
                                    />
                                  }
                                  label="Enable Fixed Market Costs"
                                />
                                {bidStrategySettings.marketEconomics.fixedMarketCosts.enabled && (
                                  <Grid container spacing={1.1} sx={{ mt: 0.1 }}>
                                    <Grid item xs={12} md={6} lg={4}>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label="Annual Market Membership Cost (EUR/year)"
                                        value={
                                          bidStrategySettings.marketEconomics.fixedMarketCosts
                                            .annualMembershipCostEur
                                        }
                                        onChange={(e) =>
                                          handleBidStrategyNestedSettingChange(
                                            "marketEconomics.fixedMarketCosts.annualMembershipCostEur",
                                            e.target.value
                                          )
                                        }
                                        disabled={!bidStrategyEditMode}
                                        error={Boolean(
                                          bidStrategyValidation.errors.annualMembershipCostEur
                                        )}
                                        helperText={
                                          bidStrategyValidation.errors.annualMembershipCostEur ||
                                          "Annual exchange or market-participation membership cost."
                                        }
                                        inputProps={{ min: 0, step: "0.01" }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={6} lg={4}>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label="Membership Daily Equivalent (EUR/day)"
                                        value={marketEconomicsDailyEquivalents.annualMembershipDailyEur.toFixed(2)}
                                        disabled
                                        helperText="Readonly: annual cost / 365."
                                        inputProps={{ readOnly: true }}
                                        sx={STRATEGY_READONLY_FIELD_SX}
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={6} lg={4}>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label="Clearing Account Cost (EUR/month)"
                                        value={
                                          bidStrategySettings.marketEconomics.fixedMarketCosts
                                            .clearingAccountMonthlyCostEur
                                        }
                                        onChange={(e) =>
                                          handleBidStrategyNestedSettingChange(
                                            "marketEconomics.fixedMarketCosts.clearingAccountMonthlyCostEur",
                                            e.target.value
                                          )
                                        }
                                        disabled={!bidStrategyEditMode}
                                        error={Boolean(
                                          bidStrategyValidation.errors.clearingAccountMonthlyCostEur
                                        )}
                                        helperText={
                                          bidStrategyValidation.errors.clearingAccountMonthlyCostEur ||
                                          "Monthly cost for clearing account administration or custody."
                                        }
                                        inputProps={{ min: 0, step: "0.01" }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={6} lg={4}>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label="Clearing Account Daily Equivalent (EUR/day)"
                                        value={marketEconomicsDailyEquivalents.clearingAccountDailyEur.toFixed(2)}
                                        disabled
                                        helperText="Readonly: monthly cost / 30."
                                        inputProps={{ readOnly: true }}
                                        sx={STRATEGY_READONLY_FIELD_SX}
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={6} lg={4}>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label="Market Data / Platform Access Cost (EUR/month)"
                                        value={
                                          bidStrategySettings.marketEconomics.fixedMarketCosts
                                            .marketDataMonthlyCostEur
                                        }
                                        onChange={(e) =>
                                          handleBidStrategyNestedSettingChange(
                                            "marketEconomics.fixedMarketCosts.marketDataMonthlyCostEur",
                                            e.target.value
                                          )
                                        }
                                        disabled={!bidStrategyEditMode}
                                        error={Boolean(
                                          bidStrategyValidation.errors.marketDataMonthlyCostEur
                                        )}
                                        helperText={
                                          bidStrategyValidation.errors.marketDataMonthlyCostEur ||
                                          "Monthly access cost for market data feeds or trading platform tools."
                                        }
                                        inputProps={{ min: 0, step: "0.01" }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={6} lg={4}>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label="Market Data Daily Equivalent (EUR/day)"
                                        value={marketEconomicsDailyEquivalents.marketDataDailyEur.toFixed(2)}
                                        disabled
                                        helperText="Readonly: monthly cost / 30."
                                        inputProps={{ readOnly: true }}
                                        sx={STRATEGY_READONLY_FIELD_SX}
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={6} lg={4}>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label="Total Fixed Market Cost (EUR/day)"
                                        value={marketEconomicsDailyEquivalents.fixedMarketCostsDailyEur.toFixed(2)}
                                        disabled
                                        helperText="Readonly sum of fixed market costs allocated per day."
                                        inputProps={{ readOnly: true }}
                                        sx={STRATEGY_READONLY_FIELD_SX}
                                      />
                                    </Grid>
                                  </Grid>
                                )}
                              </Box>
                              <Box
                                sx={{
                                  px: 0.9,
                                  py: 0.9,
                                  borderRadius: 1.4,
                                  border: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <FormControlLabel
                                  sx={{ ml: 0.1, mb: 0.85 }}
                                  control={
                                    <Switch
                                      size="small"
                                      checked={Boolean(
                                        bidStrategySettings.marketEconomics.collateralCost.enabled
                                      )}
                                      onChange={(e) =>
                                        handleBidStrategyNestedSettingChange(
                                          "marketEconomics.collateralCost.enabled",
                                          e.target.checked,
                                          { kind: "boolean" }
                                        )
                                      }
                                      disabled={!bidStrategyEditMode}
                                    />
                                  }
                                  label="Enable Collateral Cost"
                                />
                                {bidStrategySettings.marketEconomics.collateralCost.enabled && (
                                  <Grid container spacing={1.1} sx={{ mt: 0.1 }}>
                                    <Grid item xs={12} md={6} lg={4}>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label="Collateral Amount (EUR)"
                                        value={
                                          bidStrategySettings.marketEconomics.collateralCost
                                            .collateralAmountEur
                                        }
                                        onChange={(e) =>
                                          handleBidStrategyNestedSettingChange(
                                            "marketEconomics.collateralCost.collateralAmountEur",
                                            e.target.value
                                          )
                                        }
                                        disabled={!bidStrategyEditMode}
                                        error={Boolean(
                                          bidStrategyValidation.errors.collateralAmountEur
                                        )}
                                        helperText={
                                          bidStrategyValidation.errors.collateralAmountEur ||
                                          "Cash or credit support tied up as collateral for market participation."
                                        }
                                        inputProps={{ min: 0, step: "0.01" }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={6} lg={4}>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label="Annual Cost of Capital (%)"
                                        value={
                                          bidStrategySettings.marketEconomics.collateralCost
                                            .annualCostOfCapitalPct
                                        }
                                        onChange={(e) =>
                                          handleBidStrategyNestedSettingChange(
                                            "marketEconomics.collateralCost.annualCostOfCapitalPct",
                                            e.target.value
                                          )
                                        }
                                        disabled={!bidStrategyEditMode}
                                        error={Boolean(
                                          bidStrategyValidation.errors.annualCostOfCapitalPct
                                        )}
                                        helperText={
                                          bidStrategyValidation.errors.annualCostOfCapitalPct ||
                                          "Annual financing or opportunity cost applied to posted collateral."
                                        }
                                        inputProps={{ min: 0, step: "0.01" }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={6} lg={4}>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label="Collateral Carry Cost (EUR/day)"
                                        value={marketEconomicsDailyEquivalents.collateralDailyEur.toFixed(2)}
                                        disabled
                                        helperText="Readonly: (collateral x annual cost of capital %) / 365."
                                        inputProps={{ readOnly: true }}
                                        sx={STRATEGY_READONLY_FIELD_SX}
                                      />
                                    </Grid>
                                  </Grid>
                                )}
                              </Box>
                            </Stack>
                          </Box>
                        </Collapse>
                      </Box>
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block", mt: 0.25 }}
                      >
                        This section models DAM market operation economics only. Grid/network charges,
                        asset OPEX, degradation, taxes, CAPEX and debt service are excluded from this
                        section.
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block", mt: 0.35 }}
                      >
                        Daily equivalents are readonly allocations using annual / 365 and monthly / 30.
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
            </Collapse>
          </Paper>
          <Box
            sx={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                xl: "minmax(0, 1.45fr) minmax(380px, 1fr)",
              },
              gap: 1.4,
              alignItems: "start",
            }}
          >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              px: { xs: 2.25, md: 2.5 },
              py: { xs: 1.5, md: 1.75 },
              borderRadius: 3,
              border: "1px solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(94, 234, 212, 0.16)" : "divider",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(8, 35, 27, 0.5)" : "background.paper",
            }}
          >
            <Stack spacing={{ xs: 1.4, md: 1.6 }}>
              <Box>
                <Typography variant="caption" sx={{ display: "block", fontWeight: 800, letterSpacing: "0.08em", color: "text.secondary", mb: 0.35 }}>
                  BID SCHEDULE SUMMARY
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 720 }}>
                  Bid information and cleared market outcomes for the selected day-ahead schedule.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "0.86fr 0.92fr 1.26fr 1.08fr 1.2fr",
                  },
                  gap: { xs: 0.9, md: 0.95 },
                  alignItems: "start",
                }}
              >
                {bidSummaryDashboard.sections.map((section) => (
                  <Box key={section.key} sx={{ minWidth: 0 }}>
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: "0.01em", color: "text.primary", fontSize: 15, mb: 0.45 }}>
                        {section.title}
                      </Typography>
                        <Stack spacing={0.5}>
                          {section.metrics.map((metric) => (
                          <Box
                            key={metric.label}
                            sx={{
                              width: "100%",
                              px: 1.2,
                              py: 0.75,
                              minHeight: 64,
                              borderRadius: 1.7,
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark" ? "rgba(7, 31, 24, 0.62)" : "#f9fafb",
                              border: "1px solid",
                              borderColor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(94, 234, 212, 0.18)"
                                  : "rgba(229,231,235,0.9)",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              gap: 0.12,
                              transition: "background-color 150ms ease",
                              "&:hover": {
                                backgroundColor: (theme) =>
                                  theme.palette.mode === "dark" ? "rgba(10, 46, 36, 0.75)" : "#f3f4f6",
                              },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontWeight: 700,
                                letterSpacing: "0.01em",
                                fontSize: 12,
                                mb: 0.12,
                                lineHeight: 1.2,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {metric.label}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.35, flexWrap: "wrap" }}>
                              <Typography
                                sx={{
                                  fontSize: { xs: 16, md: 16.5 },
                                  fontWeight: 600,
                                  color: "text.primary",
                                  lineHeight: 1,
                                  opacity:
                                    metric.value == null &&
                                    (section.key === "financials" || section.key === "settlement") &&
                                    bidSummaryDashboard.actualFinancialsState !== "available"
                                      ? 0.72
                                      : metric.value == null
                                        ? 0.5
                                        : 1,
                                }}
                              >
                                {(section.key === "financials" || section.key === "settlement") &&
                                bidSummaryDashboard.actualFinancialsState === "pending"
                                  ? "-"
                                  : formatSummaryMetricNumber(metric.value, metric.decimals) ?? "N/A"}
                              </Typography>
                              {!((section.key === "financials" || section.key === "settlement") && bidSummaryDashboard.actualFinancialsState !== "available") && (
                                <Typography sx={{ fontSize: 10.6, fontWeight: 600, color: "text.secondary", lineHeight: 1, letterSpacing: "0.01em" }}>
                                  {metric.unit}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Box>
                ))}
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ width: "100%" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: "0.01em", color: "text.primary", fontSize: 15, mb: 0.45 }}>
                      Profit
                    </Typography>
                    <Stack spacing={0.5}>
                      <Box
                        sx={{
                          width: "100%",
                          px: 1.2,
                          py: 0.75,
                          minHeight: 64,
                          borderRadius: 1.7,
                          border: "1px solid",
                          borderColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? bidSummaryDashboard.economicProfitTone === "positive"
                                ? "rgba(74, 222, 128, 0.24)"
                                : bidSummaryDashboard.economicProfitTone === "negative"
                                  ? "rgba(248, 113, 113, 0.24)"
                                  : "rgba(94, 234, 212, 0.18)"
                              : "rgba(21,128,61,0.10)",
                          background: (theme) => {
                            if (theme.palette.mode === "dark") {
                              if (bidSummaryDashboard.economicProfitTone === "positive") {
                                return "linear-gradient(135deg, rgba(20,83,45,0.34) 0%, rgba(21,128,61,0.18) 100%)";
                              }
                              if (bidSummaryDashboard.economicProfitTone === "negative") {
                                return "linear-gradient(135deg, rgba(127,29,29,0.34) 0%, rgba(153,27,27,0.18) 100%)";
                              }
                              return "rgba(7, 31, 24, 0.62)";
                            }
                            return bidSummaryDashboard.economicProfitTone === "positive"
                              ? "linear-gradient(135deg, rgba(245,252,247,1) 0%, rgba(240,249,243,1) 100%)"
                              : bidSummaryDashboard.economicProfitTone === "negative"
                                ? "linear-gradient(135deg, rgba(254,247,247,1) 0%, rgba(252,241,241,1) 100%)"
                                : "#f9fafb";
                          },
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          gap: 0.12,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.45, mb: 0.12 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              fontWeight: 800,
                              letterSpacing: "0.01em",
                              color: (theme) => {
                                if (bidSummaryDashboard.economicProfitTone === "positive") {
                                  return theme.palette.mode === "dark" ? "#86efac" : "#166534";
                                }
                                if (bidSummaryDashboard.economicProfitTone === "negative") {
                                  return theme.palette.mode === "dark" ? "#fca5a5" : "#b91c1c";
                                }
                                return "text.secondary";
                              },
                              fontSize: 12,
                              lineHeight: 1.2,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Net Economic Profit
                          </Typography>
                          <MaybeTooltip
                            title={
                              bidSummaryDashboard.actualFinancialsState === "pending"
                                ? "Financial totals will appear after the schedule is submitted."
                                : "Net Economic Profit = net operating profit after degradation cost"
                            }
                            arrow
                          >
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: 14,
                                color: "text.secondary",
                                cursor: "help",
                                flexShrink: 0,
                              }}
                            />
                          </MaybeTooltip>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.45 }}>
                          <Typography
                            sx={{
                              fontSize: { xs: 16, md: 16.5 },
                              fontWeight: 600,
                              lineHeight: 1,
                              letterSpacing: "0",
                              color: (theme) => {
                                if (bidSummaryDashboard.economicProfitTone === "positive") {
                                  return theme.palette.mode === "dark" ? "#4ade80" : "#15803d";
                                }
                                if (bidSummaryDashboard.economicProfitTone === "negative") {
                                  return theme.palette.mode === "dark" ? "#f87171" : "#dc2626";
                                }
                                return "text.secondary";
                              },
                            }}
                          >
                            {bidSummaryDashboard.actualFinancialsState === "pending"
                              ? "-"
                              : formatSummaryMetricNumber(bidSummaryDashboard.netEconomicProfit, 2) ?? "N/A"}
                          </Typography>
                          {bidSummaryDashboard.actualFinancialsState === "available" && (
                            <Typography sx={{ fontSize: 10.6, fontWeight: 600, color: "text.secondary", lineHeight: 1 }}>
                              EUR
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          width: "100%",
                          px: 1.2,
                          py: 0.75,
                          minHeight: 64,
                          borderRadius: 1.7,
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark" ? "rgba(7, 31, 24, 0.62)" : "#f9fafb",
                          border: "1px solid",
                          borderColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? bidSummaryDashboard.operatingProfitTone === "positive"
                                ? "rgba(74, 222, 128, 0.18)"
                                : bidSummaryDashboard.operatingProfitTone === "negative"
                                  ? "rgba(248, 113, 113, 0.18)"
                                  : "rgba(94, 234, 212, 0.18)"
                              : bidSummaryDashboard.operatingProfitTone === "positive"
                                ? "rgba(21,128,61,0.12)"
                                : bidSummaryDashboard.operatingProfitTone === "negative"
                                  ? "rgba(220,38,38,0.12)"
                                  : "rgba(229,231,235,0.9)",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          gap: 0.12,
                          background: (theme) =>
                            bidSummaryDashboard.operatingProfitTone === "positive"
                              ? theme.palette.mode === "dark"
                                ? "linear-gradient(135deg, rgba(20,83,45,0.18) 0%, rgba(21,128,61,0.08) 100%)"
                                : "linear-gradient(135deg, rgba(245,252,247,1) 0%, rgba(240,249,243,1) 100%)"
                              : bidSummaryDashboard.operatingProfitTone === "negative"
                                ? theme.palette.mode === "dark"
                                  ? "linear-gradient(135deg, rgba(127,29,29,0.18) 0%, rgba(153,27,27,0.08) 100%)"
                                  : "linear-gradient(135deg, rgba(254,247,247,1) 0%, rgba(252,241,241,1) 100%)"
                                : theme.palette.mode === "dark"
                                  ? "rgba(7, 31, 24, 0.62)"
                                  : "#f9fafb",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.45, mb: 0.12 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: (theme) => {
                                if (bidSummaryDashboard.operatingProfitTone === "positive") {
                                  return theme.palette.mode === "dark" ? "#86efac" : "#166534";
                                }
                                if (bidSummaryDashboard.operatingProfitTone === "negative") {
                                  return theme.palette.mode === "dark" ? "#fca5a5" : "#b91c1c";
                                }
                                return "text.secondary";
                              },
                              fontWeight: 700,
                              letterSpacing: "0.01em",
                              fontSize: 12,
                              lineHeight: 1.2,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Net Operating Profit
                          </Typography>
                          <MaybeTooltip
                            title={
                              bidSummaryDashboard.actualFinancialsState === "pending"
                                ? "Financial totals will appear after the schedule is submitted."
                                : "Net Operating Profit = market result after fees"
                            }
                            arrow
                          >
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: 14,
                                color: "text.secondary",
                                cursor: "help",
                                flexShrink: 0,
                              }}
                            />
                          </MaybeTooltip>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.35, flexWrap: "wrap" }}>
                          <Typography
                            sx={{
                              fontSize: { xs: 16, md: 16.5 },
                              fontWeight: 600,
                              color: (theme) => {
                                if (bidSummaryDashboard.operatingProfitTone === "positive") {
                                  return theme.palette.mode === "dark" ? "#4ade80" : "#15803d";
                                }
                                if (bidSummaryDashboard.operatingProfitTone === "negative") {
                                  return theme.palette.mode === "dark" ? "#f87171" : "#dc2626";
                                }
                                return "text.primary";
                              },
                              lineHeight: 1,
                              opacity:
                                bidScheduleRevenueTotals.netOperatingProfitEur == null &&
                                bidSummaryDashboard.actualFinancialsState !== "available"
                                  ? 0.72
                                  : bidScheduleRevenueTotals.netOperatingProfitEur == null
                                    ? 0.5
                                    : 1,
                            }}
                          >
                            {bidSummaryDashboard.actualFinancialsState === "pending"
                              ? "-"
                              : formatSummaryMetricNumber(bidScheduleRevenueTotals.netOperatingProfitEur, 2) ?? "N/A"}
                          </Typography>
                          {bidSummaryDashboard.actualFinancialsState === "available" && (
                            <Typography sx={{ fontSize: 10.6, fontWeight: 600, color: "text.secondary", lineHeight: 1, letterSpacing: "0.01em" }}>
                              EUR
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Stack>
          </Paper>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              px: { xs: 2.1, md: 2.25 },
              py: { xs: 1.45, md: 1.7 },
              borderRadius: 3,
              border: "1px solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(94, 234, 212, 0.16)" : "divider",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(8, 35, 27, 0.5)" : "background.paper",
            }}
          >
            <Stack spacing={1.1}>
              <Box>
                <Typography variant="caption" sx={{ display: "block", fontWeight: 800, letterSpacing: "0.08em", color: "text.secondary", mb: 0.35 }}>
                  BATTERY HEALTH
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Latest persisted battery state of health for the selected asset and market.
                </Typography>
              </Box>
              {batteryHealthLoading ? (
                <Box
                  sx={{
                    minHeight: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 2,
                    border: "1px dashed",
                    borderColor: "divider",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Loading battery health...
                    </Typography>
                  </Stack>
                </Box>
              ) : batteryHealthError ? (
                <Alert severity="warning">{batteryHealthError}</Alert>
              ) : !batteryHealthDashboard.hasData ? (
                <Box
                  sx={{
                    minHeight: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 2,
                    border: "1px dashed",
                    borderColor: "divider",
                    px: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Battery health will appear after the first submitted schedule with actual cleared results
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={0.8}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={{ xs: 0.75, md: 0.5 }}
                    alignItems={{ xs: "stretch", md: "flex-start" }}
                    justifyContent="flex-start"
                  >
                    <Box
                      sx={{
                        px: 1.35,
                        py: 0.9,
                        minHeight: 0,
                        borderRadius: 1.9,
                        border: "1px solid",
                        borderColor: (theme) =>
                          batteryHealthDashboard.healthTone === "positive"
                            ? theme.palette.mode === "dark"
                              ? "rgba(74, 222, 128, 0.24)"
                              : "rgba(21,128,61,0.14)"
                            : batteryHealthDashboard.healthTone === "warning"
                              ? theme.palette.mode === "dark"
                                ? "rgba(251,191,36,0.24)"
                                : "rgba(217,119,6,0.16)"
                              : batteryHealthDashboard.healthTone === "negative"
                                ? theme.palette.mode === "dark"
                                  ? "rgba(248,113,113,0.24)"
                                  : "rgba(220,38,38,0.14)"
                                : "divider",
                        background: (theme) =>
                          batteryHealthDashboard.healthTone === "positive"
                            ? theme.palette.mode === "dark"
                              ? "linear-gradient(135deg, rgba(20,83,45,0.22) 0%, rgba(21,128,61,0.1) 100%)"
                              : "linear-gradient(135deg, rgba(245,252,247,1) 0%, rgba(240,249,243,1) 100%)"
                            : batteryHealthDashboard.healthTone === "warning"
                              ? theme.palette.mode === "dark"
                                ? "linear-gradient(135deg, rgba(120,53,15,0.22) 0%, rgba(180,83,9,0.08) 100%)"
                                : "linear-gradient(135deg, rgba(255,251,235,1) 0%, rgba(255,247,237,1) 100%)"
                              : theme.palette.mode === "dark"
                                ? "rgba(7, 31, 24, 0.62)"
                                : "#f9fafb",
                        width: "100%",
                        maxWidth: { xs: "100%", md: 230 },
                        flexShrink: 0,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: "0.01em", fontSize: 12, mb: 0.08, display: "block" }}>
                        State of Health
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: 22, md: 24 },
                          fontWeight: 700,
                          lineHeight: 0.95,
                          color: (theme) =>
                            batteryHealthDashboard.healthTone === "positive"
                              ? theme.palette.mode === "dark" ? "#4ade80" : "#15803d"
                              : batteryHealthDashboard.healthTone === "warning"
                                ? theme.palette.mode === "dark" ? "#fbbf24" : "#b45309"
                                : batteryHealthDashboard.healthTone === "negative"
                                  ? theme.palette.mode === "dark" ? "#f87171" : "#dc2626"
                                  : "text.primary",
                        }}
                      >
                        {batteryHealthDashboard.primaryValue}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignSelf: "flex-start",
                        minWidth: 0,
                        pt: { md: 0.35 },
                      }}
                    >
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setBatteryHealthHistoryDialogOpen(true)}
                        sx={{
                          px: 0,
                          minWidth: 0,
                          textTransform: "none",
                          fontWeight: 700,
                          alignSelf: "flex-start",
                        }}
                      >
                        View history
                      </Button>
                    </Box>
                  </Stack>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "1fr 1fr" },
                      gap: 0.7,
                    }}
                  >
                    {batteryHealthDashboard.metrics.map((metric) => (
                      <Box
                        key={metric.label}
                        sx={{
                          px: 1.2,
                          py: 0.82,
                          minHeight: 64,
                          borderRadius: 1.7,
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark" ? "rgba(7, 31, 24, 0.62)" : "#f9fafb",
                          border: "1px solid",
                          borderColor: (theme) =>
                            theme.palette.mode === "dark" ? "rgba(94, 234, 212, 0.18)" : "rgba(229,231,235,0.9)",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          gap: 0.12,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: "0.01em", fontSize: 12, mb: 0.12, lineHeight: 1.2 }}>
                          {metric.label}
                        </Typography>
                        <Typography sx={{ fontSize: { xs: 16, md: 16.5 }, fontWeight: 600, color: "text.primary", lineHeight: 1 }}>
                          {metric.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              )}
            </Stack>
          </Paper>
          <Dialog
            open={batteryHealthHistoryDialogOpen}
            onClose={() => setBatteryHealthHistoryDialogOpen(false)}
            fullWidth
            maxWidth="lg"
          >
            <Box sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Battery Health History
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Daily persisted history by delivery day for the selected asset and market.
                  </Typography>
                </Box>
                <IconButton onClick={() => setBatteryHealthHistoryDialogOpen(false)} aria-label="Close battery health history">
                  <CloseIcon />
                </IconButton>
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 1.5 }}
              >
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <InputLabel>Metric</InputLabel>
                  <Select
                    label="Metric"
                    value={batteryHealthHistoryMetric}
                    onChange={(e) => setBatteryHealthHistoryMetric(e.target.value)}
                  >
                    {BATTERY_HEALTH_HISTORY_METRICS.map((metric) => (
                      <MenuItem key={metric.key} value={metric.key}>
                        {metric.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Box sx={{ minHeight: 420 }}>
                {batteryHealthHistoryLoading ? (
                  <Box
                    sx={{
                      minHeight: 380,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 2,
                      border: "1px dashed",
                      borderColor: "divider",
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} />
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Loading battery health timeline...
                      </Typography>
                    </Stack>
                  </Box>
                ) : batteryHealthHistoryError ? (
                  <Alert severity="warning">{batteryHealthHistoryError}</Alert>
                ) : batteryHealthHistoryChartData.length === 0 ? (
                  <Box
                    sx={{
                      minHeight: 380,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 2,
                      border: "1px dashed",
                      borderColor: "divider",
                      px: 2,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      No battery health history available
                    </Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={batteryHealthHistoryChartData} margin={CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="time"
                        tick={{ fontSize: 12, fontWeight: 700 }}
                        tickFormatter={formatBatteryHealthHistoryDay}
                        axisLine={MARKET_AXIS_LINE_PROPS}
                        tickLine={MARKET_TICK_LINE_PROPS}
                      />
                      <YAxis
                        width={110}
                        tick={AXIS_TICK_SX}
                        tickMargin={8}
                        tickFormatter={selectedBatteryHealthHistoryMetric.axisFormatter}
                        axisLine={MARKET_AXIS_LINE_PROPS}
                        tickLine={MARKET_TICK_LINE_PROPS}
                        label={{
                          value: selectedBatteryHealthHistoryMetric.label,
                          angle: -90,
                          position: "insideLeft",
                          offset: 18,
                          style: AXIS_TITLE_STYLE,
                        }}
                      />
                      <RechartsTooltip content={renderBatteryHealthHistoryTooltip} />
                      <Line
                        dataKey="metricValue"
                        name={selectedBatteryHealthHistoryMetric.label}
                        stroke="#15803d"
                        dot={false}
                        strokeWidth={2.5}
                        connectNulls={false}
                      />
                      <Brush
                        dataKey="time"
                        height={24}
                        travellerWidth={10}
                        tickFormatter={formatBatteryHealthHistoryDay}
                        stroke="#90a4ae"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </Box>
          </Dialog>
          </Box>
          <Paper elevation={3} sx={{ p: 0, borderRadius: 3, overflow: "hidden" }}>
            {bidScheduleLoading ? (
              <Box sx={{ p: 3, display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} /> Loading bid schedule...
              </Box>
            ) : bidScheduleRows.length === 0 ? (
              <Typography variant="body2" sx={{ p: 3, opacity: 0.7 }}>
                No bid rows are available for the selected BD and asset.
              </Typography>
            ) : (
              <TableContainer
                sx={{
                  width: "100%",
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(7, 31, 24, 0.72)" : "background.paper",
                  "& .MuiTableCell-root": {
                    px: 1,
                    py: 0.24,
                    whiteSpace: "nowrap",
                    color: "text.primary",
                  },
                  "& .MuiTableCell-head": {
                    px: 1,
                    py: 0.38,
                    fontWeight: 800,
                    fontSize: 12.5,
                    letterSpacing: "0.02em",
                    color: (theme) => (theme.palette.mode === "dark" ? "#d1fae5" : "#0f172a"),
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "linear-gradient(180deg, rgba(10,46,36,0.98) 0%, rgba(7,31,24,0.98) 100%)"
                        : "linear-gradient(180deg, rgba(248,250,252,1) 0%, rgba(241,245,249,1) 100%)",
                    borderBottom: "1px solid",
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(94,234,212,0.22)"
                        : "rgba(148,163,184,0.28)",
                  },
                  "& .MuiTableBody-root .MuiTableRow-root": {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "rgba(7,31,24,0.52)" : "background.paper",
                  },
                  "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "rgba(10,46,36,0.62)" : "rgba(248,250,252,0.72)",
                  },
                  "& .MuiTableBody-root .MuiTableRow-root:hover": {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "rgba(20,83,45,0.32)" : "rgba(226,232,240,0.42)",
                  },
                  "& .MuiTableBody-root .MuiTableCell-root": {
                    borderBottom: "1px solid",
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(94,234,212,0.14)"
                        : "rgba(226,232,240,0.9)",
                    fontSize: 12.5,
                  },
                }}
              >
                <Table size="small" sx={{ tableLayout: "auto", width: "100%" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" sx={{ minWidth: 240 }}>MTU</TableCell>
                      <TableCell align="center" sx={{ minWidth: 84 }}>Side</TableCell>
                      <TableCell align="center" sx={{ minWidth: 96 }}>Bid Status</TableCell>
                      <TableCell align="center" sx={{ minWidth: 92 }}>Quantity (MW)</TableCell>
                      <TableCell align="center" sx={{ minWidth: 98 }}>Energy (MWh)</TableCell>
                      <TableCell align="center" sx={{ minWidth: 96 }}>Bid Price (€)</TableCell>
                      <TableCell align="center" sx={{ minWidth: 110 }}>Expected Price (€)</TableCell>
                      <TableCell align="center" sx={{ minWidth: 104 }}>Cleared Price (€)</TableCell>
                      <TableCell align="center" sx={{ minWidth: 94 }}>
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.45 }}>
                          <Box component="span">Target SoC (%)</Box>
                          <Tooltip title="Based on the optimization results.">
                            <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ minWidth: 94 }}>Actual SoC (%)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(bidEditMode ? bidDraftRows : bidScheduleRows).map((row, index) => {
                      const isEditable = bidSchedule?.status === "draft" && bidEditMode;
                      const sideMeta = getBidSideMeta(row.side);
                      const bidStatus = normalizeBidRowStatus(row);
                      const bidStatusMeta = getBidStatusMeta(bidStatus);
                      const bidStatusLabel = bidStatus === "idle" ? "-" : bidStatusMeta.label;
                      const clearedPrice = getBidClearedPrice(row);
                      const actualSoc = getBidActualSoc(row);
                      const rowKey =
                        row?.id != null
                          ? `${row.id}-${row.interval_start || index}-${row.interval_end || index}`
                          : `bid-row-${row?.interval_start || "start"}-${row?.interval_end || "end"}-${index}`;
                      return (
                        <TableRow key={rowKey} hover>
                          <TableCell align="center">{formatMtuRange(row.interval_start, row.interval_end)}</TableCell>
                          <TableCell align="center" sx={{ minWidth: 92, fontWeight: 700 }}>
                            {isEditable ? (
                              <Select
                                size="small"
                                fullWidth
                                value={row.side || "idle"}
                                onChange={(e) => handleBidRowChange(row.id, "side", e.target.value)}
                                renderValue={(value) => {
                                  const valueMeta = getBidSideMeta(value);
                                  return (
                                    <Chip
                                      label={valueMeta.label}
                                      sx={{
                                        minWidth: 78,
                                        height: 24,
                                        fontWeight: 800,
                                        letterSpacing: "0.06em",
                                        fontSize: 13,
                                        color: valueMeta.textColor,
                                        backgroundColor: valueMeta.backgroundColor,
                                        border: "1px solid",
                                        borderColor: valueMeta.borderColor,
                                        "& .MuiChip-label": {
                                          px: 1,
                                        },
                                      }}
                                    />
                                  );
                                }}
                                sx={{
                                  minWidth: 104,
                                  "& .MuiOutlinedInput-root": {
                                    minHeight: 30,
                                  },
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: sideMeta.borderColor,
                                  },
                                  "& .MuiSelect-select": {
                                    py: 0.38,
                                    fontSize: 14,
                                    display: "flex",
                                    alignItems: "center",
                                  },
                                }}
                              >
                                {["buy", "sell", "idle"].map((value) => {
                                  const valueMeta = getBidSideMeta(value);
                                  return (
                                    <MenuItem key={value} value={value}>
                                      <Chip
                                        label={valueMeta.label}
                                        sx={{
                                          minWidth: 78,
                                          height: 24,
                                          fontWeight: 800,
                                          letterSpacing: "0.06em",
                                          fontSize: 13,
                                          color: valueMeta.textColor,
                                          backgroundColor: valueMeta.backgroundColor,
                                          border: "1px solid",
                                          borderColor: valueMeta.borderColor,
                                          "& .MuiChip-label": {
                                            px: 1,
                                          },
                                        }}
                                      />
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            ) : (
                              <Chip
                                label={sideMeta.label}
                                sx={{
                                  minWidth: 78,
                                  height: 24,
                                  fontWeight: 800,
                                  letterSpacing: "0.06em",
                                  fontSize: 13,
                                  color: sideMeta.textColor,
                                  backgroundColor: sideMeta.backgroundColor,
                                  border: "1px solid",
                                  borderColor: sideMeta.borderColor,
                                  "& .MuiChip-label": {
                                    px: 1,
                                  },
                                }}
                              >
                                {sideMeta.label}
                              </Chip>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ minWidth: 112 }}>
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 800,
                                letterSpacing: "0.04em",
                                fontSize: 12,
                                color: bidStatus === "idle" ? "text.secondary" : bidStatusMeta.textColor,
                              }}
                            >
                              {bidStatusLabel}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ minWidth: 102 }}>
                            {isEditable ? (
                              <TextField
                                size="small"
                                type="number"
                                value={getBidPowerMw(row)}
                                onChange={(e) => handleBidRowChange(row.id, "quantity_mw", e.target.value)}
                                inputProps={{ step: "0.001" }}
                                sx={{
                                  width: 112,
                                  "& .MuiOutlinedInput-root": { minHeight: 30 },
                                  "& .MuiInputBase-input": { py: 0.38, fontSize: 14 },
                                }}
                              />
                            ) : (
                              <Typography component="span" sx={{ fontWeight: 700 }}>
                                {getBidPowerMw(row).toFixed(3)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">{Number(row.energy_mwh || 0).toFixed(6)}</TableCell>
                          <TableCell align="center" sx={{ minWidth: 96 }}>
                            {isEditable ? (
                              <TextField
                                size="small"
                                type="number"
                                value={row.bid_price}
                                onChange={(e) => handleBidRowChange(row.id, "bid_price", e.target.value)}
                                inputProps={{ step: "0.001" }}
                                sx={{
                                  width: 106,
                                  "& .MuiOutlinedInput-root": { minHeight: 30 },
                                  "& .MuiInputBase-input": { py: 0.38, fontSize: 14 },
                                }}
                              />
                            ) : row?.bid_price != null && Number.isFinite(Number(row.bid_price)) ? (
                              <Typography component="span" sx={{ fontWeight: 700 }}>
                                {Number(row.bid_price).toFixed(3)}
                              </Typography>
                            ) : (
                              "N/A"
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {row.expected_price != null && Number.isFinite(Number(row.expected_price))
                              ? Number(row.expected_price).toFixed(3)
                              : "N/A"}
                          </TableCell>
                          <TableCell align="center">
                            {clearedPrice != null ? Number(clearedPrice).toFixed(3) : "N/A"}
                          </TableCell>
                          <TableCell align="center" sx={{ minWidth: 102 }}>
                            {isEditable ? (
                              <TextField
                                size="small"
                                type="number"
                                value={row.target_soc ?? ""}
                                onChange={(e) => handleBidRowChange(row.id, "target_soc", e.target.value)}
                                inputProps={{ step: "0.01" }}
                                sx={{
                                  width: 104,
                                  "& .MuiOutlinedInput-root": { minHeight: 30 },
                                  "& .MuiInputBase-input": { py: 0.38, fontSize: 14 },
                                }}
                              />
                            ) : row.target_soc != null ? (
                              Number(row.target_soc).toFixed(2)
                            ) : (
                              "N/A"
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {actualSoc != null ? Number(actualSoc).toFixed(2) : "N/A"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
            </Stack></AccordionDetails>
          </Accordion>
        </Stack>
          )}
          {view === "economics" && (
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 2,
              border: "1px solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(94,234,212,0.16)" : "rgba(148,163,184,0.22)",
              background: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(8,35,27,0.58)"
                  : "linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(255,255,255,0.98) 100%)",
              "& .MuiInputBase-root": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(7,31,24,0.72)" : "#fff",
              },
              "& .MuiInputLabel-root": {
                color: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(153,246,228,0.9)" : "rgba(30,64,175,0.9)",
              },
              "& .MuiInputBase-input, & .MuiSelect-select": {
                color: "text.primary",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(94,234,212,0.22)" : "rgba(148,163,184,0.45)",
              },
            }}
          >
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", lg: "center" }}
              justifyContent="space-between"
            >
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} flexWrap="wrap" sx={{ rowGap: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>BESS Asset</InputLabel>
                  <Select
                    label="BESS Asset"
                    value={assetKey}
                    onChange={(e) => setAssetKey(e.target.value)}
                    disabled={bidAssetOptions.length === 0}
                  >
                    {bidAssetOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Refresh economics">
                  <span>
                    <IconButton
                      onClick={refreshEconomicsTab}
                      disabled={bidScheduleLoading || bidScheduleHistoryLoading || !assetKey}
                      sx={{
                        width: 40,
                        height: 40,
                        border: "1px solid",
                        borderColor: (theme) =>
                          theme.palette.mode === "dark" ? "rgba(94,234,212,0.24)" : "rgba(148,163,184,0.28)",
                        borderRadius: 1.5,
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "#99f6e4" : "#1e3a8a",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "rgba(7,31,24,0.72)" : "transparent",
                        "&:hover": {
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark" ? "rgba(10,46,36,0.8)" : "rgba(30,64,175,0.04)",
                        },
                      }}
                    >
                      {bidScheduleLoading || bidScheduleHistoryLoading ? (
                        <CircularProgress size={18} thickness={5} />
                      ) : (
                        <RefreshRoundedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          </Paper>
          {bidScheduleLoading || bidScheduleHistoryLoading ? (
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} /> Loading settled economics...
              </Box>
            </Paper>
          ) : bidScheduleRows.length === 0 && economicsHistorySchedules.length === 0 ? (
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                No submitted bid schedule economics are available for the selected asset.
              </Typography>
            </Paper>
          ) : (
            <>
              {(bidScheduleError || bidScheduleHistoryError) && (
                <Alert severity="warning">
                  {bidScheduleHistoryError || bidScheduleError}
                </Alert>
              )}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(94,234,212,0.18)" : "rgba(15,23,42,0.12)",
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "linear-gradient(180deg, rgba(8,35,27,0.98) 0%, rgba(7,31,24,0.96) 100%)"
                      : "linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 100%)",
                  color: "#f8fafc",
                }}
              >
                <Stack spacing={2.5}>
                  <Stack
                    direction={{ xs: "column", lg: "row" }}
                    spacing={2}
                    alignItems={{ xs: "stretch", lg: "center" }}
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography variant="overline" sx={{ letterSpacing: "0.18em", color: "rgba(148,163,184,0.92)" }}>
                        Operations & Financial Performance
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        Submitted bid schedule overview
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                      <AppDatePicker label="Start date" value={economicsRange.start} onChange={(v) => setEconomicsRange((prev) => ({ ...prev, start: v }))} minDate={economicsAvailableBounds.start || undefined} maxDate={economicsRange.end || economicsAvailableBounds.end || undefined} sx={{ minWidth: 170, "& .MuiOutlinedInput-root": { color: "#e2e8f0", backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(7,31,24,0.64)" : "rgba(15,23,42,0.38)" }, "& .MuiInputLabel-root": { color: (theme) => theme.palette.mode === "dark" ? "rgba(153,246,228,0.82)" : "rgba(226,232,240,0.72)" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: (theme) => theme.palette.mode === "dark" ? "rgba(94,234,212,0.22)" : "rgba(148,163,184,0.22)" } }} />
                      <AppDatePicker label="End date" value={economicsRange.end} onChange={(v) => setEconomicsRange((prev) => ({ ...prev, end: v }))} minDate={economicsRange.start || economicsAvailableBounds.start || undefined} maxDate={economicsAvailableBounds.end || undefined} sx={{ minWidth: 170, "& .MuiOutlinedInput-root": { color: "#e2e8f0", backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(7,31,24,0.64)" : "rgba(15,23,42,0.38)" }, "& .MuiInputLabel-root": { color: (theme) => theme.palette.mode === "dark" ? "rgba(153,246,228,0.82)" : "rgba(226,232,240,0.72)" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: (theme) => theme.palette.mode === "dark" ? "rgba(94,234,212,0.22)" : "rgba(148,163,184,0.22)" } }} />
                      <Button
                        variant="outlined"
                        onClick={handleEconomicsExport}
                        disabled={
                          economicsExporting ||
                          economicsExportMissingFilters ||
                          Boolean(economicsExportValidationError) ||
                          Boolean(demoExportDisabledMessage)
                        }
                        startIcon={economicsExporting ? <CircularProgress size={16} thickness={5} /> : null}
                        sx={{
                          minWidth: 220,
                          textTransform: "none",
                          color: "#e2e8f0",
                          borderColor: "rgba(148,163,184,0.34)",
                        }}
                      >
                        {economicsExporting ? "Exporting..." : "Export Economics Report"}
                      </Button>
                    </Stack>
                  </Stack>
                  {(economicsExportValidationError || economicsExportError || demoExportDisabledMessage) && (
                    <Alert
                      severity={(economicsExportValidationError || demoExportDisabledMessage) && !economicsExportError ? "warning" : "error"}
                    >
                      {economicsExportError || economicsExportValidationError || demoExportDisabledMessage}
                    </Alert>
                  )}
                  <Typography
                    variant="body2"
                    sx={{ color: (theme) => theme.palette.mode === "dark" ? "rgba(167,243,208,0.88)" : "rgba(191,219,254,0.9)", mt: -0.5 }}
                  >
                    Period-scoped metrics use the selected start/end dates. Cumulative profit is the running total up to the selected end date.
                  </Typography>
                  <Grid container spacing={1.5}>
                    {[
                      { label: "Cumulative net operating profit", scope: "Full history to end date", value: formatCurrency(economicsChartKpis.currentCumulativeNetOperatingProfit), accent: "#22c55e" },
                      { label: "Net operating profit", scope: "Selected period", value: formatCurrency(economicsChartKpis.netOperatingProfitOverPeriod), accent: Number(economicsChartKpis.netOperatingProfitOverPeriod || 0) >= 0 ? "#2dd4bf" : "#f97316" },
                      { label: "Gross market profit", scope: "Selected period", value: formatCurrency(economicsChartKpis.grossMarketProfit), accent: "#5eead4" },
                      { label: "Operating costs", scope: "Selected period", value: formatCurrency(economicsChartKpis.operatingCosts), accent: "#f59e0b" },
                      { label: "Settled sell revenue", scope: "Selected period", value: formatCurrency(economicsChartKpis.settledSellRevenue), accent: "#a7f3d0" },
                      { label: "Bid days", scope: "Selected period", value: String(economicsChartKpis.bidDays || 0), accent: "#cbd5e1" },
                    ].map((item) => (
                      <Grid item xs={12} sm={6} md={4} lg={2} key={item.label}>
                        <Box sx={{ p: 1.1, borderRadius: 1.75, border: "1px solid", borderColor: (theme) => theme.palette.mode === "dark" ? "rgba(94,234,212,0.16)" : "rgba(148,163,184,0.16)", backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(7,31,24,0.46)" : "rgba(15,23,42,0.32)", minHeight: 74 }}>
                          <Typography variant="caption" sx={{ color: "rgba(148,163,184,0.9)", textTransform: "uppercase", letterSpacing: "0.12em" }}>{item.label}</Typography>
                          <Typography variant="caption" sx={{ display: "block", mt: 0.2, color: (theme) => theme.palette.mode === "dark" ? "rgba(167,243,208,0.72)" : "rgba(191,219,254,0.78)", letterSpacing: "0.04em" }}>{item.scope}</Typography>
                          <Typography variant="h6" sx={{ mt: 0.7, fontWeight: 800, color: item.accent, fontSize: { xs: "1.05rem", md: "1.15rem" } }}>{item.value}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  <Box
                    sx={{
                      height: 360,
                      "--economics-chart-axis-color": "#cbd5e1",
                      "--economics-chart-axis-line-color": "rgba(148,163,184,0.28)",
                      "& .recharts-wrapper, & .recharts-surface, & .recharts-cartesian-axis": {
                        color: "var(--economics-chart-axis-color)",
                      },
                      "& .recharts-cartesian-axis-tick-value, & .recharts-text": {
                        fill: "var(--economics-chart-axis-color) !important",
                      },
                      "& .recharts-cartesian-axis-line, & .recharts-cartesian-axis-tick-line": {
                        stroke: "var(--economics-chart-axis-line-color) !important",
                      },
                    }}
                  >
                    {economicsChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={economicsChartData} margin={{ top: 12, right: 16, left: 0, bottom: 4 }}>
                          <CartesianGrid stroke={economicsOverviewChartColors.grid} strokeDasharray="3 3" />
                          <XAxis
                            dataKey="bidDayLabel"
                            stroke={economicsOverviewChartColors.axis}
                            tick={{ fill: economicsOverviewChartColors.tick, stroke: "none", fontSize: 12 }}
                            axisLine={{ stroke: economicsOverviewChartColors.axis }}
                            tickLine={{ stroke: economicsOverviewChartColors.axis }}
                          />
                          <YAxis
                            stroke={economicsOverviewChartColors.axis}
                            tick={{ fill: economicsOverviewChartColors.tick, stroke: "none", fontSize: 12 }}
                            axisLine={{ stroke: economicsOverviewChartColors.axis }}
                            tickLine={{ stroke: economicsOverviewChartColors.axis }}
                            tickFormatter={(value) => formatSummaryMetricNumber(value, 0) || "0"}
                          />
                          <RechartsTooltip
                            content={({ active, payload }) => {
                              if (!active || !payload?.length) return null;
                              const point = payload[0]?.payload;
                              if (!point) return null;
                              return (
                                <Box
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 1.5,
                                    backgroundColor: "rgba(7,31,24,0.96)",
                                    border: "1px solid rgba(94,234,212,0.22)",
                                    color: "#f8fafc",
                                  }}
                                >
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                                    Bid day: {formatBidDayLabel(point.bidDay)}
                                  </Typography>
                                  <Typography variant="body2">Daily net operating profit: {formatCurrency(point.dailyNetOperatingProfit)}</Typography>
                                  <Typography variant="body2">Cumulative net operating profit: {formatCurrency(point.cumulativeNetOperatingProfit)}</Typography>
                                  <Typography variant="body2">Gross market profit: {formatCurrency(point.grossMarketProfit)}</Typography>
                                  <Typography variant="body2">Operating costs: {formatCurrency(point.operatingCosts)}</Typography>
                                </Box>
                              );
                            }}
                          />
                          <Line type="monotone" dataKey="cumulativeNetOperatingProfit" stroke="#22c55e" strokeWidth={3} dot={{ r: 2, fill: "#22c55e", strokeWidth: 0 }} activeDot={{ r: 5 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 2,
                          border: "1px dashed rgba(148,163,184,0.16)",
                          backgroundColor: "rgba(7,31,24,0.3)",
                          px: 3,
                          textAlign: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#e2e8f0", mb: 0.75 }}>
                            No submitted bid schedule history yet
                          </Typography>
                          <Typography variant="body2" sx={{ color: "rgba(226,232,240,0.72)" }}>
                            The cumulative PnL chart will appear once submitted bid schedules are available for this asset.
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Stack>
              </Paper>
                <Grid container spacing={2}>
                  {[
                  { title: "Net Economic Profit", value: formatCurrency(activeEconomicsFinancials.netEconomicProfitEur), accent: Number(activeEconomicsFinancials.netEconomicProfitEur || 0) >= 0 ? "#16a34a" : "#dc2626", subtitle: "Net Economic Profit = net operating profit after degradation cost" },
                  { title: "Net Operating Profit", value: formatCurrency(activeEconomicsFinancials.netOperatingProfitEur), accent: Number(activeEconomicsFinancials.netOperatingProfitEur || 0) >= 0 ? "#15803d" : "#b91c1c", subtitle: "Net Operating Profit = market result after fees" },
                  { title: "Gross Market Profit", value: formatCurrency(activeEconomicsFinancials.grossMarketProfitEur), accent: Number(activeEconomicsFinancials.grossMarketProfitEur || 0) >= 0 ? "#22c55e" : "#dc2626", subtitle: activeEconomicsSubtitle },
                  { title: "Operating Costs", value: formatCurrency(activeEconomicsFinancials.operatingCostsEur), accent: "#7c3aed", subtitle: "Backend total of market and grid fees" },
                  { title: "Degradation Cost", value: formatCurrency(activeEconomicsFinancials.degradationCostEur), accent: "#f97316", subtitle: "Shown separately from operating profit" },
                  { title: "Accepted Sell Energy", value: formatEnergyMWh(activeEconomicsFinancials.acceptedSellEnergyMwh), accent: "#0284c7", subtitle: "Backend settled total" },
                  { title: "Accepted Buy Energy", value: formatEnergyMWh(activeEconomicsFinancials.acceptedBuyEnergyMwh), accent: "#d97706", subtitle: "Backend settled total" },
                  { title: "Accepted Traded Energy", value: formatEnergyMWh(activeEconomicsFinancials.acceptedTradedEnergyMwh), accent: "#0f766e", subtitle: "Backend settled total" },
                  ].map((metric) => (
                  <Grid item xs={12} sm={6} md={3} lg={2} key={metric.title}>
                    <Paper elevation={0} sx={{ p: 1.1, borderRadius: 1.35, border: "1px solid rgba(148,163,184,0.2)", height: "100%", minHeight: 82 }}>
                      <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: "0.09em", fontSize: 10.5, lineHeight: 1.2 }}>{metric.title}</Typography>
                      <Typography sx={{ fontWeight: 800, color: metric.accent, mt: 0.3, fontSize: { xs: "0.95rem", md: "1.02rem" }, lineHeight: 1.15 }}>{metric.value}</Typography>
                      <Typography variant="body2" sx={{ mt: 0.45, color: "text.secondary", fontSize: 11.5, lineHeight: 1.25 }}>{metric.subtitle}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
                Net Operating Profit = market result after fees. Net Economic Profit = net operating profit after degradation cost.
              </Typography>
              <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid rgba(148,163,184,0.2)", overflow: "hidden" }}>
                <Box sx={{ p: 2.25, borderBottom: "1px solid rgba(148,163,184,0.16)" }}>
                  <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems={{ xs: "stretch", lg: "center" }} justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>Submitted bid process list</Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>Latest bid days first. Open details for full process metadata and SoC outcome.</Typography>
                    </Box>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                      <AppDatePicker label="Table start" value={economicsTableRange.start} onChange={(v) => setEconomicsTableRange((prev) => ({ ...prev, start: v }))} sx={{ minWidth: 165 }} />
                      <AppDatePicker label="Table end" value={economicsTableRange.end} onChange={(v) => setEconomicsTableRange((prev) => ({ ...prev, end: v }))} sx={{ minWidth: 165 }} />
                    </Stack>
                  </Stack>
                </Box>
                <TableContainer sx={{ maxHeight: 520 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                          <TableCell>Bid Day</TableCell>
                          <TableCell>BESS Asset</TableCell>
                          <TableCell align="right">Net Economic Profit</TableCell>
                          <TableCell align="right">Net Operating Profit</TableCell>
                          <TableCell align="right">Gross Market Profit</TableCell>
                          <TableCell align="right">Operating Costs</TableCell>
                          <TableCell align="right">Degradation Cost</TableCell>
                          <TableCell align="right">Accepted MTUs</TableCell>
                          <TableCell align="right">Rejected MTUs</TableCell>
                          <TableCell align="right">Pending MTUs</TableCell>
                        <TableCell>Submitted At</TableCell>
                        <TableCell>Last Updated</TableCell>
                        <TableCell align="right">Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {economicsTableSchedules.map((item) => (
                        <TableRow
                          hover
                          key={item?.id || `${item?.bid_day}-${item?.submitted_at}`}
                          selected={String(selectedEconomicsProcess?.id || `${selectedEconomicsProcess?.bid_day || ""}-${selectedEconomicsProcess?.submitted_at || ""}`) === String(item?.id || `${item?.bid_day || ""}-${item?.submitted_at || ""}`)}
                          onClick={() => setSelectedEconomicsProcess(item)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell sx={{ fontWeight: 700 }}>{formatBidDayLabel(item?.bid_day)}</TableCell>
                          <TableCell>{item?.asset_id || item?.assetKey || assetKey || "N/A"}</TableCell>
                          <TableCell align="right" sx={{ color: Number(item?.financials?.netEconomicProfitEur || 0) >= 0 ? "#15803d" : "#b91c1c", fontWeight: 700 }}>{formatCurrency(item?.financials?.netEconomicProfitEur)}</TableCell>
                          <TableCell align="right" sx={{ color: Number(item?.financials?.netOperatingProfitEur || 0) >= 0 ? "#15803d" : "#b91c1c", fontWeight: 700 }}>{formatCurrency(item?.financials?.netOperatingProfitEur)}</TableCell>
                          <TableCell align="right">{formatCurrency(item?.financials?.grossMarketProfitEur)}</TableCell>
                          <TableCell align="right">{formatCurrency(item?.financials?.operatingCostsEur)}</TableCell>
                          <TableCell align="right">{formatCurrency(item?.financials?.degradationCostEur)}</TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: getMtuCountColor("Accepted MTUs", item?.summary?.acceptedCount), fontWeight: 700 }}
                          >
                            {item?.summary?.acceptedCount ?? 0}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: getMtuCountColor("Rejected MTUs", item?.summary?.rejectedCount), fontWeight: 700 }}
                          >
                            {item?.summary?.rejectedCount ?? 0}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: getMtuCountColor("Pending MTUs", item?.summary?.pendingCount), fontWeight: 700 }}
                          >
                            {item?.summary?.pendingCount ?? 0}
                          </TableCell>
                          <TableCell>{formatDateTimeValue(item?.submitted_at)}</TableCell>
                          <TableCell>{formatDateTimeValue(item?.updated_at)}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedEconomicsProcess(item);
                                setEconomicsDetailsProcess(item);
                              }}
                            >
                              Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {economicsTableSchedules.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={13} sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                            No submitted bid processes found in the selected table date range.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              <Box sx={{ display: "none" }}>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Chip color="success" variant="outlined" label={`Settled net revenue: ${bidScheduleRevenueTotals.actualNetRevenueEur != null ? bidScheduleRevenueTotals.actualNetRevenueEur.toFixed(2) : "N/A"} €`} sx={{ "& .MuiChip-label": { fontWeight: 700 } }} />
                <Chip color="success" variant="outlined" label={`Settled sell revenue: ${bidScheduleRevenueTotals.actualSellRevenueEur != null ? bidScheduleRevenueTotals.actualSellRevenueEur.toFixed(2) : "N/A"} €`} sx={{ "& .MuiChip-label": { fontWeight: 700 } }} />
                <Chip color="warning" variant="outlined" label={`Settled buy cost: ${bidScheduleRevenueTotals.actualBuyCostEur != null ? bidScheduleRevenueTotals.actualBuyCostEur.toFixed(2) : "N/A"} €`} sx={{ "& .MuiChip-label": { fontWeight: 700 } }} />
                <Chip variant="outlined" label={`Accepted rows: ${bidEconomicsSummary.acceptedCount}`} />
                <Chip variant="outlined" label={`Rejected rows: ${bidEconomicsSummary.rejectedCount}`} />
                <Chip variant="outlined" label={`Pending rows: ${bidEconomicsSummary.pendingCount}`} />
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper elevation={3} sx={{ p: 2.5, borderRadius: 3, height: "100%" }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
                      Settled Totals
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body2"><strong>Accepted sell energy:</strong> {bidEconomicsSummary.acceptedSellEnergyMWh.toFixed(3)} MWh</Typography>
                      <Typography variant="body2"><strong>Accepted buy energy:</strong> {bidEconomicsSummary.acceptedBuyEnergyMWh.toFixed(3)} MWh</Typography>
                      <Typography variant="body2"><strong>Avg accepted sell price:</strong> {bidEconomicsSummary.avgAcceptedSellPrice != null ? bidEconomicsSummary.avgAcceptedSellPrice.toFixed(2) : "N/A"} €/MWh</Typography>
                      <Typography variant="body2"><strong>Avg accepted buy price:</strong> {bidEconomicsSummary.avgAcceptedBuyPrice != null ? bidEconomicsSummary.avgAcceptedBuyPrice.toFixed(2) : "N/A"} €/MWh</Typography>
                      <Typography variant="body2"><strong>Rows with cleared price:</strong> {bidEconomicsSummary.pricedAcceptedCount}</Typography>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper elevation={3} sx={{ p: 2.5, borderRadius: 3, height: "100%" }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
                      Bid Benchmarks
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body2"><strong>Avg bid sell price:</strong> {bidEconomicsSummary.avgBidSellPrice != null ? bidEconomicsSummary.avgBidSellPrice.toFixed(2) : "N/A"} €/MWh</Typography>
                      <Typography variant="body2"><strong>Avg bid buy price:</strong> {bidEconomicsSummary.avgBidBuyPrice != null ? bidEconomicsSummary.avgBidBuyPrice.toFixed(2) : "N/A"} €/MWh</Typography>
                      <Typography variant="body2"><strong>Avg expected sell price:</strong> {bidEconomicsSummary.avgExpectedSellPrice != null ? bidEconomicsSummary.avgExpectedSellPrice.toFixed(2) : "N/A"} €/MWh</Typography>
                      <Typography variant="body2"><strong>Avg expected buy price:</strong> {bidEconomicsSummary.avgExpectedBuyPrice != null ? bidEconomicsSummary.avgExpectedBuyPrice.toFixed(2) : "N/A"} €/MWh</Typography>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper elevation={3} sx={{ p: 2.5, borderRadius: 3, height: "100%" }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
                      SoC Outcome
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body2"><strong>Final target SoC:</strong> {bidEconomicsSummary.targetSocEnd != null ? bidEconomicsSummary.targetSocEnd.toFixed(2) : "N/A"} %</Typography>
                      <Typography variant="body2"><strong>Final actual SoC:</strong> {bidEconomicsSummary.actualSocEnd != null ? bidEconomicsSummary.actualSocEnd.toFixed(2) : "N/A"} %</Typography>
                      <Typography variant="body2"><strong>Total MTUs:</strong> {bidEconomicsSummary.totalRows}</Typography>
                      <Typography variant="body2"><strong>Idle MTUs:</strong> {bidEconomicsSummary.idleCount}</Typography>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
              <Paper elevation={3} sx={{ p: 0, borderRadius: 3, overflow: "hidden" }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Metric</TableCell>
                        <TableCell align="right">Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[
                        ["Settled Net Revenue (€)", bidScheduleRevenueTotals.actualNetRevenueEur != null ? bidScheduleRevenueTotals.actualNetRevenueEur.toFixed(2) : "N/A"],
                        ["Settled Sell Revenue (€)", bidScheduleRevenueTotals.actualSellRevenueEur != null ? bidScheduleRevenueTotals.actualSellRevenueEur.toFixed(2) : "N/A"],
                        ["Settled Buy Cost (€)", bidScheduleRevenueTotals.actualBuyCostEur != null ? bidScheduleRevenueTotals.actualBuyCostEur.toFixed(2) : "N/A"],
                        ["Accepted Sell Energy (MWh)", bidEconomicsSummary.acceptedSellEnergyMWh.toFixed(3)],
                        ["Accepted Buy Energy (MWh)", bidEconomicsSummary.acceptedBuyEnergyMWh.toFixed(3)],
                        ["Accepted Rows", bidEconomicsSummary.acceptedCount],
                        ["Rejected Rows", bidEconomicsSummary.rejectedCount],
                        ["Pending Rows", bidEconomicsSummary.pendingCount],
                      ].map(([label, value]) => (
                        <TableRow key={label}>
                          <TableCell sx={{ fontWeight: 700 }}>{label}</TableCell>
                          <TableCell align="right">{value}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              <Paper elevation={3} sx={{ p: 0, borderRadius: 3, overflow: "hidden" }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Bid Day</TableCell>
                        <TableCell align="right">Accepted Sell MWh</TableCell>
                        <TableCell align="right">Accepted Buy MWh</TableCell>
                        <TableCell align="right">Sell Revenue (€)</TableCell>
                        <TableCell align="right">Buy Cost (€)</TableCell>
                        <TableCell align="right">Net (€)</TableCell>
                        <TableCell align="right">Accepted</TableCell>
                        <TableCell align="right">Rejected</TableCell>
                        <TableCell align="right">Pending</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bidEconomicsSummary.dailyRows.map((row) => (
                        <TableRow key={row.day}>
                          <TableCell sx={{ fontWeight: 700 }}>{row.day}</TableCell>
                          <TableCell align="right">{row.acceptedSellEnergyMWh.toFixed(3)}</TableCell>
                          <TableCell align="right">{row.acceptedBuyEnergyMWh.toFixed(3)}</TableCell>
                          <TableCell align="right">{row.grossSellRevenue.toFixed(2)}</TableCell>
                          <TableCell align="right">{row.grossBuyCost.toFixed(2)}</TableCell>
                          <TableCell align="right">{(row.grossSellRevenue - row.grossBuyCost).toFixed(2)}</TableCell>
                          <TableCell align="right">{row.acceptedCount}</TableCell>
                          <TableCell align="right">{row.rejectedCount}</TableCell>
                          <TableCell align="right">{row.pendingCount}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              </Box>
            </>
          )}
          <Drawer
            anchor="right"
            open={Boolean(economicsDetailsProcess)}
            onClose={() => setEconomicsDetailsProcess(null)}
            PaperProps={{
              sx: {
                width: { xs: "100%", sm: 420 },
                overflow: "hidden",
              },
            }}
          >
            {economicsDetailsProcess && (
              <Box sx={{ height: "100%", overflowY: "auto", pt: { xs: 8, sm: 9 } }}>
                <Box
                  sx={{
                    position: "sticky",
                    top: { xs: 8, sm: 9 },
                    zIndex: 1,
                    px: 2.5,
                    pt: 2.5,
                    pb: 2,
                    backgroundColor: "#fff",
                    borderBottom: "1px solid rgba(148,163,184,0.16)",
                    boxShadow: "0 1px 0 rgba(148,163,184,0.12)",
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="overline" sx={{ letterSpacing: "0.12em", color: "text.secondary" }}>
                        Bid Process Details
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, wordBreak: "break-word" }}>
                        {formatBidDayLabel(economicsDetailsProcess?.bid_day)}
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={() => setEconomicsDetailsProcess(null)}
                      aria-label="Close details"
                      sx={{
                        mt: -0.5,
                        mr: -0.5,
                        border: "1px solid rgba(148,163,184,0.24)",
                        backgroundColor: "#fff",
                        boxShadow: "0 1px 2px rgba(15,23,42,0.08)",
                        flexShrink: 0,
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                </Box>
                <Stack spacing={1.25} sx={{ px: 2.5, py: 2 }}>
                  {[
                    ["Bid Day", formatBidDayLabel(economicsDetailsProcess?.bid_day)],
                    ["BESS Asset", economicsDetailsProcess?.asset_id || economicsDetailsProcess?.assetKey || assetKey || "N/A"],
                    ["Status", economicsDetailsProcess?.status || "N/A"],
                    ["Source Run Timestamp", formatDateTimeValue(getScheduleSourceRunTimestamp(economicsDetailsProcess, runs))],
                    ["Submitted At", formatDateTimeValue(economicsDetailsProcess?.submitted_at)],
                    ["Last Updated", formatDateTimeValue(economicsDetailsProcess?.updated_at)],
                    ["Source Run ID", economicsDetailsProcess?.source_run_id || "N/A"],
                    ["Accepted MTUs", String(economicsDetailsProcess?.summary?.acceptedCount ?? 0)],
                    ["Rejected MTUs", String(economicsDetailsProcess?.summary?.rejectedCount ?? 0)],
                    ["Pending MTUs", String(economicsDetailsProcess?.summary?.pendingCount ?? 0)],
                    ["Accepted Sell Energy", formatEnergyMWh(economicsDetailsProcess?.financials?.acceptedSellEnergyMwh)],
                    ["Accepted Buy Energy", formatEnergyMWh(economicsDetailsProcess?.financials?.acceptedBuyEnergyMwh)],
                    ["Accepted Traded Energy", formatEnergyMWh(economicsDetailsProcess?.financials?.acceptedTradedEnergyMwh)],
                    ["Gross Market Profit", formatCurrency(economicsDetailsProcess?.financials?.grossMarketProfitEur)],
                    ["Net Operating Profit", formatCurrency(economicsDetailsProcess?.financials?.netOperatingProfitEur)],
                    ["Net Economic Profit", formatCurrency(economicsDetailsProcess?.financials?.netEconomicProfitEur)],
                    ["Operating Costs", formatCurrency(economicsDetailsProcess?.financials?.operatingCostsEur)],
                    ["Degradation Method", economicsDetailsProcess?.financials?.degradationMethod == null ? "N/A" : economicsDetailsProcess.financials.degradationMethod === "throughput" ? "Throughput" : "Discharge only"],
                    ["Degradation Basis Energy", economicsDetailsProcess?.financials?.degradationBasisEnergyMwh != null ? `${economicsDetailsProcess.financials.degradationBasisEnergyMwh.toFixed(3)} MWh` : "N/A"],
                    ["Degradation Cost", formatCurrency(economicsDetailsProcess?.financials?.degradationCostEur)],
                    ["Market Fee", formatCurrency(economicsDetailsProcess?.financials?.marketFeeEur)],
                    ["Grid Import Fee", formatCurrency(economicsDetailsProcess?.financials?.gridImportFeeEur)],
                    ["Grid Export Fee", formatCurrency(economicsDetailsProcess?.financials?.gridExportFeeEur)],
                    ["Settled Sell Revenue", formatCurrency(economicsDetailsProcess?.financials?.settledSellRevenueEur)],
                    ["Settled Buy Cost", formatCurrency(economicsDetailsProcess?.financials?.settledBuyCostEur)],
                    ["Settled Net Revenue", formatCurrency(economicsDetailsProcess?.financials?.settledNetRevenueEur)],
                    ["Final Target SoC", economicsDetailsProcess?.summary?.targetSocEnd != null ? `${economicsDetailsProcess.summary.targetSocEnd.toFixed(2)}%` : "N/A"],
                    ["Final Actual SoC", economicsDetailsProcess?.summary?.actualSocEnd != null ? `${economicsDetailsProcess.summary.actualSocEnd.toFixed(2)}%` : "N/A"],
                    ["Total MTUs", String(economicsDetailsProcess?.summary?.totalRows ?? 0)],
                    ["Idle MTUs", String(economicsDetailsProcess?.summary?.idleCount ?? 0)],
                  ].map(([label, value]) => (
                    <Box
                      key={label}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2,
                        py: 1,
                        borderBottom: "1px solid rgba(148,163,184,0.16)",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>{label}</Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          textAlign: "right",
                          color: getMtuCountColor(label, value) || "inherit",
                        }}
                      >
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Drawer>
        </Stack>
          )}
      {view === "optimization" && (
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
        >
          <Stack spacing={1.25} sx={{ width: "100%" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Optimization Results
            </Typography>
            <Typography variant="caption" color="text.secondary">Delivery period times: {optimizationChartTimeZone}</Typography>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", md: "center" }}
              flexWrap="wrap"
              sx={{ rowGap: 1.5 }}
            >
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Run</InputLabel>
              <Select
                label="Run"
                value={selectedRunId}
                onChange={(e) => setSelectedRunId(e.target.value)}
                disabled={loadingRuns || runs.length === 0}
                MenuProps={{
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  PaperProps: {
                    sx: {
                      mt: 0.5,
                      maxHeight: 240,
                      minWidth: 220,
                    },
                  },
                  MenuListProps: { dense: true },
                }}
              >
                {runs.map((r, index) => {
                  const id = normalizeRunId(r);
                  const started = r.started_at || r.created_at || "";
                  const executionLabel = started ? new Date(started).toLocaleString("en-GB") : id.slice(0, 8);
                  const deliveryLabel = formatRunDeliveryPeriod(r, operationalTimeZone(config));
                  const label = `${deliveryLabel} · run ${executionLabel}`;
                  return (
                    <MenuItem key={id || `run-${index}`} value={id}>
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {chartData.assets?.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>BESS Asset</InputLabel>
                <Select
                  label="BESS Asset"
                  value={assetKey}
                  onChange={(e) => setAssetKey(e.target.value)}
                >
                  {chartData.assets.map((k) => (
                    <MenuItem key={k} value={k}>
                      {k}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
              <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                Delivery day:
              </Typography>
              <Typography variant="body2" sx={{ minWidth: 200 }}>
                {formatRunDeliveryPeriod(
                  runDeliveryInfo?.run_id === String(selectedRunId)
                    ? runDeliveryInfo
                    : runs.find((run) => normalizeRunId(run) === String(selectedRunId)) || {},
                  operationalTimeZone(config)
                )}
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={handleExportExcel}
                disabled={!runSuccess || exportingExcel || loadingResult || Boolean(demoExportDisabledMessage)}
                sx={{ textTransform: "none", minWidth: 160 }}
              >
                {exportingExcel ? "Exporting..." : "Download Excel"}
              </Button>
            </Stack>
          </Stack>
          </Stack>
        </Stack>
          )}
      {view === "optimization" && (exportError || demoExportDisabledMessage) && (
        <Alert severity={exportError ? "error" : "warning"} sx={{ mt: 2 }}>
          {exportError || demoExportDisabledMessage}
        </Alert>
      )}
      {view === "optimization" && (
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3, position: "relative" }}>
          {chartData.assets?.length > 0 && (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              {(() => {
                const chosen = assetKey || chartData.assets[0];
                const cap = kpis[chosen]?.Capacity ?? kpis[chosen]?.capacity ?? null;
                const estimatedRev = kpis[chosen]?.Profit ?? kpis[chosen]?.profit ?? null;
                return (
                  <Stack
                    key={`metric-${chosen}`}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ p: 1.5, borderRadius: 2, border: "1px solid", borderColor: "divider" }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>{chosen}</Typography>
                    {cap != null && (
                      <Chip
                        label={`Capacity: ${(Number(cap) / 1000).toFixed(2)} MWh`}
                        size="big"
                        color="primary"
                        variant="outlined"
                        sx={{ "& .MuiChip-label": { fontSize: 14, fontWeight: 700 } }}
                      />
                    )}
                    {estimatedRev != null && (
                      <Chip
                        label={`Revenues (Estimated): ${Number(estimatedRev).toFixed(2)} €`}
                        size="big"
                        color="success"
                        variant="outlined"
                        sx={{ "& .MuiChip-label": { fontSize: 14, fontWeight: 700 } }}
                      />
                    )}
                  </Stack>
                );
              })()}
            </Box>
          )}
        <Stack spacing={1.25} alignItems="stretch">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Energy Series
            </Typography>
            <IconButton size="small" onClick={() => setFullscreen("energy")} title="Full screen" sx={FULLSCREEN_BTN_SX}>
              <FullscreenGlyph fontSize="small" />
            </IconButton>
          </Stack>
          <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Filter Series:
                </Typography>
                {visibleEnergySeriesConfig.map((series) => (
                  <Box key={series.key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "2px",
                        backgroundColor: series.color,
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {series.filterLabel}
                    </Typography>
                    <Checkbox
                      size="small"
                      checked={seriesVisible[series.key]}
                      onChange={(e) =>
                        setSeriesVisible((prev) => ({ ...prev, [series.key]: e.target.checked }))
                      }
                      sx={{ ml: 0.5 }}
                    />
                  </Box>
                ))}
              </Stack>
              {!loadingResult && energySeriesWithDegradation.length > 0 && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Display mode:
                </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={energyDisplayMode}
                    onChange={(_event, value) => {
                      if (value) setEnergyDisplayMode(value);
                    }}
                    sx={MARKET_VIEW_TOGGLE_SX}
                  >
                    <Tooltip title="Graph view" arrow>
                      <ToggleButton value="chart" aria-label="Graph view">
                        <MultilineChartRoundedIcon fontSize="inherit" />
                      </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Table view" arrow>
                      <ToggleButton value="table" aria-label="Table view">
                        <TableChartOutlinedIcon fontSize="inherit" />
                      </ToggleButton>
                    </Tooltip>
                  </ToggleButtonGroup>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={chartDisplayMode}
                    onChange={(event, value) => {
                      if (value) setChartDisplayMode(value);
                    }}
                    color="primary"
                    sx={{ borderRadius: 1 }}
                    disabled={energyDisplayMode !== "chart"}
                  >
                    <ToggleButton value="area">Area</ToggleButton>
                    <ToggleButton value="line">Line</ToggleButton>
                    <ToggleButton value="bar">Bar</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              )}
            </Stack>
        </Stack>
        <Box sx={{ mt: 1, height: 480 }}>
          {loadingResult ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} /> Loading run data...
            </Box>
          ) : energySeriesWithDegradation.length === 0 ? (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No data available for the selected run.
              {chartData.available?.length
                ? ` Available fields: ${chartData.available.join(", ")}`
                : ""}
            </Typography>
          ) : energyDisplayMode === "table" ? (
            renderOptimizationEnergyTable()
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              {chartDisplayMode === "line" ? (
                <LineChart data={optimizationDisplayRows} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" />
                    <XAxis {...getOptimizationXAxisProps(optimizationDisplayRows)} />
                  <YAxis
                    yAxisId="left"
                    {...OPTIMIZATION_LEFT_AXIS_PROPS}
                    tickCount={6}
                    tickFormatter={formatOptimizationEnergyAxisValue}
                    label={{
                      value: "Power [MW]",
                      angle: -90,
                      position: "insideLeft",
                      offset: OPTIMIZATION_LEFT_AXIS_LABEL_OFFSET,
                      style: AXIS_TITLE_STYLE,
                    }}
                    axisLine={{ strokeWidth: 1.5, stroke: "#90a4ae" }}
                    tickLine={{ stroke: "#90a4ae" }}
                  />
                  {showEfcAxis && (
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      width={104}
                      tick={AXIS_TICK_SX}
                      tickMargin={8}
                      label={{
                        value: "Cycles [EFC]",
                        angle: 90,
                        position: "insideRight",
                        offset: 20,
                        style: AXIS_TITLE_STYLE,
                      }}
                      axisLine={{ strokeWidth: 1.5, stroke: "#90a4ae" }}
                      tickLine={{ stroke: "#90a4ae" }}
                    />
                  )}
                  <RechartsTooltip
                    content={
                      <DashboardTooltip
                        labelFormatter={(label, payload) => formatOptimizationLabelFromPayload(label, payload, true)}
                        valueFormatter={(value, name, entry) => {
                          const key = entry?.dataKey;
                          if (key === "deg_efc") return formatOptimizationEnergyValue(value, "EFC");
                          if (typeof key === "string" && key.endsWith("_mw")) {
                            return formatOptimizationEnergyValue(value, "MW");
                          }
                          return formatTooltipSmart(value, name, entry);
                        }}
                      />
                    }
                  />
                  {renderEnergySeriesElements("line")}
                  <Brush
                    dataKey="time"
                    height={24}
                    travellerWidth={10}
                    tickFormatter={formatOptimizationTickFromRows(optimizationDisplayRows)}
                    stroke="#90a4ae"
                  />
                </LineChart>
              ) : chartDisplayMode === "bar" ? (
              <BarChart data={optimizationDisplayRows} margin={CHART_MARGIN} {...BAR_CHART_PROPS}>
                  <CartesianGrid strokeDasharray="3 3" />
                    <XAxis {...getOptimizationXAxisProps(optimizationDisplayRows)} />
                  <YAxis
                    yAxisId="left"
                    {...OPTIMIZATION_LEFT_AXIS_PROPS}
                    tickCount={6}
                    tickFormatter={formatOptimizationEnergyAxisValue}
                    label={{
                      value: "Power [MW]",
                      angle: -90,
                      position: "insideLeft",
                      offset: OPTIMIZATION_LEFT_AXIS_LABEL_OFFSET,
                      style: AXIS_TITLE_STYLE,
                    }}
                    axisLine={{ strokeWidth: 1.5, stroke: "#90a4ae" }}
                    tickLine={{ stroke: "#90a4ae" }}
                  />
                  {showEfcAxis && (
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      width={104}
                      tick={AXIS_TICK_SX}
                      tickMargin={8}
                      label={{
                        value: "Cycles [EFC]",
                        angle: 90,
                        position: "insideRight",
                        offset: 20,
                        style: AXIS_TITLE_STYLE,
                      }}
                      axisLine={{ strokeWidth: 1.5, stroke: "#90a4ae" }}
                      tickLine={{ stroke: "#90a4ae" }}
                    />
                  )}
                  <RechartsTooltip
                    content={
                      <DashboardTooltip
                        labelFormatter={(label, payload) => formatOptimizationLabelFromPayload(label, payload, true)}
                        valueFormatter={(value, name, entry) => {
                          const key = entry?.dataKey;
                          if (key === "deg_efc") return formatOptimizationEnergyValue(value, "EFC");
                          if (typeof key === "string" && key.endsWith("_mw")) {
                            return formatOptimizationEnergyValue(value, "MW");
                          }
                          return formatTooltipSmart(value, name, entry);
                        }}
                      />
                    }
                  />
                  {renderEnergySeriesElements("bar")}
                    <Brush
                      dataKey="time"
                      height={24}
                      travellerWidth={10}
                      tickFormatter={formatOptimizationTickFromRows(optimizationDisplayRows)}
                      stroke="#90a4ae"
                    />
                </BarChart>
              ) : (
                <AreaChart data={optimizationDisplayRows} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" />
                    <XAxis {...getOptimizationXAxisProps(optimizationDisplayRows)} />
                  <YAxis
                    yAxisId="left"
                    {...OPTIMIZATION_LEFT_AXIS_PROPS}
                    tickCount={6}
                    tickFormatter={formatOptimizationEnergyAxisValue}
                    label={{
                      value: "Power [MW]",
                      angle: -90,
                      position: "insideLeft",
                      offset: OPTIMIZATION_LEFT_AXIS_LABEL_OFFSET,
                      style: AXIS_TITLE_STYLE,
                    }}
                    axisLine={{ strokeWidth: 1.5, stroke: "#90a4ae" }}
                    tickLine={{ stroke: "#90a4ae" }}
                  />
                  {showEfcAxis && (
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      width={104}
                      tick={AXIS_TICK_SX}
                      tickMargin={8}
                      label={{
                        value: "Cycles [EFC]",
                        angle: 90,
                        position: "insideRight",
                        offset: 20,
                        style: AXIS_TITLE_STYLE,
                      }}
                      axisLine={{ strokeWidth: 1.5, stroke: "#90a4ae" }}
                      tickLine={{ stroke: "#90a4ae" }}
                    />
                  )}
                  <RechartsTooltip
                    content={
                      <DashboardTooltip
                        labelFormatter={(label, payload) => formatOptimizationLabelFromPayload(label, payload, true)}
                        valueFormatter={(value, name, entry) => {
                          const key = entry?.dataKey;
                          if (key === "deg_efc") return formatOptimizationEnergyValue(value, "EFC");
                          if (typeof key === "string" && key.endsWith("_mw")) {
                            return formatOptimizationEnergyValue(value, "MW");
                          }
                          return formatTooltipSmart(value, name, entry);
                        }}
                      />
                    }
                  />
                  {renderEnergySeriesElements("area")}
                    <Brush
                      dataKey="time"
                      height={24}
                      travellerWidth={10}
                      tickFormatter={formatOptimizationTickFromRows(optimizationDisplayRows)}
                      stroke="#90a4ae"
                    />
                </AreaChart>
              )}
            </ResponsiveContainer>
          )}
        </Box>
      </Paper>
      )}
      {view === "optimization" && (
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3, position: "relative" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Price vs Dispatch
            </Typography>
            <IconButton size="small" onClick={() => setFullscreen("price_dispatch")} sx={FULLSCREEN_BTN_SX}>
              <FullscreenGlyph fontSize="small" />
            </IconButton>
          </Stack>

          <Box sx={{ mt: 2, height: 360 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={priceDispatchRows} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis {...getOptimizationXAxisProps(priceDispatchRows)} />

                <YAxis
                  yAxisId="price"
                  tick={AXIS_TICK_SX}
                  width={104}
                  tickMargin={8}
                  label={{ value: "Price [€/MWh]", angle: -90, position: "insideLeft", style: AXIS_TITLE_STYLE }}
                  tickFormatter={formatEurMwhAxisValue}
                />
                <YAxis
                  yAxisId="power"
                  orientation="right"
                  tick={AXIS_TICK_SX}
                  width={104}
                  tickMargin={8}
                  label={{ value: "POI [MW]", angle: 90, position: "insideRight", style: AXIS_TITLE_STYLE }}
                  tickFormatter={formatMwAxisValue}
                />

                <RechartsTooltip
                  content={
                    <DashboardTooltip
                      labelFormatter={(label, payload) => formatOptimizationLabelFromPayload(label, payload, true)}
                      valueFormatter={formatTooltipSmart}
                    />
                  }
                />

                <Line yAxisId="price" dataKey="price_eur_mwh" name="Price [€/MWh]" dot={false} strokeWidth={2} stroke="#0b9aa2" />
                <Line yAxisId="power" dataKey="p_poi_mw" name="POI [MW]" dot={false} strokeWidth={2.2} stroke="#F57C00" />
                <Brush dataKey="time" height={24} travellerWidth={10} tickFormatter={formatOptimizationTickFromRows(priceDispatchRows)} />

              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}
      {view === "optimization" && (
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3, position: "relative" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              DAM Price Forecast
            </Typography>
            <IconButton size="small" onClick={() => setFullscreen("price")} title="Full screen" sx={FULLSCREEN_BTN_SX}>
              <FullscreenGlyph fontSize="small" />
            </IconButton>
          </Stack>
          {energySeriesWithDegradation.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Display mode:
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={priceDisplayMode}
                onChange={(event, value) => {
                  if (value) setPriceDisplayMode(value);
                }}
                color="primary"
              >
                <ToggleButton value="line">Line</ToggleButton>
                <ToggleButton value="bar">Bar</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          )}
          <Box sx={{ mt: 2, height: 480 }}>
            {loadingResult ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} /> Loading price data...
              </Box>
            ) : energySeriesWithDegradation.length === 0 ? (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                No data available for the selected run.
                {chartData.available?.length
                  ? ` Available fields: ${chartData.available.join(", ")}`
                  : ""}
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                {priceDisplayMode === "line" ? (
                  <LineChart data={optimizationDisplayRows} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" />
                      <XAxis {...getOptimizationXAxisProps(optimizationDisplayRows)} />
                  <YAxis
                    {...OPTIMIZATION_LEFT_AXIS_PROPS}
                    label={{
                      value: "Price [€/MWh]",
                      angle: -90,
                      position: "insideLeft",
                      offset: OPTIMIZATION_LEFT_AXIS_LABEL_OFFSET,
                      style: AXIS_TITLE_STYLE,
                    }}
                    axisLine={{ strokeWidth: 1.5, stroke: "#90a4ae" }}
                    tickLine={{ stroke: "#90a4ae" }}
                    tickFormatter={formatEurMwhAxisValue}
                  />
                  <RechartsTooltip
                    content={
                        <DashboardTooltip
                          labelFormatter={(label, payload) => formatOptimizationLabelFromPayload(label, payload, true)}
                          valueFormatter={formatPriceEurMwhTooltipValue}
                        />
                      }
                    />
                    <Line dataKey="price_eur_mwh" name="Price [€/MWh]" stroke="#6a1b9a" dot={false} strokeWidth={2} />
                      <Brush
                        dataKey="time"
                        height={24}
                        travellerWidth={10}
                        tickFormatter={formatOptimizationTickFromRows(optimizationDisplayRows)}
                        stroke="#90a4ae"
                      />
                  </LineChart>
                ) : (
                  <BarChart data={optimizationDisplayRows} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" />
                      <XAxis {...getOptimizationXAxisProps(optimizationDisplayRows)} />
                  <YAxis
                    tick={{ fontSize: 12, fontWeight: 700 }}
                    width={90}
                    label={{
                      value: "Price [€/MWh]",
                      angle: -90,
                      position: "insideLeft",
                      offset: OPTIMIZATION_LEFT_AXIS_LABEL_OFFSET,
                      style: AXIS_TITLE_STYLE,
                    }}
                    axisLine={{ strokeWidth: 1.5, stroke: "#90a4ae" }}
                    tickLine={{ stroke: "#90a4ae" }}
                    tickFormatter={formatEurMwhAxisValue}
                  />
                  <RechartsTooltip
                    content={
                        <DashboardTooltip
                          labelFormatter={(label, payload) => formatOptimizationLabelFromPayload(label, payload, true)}
                          valueFormatter={formatPriceEurMwhTooltipValue}
                        />
                      }
                    />
                    <Bar dataKey="price_eur_mwh" name="Price [€/MWh]" fill="#6a1b9a" barSize={14} />
                      <Brush
                        dataKey="time"
                        height={24}
                        travellerWidth={10}
                        tickFormatter={formatOptimizationTickFromRows(optimizationDisplayRows)}
                        stroke="#90a4ae"
                      />
                  </BarChart>
                )}
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>
      )}
      {view === "optimization" && (
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3, position: "relative" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              State of Charge
            </Typography>
            <IconButton
              size="small"
              onClick={() => setFullscreen("soc")}
              title="Full screen"
              sx={FULLSCREEN_BTN_SX}
            >
              <FullscreenGlyph fontSize="small" />
            </IconButton>
          </Stack>

          <Box sx={{ mt: 2, height: 360 }}>
            {loadingResult ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} /> Loading SoC...
              </Box>
            ) : socSeries.length === 0 ? (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                No SoC data available.
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={socSeries} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis {...getOptimizationXAxisProps(socSeries)} />
                  <YAxis
                    domain={[0, 100]}
                    {...OPTIMIZATION_LEFT_AXIS_PROPS}
                    label={{
                      value: "SoC [%]",
                      angle: -90,
                      position: "insideLeft",
                      offset: OPTIMIZATION_LEFT_AXIS_LABEL_OFFSET,
                      style: AXIS_TITLE_STYLE,
                    }}
                  />
                  <RechartsTooltip
                    content={
                        <DashboardTooltip
                          labelFormatter={(label, payload) => formatOptimizationLabelFromPayload(label, payload, true)}
                          valueFormatter={formatTwoDecimalTooltipValue}
                        />
                    }
                  />
                  {socSeries[0]?.socMin != null && socSeries[0]?.socMax != null && (
                    <ReferenceArea
                      y1={socSeries[0].socMin}
                      y2={socSeries[0].socMax}
                      ifOverflow="extendDomain"
                      fill="rgba(93, 178, 248, 0.08)"
                      strokeOpacity={0}
                    />
                  )}
                  {/* Optional limit lines */}
                  <Line
                    dataKey="socMin"
                    name="Min SoC"
                    dot={false}
                    stroke="#c62828"          // red
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    isAnimationActive={false}
                    connectNulls={false}
                    strokeOpacity={0.55}
                  />
                  <Line
                    dataKey="socMax"
                    name="Max SoC"
                    dot={false}
                    stroke="#2e7d32"          // green
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    isAnimationActive={false}
                    connectNulls={false}
                    strokeOpacity={0.55}
                  />
                  <Line
                    dataKey="soc"
                    name="SoC"
                    dot={false}
                    stroke="#1565c0"          // blue (or whatever you prefer)
                    strokeWidth={2.4}
                    isAnimationActive={false}
                    connectNulls={false}
                  />
                  <Brush dataKey="time" height={24} travellerWidth={10} tickFormatter={formatOptimizationTickFromRows(socSeries)} stroke="#90a4ae" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>
      )}
      <Dialog fullScreen open={Boolean(fullscreen)} onClose={() => setFullscreen(null)}>
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar sx={{ gap: 2 }}>
            <Typography sx={{ flex: 1, fontWeight: 700 }}>
              {fullscreenTitleMap[fullscreen] || 'Full screen'}
            </Typography>
            <IconButton edge='end' color='inherit' onClick={() => setFullscreen(null)}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 2, height: '100%', bgcolor: 'background.default' }}>{renderFullscreenChart()}</Box>
      </Dialog>

          {view === "market" && marketDataDisabled && (
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
              <Alert severity="info">{marketDataDisabledMessage}</Alert>
            </Paper>
          )}
          {view === "market" && !marketDataDisabled && (
        <Stack spacing={3}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Zone selection
              </Typography>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Zone</InputLabel>
                <Select
                  label="Zone"
                  value={selectedMarket}
                  onChange={(e) => handleMarketChange(e.target.value)}
                >
                  {AVAILABLE_MARKETS.map((mkt) => (
                    <MenuItem key={mkt} value={mkt}>
                      {MARKET_OPTION_LABELS[mkt] || mkt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minHeight: 40,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  backgroundColor: "rgba(21, 101, 192, 0.08)",
                  border: "1px solid rgba(21, 101, 192, 0.18)",
                  maxWidth: 620,
                }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.35 }}>
                  All displayed values and timestamps use the local time of the selected zone:
                  <Box
                    component="span"
                    sx={{ display: "block", fontWeight: 700, color: "text.primary" }}
                  >
                    {selectedMarketTimeZoneLabel}
                    {" "}
                    <Box component="span" sx={{ fontWeight: 400 }}>
                      ({selectedMarketTimeZone})
                    </Box>
                    .
                  </Box>
                </Typography>
              </Box>
            </Stack>
          </Stack>
          <Box sx={{ mb: 0.5 }}>
            <Tabs
              value={marketWorkspaceTab}
              onChange={(_, value) => setMarketWorkspaceTab(value)}
              sx={{
                minHeight: 52,
                "& .MuiTabs-indicator": {
                  height: 4,
                  borderRadius: 999,
                },
                "& .MuiTab-root": {
                  minHeight: 52,
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "0.98rem",
                  letterSpacing: "0.01em",
                  borderRadius: 2,
                  mr: 1,
                  py: 0.5,
                },
              }}
            >
              <Tab value="prices" label="Energy prices" />
              <Tab value="fundamentals" label="System Fundamentals" />
            </Tabs>
          </Box>
          {marketWorkspaceTab === "prices" && (
          <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 860 }}>
              Review forecast and actual price series across the available market layers to obtain a
              consolidated view of price formation over the selected interval and bidding zone.
            </Typography>
          </Box>
          <Paper elevation={3} sx={{ p: 2.5, borderRadius: 3, position: "relative" }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              rowGap={1}
              sx={{ mb: 1 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  DAM Price
                </Typography>
                {renderMarketDisplayToggle("dam")}
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={1} flexShrink={0}>
                <IconButton size="small" onClick={() => setFullscreen("dam")} title="Full screen" sx={FULLSCREEN_BTN_SX}>
                  <FullscreenGlyph fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="flex-start">
              <PublicationNote product="dam" market={selectedMarket} />
              <MarketInfoDialog market={selectedMarket} deliveryDate={damRange.start} />
            </Stack>
            {!marketPublishesDamForecast && (
              <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
                {`DAM price forecast is published for ${getDamForecastMarketsLabel()}.`}{" "}
                This market is loaded with actual prices only.
              </Alert>
            )}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              sx={{
                mb: 1,
                flexWrap: "nowrap",
                overflowX: "auto",
                rowGap: 1,
              }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  flexWrap: "nowrap",
                  rowGap: 0.5,
                  overflowX: "auto",
                  width: "100%",
                  pb: 0.5,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Filter Data:
                </Typography>
                  {[
                    { key: "actual", label: "Actual Price", color: MARKET_PRICE_COLORS.actual },
                    ...(marketPublishesDamForecast
                      ? [
                          { key: "p10", label: "Forecast P10", color: MARKET_PRICE_COLORS.p10 },
                          { key: "p50", label: "Forecast P50", color: MARKET_PRICE_COLORS.p50 },
                          { key: "p90", label: "Forecast P90", color: MARKET_PRICE_COLORS.p90 },
                        ]
                      : []),
                  ].map((m) => (
                    <Box key={m.key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "2px",
                          backgroundColor: m.color,
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {m.label}
                      </Typography>
                      <Checkbox
                        size="small"
                        checked={damVisible[m.key]}
                        onChange={(e) =>
                          setDamVisible((prev) => ({ ...prev, [m.key]: e.target.checked }))
                        }
                        sx={{ ml: 0.5 }}
                      />
                    </Box>
                  ))}
                  {hasDamActualPriceData && marketPublishesDamForecast && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, ml: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        Forecast Metrics
                      </Typography>
                      <Checkbox
                        size="small"
                        checked={showDamForecastMetrics}
                        onChange={(e) => setShowDamForecastMetrics(e.target.checked)}
                        sx={{ ml: 0.5 }}
                      />
                    </Box>
                  )}
                  {hasDamActualPriceData && (
                    <Tooltip title="Show actual price statistics" arrow>
                      <ToggleButton
                        size="small"
                        value="actual-stats"
                        selected={showDamActualStats}
                        onChange={() => setShowDamActualStats((prev) => !prev)}
                        aria-label="Toggle actual price statistics"
                        sx={{
                          height: 30,
                          px: 1.1,
                          gap: 0.65,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 700,
                          "&.Mui-selected": {
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            "&:hover": {
                              bgcolor: "primary.dark",
                            },
                          },
                        }}
                      >
                        <QueryStatsOutlinedIcon fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                          Actual Stats
                        </Typography>
                      </ToggleButton>
                    </Tooltip>
                  )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="nowrap">
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    Date Interval:
                  </Typography>
                  <MaybeTooltip
                    arrow
                    placement="top"
                    title="Date range is inclusive of both start and end days."
                  >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </MaybeTooltip>
                </Stack>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => openRangePicker("dam", e)}
                  startIcon={<CalendarMonthIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                  endIcon={<ArrowDropDownIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                  sx={{ minWidth: 160, textTransform: "none" }}
                >
                  {rangeLabel("dam")}
                </Button>
              </Stack>
            </Stack>
              {marketNotices.dam.map((notice, idx) => (
                <Alert key={`dam-${idx}`} severity={notice.severity} sx={{ mb: 2 }}>
                  {notice.message}
                </Alert>
              ))}
              {damError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {damError}
                </Alert>
              )}
              {showDamActualStats && renderDamActualStats()}
              {showDamForecastMetrics && renderDamMetrics()}
              <Box sx={{ height: 480 }}>
                {damLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={20} /> Loading DAM prices...
                  </Box>
                ) : damSeries.length === 0 ? (
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    No data loaded yet. Select a date interval to load DAM prices.
                  </Typography>
                ) : marketDisplayMode.dam === "table" ? (
                  renderMarketDataTable({
                    rows: damSeries,
                    columns: [
                      { key: "actual", label: "Actual Price", unit: "EUR/MWh" },
                      { key: "p10", label: "Forecast P10", unit: "EUR/MWh" },
                      { key: "p50", label: "Forecast P50", unit: "EUR/MWh" },
                      { key: "p90", label: "Forecast P90", unit: "EUR/MWh" },
                    ].filter(
                      (column) =>
                        damVisible[column.key] &&
                        (column.key !== "actual" || hasDamActualPriceData)
                    ),
                    valueFormatter: (value) => formatMarketTableValue(value, 2),
                  })
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={damSeries} margin={CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="time"
                        tick={{ fontSize: 12, fontWeight: 700 }}
                        tickFormatter={formatMarketTimeLabel}
                        axisLine={MARKET_AXIS_LINE_PROPS}
                        tickLine={MARKET_TICK_LINE_PROPS}
                      />
                      <YAxis
                        width={90}
                        tick={{ fontSize: 12, fontWeight: 700 }}
                        axisLine={MARKET_AXIS_LINE_PROPS}
                        tickLine={MARKET_TICK_LINE_PROPS}
                        label={{
                          value: "Price [€/MWh]",
                          angle: -90,
                          position: "insideLeft",
                          offset: 24,
                          style: AXIS_TITLE_STYLE,
                        }}
                      />
                      <RechartsTooltip content={<DashboardTooltip labelFormatter={formatMarketTimeLabel} />} />
                      {showDamActualSeries && (
                        <Line
                          dataKey="actual"
                          name="Actual"
                          stroke={MARKET_PRICE_COLORS.actual}
                          dot={false}
                          strokeWidth={2.4}
                        />
                      )}
                      {damVisible.p10 && (
                        <Line
                          dataKey="p10"
                          name="Forecast P10"
                          stroke="#2e7d32"
                          dot={false}
                          strokeWidth={2}
                          strokeDasharray="6 4"
                          strokeOpacity={0.85}
                          connectNulls={false}
                        />
                      )}
                      {damVisible.p50 && (
                        <Line
                          dataKey="p50"
                          name="Forecast P50"
                          stroke={MARKET_PRICE_COLORS.p50}
                          dot={false}
                          strokeWidth={2.2}
                          strokeDasharray={MARKET_FORECAST_DASH}
                          strokeOpacity={0.95}
                          connectNulls={false}
                        />
                      )}
                      {damVisible.p90 && (
                        <Line
                          dataKey="p90"
                          name="Forecast P90"
                          stroke="#ef6c00"
                          dot={false}
                          strokeWidth={2}
                          strokeDasharray="6 4"
                          strokeOpacity={0.85}
                          connectNulls={false}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Box>
          </Paper>
          <Paper elevation={3} sx={{ p: 2.5, borderRadius: 3, position: "relative" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Intraday Price
                </Typography>
                {renderMarketDisplayToggle("intraday")}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton size="small" onClick={() => setFullscreen("intraday")} title="Full screen" sx={FULLSCREEN_BTN_SX}>
                  <FullscreenGlyph fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
            <PublicationNote
              product={intradayProduct === "XBID" ? "xbid" : intradayProduct.toLowerCase()}
              market={selectedMarket}
            />
            <Stack
              direction={{ xs: "column", xl: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", xl: "center" }}
              justifyContent="space-between"
              sx={{ mb: 1.25 }}
            >
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ xs: "stretch", md: "center" }} flexWrap="wrap">
                <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    Product
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap" }}
                      >
                        Auctions
                      </Typography>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={INTRADAY_AUCTION_PRODUCTS.includes(intradayProduct) ? intradayProduct : null}
                        onChange={(_event, value) => {
                          if (value) {
                            setIntradayProduct(value);
                          }
                        }}
                        sx={MARKET_SELECTOR_TOGGLE_SX}
                      >
                        {INTRADAY_AUCTION_PRODUCTS.map((product) => (
                          <ToggleButton key={product} value={product}>
                            {INTRADAY_PRODUCT_LABELS[product] || product}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap" }}
                      >
                        Continuous
                      </Typography>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={INTRADAY_CONTINUOUS_PRODUCTS.includes(intradayProduct) ? intradayProduct : null}
                        onChange={(_event, value) => {
                          if (value) {
                            setIntradayProduct(value);
                          }
                        }}
                        sx={MARKET_SELECTOR_TOGGLE_SX}
                      >
                        {INTRADAY_CONTINUOUS_PRODUCTS.map((product) => (
                          <ToggleButton key={product} value={product}>
                            {INTRADAY_PRODUCT_LABELS[product] || product}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="nowrap">
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    Date Interval:
                  </Typography>
                  <MaybeTooltip
                    arrow
                    placement="top"
                    title="Date range is inclusive of both start and end days."
                  >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </MaybeTooltip>
                </Stack>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => openRangePicker("intraday", e)}
                  startIcon={<CalendarMonthIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                  endIcon={<ArrowDropDownIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                  sx={{ minWidth: 160, textTransform: "none" }}
                >
                  {rangeLabel("intraday")}
                </Button>
              </Stack>
            </Stack>
            {isXbidProduct && (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 1, mb: 1.25 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    Duration
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={xbidDuration}
                    onChange={(_event, value) => {
                      if (value) setXbidDuration(value);
                    }}
                    sx={MARKET_SELECTOR_TOGGLE_SX}
                  >
                    {XBID_DURATIONS.map((d) => (
                      <ToggleButton key={d} value={d}>
                        {d} min
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Stack>
                {[
                  { label: "Asset", options: XBID_ASSETS, value: xbidAssets, setter: setXbidAssets, minWidth: 140, single: true },
                  { label: "Side", options: XBID_SIDES, value: xbidSides, setter: setXbidSides, minWidth: 120 },
                  { label: "Technology", options: xbidAvailableTechnologies, value: xbidTechnologies, setter: setXbidTechnologies, minWidth: 160, maxWidth: 220 },
                ].map((f) => (
                  <Stack key={f.label} direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      {f.label}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: f.minWidth, maxWidth: f.maxWidth }}>
                      {f.single ? (
                        <Select
                          displayEmpty
                          value={f.value[0] || ""}
                          onChange={(e) => f.setter(e.target.value ? [e.target.value] : [])}
                          renderValue={(v) => v || ""}
                        >
                          {f.options.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              <ListItemText primary={opt} />
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Select
                          multiple
                          displayEmpty
                          value={f.value}
                          onChange={(e) => f.setter(normalizeMultiSelectValue(e.target.value))}
                          renderValue={renderXbidFilterValue}
                        >
                          {f.options.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              <Checkbox size="small" checked={f.value.indexOf(opt) > -1} />
                              <ListItemText primary={opt} />
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </FormControl>
                  </Stack>
                ))}
                <Stack direction="row" spacing={0.25} alignItems="center">
                  <Switch
                    size="small"
                    checked={xbidShowVolume}
                    onChange={(e) => setXbidShowVolume(e.target.checked)}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    Show volume
                  </Typography>
                </Stack>
              </Stack>
            )}
            {!isXbidProduct && (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: "wrap", mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Filter Data:
              </Typography>
              {(isXbidProduct
                ? xbidChartSeries
                : [{ key: "actual", label: "Actual", color: MARKET_PRICE_COLORS.actual }].concat(
                    intradaySeriesMode === "both"
                      ? [
                          { key: "p10", label: "Forecast P10", color: MARKET_PRICE_COLORS.p10 },
                          { key: "p50", label: "Forecast P50", color: MARKET_PRICE_COLORS.p50 },
                          { key: "p90", label: "Forecast P90", color: MARKET_PRICE_COLORS.p90 },
                        ]
                      : []
                  )
              ).map((seriesItem) => (
                <Box key={seriesItem.key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "2px",
                      backgroundColor: seriesItem.color,
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {seriesItem.label}
                  </Typography>
                  <Checkbox
                    size="small"
                    checked={
                      isXbidProduct
                        ? xbidVisible[seriesItem.key] !== false
                        : intradayVisible[seriesItem.key]
                    }
                    onChange={(e) =>
                      isXbidProduct
                        ? setXbidVisible((prev) => ({ ...prev, [seriesItem.key]: e.target.checked }))
                        : setIntradayVisible((prev) => ({ ...prev, [seriesItem.key]: e.target.checked }))
                    }
                    sx={{ ml: 0.5 }}
                  />
                </Box>
              ))}
              {!isXbidProduct && hasIntradayActualPriceData && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, ml: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                    Forecast Metrics
                  </Typography>
                  <Checkbox
                    size="small"
                    checked={showIntradayForecastMetrics}
                    onChange={(e) => setShowIntradayForecastMetrics(e.target.checked)}
                    sx={{ ml: 0.5 }}
                  />
                </Box>
              )}
              {!isXbidProduct && hasIntradayActualPriceData && (
                <Tooltip title="Show actual price statistics" arrow>
                  <ToggleButton
                    size="small"
                    value="actual-stats"
                    selected={showIntradayActualStats}
                    onChange={() => setShowIntradayActualStats((prev) => !prev)}
                    aria-label="Toggle actual price statistics"
                    sx={{
                      height: 30,
                      px: 1.1,
                      gap: 0.65,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        "&:hover": {
                          bgcolor: "primary.dark",
                        },
                      },
                    }}
                  >
                    <QueryStatsOutlinedIcon fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      Actual Stats
                    </Typography>
                  </ToggleButton>
                </Tooltip>
              )}
            </Stack>
            )}
            {marketNotices.intraday.map((notice, idx) => (
              <Alert key={`intraday-${idx}`} severity={notice.severity} sx={{ mb: 2 }}>
                {notice.message}
              </Alert>
            ))}
            {intradayError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {intradayError}
              </Alert>
            )}
            {!isXbidProduct && showIntradayActualStats && renderIntradayActualStats()}
            {!isXbidProduct && showIntradayForecastMetrics && renderIntradayMetrics()}
            <Box sx={{ height: 440 }}>
              {intradayLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} /> Loading intraday prices...
                </Box>
              ) : intradaySeries.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  No intraday data loaded yet. Select a product or date interval to load prices.
                </Typography>
              ) : marketDisplayMode.intraday === "table" ? (
                renderMarketDataTable({
                  rows: intradaySeries,
                  columns: (isXbidProduct
                    ? xbidChartSeries.map((s) => ({
                        key: s.key,
                        label: s.label,
                        unit: "EUR/MWh",
                      }))
                    : [
                        { key: "actual", label: "Actual Price", unit: "EUR/MWh" },
                        ...(intradaySeriesMode === "both"
                          ? [
                              { key: "p10", label: "Forecast P10", unit: "EUR/MWh" },
                              { key: "p50", label: "Forecast P50", unit: "EUR/MWh" },
                              { key: "p90", label: "Forecast P90", unit: "EUR/MWh" },
                            ]
                          : []),
                      ]
                  ).filter((column) =>
                    isXbidProduct
                      ? xbidVisible[column.key] !== false
                      : intradayVisible[column.key] !== false
                  ),
                  valueFormatter: (value, column) => formatMarketTableValue(value, 2),
                  timeHeader: "Delivery Interval",
                })
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={intradaySeries} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="time"
                      tick={{ fontSize: 12, fontWeight: 700 }}
                      tickFormatter={formatMarketTimeLabel}
                      axisLine={MARKET_AXIS_LINE_PROPS}
                      tickLine={MARKET_TICK_LINE_PROPS}
                    />
                    <YAxis
                      width={90}
                      tick={{ fontSize: 12, fontWeight: 700 }}
                      axisLine={MARKET_AXIS_LINE_PROPS}
                      tickLine={MARKET_TICK_LINE_PROPS}
                      label={{
                        value: "Price [€/MWh]",
                        angle: -90,
                        position: "insideLeft",
                        offset: 24,
                        style: AXIS_TITLE_STYLE,
                      }}
                    />
                    <RechartsTooltip content={<DashboardTooltip labelFormatter={formatMarketTimeLabel} />} />
                    {isXbidProduct && xbidShowVolume && (
                      <YAxis
                        yAxisId="vol"
                        orientation="right"
                        width={72}
                        tick={{ fontSize: 12, fontWeight: 700 }}
                        axisLine={MARKET_AXIS_LINE_PROPS}
                        tickLine={MARKET_TICK_LINE_PROPS}
                        label={{
                          value: "Volume [MW]",
                          angle: 90,
                          position: "insideRight",
                          offset: 16,
                          style: AXIS_TITLE_STYLE,
                        }}
                      />
                    )}
                    {isXbidProduct &&
                      xbidShowVolume &&
                      xbidVolumeSeries.map((s) => (
                        <Bar
                          key={s.key}
                          yAxisId="vol"
                          dataKey={s.key}
                          name={`${s.label} (MW)`}
                          fill={s.color}
                          fillOpacity={0.28}
                          stackId={undefined}
                          isAnimationActive={false}
                        />
                      ))}
                    {isXbidProduct &&
                      xbidChartSeries.map((s) =>
                        xbidVisible[s.key] !== false ? (
                          <Line
                            key={s.key}
                            dataKey={s.key}
                            name={s.label}
                            stroke={s.color}
                            dot={false}
                            strokeWidth={2.2}
                            strokeDasharray={s.dashed ? MARKET_FORECAST_DASH : undefined}
                            strokeOpacity={s.dashed ? 0.55 : 1}
                            connectNulls={false}
                          />
                        ) : null
                      )}
                    {!isXbidProduct && showIntradayActualSeries && (
                      <Line
                        dataKey="actual"
                        name="Actual"
                        stroke={MARKET_PRICE_COLORS.actual}
                        dot={false}
                        strokeWidth={2.4}
                      />
                    )}
                    {!isXbidProduct && intradaySeriesMode === "both" && intradayVisible.p10 && (
                      <Line
                        dataKey="p10"
                        name="Forecast P10"
                        stroke={MARKET_PRICE_COLORS.p10}
                        dot={false}
                        strokeWidth={2}
                        strokeDasharray={MARKET_FORECAST_DASH}
                        strokeOpacity={0.85}
                        connectNulls={false}
                      />
                    )}
                    {!isXbidProduct && intradaySeriesMode === "both" && intradayVisible.p50 && (
                      <Line
                        dataKey="p50"
                        name="Forecast P50"
                        stroke={MARKET_PRICE_COLORS.p50}
                        dot={false}
                        strokeWidth={2.2}
                        strokeDasharray={MARKET_FORECAST_DASH}
                        strokeOpacity={0.95}
                        connectNulls={false}
                      />
                    )}
                    {!isXbidProduct && intradaySeriesMode === "both" && intradayVisible.p90 && (
                      <Line
                        dataKey="p90"
                        name="Forecast P90"
                        stroke={MARKET_PRICE_COLORS.p90}
                        dot={false}
                        strokeWidth={2}
                        strokeDasharray={MARKET_FORECAST_DASH}
                        strokeOpacity={0.85}
                        connectNulls={false}
                      />
                    )}
                    <Brush
                      dataKey="time"
                      height={24}
                      travellerWidth={10}
                      tickFormatter={formatMarketTimeLabelShort}
                      stroke="#90a4ae"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
          {/* Phase 2 (price-forecast expansion): the XBID per-MTU forecast sits
              directly under the XBID actuals chart, and only when XBID is the
              selected product - the auction products have their own forecast
              surfaces above and must not gain a second one. */}
          {isXbidProduct && <XbidForecastPanel market={selectedMarket} />}
          <Paper elevation={3} sx={{ p: 2.5, borderRadius: 3, position: "relative" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Auctions Actual Price Comparison
                </Typography>
                {renderMarketDisplayToggle("compare")}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton size="small" onClick={() => setFullscreen("compare")} title="Full screen" sx={FULLSCREEN_BTN_SX}>
                  <FullscreenGlyph fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", lg: "center" }}
              justifyContent="space-between"
              sx={{ mb: 1.25 }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: "wrap" }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Display Markets:
                </Typography>
                {ACTUAL_PRICE_COMPARISON_OPTIONS.map((seriesItem) => (
                  <Box key={seriesItem.key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "2px",
                        backgroundColor: seriesItem.color,
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {seriesItem.label}
                    </Typography>
                    <Checkbox
                      size="small"
                      checked={actualComparisonVisible[seriesItem.key]}
                      onChange={(e) =>
                        setActualComparisonVisible((prev) => ({
                          ...prev,
                          [seriesItem.key]: e.target.checked,
                        }))
                      }
                      sx={{ ml: 0.5 }}
                    />
                  </Box>
                ))}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="nowrap">
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    Date Interval:
                  </Typography>
                  <MaybeTooltip
                    arrow
                    placement="top"
                    title="Date range is inclusive of both start and end days."
                  >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </MaybeTooltip>
                </Stack>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => openRangePicker("compare", e)}
                  startIcon={<CalendarMonthIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                  endIcon={<ArrowDropDownIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                  sx={{ minWidth: 160, textTransform: "none" }}
                >
                  {rangeLabel("compare")}
                </Button>
              </Stack>
            </Stack>
            {actualComparisonError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {actualComparisonError}
              </Alert>
            )}
            <Box sx={{ height: 420 }}>
              {actualComparisonLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} /> Loading actual price comparison...
                </Box>
              ) : actualComparisonSeries.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  No actual price data loaded yet for the selected interval.
                </Typography>
              ) : !ACTUAL_PRICE_COMPARISON_OPTIONS.some(
                  (seriesItem) => actualComparisonVisible[seriesItem.key]
                ) ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  Select at least one market series to display the comparison chart.
                </Typography>
              ) : marketDisplayMode.compare === "table" ? (
                renderMarketDataTable({
                  rows: actualComparisonSeries,
                  columns: ACTUAL_PRICE_COMPARISON_OPTIONS.filter(
                    (seriesItem) => actualComparisonVisible[seriesItem.key]
                  ).map((seriesItem) => ({
                    key: seriesItem.key,
                    label: seriesItem.label,
                    unit: "EUR/MWh",
                  })),
                  valueFormatter: (value) => formatMarketTableValue(value, 2),
                  timeHeader: "Delivery Interval",
                })
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={actualComparisonSeries} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="time"
                      tick={{ fontSize: 12, fontWeight: 700 }}
                      tickFormatter={formatMarketTimeLabel}
                      axisLine={MARKET_AXIS_LINE_PROPS}
                      tickLine={MARKET_TICK_LINE_PROPS}
                    />
                    <YAxis
                      width={90}
                      tick={{ fontSize: 12, fontWeight: 700 }}
                      axisLine={MARKET_AXIS_LINE_PROPS}
                      tickLine={MARKET_TICK_LINE_PROPS}
                      label={{
                        value: "Price [€/MWh]",
                        angle: -90,
                        position: "insideLeft",
                        offset: 24,
                        style: AXIS_TITLE_STYLE,
                      }}
                    />
                    <RechartsTooltip content={<DashboardTooltip labelFormatter={formatMarketTimeLabel} />} />
                    {ACTUAL_PRICE_COMPARISON_OPTIONS.filter(
                      (seriesItem) => actualComparisonVisible[seriesItem.key]
                    ).map((seriesItem) => (
                      <Line
                        key={seriesItem.key}
                        dataKey={seriesItem.key}
                        name={seriesItem.label}
                        stroke={seriesItem.color}
                        dot={false}
                        strokeWidth={2.3}
                      />
                    ))}
                    <Brush
                      dataKey="time"
                      height={24}
                      travellerWidth={10}
                      tickFormatter={formatMarketTimeLabelShort}
                      stroke="#90a4ae"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
          <Paper elevation={3} sx={{ p: 2.5, borderRadius: 3, position: "relative" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Balancing Market
                </Typography>
                {renderMarketDisplayToggle("balancing")}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton size="small" onClick={() => setFullscreen("balancing")} title="Full screen" sx={FULLSCREEN_BTN_SX}>
                  <FullscreenGlyph fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="flex-start">
              <PublicationNote product="balancing_actuals" market={selectedMarket} />
              <MarketInfoDialog market={selectedMarket} deliveryDate={damRange.start} />
            </Stack>
            <Stack
              direction={{ xs: "column", xl: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", xl: "center" }}
              justifyContent="space-between"
              sx={{ mb: 1.25 }}
            >
              <Stack spacing={1.25} sx={{ minWidth: 0, flex: 1 }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={balancingDataset}
                  onChange={(_event, value) => {
                    if (value) {
                      setBalancingDataset(value);
                    }
                  }}
                  sx={MARKET_SELECTOR_TOGGLE_SX}
                >
                  {BALANCING_DATASET_OPTIONS.map((option) => (
                    <ToggleButton key={option.key} value={option.key}>
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ xs: "stretch", md: "center" }} flexWrap="wrap">
                  {balancingZones.length > 1 && (
                    <FormControl size="small" sx={{ minWidth: 170 }}>
                      <InputLabel>Zone</InputLabel>
                      <Select
                        label="Zone"
                        value={balancingZone}
                        onChange={(e) => setBalancingZone(e.target.value)}
                      >
                        <MenuItem value="all">All zones</MenuItem>
                        {balancingZones.map((zone) => (
                          <MenuItem key={zone} value={zone}>
                            {zone}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {balancingDataset !== "imbalance_prices" &&
                    balancingNumericFields.length > 1 && (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>{balancingMetricSelectorLabel}</InputLabel>
                      <Select
                        label={balancingMetricSelectorLabel}
                        value={balancingMetric}
                        onChange={(e) => setBalancingMetric(e.target.value)}
                      >
                        {balancingNumericFields.map((field) => (
                          <MenuItem key={field} value={field}>
                            {humanizeBalancingKey(field)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Stack>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="nowrap">
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    Date Interval:
                  </Typography>
                  <MaybeTooltip
                    arrow
                    placement="top"
                    title="Date range is inclusive of both start and end days."
                  >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </MaybeTooltip>
                </Stack>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => openRangePicker("balancing", e)}
                  startIcon={<CalendarMonthIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                  endIcon={<ArrowDropDownIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                  sx={{ minWidth: 160, textTransform: "none" }}
                >
                  {rangeLabel("balancing")}
                </Button>
              </Stack>
            </Stack>
            {balancingFetchError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {balancingFetchError}
              </Alert>
            )}
            <Box sx={{ height: 420 }}>{renderBalancingContent()}</Box>
          </Paper>
          <BalancingForecastPanel market={selectedMarket} />
          </>
          )}
          {marketWorkspaceTab === "fundamentals" && (
          <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 860, mb: 1.5 }}>
              Review ENTSO-E published system data to obtain a structured overview of network conditions,
              generation composition, and demand behavior across the selected interval and bidding zone.
            </Typography>
            <Tabs
              value={marketInsightsSubtab}
              onChange={(_, value) => setMarketInsightsSubtab(value)}
              sx={{
                minHeight: 52,
                "& .MuiTabs-indicator": {
                  height: 4,
                  borderRadius: 999,
                },
                "& .MuiTab-root": {
                  minHeight: 52,
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "0.98rem",
                  letterSpacing: "0.01em",
                  borderRadius: 2,
                  mr: 1,
                  py: 0.5,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                },
                "& .MuiTab-root.Mui-selected": {
                  fontWeight: 900,
                },
                "& .MuiTab-iconWrapper": {
                  marginBottom: 0,
                },
              }}
            >
              <Tab
                value="generation"
                icon={<BoltOutlinedIcon fontSize="small" />}
                iconPosition="start"
                label="System Generation"
              />
              <Tab
                value="load"
                icon={<ElectricMeterOutlinedIcon fontSize="small" />}
                iconPosition="start"
                label="System Load"
              />
            </Tabs>
          </Box>
          {marketInsightsSubtab === "generation" && (
          <>
          <Paper elevation={3} sx={{ p: 2.5, borderRadius: 3, position: "relative" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Energy Mix
                </Typography>
                {renderMarketDisplayToggle("mix")}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton size="small" onClick={() => setFullscreen("mix")} title="Full screen" sx={FULLSCREEN_BTN_SX}>
                  <FullscreenGlyph fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ mb: 1, rowGap: 1 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  flexWrap="wrap"
                  sx={{ gap: 1, width: "100%", overflowX: "auto" }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                  >
                    Filter Data:
                  </Typography>
                  {currentMixMetrics.map((m) => {
                    const label =
                      m === RT_SYSTEM_GENERATION_METRIC
                        ? "Total Generation"
                        : SERIES_LABELS[m];
                    return (
                      <Box
                        key={m}
                        sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "2px",
                            backgroundColor: SERIES_COLORS[m] || "primary.main",
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                        >
                          {label || m}
                        </Typography>
                        <Checkbox
                          size="small"
                          checked={marketVisible.mix[m]}
                          onChange={(e) =>
                            setMarketVisible((prev) => ({
                              ...prev,
                              mix: { ...prev.mix, [m]: e.target.checked },
                            }))
                          }
                          sx={{ ml: 0.5 }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flexWrap: "nowrap", flexShrink: 0, alignSelf: "flex-start" }}
              >
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                  >
                    Date Interval:
                  </Typography>
                  <MaybeTooltip
                    arrow
                    placement="top"
                    title="Date range is inclusive of both start and end days."
                  >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </MaybeTooltip>
                </Stack>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => openRangePicker("mix", e)}
                  startIcon={<CalendarMonthIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                  endIcon={<ArrowDropDownIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                  sx={{ minWidth: 160, textTransform: "none" }}
                >
                  {rangeLabel("mix")}
                </Button>
              </Stack>
            </Stack>
            {marketNotices.mix.map((notice, idx) => (
              <Alert key={`mix-${idx}`} severity={notice.severity} sx={{ mb: 2 }}>
                {notice.message}
              </Alert>
            ))}
            {marketError.mix && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {marketError.mix}
              </Alert>
            )}
            <Box sx={{ height: 480 }}>
              {marketLoading.mix ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} /> Loading energy mix...
                </Box>
              ) : marketSeries.mix.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  No data loaded yet. Select a date interval to load energy mix data.
                </Typography>
              ) : marketDisplayMode.mix === "table" ? (
                renderMarketDataTable({
                  rows: mixChartData,
                  columns: currentMixMetrics
                    .filter((metric) => marketVisible.mix[metric])
                    .map((metric) => ({
                      key: metric,
                      label:
                        metric === RT_SYSTEM_GENERATION_METRIC
                          ? "Total Generation"
                          : SERIES_LABELS[metric] || metric,
                      unit: "MW",
                    })),
                  valueFormatter: (value) => formatMarketTableValue(value, 2),
                })
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={mixChartData} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="time"
                      tick={{ fontSize: 12, fontWeight: 700 }}
                      tickFormatter={formatMarketTimeLabel}
                      axisLine={MARKET_AXIS_LINE_PROPS}
                      tickLine={MARKET_TICK_LINE_PROPS}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fontWeight: 700 }}
                      width={90}
                      axisLine={MARKET_AXIS_LINE_PROPS}
                      tickLine={MARKET_TICK_LINE_PROPS}
                      label={{
                        value: "Net Generation (MW)",
                        angle: -90,
                        position: "insideLeft",
                        offset: 14,
                        style: AXIS_TITLE_STYLE,
                      }}
                    />
                    <RechartsTooltip content={<DashboardTooltip labelFormatter={formatMarketTimeLabel} />} />
                    {currentMixMetrics
                      .filter((m) => marketVisible.mix[m])
                      .map((m) => (
                        <Line
                          key={m}
                          dataKey={m}
                          name={
                            m === RT_SYSTEM_GENERATION_METRIC
                              ? "Total Generation"
                              : SERIES_LABELS[m] || m
                          }
                          stroke={SERIES_COLORS[m] || "#1976d2"}
                          dot={false}
                          strokeWidth={2}
                        />
                      ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
          <Paper elevation={3} sx={{ p: 2.5, borderRadius: 3, position: "relative" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  System Generation Forecast
                </Typography>
                {renderMarketDisplayToggle("generation")}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton size="small" onClick={() => setFullscreen("generation")} title="Full screen" sx={FULLSCREEN_BTN_SX}>
                  <FullscreenGlyph fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems="stretch"
                justifyContent="space-between"
                sx={{ mb: 1, rowGap: 1 }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ gap: 1, width: "100%", overflowX: "auto" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                    >
                      Filter Data:
                    </Typography>
                    {currentGenerationMetrics.map((m) => (
                      <Box key={m} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "2px",
                            backgroundColor: SERIES_COLORS[m] || "primary.main",
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                        >
                          {SERIES_LABELS[m] || m}
                        </Typography>
                        <Checkbox
                          size="small"
                          checked={marketVisible.generation[m]}
                          onChange={(e) =>
                            setMarketVisible((prev) => ({
                              ...prev,
                              generation: { ...prev.generation, [m]: e.target.checked },
                            }))
                          }
                          sx={{ ml: 0.5 }}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ flexWrap: "nowrap", flexShrink: 0 }}
                >
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                    >
                      Date Interval:
                    </Typography>
                    <MaybeTooltip
                      arrow
                      placement="top"
                      title="Date range is inclusive of both start and end days."
                    >
                      <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    </MaybeTooltip>
                  </Stack>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => openRangePicker("generation", e)}
                    startIcon={<CalendarMonthIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                    endIcon={<ArrowDropDownIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                    sx={{ minWidth: 160, textTransform: "none" }}
                  >
                    {rangeLabel("generation")}
                  </Button>
                </Stack>
              </Stack>
            {marketNotices.generation.map((notice, idx) => (
              <Alert key={`generation-${idx}`} severity={notice.severity} sx={{ mb: 2 }}>
                {notice.message}
              </Alert>
            ))}
            {marketError.generation && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {marketError.generation}
              </Alert>
            )}
            <Box sx={{ height: 480 }}>
              {marketLoading.generation ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} /> Loading system generation...
                </Box>
              ) : marketSeries.generation.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  No data loaded yet. Select a date interval to load system generation data.
                </Typography>
              ) : marketDisplayMode.generation === "table" ? (
                renderMarketDataTable({
                  rows: generationChartData,
                  columns: currentGenerationMetrics
                    .filter((metric) => marketVisible.generation[metric])
                    .map((metric) => ({
                      key: metric,
                      label: SERIES_LABELS[metric] || metric,
                      unit: "MW",
                    })),
                  valueFormatter: (value) => formatMarketTableValue(value, 2),
                })
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={generationChartData} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="time"
                      tick={{ fontSize: 12, fontWeight: 700 }}
                      tickFormatter={formatMarketTimeLabel}
                      axisLine={MARKET_AXIS_LINE_PROPS}
                      tickLine={MARKET_TICK_LINE_PROPS}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fontWeight: 700 }}
                      width={90}
                      axisLine={MARKET_AXIS_LINE_PROPS}
                      tickLine={MARKET_TICK_LINE_PROPS}
                      label={{
                        value: "Forecast Generation (MW)",
                        angle: -90,
                        position: "insideLeft",
                        offset: 14,
                        style: AXIS_TITLE_STYLE,
                      }}
                    />
                    <RechartsTooltip content={<DashboardTooltip labelFormatter={formatMarketTimeLabel} />} />
                      {currentGenerationMetrics
                        .filter((m) => marketVisible.generation[m])
                        .map((m) => (
                          <Line
                            key={m}
                            dataKey={m}
                            name={SERIES_LABELS[m] || m}
                            stroke={SERIES_COLORS[m] || "#1976d2"}
                            dot={false}
                            strokeWidth={m.includes("forecast") ? 2 : 2.6}
                            strokeOpacity={m.includes("forecast") ? 0.85 : 1}
                            connectNulls={false}
                          />
                        ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
          </>
          )}
          {marketInsightsSubtab === "load" && (
            <Paper elevation={3} sx={{ p: 2.5, borderRadius: 3, position: "relative" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  System Load
                </Typography>
                {renderMarketDisplayToggle("load")}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton size="small" onClick={() => setFullscreen("load")} title="Full screen" sx={FULLSCREEN_BTN_SX}>
                  <FullscreenGlyph fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              sx={{ mb: 1, rowGap: 1 }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Filter Data:
                </Typography>
                {LOAD_METRICS.map((m) => (
                  <Box key={m} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "2px",
                        backgroundColor: SERIES_COLORS[m] || "primary.main",
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {SERIES_LABELS[m]}
                    </Typography>
                    <Checkbox
                      size="small"
                      checked={marketVisible.load[m]}
                      onChange={(e) =>
                        setMarketVisible((prev) => ({
                          ...prev,
                          load: { ...prev.load, [m]: e.target.checked },
                        }))
                      }
                      sx={{ ml: 0.5 }}
                    />
                  </Box>
                ))}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Date Interval:
                  </Typography>
                  <MaybeTooltip
                    arrow
                    placement="top"
                    title="Date range is inclusive of both start and end days."
                  >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </MaybeTooltip>
                </Stack>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => openRangePicker("load", e)}
                  startIcon={<CalendarMonthIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                  endIcon={<ArrowDropDownIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                  sx={{ minWidth: 160, textTransform: "none" }}
                >
                  {rangeLabel("load")}
                </Button>
              </Stack>
            </Stack>
            {marketNotices.load.map((notice, idx) => (
              <Alert key={`load-${idx}`} severity={notice.severity} sx={{ mb: 2 }}>
                {notice.message}
              </Alert>
            ))}
            {marketError.load && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {marketError.load}
              </Alert>
            )}
            <Box sx={{ height: 480 }}>
              {marketLoading.load ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} /> Loading system load...
                </Box>
              ) : marketSeries.load.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  No data loaded yet. Select a date interval to load system load data.
                </Typography>
              ) : marketDisplayMode.load === "table" ? (
                renderMarketDataTable({
                  rows: marketSeries.load,
                  columns: LOAD_METRICS
                    .filter((metric) => marketVisible.load[metric])
                    .map((metric) => ({
                      key: metric,
                      label: SERIES_LABELS[metric] || metric,
                      unit: "MW",
                    })),
                  valueFormatter: (value) => formatMarketTableValue(value, 2),
                })
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={marketSeries.load} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="time"
                      tick={{ fontSize: 12, fontWeight: 700 }}
                      tickFormatter={formatMarketTimeLabel}
                      axisLine={MARKET_AXIS_LINE_PROPS}
                      tickLine={MARKET_TICK_LINE_PROPS}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fontWeight: 700 }}
                      width={90}
                      axisLine={MARKET_AXIS_LINE_PROPS}
                      tickLine={MARKET_TICK_LINE_PROPS}
                      label={{
                        value: "Load (MW)",
                        angle: -90,
                        position: "insideLeft",
                        offset: 14,
                        style: AXIS_TITLE_STYLE,
                      }}
                    />
                    <RechartsTooltip content={<DashboardTooltip labelFormatter={formatMarketTimeLabel} />} />
                    {LOAD_METRICS.filter((m) => marketVisible.load[m]).map((m) => (
                      <Line
                        key={m}
                        dataKey={m}
                        name={SERIES_LABELS[m] || m}
                        stroke={SERIES_COLORS[m] || "#1976d2"}
                        dot={false}
                        strokeWidth={m.includes("forecast") ? 2 : 2.6}
                        strokeOpacity={m.includes("forecast") ? 0.85 : 1}
                        connectNulls={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Box>
            </Paper>
          )}
          </>
          )}
        </Stack>
      )}
      <Popover
        open={Boolean(rangeAnchor)}
        anchorEl={rangeAnchor}
        onClose={closeRangePicker}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2 }}>
          {rangeSection && (
            <SimpleRangeCalendar
              value={rangeValue[rangeSection]}
              month={rangeMonth[rangeSection]}
              onChange={(newValue) => handleRangeChange(rangeSection, newValue)}
              onMonthChange={(d) =>
                setRangeMonth((prev) => ({
                  ...prev,
                  [rangeSection]: d,
                }))
              }
              highlightColor="rgba(33, 150, 243, 0.2)"
              edgeColor="rgba(33, 150, 243, 0.55)"
              onClose={closeRangePicker}
              minDate={rangeSection === "simulation" ? simulationMinDate : undefined}
              maxDate={rangeSection === "simulation" ? simulationMaxDate : undefined}
            />
          )}
        </Box>
      </Popover>
      {view === "weather" && (
        <Stack spacing={3}>
          <Box>
            <Tabs
              value={weatherWorkspaceTab}
              onChange={(_, value) => setWeatherWorkspaceTab(value)}
              sx={{
                minHeight: 52,
                mb: 1.5,
                "& .MuiTabs-indicator": {
                  height: 4,
                  borderRadius: 999,
                },
                "& .MuiTab-root": {
                  minHeight: 52,
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "0.98rem",
                  letterSpacing: "0.01em",
                  borderRadius: 2,
                  mr: 1,
                  py: 0.5,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                },
                "& .MuiTab-root.Mui-selected": {
                  fontWeight: 900,
                },
                "& .MuiTab-iconWrapper": {
                  marginBottom: 0,
                },
              }}
            >
              <Tab
                value="weather"
                icon={<SunCloudIcon fontSize="small" />}
                iconPosition="start"
                label="Weather Data"
              />
              <Tab
                value="generation"
                disabled={weatherAsset === "wind" && windGenerationDisabled}
                icon={
                  weatherAsset === "wind" ? (
                    <WindPowerOutlinedIcon fontSize="small" />
                  ) : (
                    <SolarBoltIcon fontSize="small" />
                  )
                }
                iconPosition="start"
                label={weatherAsset === "wind" ? "Wind Generation" : "PV Generation"}
              />
            </Tabs>
          </Box>
          {weatherWorkspaceTab === "weather" && weatherAsset === "pv" && (
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
              <Stack spacing={1}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Weather Data — PV
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Weather inputs used by the PV generation model at the project location.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label={`Lat: ${weatherLat ?? "N/A"}`} />
                  <Chip label={`Lon: ${weatherLon ?? "N/A"}`} />
                  <Chip
                    label={`Timezone: ${
                      weatherTz ?? "N/A"
                    }`}
                  />
                </Stack>
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Mode:
                </Typography>
                <Stack direction="row" spacing={1}>
                  {WEATHER_MODE_OPTIONS.map((m) => (
                    <Chip
                      key={m.key}
                      label={m.label}
                      color={weatherMode === m.key ? "primary" : "default"}
                      variant={weatherMode === m.key ? "filled" : "outlined"}
                      onClick={() => handleWeatherModeChange(m.key)}
                      sx={{ fontWeight: 700 }}
                    />
                  ))}
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Date Interval:
                  </Typography>
                  <MaybeTooltip
                    arrow
                    placement="top"
                    title="Date range is inclusive of both start and end days."
                  >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </MaybeTooltip>
                </Stack>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => openRangePicker("weather", e)}
                  startIcon={<CalendarMonthIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                  endIcon={<ArrowDropDownIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                  sx={{ minWidth: 180, textTransform: "none" }}
                >
                  {rangeLabel("weather")}
                </Button>
              </Stack>
            </Stack>
            {weatherNotices.map((notice, idx) => (
              <Alert key={idx} severity={notice.severity} sx={{ mt: 2 }}>
                {notice.message}
              </Alert>
            ))}
            {weatherError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {weatherError}
              </Alert>
            )}
            {weatherLoading ? (
              <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} /> Loading weather data...
              </Box>
            ) : (
              <Stack spacing={3} sx={{ mt: 2 }}>
                    <Box sx={{ height: 480, position: "relative", pb: 10 }}>
                      <Stack spacing={1} sx={{ mb: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Irradiance Components
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => setFullscreen("weather-irradiance")}
                            title="Full screen"
                            sx={{ ...FULLSCREEN_BTN_SX, top: "auto", bottom: 20, right: 8 }}
                          >
                            <FullscreenGlyph fontSize="small" />
                          </IconButton>
                        </Stack>
                        <Stack spacing={0.5} alignItems="flex-start">
                          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Filter Data:
                            </Typography>
                            {IRRADIANCE_KEYS.map((k) => (
                              <Box key={k} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: "2px",
                                    backgroundColor: k === "ghi" ? "#1565c0" : k === "dni" ? "#f57f17" : "#2e7d32",
                                  }}
                                />
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {k.toUpperCase()}
                                </Typography>
                                <Checkbox
                                  size="small"
                                  checked={weatherVisible.irradiance[k]}
                                  onChange={(e) =>
                                    setWeatherVisible((prev) => ({
                                      ...prev,
                                      irradiance: { ...prev.irradiance, [k]: e.target.checked },
                                    }))
                                  }
                                  sx={{ ml: 0.5 }}
                                />
                              </Box>
                            ))}
                          </Stack>
                        </Stack>
                      </Stack>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={weatherSeries.irradiance} margin={CHART_MARGIN}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="time" tick={{ fontSize: 12, fontWeight: 700 }} tickFormatter={formatTimeLabel} />
                          <YAxis
                            tick={{ fontSize: 12, fontWeight: 700 }}
                            width={90}
                            label={{
                              value: "W/m²",
                              angle: -90,
                              position: "insideLeft",
                              offset: 14,
                            }}
                          />
                          <RechartsTooltip content={<DashboardTooltip labelFormatter={formatTimeLabel} note={weatherTooltipNote} valueFormatter={formatWeatherTooltipValue} />} />
                          {renderIrradianceLines()}
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                    <Box sx={{ height: 380, position: "relative", pb: 3 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          Temperature
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setFullscreen("weather-temperature")}
                          title="Full screen"
                          sx={{ ...FULLSCREEN_BTN_SX, top: "auto", bottom: 6, right: 6 }}
                        >
                          <FullscreenGlyph fontSize="small" />
                        </IconButton>
                      </Stack>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={weatherSeries.temperature} margin={CHART_MARGIN}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="time" tick={{ fontSize: 12, fontWeight: 700 }} tickFormatter={formatTimeLabel} />
                          <YAxis
                            tick={{ fontSize: 12, fontWeight: 700 }}
                            width={70}
                            label={{
                              value: "°C",
                              angle: -90,
                              position: "insideLeft",
                              offset: 12,
                            }}
                          />
                          <RechartsTooltip content={<DashboardTooltip labelFormatter={formatTimeLabel} note={weatherTooltipNote} valueFormatter={formatWeatherTooltipValue} />} />
                          {renderTemperatureLines()}
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                    <Box sx={{ height: 380, position: "relative", pb: 3 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          Humidity
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setFullscreen("weather-humidity")}
                          title="Full screen"
                          sx={{ ...FULLSCREEN_BTN_SX, top: "auto", bottom: 6, right: 6 }}
                        >
                          <FullscreenGlyph fontSize="small" />
                        </IconButton>
                      </Stack>
                      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={240}>
                        <LineChart data={weatherSeries.humidity} margin={CHART_MARGIN}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="time" tick={{ fontSize: 12, fontWeight: 700 }} tickFormatter={formatTimeLabel} />
                          <YAxis
                            tick={{ fontSize: 12, fontWeight: 700 }}
                            width={70}
                            label={{
                              value: "%",
                              angle: -90,
                              position: "insideLeft",
                              offset: 12,
                            }}
                          />
                          <RechartsTooltip content={<DashboardTooltip labelFormatter={formatTimeLabel} note={weatherTooltipNote} valueFormatter={formatWeatherTooltipValue} />} />
                          {renderHumidityLines()}
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                    <Box sx={{ height: 380, position: "relative", pb: 3 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          Wind speed
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setFullscreen("weather-wind")}
                          title="Full screen"
                          sx={{ ...FULLSCREEN_BTN_SX, top: "auto", bottom: 6, right: 6 }}
                        >
                          <FullscreenGlyph fontSize="small" />
                        </IconButton>
                      </Stack>
                      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={240}>
                        <LineChart data={weatherSeries.wind} margin={CHART_MARGIN}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="time" tick={{ fontSize: 12, fontWeight: 700 }} tickFormatter={formatTimeLabel} />
                          <YAxis
                            tick={{ fontSize: 12, fontWeight: 700 }}
                            width={80}
                            label={{
                              value: "m/s",
                              angle: -90,
                              position: "insideLeft",
                              offset: 14,
                            }}
                          />
                          <RechartsTooltip content={<DashboardTooltip labelFormatter={formatTimeLabel} note={weatherTooltipNote} valueFormatter={formatWeatherTooltipValue} />} />
                          {renderWindLines()}
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
              </Stack>
            )}
          </Paper>
          )}
          {weatherWorkspaceTab === "weather" && weatherAsset === "wind" && (
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
              <Stack spacing={1}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Weather Data — Wind
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Weather inputs used by the wind generation model
                  {windLocationActive ? " at the wind park location." : " at the project location."}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label={`Lat: ${weatherLat ?? "N/A"}`} />
                  <Chip label={`Lon: ${weatherLon ?? "N/A"}`} />
                  <Chip label={`Timezone: ${weatherTz ?? "N/A"}`} />
                </Stack>
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Mode:
                </Typography>
                <Stack direction="row" spacing={1}>
                  {WEATHER_MODE_OPTIONS.map((m) => (
                    <Chip
                      key={m.key}
                      label={m.label}
                      color={weatherMode === m.key ? "primary" : "default"}
                      variant={weatherMode === m.key ? "filled" : "outlined"}
                      onClick={() => handleWeatherModeChange(m.key)}
                      sx={{ fontWeight: 700 }}
                    />
                  ))}
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Date Interval:
                  </Typography>
                  <MaybeTooltip
                    arrow
                    placement="top"
                    title="Date range is inclusive of both start and end days."
                  >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </MaybeTooltip>
                </Stack>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => openRangePicker("weather", e)}
                  startIcon={<CalendarMonthIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                  endIcon={<ArrowDropDownIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                  sx={{ minWidth: 180, textTransform: "none" }}
                >
                  {rangeLabel("weather")}
                </Button>
              </Stack>
            </Stack>
            {windWeatherNotices.map((notice, idx) => (
              <Alert key={idx} severity={notice.severity} sx={{ mt: 2 }}>
                {notice.message}
              </Alert>
            ))}
            {windWeatherError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {windWeatherError}
              </Alert>
            )}
            {windWeatherLoading ? (
              <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} /> Loading weather data...
              </Box>
            ) : (
              <Stack spacing={3} sx={{ mt: 2 }}>
                <Box sx={{ height: 440, position: "relative", pb: 8 }}>
                  <Stack spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Wind speed
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Filter Data:
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: "2px", backgroundColor: "#2e7d32" }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          10 m
                        </Typography>
                        <Checkbox
                          size="small"
                          checked={windWeatherVisible.ten}
                          onChange={(e) =>
                            setWindWeatherVisible((prev) => ({ ...prev, ten: e.target.checked }))
                          }
                          sx={{ ml: 0.5 }}
                        />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: "2px", backgroundColor: "#1565c0" }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {windWeatherUpperLabel ? `Upper (${windWeatherUpperLabel})` : "Upper level"}
                        </Typography>
                        <Checkbox
                          size="small"
                          checked={windWeatherVisible.upper}
                          onChange={(e) =>
                            setWindWeatherVisible((prev) => ({ ...prev, upper: e.target.checked }))
                          }
                          sx={{ ml: 0.5 }}
                        />
                      </Box>
                    </Stack>
                  </Stack>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={windWeatherSeries} margin={CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" tick={{ fontSize: 12, fontWeight: 700 }} tickFormatter={formatTimeLabel} />
                      <YAxis
                        tick={{ fontSize: 12, fontWeight: 700 }}
                        width={80}
                        label={{ value: "m/s", angle: -90, position: "insideLeft", offset: 14 }}
                      />
                      <RechartsTooltip content={<DashboardTooltip labelFormatter={formatTimeLabel} note={weatherTooltipNote} valueFormatter={formatWeatherTooltipValue} />} />
                      {renderWindAssetSpeedLines()}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ height: 380, position: "relative", pb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Temperature
                  </Typography>
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={240}>
                    <LineChart data={windWeatherSeries} margin={CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" tick={{ fontSize: 12, fontWeight: 700 }} tickFormatter={formatTimeLabel} />
                      <YAxis
                        tick={{ fontSize: 12, fontWeight: 700 }}
                        width={70}
                        label={{ value: "°C", angle: -90, position: "insideLeft", offset: 12 }}
                      />
                      <RechartsTooltip content={<DashboardTooltip labelFormatter={formatTimeLabel} note={weatherTooltipNote} valueFormatter={formatWeatherTooltipValue} />} />
                      {renderWindAssetTempLines()}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ height: 380, position: "relative", pb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Surface pressure
                  </Typography>
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={240}>
                    <LineChart data={windWeatherSeries} margin={CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" tick={{ fontSize: 12, fontWeight: 700 }} tickFormatter={formatTimeLabel} />
                      <YAxis
                        tick={{ fontSize: 12, fontWeight: 700 }}
                        width={80}
                        domain={["auto", "auto"]}
                        label={{ value: "hPa", angle: -90, position: "insideLeft", offset: 14 }}
                      />
                      <RechartsTooltip content={<DashboardTooltip labelFormatter={formatTimeLabel} note={weatherTooltipNote} valueFormatter={formatWeatherTooltipValue} />} />
                      {renderWindAssetPressureLines()}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Stack>
            )}
          </Paper>
          )}
          {weatherWorkspaceTab === "generation" && weatherAsset === "pv" && (
          <Stack spacing={3}>
            {!projectHasPv ? (
              <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                <Alert severity="info">
                  This project does not include a PV system. PV generation estimation is
                  only available for projects with a PV asset (Hybrid PV or Hybrid PV +
                  Wind).
                </Alert>
              </Paper>
            ) : (
              <>
            <PvGenerationPanel
              projectId={currentProject?.id}
              runType={pvGenRunType}
              onRunTypeChange={setPvGenRunType}
              onOpenRangePicker={(e) => openRangePicker("pvGenTrigger", e)}
              rangeLabelNode={rangeLabel("pvGenTrigger")}
              onTrigger={handlePvGenTrigger}
              triggering={pvGenTriggering}
              triggerError={pvGenTriggerError}
              runs={pvGenRuns}
              runsLoading={pvGenRunsLoading}
              runsError={pvGenRunsError}
              selectedRunIds={pvGenSelectedRunIds}
              onToggleRun={handlePvGenRunToggle}
              seriesByRun={pvGenSeriesByRun}
              seriesLoadingIds={pvGenSeriesLoadingIds}
              historicalRun={pvGenHistoricalRun}
              forecastRuns={pvGenForecastRuns}
              measuredSeries={pvGenMeasuredSeries}
              measuredAvailable={pvGenIncludeMeasured && pvGenMeasuredAvailable}
              measuredLoading={pvGenMeasuredLoading}
              measuredError={pvGenMeasuredError}
              onExport={handlePvGenExport}
              onExportRun={handlePvGenExportRun}
              onRerun={handlePvGenRerun}
              exporting={pvGenExporting}
              exportError={pvGenExportError}
              onFullscreen={() => setFullscreen("pv-generation")}
            />
            <ForecastConvergencePanel
              targetDate={pvGenConvergenceDate}
              onTargetDateChange={setPvGenConvergenceDate}
              runs={pvGenConvergenceRuns}
              seriesByRun={pvGenSeriesByRun}
              seriesLoadingIds={pvGenSeriesLoadingIds}
              actualRun={pvGenConvergenceActualRun}
              measured={pvGenConvergenceMeasured}
              measuredLoading={pvGenConvergenceMeasuredLoading}
              measuredError={pvGenConvergenceMeasuredError}
            />
              </>
            )}
          </Stack>
          )}
          {weatherWorkspaceTab === "generation" && weatherAsset === "wind" && (
          <Stack spacing={3}>
            {windGenerationDisabled ? (
              <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                <Alert severity="info">{windGenerationDisabledMessage}</Alert>
              </Paper>
            ) : !windConfigEnabled ? (
              <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                <Alert severity="info">
                  This project does not include a wind park. Enable wind generation in the
                  Project page and configure the park in the Wind System page to estimate
                  wind production.
                </Alert>
              </Paper>
            ) : (
              <WindGenerationPanel
                projectId={currentProject?.id}
                accessToken={accessToken}
                timeZone={weatherTz}
                windEnabled={windConfigEnabled}
                runType={windGenRunType}
                onRunTypeChange={setWindGenRunType}
                onOpenRangePicker={(e) => openRangePicker("windGenTrigger", e)}
                rangeLabelNode={rangeLabel("windGenTrigger")}
                triggerRange={marketFilters.windGenTrigger || { start: "", end: "" }}
              />
            )}
          </Stack>
          )}
        </Stack>
          )}
        </Stack>
      </Box>
      {marketStrategyDisabled && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
            zIndex: 2,
          }}
        >
          <Alert
            severity="warning"
            sx={{
              maxWidth: 720,
              width: "100%",
              boxShadow: 6,
              alignItems: "flex-start",
              backdropFilter: "blur(10px)",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              Market Strategy is unavailable for bidding zone {projectBiddingZone}.
            </Typography>
            <Typography variant="body2">
              The selected bidding zone is not supported for the Market Strategy features.
              Supported bidding zones: {supportedMarketsLabel}.
            </Typography>
          </Alert>
        </Box>
      )}
      <Snackbar
        open={bidScheduleNoticeOpen}
        autoHideDuration={4000}
        onClose={handleBidScheduleNoticeClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleBidScheduleNoticeClose}
          severity={bidScheduleNoticeSeverity}
          variant="filled"
          sx={{ width: "100%", boxShadow: 6, alignItems: "center" }}
        >
          {bidScheduleNotice}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function formatDateTimeForInput(value) {
  const date =
    value instanceof Date ? value : value ? parseWallClockTimestamp(value) : null;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}, ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

function formatMtuRange(startValue, endValue) {
  const start = formatDateTimeForInput(startValue);
  const end = formatDateTimeForInput(endValue);
  if (!start && !end) return "";
  if (!end) return start;
  if (!start) return end;
  return `${start} - ${end}`;
}

function getDefaultBidStrategySettings() {
  return {
    sellForecastBasis: "P50",
    buyForecastBasis: "P50",
    marginType: "absolute",
    sellMargin: 5,
    buyMargin: 5,
    minSellPriceEurPerMwh: null,
    maxSellPriceEurPerMwh: null,
    minBuyPriceEurPerMwh: null,
    maxBuyPriceEurPerMwh: null,
    minBidQuantityMw: 0.01,
    quantityStepMw: 0.01,
    priceTickEurPerMwh: 0.1,
    degradationMethod: "discharge_only",
    degradationCostEurPerMwh: 0,
    marketEconomics: getDefaultMarketEconomicsSettings(),
  };
}

function getDefaultMarketEconomicsSettings() {
  return {
    scope: "DAM_ONLY",
    settlementFrequency: "monthly",
    damFees: {
      tradingFeeEurPerMWh: 0.054,
      clearingFeeEurPerMWh: 0.019,
      applyTo: "bought_plus_sold_mwh",
    },
    brpAggregator: {
      enabled: false,
      feeType: "none",
      value: 0,
    },
    fixedMarketCosts: {
      enabled: false,
      annualMembershipCostEur: 0,
      clearingAccountMonthlyCostEur: 0,
      marketDataMonthlyCostEur: 0,
    },
    collateralCost: {
      enabled: false,
      collateralAmountEur: 0,
      annualCostOfCapitalPct: 0,
    },
  };
}

function normalizeDegradationMethod(value, fallback = "discharge_only") {
  const raw = String(value || fallback || "").toLowerCase();
  return raw === "throughput" ? "throughput" : "discharge_only";
}

function normalizeBidStrategySettings(settings = {}) {
  const defaults = getDefaultBidStrategySettings();
  const marketEconomicsDefaults = getDefaultMarketEconomicsSettings();
  const normalizeForecastBasis = (value, fallback) => {
    const raw = String(value || fallback || "").toUpperCase();
    return ["P10", "P50", "P90"].includes(raw) ? raw : fallback;
  };
  const normalizeMarginType = (value) => {
    const raw = String(value || "").toLowerCase();
    return raw === "percentage" ? "percent" : raw === "percent" ? "percent" : "absolute";
  };
  const normalizeFeeType = (value, fallback = "none") => {
    const raw = String(value || fallback || "").toLowerCase();
    return [
      "none",
      "eur_per_mwh_traded",
      "percent_of_gross_margin",
      "fixed_monthly",
    ].includes(raw)
      ? raw
      : fallback;
  };
  const normalizeBoolean = (value, fallback = false) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      const raw = value.trim().toLowerCase();
      if (raw === "true") return true;
      if (raw === "false") return false;
    }
    if (typeof value === "number") return value !== 0;
    return fallback;
  };
  const providedMarketEconomics =
    settings.marketEconomics && typeof settings.marketEconomics === "object"
      ? settings.marketEconomics
      : settings.market_economics && typeof settings.market_economics === "object"
        ? settings.market_economics
        : null;
  const providedDamFees =
    providedMarketEconomics?.damFees && typeof providedMarketEconomics.damFees === "object"
      ? providedMarketEconomics.damFees
      : providedMarketEconomics?.dam_fees && typeof providedMarketEconomics.dam_fees === "object"
        ? providedMarketEconomics.dam_fees
        : null;
  const legacyMarketFee =
    toNullableNumber(
      settings.marketFeeEurPerMwh ??
        settings.market_fee_eur_per_mwh ??
        settings.marketFee ??
        settings.market_fee ??
        null
    ) ?? marketEconomicsDefaults.damFees.tradingFeeEurPerMWh;
  const normalized = {
    sellForecastBasis: normalizeForecastBasis(
      settings.sellForecastBasis ?? settings.sell_forecast_basis,
      defaults.sellForecastBasis
    ),
    buyForecastBasis: normalizeForecastBasis(
      settings.buyForecastBasis ?? settings.buy_forecast_basis,
      defaults.buyForecastBasis
    ),
    marginType: normalizeMarginType(settings.marginType ?? settings.margin_type ?? defaults.marginType),
    sellMargin: settings.sellMargin ?? settings.sell_margin ?? defaults.sellMargin,
    buyMargin:
      settings.buyMargin ??
      (settings.buy_margin != null ? Math.abs(Number(settings.buy_margin)) : undefined) ??
      defaults.buyMargin,
    minSellPriceEurPerMwh:
      toNullableNumber(
        settings.minSellPriceEurPerMwh ?? settings.min_sell_price_eur_per_mwh ?? null
      ) ?? defaults.minSellPriceEurPerMwh,
    maxSellPriceEurPerMwh:
      toNullableNumber(
        settings.maxSellPriceEurPerMwh ?? settings.max_sell_price_eur_per_mwh ?? null
      ) ?? defaults.maxSellPriceEurPerMwh,
    minBuyPriceEurPerMwh:
      toNullableNumber(settings.minBuyPriceEurPerMwh ?? settings.min_buy_price_eur_per_mwh ?? null) ??
      defaults.minBuyPriceEurPerMwh,
    maxBuyPriceEurPerMwh:
      toNullableNumber(settings.maxBuyPriceEurPerMwh ?? settings.max_buy_price_eur_per_mwh ?? null) ??
      defaults.maxBuyPriceEurPerMwh,
    minBidQuantityMw: settings.minBidQuantityMw ?? defaults.minBidQuantityMw,
    quantityStepMw: settings.quantityStepMw ?? defaults.quantityStepMw,
    priceTickEurPerMwh: settings.priceTickEurPerMwh ?? defaults.priceTickEurPerMwh,
    degradationMethod: normalizeDegradationMethod(
      settings.degradationMethod ?? settings.degradation_method,
      defaults.degradationMethod
    ),
    degradationCostEurPerMwh:
      toNullableNumber(
        settings.degradationCostEurPerMwh ?? settings.degradation_cost_eur_per_mwh ?? null
      ) ?? defaults.degradationCostEurPerMwh,
    marketEconomics: {
      scope: "DAM_ONLY",
      settlementFrequency: "monthly",
      damFees: {
        tradingFeeEurPerMWh:
          toNullableNumber(
            providedDamFees?.tradingFeeEurPerMWh ??
              providedDamFees?.trading_fee_eur_per_mwh ??
              providedDamFees?.tradingFee ??
              null
          ) ?? legacyMarketFee,
        clearingFeeEurPerMWh:
          toNullableNumber(
            providedDamFees?.clearingFeeEurPerMWh ??
              providedDamFees?.clearing_fee_eur_per_mwh ??
              providedDamFees?.clearingFee ??
              null
          ) ?? marketEconomicsDefaults.damFees.clearingFeeEurPerMWh,
        applyTo: "bought_plus_sold_mwh",
      },
      brpAggregator: {
        enabled: normalizeBoolean(
          providedMarketEconomics?.brpAggregator?.enabled ??
            providedMarketEconomics?.brp_aggregator?.enabled,
          marketEconomicsDefaults.brpAggregator.enabled
        ),
        feeType: normalizeFeeType(
          providedMarketEconomics?.brpAggregator?.feeType ??
            providedMarketEconomics?.brpAggregator?.fee_type ??
            providedMarketEconomics?.brp_aggregator?.feeType ??
            providedMarketEconomics?.brp_aggregator?.fee_type,
          marketEconomicsDefaults.brpAggregator.feeType
        ),
        value:
          toNullableNumber(
            providedMarketEconomics?.brpAggregator?.value ??
              providedMarketEconomics?.brp_aggregator?.value ??
              null
          ) ?? marketEconomicsDefaults.brpAggregator.value,
      },
      fixedMarketCosts: {
        enabled: normalizeBoolean(
          providedMarketEconomics?.fixedMarketCosts?.enabled ??
            providedMarketEconomics?.fixed_market_costs?.enabled,
          marketEconomicsDefaults.fixedMarketCosts.enabled
        ),
        annualMembershipCostEur:
          toNullableNumber(
            providedMarketEconomics?.fixedMarketCosts?.annualMembershipCostEur ??
              providedMarketEconomics?.fixedMarketCosts?.annual_membership_cost_eur ??
              providedMarketEconomics?.fixed_market_costs?.annualMembershipCostEur ??
              providedMarketEconomics?.fixed_market_costs?.annual_membership_cost_eur ??
              null
          ) ?? marketEconomicsDefaults.fixedMarketCosts.annualMembershipCostEur,
        clearingAccountMonthlyCostEur:
          toNullableNumber(
            providedMarketEconomics?.fixedMarketCosts?.clearingAccountMonthlyCostEur ??
              providedMarketEconomics?.fixedMarketCosts?.clearing_account_monthly_cost_eur ??
              providedMarketEconomics?.fixed_market_costs?.clearingAccountMonthlyCostEur ??
              providedMarketEconomics?.fixed_market_costs?.clearing_account_monthly_cost_eur ??
              null
          ) ?? marketEconomicsDefaults.fixedMarketCosts.clearingAccountMonthlyCostEur,
        marketDataMonthlyCostEur:
          toNullableNumber(
            providedMarketEconomics?.fixedMarketCosts?.marketDataMonthlyCostEur ??
              providedMarketEconomics?.fixedMarketCosts?.market_data_monthly_cost_eur ??
              providedMarketEconomics?.fixed_market_costs?.marketDataMonthlyCostEur ??
              providedMarketEconomics?.fixed_market_costs?.market_data_monthly_cost_eur ??
              null
          ) ?? marketEconomicsDefaults.fixedMarketCosts.marketDataMonthlyCostEur,
      },
      collateralCost: {
        enabled: normalizeBoolean(
          providedMarketEconomics?.collateralCost?.enabled ??
            providedMarketEconomics?.collateral_cost?.enabled,
          marketEconomicsDefaults.collateralCost.enabled
        ),
        collateralAmountEur:
          toNullableNumber(
            providedMarketEconomics?.collateralCost?.collateralAmountEur ??
              providedMarketEconomics?.collateralCost?.collateral_amount_eur ??
              providedMarketEconomics?.collateral_cost?.collateralAmountEur ??
              providedMarketEconomics?.collateral_cost?.collateral_amount_eur ??
              null
          ) ?? marketEconomicsDefaults.collateralCost.collateralAmountEur,
        annualCostOfCapitalPct:
          toNullableNumber(
            providedMarketEconomics?.collateralCost?.annualCostOfCapitalPct ??
              providedMarketEconomics?.collateralCost?.annual_cost_of_capital_pct ??
              providedMarketEconomics?.collateral_cost?.annualCostOfCapitalPct ??
              providedMarketEconomics?.collateral_cost?.annual_cost_of_capital_pct ??
              null
          ) ?? marketEconomicsDefaults.collateralCost.annualCostOfCapitalPct,
      },
    },
  };
  return normalized;
}

function toNullableNumber(value) {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : NaN;
}

function isNullableBidPriceBoundField(field) {
  return (
    field === "minSellPriceEurPerMwh" ||
    field === "maxSellPriceEurPerMwh" ||
    field === "minBuyPriceEurPerMwh" ||
    field === "maxBuyPriceEurPerMwh"
  );
}

function formatPriceBoundSummary(minValue, maxValue) {
  const formatBound = (value, fallback) =>
    value == null || value === "" || Number.isNaN(Number(value))
      ? fallback
      : Number(value).toFixed(2);
  return `${formatBound(minValue, "No minimum")} -> ${formatBound(maxValue, "No maximum")}`;
}

function getBidPowerMw(row) {
  if (row?.quantity_mw != null && Number.isFinite(Number(row.quantity_mw))) {
    return Number(row.quantity_mw);
  }
  if (row?.power_mw != null && Number.isFinite(Number(row.power_mw))) {
    return Number(row.power_mw);
  }
  if (row?.power_kw != null && Number.isFinite(Number(row.power_kw))) {
    return Number(row.power_kw) / 1000;
  }
  return 0;
}

function updateBidRowValue(row, field, value) {
  const next = { ...row, [field]: value };
  if (field === "side" && value === "idle") {
    next.quantity_mw = 0;
    next.power_mw = 0;
    next.power_kw = 0;
  }
  if (field === "quantity_mw" || field === "power_mw" || field === "power_kw" || field === "interval_minutes") {
    const powerMw = getBidPowerMw(next);
    next.quantity_mw = powerMw;
    next.power_mw = powerMw;
    next.power_kw = Number((powerMw * 1000).toFixed(3));
    const minutes = Number(next.interval_minutes || 0);
    next.energy_mwh =
      Number.isFinite(powerMw) && Number.isFinite(minutes)
        ? Number((powerMw * (minutes / 60)).toFixed(6))
        : 0;
  }
  if (field === "side" && value === "idle") {
    next.energy_mwh = 0;
  }
  return next;
}

function getBidSideMeta(side) {
  switch (String(side || "idle").toLowerCase()) {
    case "sell":
      return {
        label: "SELL",
        textColor: "#166534",
        backgroundColor: "rgba(220,252,231,0.9)",
        borderColor: "rgba(34,197,94,0.38)",
      };
    case "buy":
      return {
        label: "BUY",
        textColor: "#b45309",
        backgroundColor: "rgba(254,243,199,0.95)",
        borderColor: "rgba(245,158,11,0.38)",
      };
    case "idle":
    default:
      return {
        label: "IDLE",
        textColor: "#334155",
        backgroundColor: "rgba(226,232,240,0.9)",
        borderColor: "rgba(100,116,139,0.3)",
      };
  }
}

function normalizeBidRowStatus(row) {
  if (String(row?.side || "").trim().toLowerCase() === "idle") {
    return "idle";
  }
  const raw =
    row?.bid_status ??
    row?.status ??
    row?.execution_status ??
    row?.acceptance_status ??
    row?.market_status ??
    row?.clearing_status ??
    row?.result_status ??
    "";
  const value = String(raw || "").trim().toLowerCase();
  if (!value) {
    return String(row?.side || "").toLowerCase() === "idle" ? "idle" : "pending";
  }
  if (["accepted", "accept", "cleared", "filled", "matched", "executed"].includes(value)) {
    return "accepted";
  }
  if (["rejected", "reject", "declined", "failed", "cancelled", "canceled"].includes(value)) {
    return "rejected";
  }
  if (["idle", "none", "not_applicable", "not-applicable", "n/a"].includes(value)) {
    return "idle";
  }
  if (["pending", "submitted", "open", "queued", "processing"].includes(value)) {
    return "pending";
  }
  return value;
}

function getBidStatusMeta(status) {
  switch (normalizeBidRowStatus({ status })) {
    case "accepted":
      return {
        label: "ACCEPTED",
        textColor: "#16a34a",
        backgroundColor: "rgba(220,252,231,0.95)",
        borderColor: "rgba(34,197,94,0.38)",
      };
    case "rejected":
      return {
        label: "REJECTED",
        textColor: "#dc2626",
        backgroundColor: "rgba(254,226,226,0.95)",
        borderColor: "rgba(239,68,68,0.38)",
      };
    case "idle":
      return {
        label: "IDLE",
        textColor: "#64748b",
        backgroundColor: "rgba(226,232,240,0.9)",
        borderColor: "rgba(100,116,139,0.3)",
      };
    case "pending":
    default:
      return {
        label: "PENDING",
        textColor: "#d97706",
        backgroundColor: "rgba(254,243,199,0.95)",
        borderColor: "rgba(245,158,11,0.38)",
      };
  }
}

function getMtuCountColor(label, value) {
  if (Number(value) === 0) return null;
  switch (label) {
    case "Accepted MTUs":
      return "#16a34a";
    case "Rejected MTUs":
      return "#dc2626";
    case "Pending MTUs":
      return "#f59e0b";
    default:
      return null;
  }
}

function getBidClearedPrice(row) {
  const value =
    row?.cleared_price ??
    row?.clearing_price ??
    row?.clearedPrice ??
    row?.market_clearing_price ??
    row?.market_price ??
    row?.actual_price ??
    row?.settled_price ??
    null;
  return value != null && Number.isFinite(Number(value)) ? Number(value) : null;
}

function getBidActualSoc(row) {
  const value = row?.actual_soc ?? row?.actualSoC ?? row?.soc_actual ?? row?.realized_soc ?? null;
  return value != null && Number.isFinite(Number(value)) ? Number(value) : null;
}

function summarizeBidScheduleEconomics(rowsInput = [], fallbackBidDay = "") {
  const rows = (Array.isArray(rowsInput) ? rowsInput : []).map((row) => {
    const side = String(row?.side || "idle").toLowerCase();
    const status = normalizeBidRowStatus(row);
    const energyMWh = Number(row?.energy_mwh || 0);
    const bidPrice = Number.isFinite(Number(row?.bid_price)) ? Number(row.bid_price) : null;
    const expectedPrice =
      row?.expected_price != null && Number.isFinite(Number(row.expected_price))
        ? Number(row.expected_price)
        : null;
    const clearedPrice = getBidClearedPrice(row);
    const actualSoc = getBidActualSoc(row);
    return {
      ...row,
      side,
      status,
      energyMWh,
      bidPrice,
      expectedPrice,
      clearedPrice,
      actualSoc,
    };
  });

  const summary = {
    totalRows: rows.length,
    acceptedCount: 0,
    pendingCount: 0,
    rejectedCount: 0,
    idleCount: 0,
    pricedAcceptedCount: 0,
    acceptedSellEnergyMWh: 0,
    acceptedBuyEnergyMWh: 0,
    grossSellRevenue: 0,
    grossBuyCost: 0,
    avgAcceptedSellPrice: null,
    avgAcceptedBuyPrice: null,
    avgBidSellPrice: null,
    avgBidBuyPrice: null,
    avgExpectedSellPrice: null,
    avgExpectedBuyPrice: null,
    actualSocEnd: null,
    targetSocEnd: null,
    daily: {},
  };
  let acceptedSellPriceWeighted = 0;
  let acceptedBuyPriceWeighted = 0;
  let bidSellWeighted = 0;
  let bidBuyWeighted = 0;
  let expectedSellWeighted = 0;
  let expectedBuyWeighted = 0;
  let bidSellEnergy = 0;
  let bidBuyEnergy = 0;
  let expectedSellEnergy = 0;
  let expectedBuyEnergy = 0;

  rows.forEach((row) => {
    if (row.status === "accepted") summary.acceptedCount += 1;
    else if (row.status === "rejected") summary.rejectedCount += 1;
    else if (row.status === "idle") summary.idleCount += 1;
    else summary.pendingCount += 1;

    const dayKey = String(row?.interval_start || row?.time || "").slice(0, 10) || fallbackBidDay || "N/A";
    const bucket = summary.daily[dayKey] || {
      day: dayKey,
      acceptedSellEnergyMWh: 0,
      acceptedBuyEnergyMWh: 0,
      grossSellRevenue: 0,
      grossBuyCost: 0,
      acceptedCount: 0,
      rejectedCount: 0,
      pendingCount: 0,
    };

    if (row.status === "accepted") {
      bucket.acceptedCount += 1;
      if (row.clearedPrice != null) summary.pricedAcceptedCount += 1;
    } else if (row.status === "rejected") {
      bucket.rejectedCount += 1;
    } else if (row.status !== "idle") {
      bucket.pendingCount += 1;
    }

    if (row.side === "sell") {
      if (row.bidPrice != null) {
        bidSellWeighted += row.energyMWh * row.bidPrice;
        bidSellEnergy += row.energyMWh;
      }
      if (row.expectedPrice != null) {
        expectedSellWeighted += row.energyMWh * row.expectedPrice;
        expectedSellEnergy += row.energyMWh;
      }
      if (row.status === "accepted" && row.clearedPrice != null) {
        summary.acceptedSellEnergyMWh += row.energyMWh;
        acceptedSellPriceWeighted += row.energyMWh * row.clearedPrice;
        summary.grossSellRevenue += row.energyMWh * row.clearedPrice;
        bucket.acceptedSellEnergyMWh += row.energyMWh;
        bucket.grossSellRevenue += row.energyMWh * row.clearedPrice;
      }
    } else if (row.side === "buy") {
      if (row.bidPrice != null) {
        bidBuyWeighted += row.energyMWh * row.bidPrice;
        bidBuyEnergy += row.energyMWh;
      }
      if (row.expectedPrice != null) {
        expectedBuyWeighted += row.energyMWh * row.expectedPrice;
        expectedBuyEnergy += row.energyMWh;
      }
      if (row.status === "accepted" && row.clearedPrice != null) {
        summary.acceptedBuyEnergyMWh += row.energyMWh;
        acceptedBuyPriceWeighted += row.energyMWh * row.clearedPrice;
        summary.grossBuyCost += row.energyMWh * row.clearedPrice;
        bucket.acceptedBuyEnergyMWh += row.energyMWh;
        bucket.grossBuyCost += row.energyMWh * row.clearedPrice;
      }
    }

    summary.daily[dayKey] = bucket;
  });

  summary.avgAcceptedSellPrice =
    summary.acceptedSellEnergyMWh > 0 ? acceptedSellPriceWeighted / summary.acceptedSellEnergyMWh : null;
  summary.avgAcceptedBuyPrice =
    summary.acceptedBuyEnergyMWh > 0 ? acceptedBuyPriceWeighted / summary.acceptedBuyEnergyMWh : null;
  summary.avgBidSellPrice = bidSellEnergy > 0 ? bidSellWeighted / bidSellEnergy : null;
  summary.avgBidBuyPrice = bidBuyEnergy > 0 ? bidBuyWeighted / bidBuyEnergy : null;
  summary.avgExpectedSellPrice = expectedSellEnergy > 0 ? expectedSellWeighted / expectedSellEnergy : null;
  summary.avgExpectedBuyPrice = expectedBuyEnergy > 0 ? expectedBuyWeighted / expectedBuyEnergy : null;
  summary.netSettledRevenue = summary.grossSellRevenue - summary.grossBuyCost;

  const rowsWithActualSoc = rows.filter((row) => row.actualSoc != null);
  if (rowsWithActualSoc.length) {
    summary.actualSocEnd = rowsWithActualSoc[rowsWithActualSoc.length - 1].actualSoc;
  }
  const rowsWithTargetSoc = rows.filter((row) => row?.target_soc != null);
  if (rowsWithTargetSoc.length) {
    summary.targetSocEnd = Number(rowsWithTargetSoc[rowsWithTargetSoc.length - 1].target_soc);
  }
  summary.dailyRows = Object.values(summary.daily).sort((a, b) => String(a.day).localeCompare(String(b.day)));
  return summary;
}

const STRATEGY_READONLY_FIELD_SX = {
  "& .MuiInputBase-root.Mui-disabled": {
    color: "text.primary",
    WebkitTextFillColor: (theme) =>
      theme.palette.mode === "dark" ? "rgba(226,232,240,0.94)" : "rgba(15,23,42,0.92)",
    backgroundColor: (theme) =>
      theme.palette.mode === "dark" ? "rgba(7, 31, 24, 0.72)" : "#fff",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "text.primary",
    WebkitTextFillColor: (theme) =>
      theme.palette.mode === "dark" ? "rgba(226,232,240,0.94)" : "rgba(15,23,42,0.92)",
    opacity: 1,
  },
  "& .MuiSelect-select.Mui-disabled": {
    color: "text.primary",
    WebkitTextFillColor: (theme) =>
      theme.palette.mode === "dark" ? "rgba(226,232,240,0.94)" : "rgba(15,23,42,0.92)",
    opacity: 1,
  },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: (theme) =>
      theme.palette.mode === "dark" ? "rgba(153,246,228,0.9)" : "rgba(30,64,175,0.9)",
  },
  "& .MuiSvgIcon-root.Mui-disabled": {
    color: (theme) =>
      theme.palette.mode === "dark" ? "rgba(203,213,225,0.8)" : "rgba(100,116,139,0.92)",
  },
  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: (theme) =>
      theme.palette.mode === "dark" ? "rgba(148,163,184,0.28)" : "rgba(148,163,184,0.45)",
  },
};

const STRATEGY_PANEL_SX = {
  p: { xs: 1.15, md: 1.25 },
  height: "100%",
  borderRadius: 2,
  border: "1px solid",
  borderColor: (theme) =>
    theme.palette.mode === "dark" ? "rgba(94, 234, 212, 0.16)" : "divider",
  backgroundColor: (theme) =>
    theme.palette.mode === "dark" ? "rgba(8, 35, 27, 0.62)" : "rgba(248,250,252,0.78)",
  "& .MuiTypography-subtitle2": {
    color: "text.primary",
  },
  "& .MuiFormHelperText-root, & .MuiTypography-caption": {
    color: "text.secondary",
    lineHeight: 1.2,
    fontSize: "0.72rem",
  },
  "& .MuiFormHelperText-root, & .MuiTypography-caption:not(.MuiTypography-subtitle2)": {
    marginTop: 1,
  },
  "& .MuiInputBase-root": {
    backgroundColor: (theme) =>
      theme.palette.mode === "dark" ? "rgba(7, 31, 24, 0.72)" : "#fff",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: (theme) =>
      theme.palette.mode === "dark" ? "rgba(94, 234, 212, 0.22)" : "rgba(148,163,184,0.45)",
  },
  "& .MuiInputLabel-root": {
    color: (theme) =>
      theme.palette.mode === "dark" ? "rgba(153, 246, 228, 0.9)" : "rgba(30,64,175,0.9)",
  },
  "& .MuiInputBase-input, & .MuiSelect-select": {
    color: "text.primary",
  },
};

const STRATEGY_BOUNDS_CHIP_SX = {
  height: 32,
  color: "text.secondary",
  borderColor: (theme) =>
    theme.palette.mode === "dark" ? "rgba(94,234,212,0.28)" : "rgba(100,116,139,0.35)",
  backgroundColor: (theme) =>
    theme.palette.mode === "dark" ? "rgba(7,31,24,0.5)" : "rgba(255,255,255,0.36)",
  "& .MuiChip-label": {
    fontWeight: 700,
    px: 1.1,
    fontSize: "0.8rem",
  },
};
